// src/document-contexts/helpers.ts
function flattenSchemaPaths(schema, prefix = "") {
  const paths = [];
  for (const node of schema) {
    const currentPath = prefix ? `${prefix}.${node.key}` : node.key;
    if (node.kind === "object" && "children" in node && node.children) {
      paths.push(...flattenSchemaPaths(node.children, currentPath));
    } else {
      paths.push(currentPath);
    }
  }
  return paths;
}
function schemaToSnapshot(schema) {
  return schema;
}

// src/document-contexts/patient-facility-agreement/schema.ts
var PATIENT_FACILITY_AGREEMENT_SCHEMA = [
  {
    key: "user",
    kind: "object",
    nullable: false,
    children: [
      { key: "firstName", kind: "string" },
      { key: "lastName", kind: "string" },
      { key: "fullName", kind: "string" },
      { key: "email", kind: "string" },
      { key: "phone", kind: "string", nullable: true },
      { key: "dateOfBirth", kind: "date", nullable: true },
      { key: "gender", kind: "string", nullable: true },
      { key: "countryOfOrigin", kind: "string", nullable: true },
      { key: "preferredContactLanguage", kind: "string", nullable: true },
      { key: "timeZone", kind: "string", nullable: true }
    ]
  },
  {
    key: "order",
    kind: "object",
    nullable: false,
    children: [
      { key: "orderNumber", kind: "string" },
      { key: "date", kind: "date" },
      { key: "price", kind: "string" },
      { key: "preferredDatesFrom", kind: "date", nullable: true },
      { key: "preferredDatesTo", kind: "date", nullable: true },
      { key: "requiresConsultation", kind: "boolean" },
      { key: "message", kind: "string", nullable: true },
      {
        key: "agent",
        kind: "object",
        nullable: true,
        children: [
          { key: "firstName", kind: "string" },
          { key: "lastName", kind: "string" },
          { key: "email", kind: "string" },
          { key: "phone", kind: "string", nullable: true }
        ]
      }
    ]
  },
  {
    key: "offer",
    kind: "object",
    nullable: true,
    children: [
      { key: "id", kind: "string" },
      { key: "title", kind: "string" },
      { key: "price", kind: "string" },
      { key: "startDate", kind: "date", nullable: true },
      { key: "endDate", kind: "date", nullable: true },
      { key: "createdAt", kind: "date" }
    ]
  },
  {
    key: "place",
    kind: "object",
    nullable: true,
    children: [
      { key: "id", kind: "string" },
      { key: "name", kind: "string" },
      { key: "email", kind: "string", nullable: true },
      { key: "phone", kind: "string", nullable: true },
      { key: "type", kind: "string" },
      { key: "requiresConsultation", kind: "boolean" },
      {
        key: "address",
        kind: "object",
        nullable: true,
        children: [
          { key: "street", kind: "string", nullable: true },
          { key: "postalCode", kind: "string", nullable: true },
          { key: "city", kind: "string", nullable: true },
          { key: "country", kind: "string", nullable: true },
          { key: "province", kind: "string", nullable: true }
        ]
      }
    ]
  }
];

// src/document-contexts/patient-facility-agreement/paths.ts
var PATIENT_FACILITY_AGREEMENT_PATHS = flattenSchemaPaths(PATIENT_FACILITY_AGREEMENT_SCHEMA);

// src/document-contexts/registry.ts
var DOCUMENT_CONTEXT_REGISTRY = {
  patient_facility_agreement: {
    schema: PATIENT_FACILITY_AGREEMENT_SCHEMA,
    paths: PATIENT_FACILITY_AGREEMENT_PATHS
  }
};
function getContextSchema(templateType) {
  return DOCUMENT_CONTEXT_REGISTRY[templateType]?.schema ?? [];
}
function getContextPaths(templateType) {
  return DOCUMENT_CONTEXT_REGISTRY[templateType]?.paths ?? [];
}

// src/options-source.ts
var OPTIONS_SOURCES = [
  "countries",
  "languages",
  "airports",
  "accommodationCategories",
  "genders"
];

// src/languages.ts
var SUPPORTED_LANGUAGES = [
  { code: "pl", name: "Polish", nativeName: "Polski" },
  { code: "en", name: "English", nativeName: "English" }
];
var LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);
var LANGUAGE_CODES_SET = new Set(LANGUAGE_CODES);

// src/airports.ts
var AIRPORTS = [
  { code: "AAA", name: "Anaa Airport", country: "PF", size: "medium" },
  { code: "AAC", name: "El Arish International Airport", country: "EG", size: "large" },
  { code: "AAE", name: "Annaba Rabah Bitat Airport", country: "DZ", size: "large" },
  { code: "AAK", name: "Buariki \u2013 Aranuka Airport", country: "KI", size: "small" },
  { code: "AAL", name: "Aalborg Airport", country: "DK", size: "large" },
  { code: "AAN", name: "Al Ain International Airport", country: "AE", size: "large" },
  {
    code: "AAP",
    name: "Samarinda \u2013 Aji Pangeran Tumenggung Pranoto International Airport",
    country: "ID",
    size: "medium"
  },
  { code: "AAR", name: "Aarhus Airport", country: "DK", size: "large" },
  { code: "AAT", name: "Altay Xuedu Airport", country: "CN", size: "medium" },
  { code: "AAX", name: "Arax\xE1 \u2013 Romeu Zema Airport", country: "BR", size: "medium" },
  { code: "AAY", name: "Al Ghaydah International Airport", country: "YE", size: "medium" },
  { code: "AAZ", name: "Quezaltenango Airport", country: "GT", size: "small" },
  { code: "ABA", name: "Abakan International Airport", country: "RU", size: "large" },
  { code: "ABB", name: "Asaba International Airport", country: "NG", size: "large" },
  { code: "ABD", name: "Abadan Ayatollah Jami International Airport", country: "IR", size: "large" },
  { code: "ABE", name: "Allentown/Bethlehem \u2013 Lehigh Valley International Airport", country: "US", size: "medium" },
  { code: "ABI", name: "Abilene Regional Airport", country: "US", size: "medium" },
  { code: "ABJ", name: "Abidjan \u2013 F\xE9lix-Houphou\xEBt-Boigny International Airport", country: "CI", size: "large" },
  { code: "ABK", name: "Kebri Dahar Airport", country: "ET", size: "medium" },
  { code: "ABL", name: "Ambler Airport", country: "US", size: "medium" },
  { code: "ABM", name: "Bamaga \u2013 Northern Peninsula Airport", country: "AU", size: "small" },
  { code: "ABQ", name: "Albuquerque International Sunport", country: "US", size: "large" },
  { code: "ABR", name: "Aberdeen Regional Airport", country: "US", size: "medium" },
  { code: "ABS", name: "Abu Simbel Airport", country: "EG", size: "medium" },
  { code: "ABT", name: "Al-Baha \u2013 King Saud Bin Abdulaziz (Al Baha) Airport", country: "SA", size: "medium" },
  { code: "ABU", name: "Atambua \u2013 AA Bere Tallo (Haliwen) Airport", country: "ID", size: "small" },
  { code: "ABV", name: "Abuja \u2013 Nnamdi Azikiwe International Airport", country: "NG", size: "large" },
  { code: "ABX", name: "East Albury \u2013 Albury Airport", country: "AU", size: "medium" },
  { code: "ABY", name: "Albany \u2013 Southwest Georgia Regional Airport", country: "US", size: "medium" },
  { code: "ABZ", name: "Aberdeen International Airport", country: "GB", size: "large" },
  { code: "ACA", name: "Acapulco \u2013 General Juan N. \xC1lvarez International Airport", country: "MX", size: "large" },
  { code: "ACC", name: "Accra \u2013 Kotoka International Airport", country: "GH", size: "large" },
  { code: "ACE", name: "San Bartolom\xE9 \u2013 C\xE9sar Manrique-Lanzarote Airport", country: "ES", size: "large" },
  { code: "ACF", name: "Aral Tarim Airport", country: "CN", size: "small" },
  { code: "ACH", name: "St. Gallen \u2013 Sankt Gallen Altenrhein Airport", country: "CH", size: "medium" },
  { code: "ACI", name: "Saint Anne \u2013 Alderney Airport", country: "GG", size: "medium" },
  { code: "ACK", name: "Nantucket Memorial Airport", country: "US", size: "medium" },
  { code: "ACT", name: "Waco Regional Airport", country: "US", size: "medium" },
  {
    code: "ACV",
    name: "Arcata/Eureka \u2013 California Redwood Coast-Humboldt County Airport",
    country: "US",
    size: "medium"
  },
  { code: "ACX", name: "Xingyi Wanfenglin Airport", country: "CN", size: "medium" },
  { code: "ACY", name: "Atlantic City International Airport", country: "US", size: "medium" },
  { code: "ADB", name: "Gaziemir \u2013 Adnan Menderes International Airport", country: "TR", size: "large" },
  { code: "ADD", name: "Addis Ababa Bole International Airport", country: "ET", size: "large" },
  { code: "ADE", name: "Aden International Airport", country: "YE", size: "large" },
  { code: "ADF", name: "Ad\u0131yaman Airport", country: "TR", size: "medium" },
  { code: "ADJ", name: "Amman \u2013 Marka International (Amman Civil) Airport", country: "JO", size: "large" },
  { code: "ADK", name: "Adak Airport", country: "US", size: "medium" },
  { code: "ADL", name: "Adelaide International Airport", country: "AU", size: "large" },
  { code: "ADQ", name: "Kodiak Airport", country: "US", size: "medium" },
  { code: "ADU", name: "Ardabil Airport", country: "IR", size: "medium" },
  { code: "ADZ", name: "San Andr\xE9s \u2013 Gustavo Rojas Pinilla International Airport", country: "CO", size: "large" },
  { code: "AEB", name: "Baise (Tianyang) \u2013 Baise (Bose) Bama Airport", country: "CN", size: "medium" },
  { code: "AEP", name: "Buenos Aires \u2013 Aeroparque Jorge Newbery", country: "AR", size: "large" },
  { code: "AER", name: "Sochi International Airport", country: "RU", size: "large" },
  { code: "AES", name: "\xC5lesund Airport", country: "NO", size: "large" },
  { code: "AET", name: "Allakaket Airport", country: "US", size: "small" },
  { code: "AEU", name: "Abu Musa Island Airport", country: "IR", size: "medium" },
  { code: "AEX", name: "Alexandria International Airport", country: "US", size: "medium" },
  { code: "AEY", name: "Akureyri International Airport", country: "IS", size: "large" },
  { code: "AFA", name: "San Rafael \u2013 Suboficial Ay Santiago Germano Airport", country: "AR", size: "medium" },
  { code: "AFL", name: "Alta Floresta \u2013 Piloto Osvaldo Marques Dias Airport", country: "BR", size: "medium" },
  { code: "AFZ", name: "Sabzevar National Airport", country: "IR", size: "medium" },
  { code: "AGA", name: "Agadir (Temsia) \u2013 Al Massira Airport", country: "MA", size: "large" },
  { code: "AGE", name: "Wangerooge Airport", country: "DE", size: "small" },
  { code: "AGH", name: "\xC4ngelholm-Helsingborg Airport", country: "SE", size: "medium" },
  { code: "AGI", name: "Wageningen Airstrip", country: "SR", size: "small" },
  { code: "AGJ", name: "Aguni Airport", country: "JP", size: "small" },
  { code: "AGP", name: "M\xE1laga-Costa del Sol Airport", country: "ES", size: "large" },
  { code: "AGR", name: "Agra Airport / Agra Air Force Station", country: "IN", size: "medium" },
  { code: "AGS", name: "Augusta Regional At Bush Field", country: "US", size: "medium" },
  { code: "AGT", name: "Ciudad del Este \u2013 Guaran\xED International Airport", country: "PY", size: "large" },
  { code: "AGU", name: "Aguascalientes International Airport", country: "MX", size: "large" },
  { code: "AGX", name: "Agatti Airport", country: "IN", size: "medium" },
  { code: "AHA", name: "Ambikapur \u2013 Maa Mahamaya Airport", country: "IN", size: "medium" },
  { code: "AHB", name: "Abha International Airport", country: "SA", size: "large" },
  { code: "AHE", name: "Ahe Atoll \u2013 Ahe Airport", country: "PF", size: "medium" },
  { code: "AHO", name: "Alghero-Fertilia Airport", country: "IT", size: "medium" },
  { code: "AHU", name: "Al Hoceima \u2013 Cherif Al Idrissi Airport", country: "MA", size: "medium" },
  { code: "AIA", name: "Alliance Municipal Airport", country: "US", size: "medium" },
  { code: "AIN", name: "Wainwright Airport", country: "US", size: "medium" },
  { code: "AIP", name: "Adampur Airport", country: "IN", size: "small" },
  { code: "AIT", name: "Aitutaki Airport", country: "CK", size: "small" },
  { code: "AIU", name: "Atiu Island \u2013 Enua Airport", country: "CK", size: "small" },
  { code: "AJA", name: "Ajaccio Napol\xE9on Bonaparte airport", country: "FR", size: "medium" },
  { code: "AJF", name: "Al-Jawf International Airport", country: "SA", size: "large" },
  { code: "AJI", name: "A\u011Fr\u0131 Airport", country: "TR", size: "medium" },
  { code: "AJL", name: "Aizawl (Lengpui) \u2013 Lengpui Airport", country: "IN", size: "medium" },
  { code: "AJN", name: "Ouani Airport", country: "KM", size: "medium" },
  { code: "AJR", name: "Arvidsjaur Airport", country: "SE", size: "medium" },
  { code: "AJU", name: "Aracaju - Santa Maria Airport", country: "BR", size: "medium" },
  { code: "AKA", name: "Ankang (Hanbin) \u2013 Ankang Fuqiang Airport", country: "CN", size: "small" },
  { code: "AKB", name: "Atka Airport", country: "US", size: "small" },
  { code: "AKF", name: "Kufra Airport", country: "LY", size: "medium" },
  { code: "AKI", name: "Akiak Airport", country: "US", size: "small" },
  { code: "AKJ", name: "Higashikagura \u2013 Asahikawa Airport", country: "JP", size: "medium" },
  { code: "AKK", name: "Akhiok Airport", country: "US", size: "small" },
  { code: "AKL", name: "Auckland International Airport", country: "NZ", size: "large" },
  { code: "AKN", name: "King Salmon Airport", country: "US", size: "medium" },
  { code: "AKP", name: "Anaktuvuk Pass Airport", country: "US", size: "medium" },
  { code: "AKR", name: "Akure Airport", country: "NG", size: "medium" },
  { code: "AKS", name: "Auki \u2013 Gwaunaru'u Airport", country: "SB", size: "small" },
  { code: "AKU", name: "Aksu (Onsu) \u2013 Aksu Hongqipo Airport", country: "CN", size: "medium" },
  { code: "AKV", name: "Akulivik Airport", country: "CA", size: "small" },
  { code: "AKX", name: "Aktobe International Airport", country: "KZ", size: "large" },
  { code: "AKY", name: "Sittwe Airport", country: "MM", size: "medium" },
  { code: "ALA", name: "Almaty International Airport", country: "KZ", size: "large" },
  { code: "ALB", name: "Albany International Airport", country: "US", size: "large" },
  { code: "ALC", name: "Alicante-Elche Miguel Hern\xE1ndez Airport", country: "ES", size: "large" },
  { code: "ALF", name: "Alta Airport", country: "NO", size: "medium" },
  { code: "ALG", name: "Algiers \u2013 Houari Boumediene Airport", country: "DZ", size: "large" },
  { code: "ALH", name: "Albany Airport", country: "AU", size: "medium" },
  { code: "ALO", name: "Waterloo Regional Airport", country: "US", size: "medium" },
  { code: "ALP", name: "Aleppo International Airport", country: "SY", size: "large" },
  { code: "ALS", name: "Alamosa \u2013 San Luis Valley Regional Airport/Bergman Field", country: "US", size: "medium" },
  { code: "ALW", name: "Walla Walla Regional Airport", country: "US", size: "medium" },
  { code: "AMA", name: "Amarillo \u2013 Rick Husband Amarillo International Airport", country: "US", size: "medium" },
  { code: "AMD", name: "Ahmedabad \u2013 Sardar Vallabh Patel International Airport", country: "IN", size: "large" },
  { code: "AMH", name: "Arba Minch Airport", country: "ET", size: "medium" },
  { code: "AMM", name: "Amman \u2013 Queen Alia International Airport", country: "JO", size: "large" },
  { code: "AMQ", name: "Ambon \u2013 Pattimura International Airport", country: "ID", size: "large" },
  { code: "AMS", name: "Amsterdam Airport Schiphol", country: "NL", size: "large" },
  { code: "AMV", name: "Amderma Airport", country: "RU", size: "medium" },
  { code: "ANC", name: "Anchorage \u2013 Ted Stevens Anchorage International Airport", country: "US", size: "large" },
  { code: "ANF", name: "Antofagasta \u2013 Andr\xE9s Sabella G\xE1lvez International Airport", country: "CL", size: "large" },
  { code: "ANI", name: "Aniak Airport", country: "US", size: "medium" },
  { code: "ANR", name: "Antwerp International Airport (Deurne)", country: "BE", size: "medium" },
  { code: "ANS", name: "Andahuaylas Airport", country: "PE", size: "small" },
  { code: "ANU", name: "Osbourn \u2013 V. C. Bird International Airport", country: "AG", size: "large" },
  { code: "ANV", name: "Anvik Airport", country: "US", size: "medium" },
  { code: "ANX", name: "Andenes \u2013 And\xF8ya Airport, Andenes", country: "NO", size: "medium" },
  { code: "AOE", name: "Eski\u015Fehir \u2013 Hasan Polatkan Airport", country: "TR", size: "large" },
  { code: "AOG", name: "Anshan Teng'ao Airport / Anshan Air Base", country: "CN", size: "medium" },
  { code: "AOI", name: "Falconara Marittima (AN) \u2013 Marche Airport", country: "IT", size: "medium" },
  { code: "AOJ", name: "Aomori Airport", country: "JP", size: "large" },
  { code: "AOK", name: "Karpathos Island \u2013 Karpathos Airport", country: "GR", size: "medium" },
  { code: "AOO", name: "Altoona Blair County Airport", country: "US", size: "medium" },
  { code: "AOR", name: "Alor Satar \u2013 Sultan Abdul Halim Airport", country: "MY", size: "medium" },
  { code: "APK", name: "Apataki Airport", country: "PF", size: "small" },
  { code: "APL", name: "Nampula Airport", country: "MZ", size: "large" },
  { code: "APN", name: "Alpena County Regional Airport", country: "US", size: "medium" },
  { code: "APO", name: "Carepa \u2013 Antonio Rold\xE1n Betancur Airport", country: "CO", size: "medium" },
  { code: "APW", name: "Apia \u2013 Faleolo International Airport", country: "WS", size: "large" },
  { code: "AQA", name: "Araraquara Airport", country: "BR", size: "medium" },
  { code: "AQG", name: "Anqing Tianzhushan Airport / Anqing North Air Base", country: "CN", size: "medium" },
  { code: "AQI", name: "Qaisumah\u2013Hafar Al-Batin International Airport", country: "SA", size: "large" },
  { code: "AQJ", name: "Aqaba \u2013 King Hussein International Airport", country: "JO", size: "large" },
  { code: "AQP", name: "Arequipa \u2013 Rodr\xEDguez Ball\xF3n International Airport", country: "PE", size: "large" },
  { code: "ARC", name: "Arctic Village Airport", country: "US", size: "medium" },
  { code: "ARD", name: "Kabola \u2013 Alor Island - Mali Airport", country: "ID", size: "small" },
  { code: "ARH", name: "Archangelsk \u2013 Talagi Airport", country: "RU", size: "medium" },
  { code: "ARI", name: "Arica \u2013 Chacalluta International Airport", country: "CL", size: "medium" },
  { code: "ARK", name: "Arusha Airport", country: "TZ", size: "medium" },
  { code: "ARM", name: "Armidale Airport", country: "AU", size: "medium" },
  { code: "ARN", name: "Stockholm-Arlanda Airport", country: "SE", size: "large" },
  { code: "ART", name: "Watertown International Airport", country: "US", size: "medium" },
  { code: "ARU", name: "Ara\xE7atuba Airport", country: "BR", size: "medium" },
  { code: "ARW", name: "Arad International Airport", country: "RO", size: "medium" },
  { code: "ASB", name: "Ashgabat International Airport", country: "TM", size: "large" },
  { code: "ASD", name: "Andros Town Airport", country: "BS", size: "medium" },
  { code: "ASE", name: "Aspen-Pitkin County Airport (Sardy Field)", country: "US", size: "medium" },
  { code: "ASF", name: "Astrakhan Narimanovo Boris M. Kustodiev International Airport", country: "RU", size: "large" },
  { code: "ASI", name: "Cat Hill \u2013 RAF Ascension Island", country: "SH", size: "medium" },
  { code: "ASJ", name: "Amami Airport", country: "JP", size: "medium" },
  { code: "ASM", name: "Asmara International Airport", country: "ER", size: "medium" },
  { code: "ASO", name: "Asosa Airport", country: "ET", size: "medium" },
  { code: "ASP", name: "Alice Springs Airport", country: "AU", size: "medium" },
  { code: "ASR", name: "Kayseri Erkilet International Airport", country: "TR", size: "large" },
  { code: "ASU", name: "Asunci\xF3n \u2013 Silvio Pettirossi International Airport", country: "PY", size: "large" },
  { code: "ASV", name: "Ol Tukai \u2013 Amboseli Airport", country: "KE", size: "medium" },
  { code: "ASW", name: "Aswan International Airport", country: "EG", size: "large" },
  { code: "ATC", name: "Arthur's Town Airport", country: "BS", size: "medium" },
  {
    code: "ATH",
    name: "Spata-Artemida \u2013 Athens Eleftherios Venizelos International Airport",
    country: "GR",
    size: "large"
  },
  { code: "ATK", name: "Atqasuk Edward Burnell Sr Memorial Airport", country: "US", size: "medium" },
  { code: "ATL", name: "Atlanta \u2013 Hartsfield Jackson Atlanta International Airport", country: "US", size: "large" },
  { code: "ATM", name: "Altamira Interstate Airport", country: "BR", size: "medium" },
  { code: "ATQ", name: "Amritsar \u2013 Sri Guru Ram Das Ji International Airport", country: "IN", size: "large" },
  { code: "ATT", name: "Atmautluak Airport", country: "US", size: "small" },
  { code: "ATW", name: "Appleton International Airport", country: "US", size: "medium" },
  { code: "ATY", name: "Watertown Regional Airport", country: "US", size: "medium" },
  { code: "ATZ", name: "Asyut International Airport", country: "EG", size: "large" },
  { code: "AUA", name: "Oranjestad \u2013 Queen Beatrix International Airport", country: "AW", size: "large" },
  { code: "AUC", name: "Arauca \u2013 Santiago Perez Airport", country: "CO", size: "medium" },
  { code: "AUG", name: "Augusta State Airport", country: "US", size: "medium" },
  { code: "AUH", name: "Abu Dhabi \u2013 Zayed International Airport", country: "AE", size: "large" },
  { code: "AUK", name: "Alakanuk Airport", country: "US", size: "small" },
  { code: "AUL", name: "Aur Atoll \u2013 Aur Island Airport", country: "MH", size: "small" },
  { code: "AUQ", name: "Hiva Oa Island \u2013 Hiva Oa-Atuona Airport", country: "PF", size: "medium" },
  { code: "AUR", name: "Aurillac airport", country: "FR", size: "medium" },
  { code: "AUS", name: "Austin Bergstrom International Airport", country: "US", size: "large" },
  { code: "AUU", name: "Aurukun Airport", country: "AU", size: "small" },
  { code: "AUX", name: "Aragua\xEDna Airport", country: "BR", size: "medium" },
  { code: "AVA", name: "Anshun (Xixiu) \u2013 Anshun Huangguoshu Airport", country: "CN", size: "medium" },
  { code: "AVK", name: "Arvaikheer Airport", country: "MN", size: "medium" },
  { code: "AVL", name: "Asheville Regional Airport", country: "US", size: "medium" },
  { code: "AVN", name: "Avignon Caumont airport", country: "FR", size: "medium" },
  { code: "AVP", name: "Wilkes-Barre/Scranton International Airport", country: "US", size: "medium" },
  { code: "AVR", name: "Amravati  Airport", country: "IN", size: "medium" },
  { code: "AVV", name: "Geelong/Melbourne \u2013 Melbourne Avalon International Airport", country: "AU", size: "large" },
  { code: "AWA", name: "Hawassa International Airport", country: "ET", size: "large" },
  { code: "AWK", name: "Wake Island Airfield", country: "UM", size: "medium" },
  { code: "AWZ", name: "Ahvaz \u2013 Qasem Soleimani International Airport", country: "IR", size: "large" },
  { code: "AXA", name: "The Valley \u2013 Clayton J. Lloyd International Airport", country: "AI", size: "medium" },
  { code: "AXD", name: "Alexandroupolis \u2013 Alexandroupoli Democritus Airport", country: "GR", size: "medium" },
  { code: "AXF", name: "Bayanhot \u2013 Alxa Left Banner Bayanhot Airport", country: "CN", size: "medium" },
  { code: "AXJ", name: "Amakusa Airport", country: "JP", size: "medium" },
  { code: "AXM", name: "Armenia \u2013 El Eden Airport", country: "CO", size: "medium" },
  { code: "AXP", name: "Spring Point Airport", country: "BS", size: "medium" },
  { code: "AXR", name: "Arutua Airport", country: "PF", size: "medium" },
  { code: "AXT", name: "Akita Airport", country: "JP", size: "medium" },
  { code: "AXU", name: "Axum Airport", country: "ET", size: "medium" },
  { code: "AYJ", name: "Faizabad \u2013 Maharshi Valmiki International Airport", country: "IN", size: "medium" },
  { code: "AYP", name: "Ayacucho \u2013 Air Force Colonel Alfredo Mendivil Duarte Airport", country: "PE", size: "medium" },
  { code: "AYQ", name: "Yulara \u2013 Ayers Rock Connellan Airport", country: "AU", size: "medium" },
  { code: "AYT", name: "Antalya International Airport", country: "TR", size: "large" },
  { code: "AZA", name: "Mesa Gateway Airport", country: "US", size: "medium" },
  { code: "AZD", name: "Yazd \u2013 Shahid Sadooghi Airport", country: "IR", size: "medium" },
  { code: "AZN", name: "Andijan International Airport", country: "UZ", size: "medium" },
  { code: "AZO", name: "Kalamazoo/Battle Creek International Airport", country: "US", size: "medium" },
  { code: "AZR", name: "Adrar \u2013 Touat-Cheikh Sidi Mohamed Belkebir Airport", country: "DZ", size: "medium" },
  { code: "AZS", name: "Samana \u2013 Saman\xE1 El Catey International Airport", country: "DO", size: "medium" },
  { code: "BAH", name: "Manama \u2013 Bahrain International Airport", country: "BH", size: "large" },
  { code: "BAL", name: "Batman Airport", country: "TR", size: "medium" },
  { code: "BAQ", name: "Barranquilla \u2013 Ernesto Cortissoz International Airport", country: "CO", size: "large" },
  { code: "BAR", name: "Qionghai (Basuo) \u2013 Qionghai Bo'ao Airport", country: "CN", size: "medium" },
  { code: "BAS", name: "Ballalae Airport", country: "SB", size: "small" },
  { code: "BAV", name: "Baotou Donghe International Airport", country: "CN", size: "large" },
  { code: "BAX", name: "Barnaul Gherman Titov International Airport", country: "RU", size: "large" },
  { code: "BAY", name: "T\u0103u\u021Bii-M\u0103gher\u0103u\u0219 \u2013 Maramure\u0219 International Airport", country: "RO", size: "medium" },
  { code: "BAZ", name: "Barcelos Airport", country: "BR", size: "small" },
  { code: "BBA", name: "Balmaceda Airport", country: "CL", size: "medium" },
  { code: "BBG", name: "Butaritari Airport", country: "KI", size: "small" },
  { code: "BBI", name: "Bhubaneswar \u2013 Biju Patnaik International Airport", country: "IN", size: "large" },
  { code: "BBK", name: "Kasane International Airport", country: "BW", size: "large" },
  { code: "BBM", name: "Battambang Airport", country: "KH", size: "medium" },
  { code: "BBN", name: "Bario Airport", country: "MY", size: "medium" },
  { code: "BBO", name: "Berbera Airport", country: "SO", size: "medium" },
  { code: "BBQ", name: "Codrington \u2013 Burton-Nibbs International Airport", country: "AG", size: "medium" },
  { code: "BBR", name: "Basse-Terre Baillif Airport", country: "GP", size: "small" },
  { code: "BBU", name: "Bucharest B\u0103neasa Aurel Vlaicu International Airport", country: "RO", size: "large" },
  { code: "BCA", name: "Baracoa \u2013 Gustavo Rizo Airport", country: "CU", size: "medium" },
  { code: "BCD", name: "Bacolod City \u2013 Bacolod-Silay International Airport", country: "PH", size: "large" },
  { code: "BCH", name: "Baucau Airport", country: "TL", size: "medium" },
  { code: "BCI", name: "Barcaldine Airport", country: "AU", size: "medium" },
  { code: "BCM", name: "Bac\u0103u George Enescu International  Airport", country: "RO", size: "large" },
  { code: "BCN", name: "Barcelona \u2013 Josep Tarradellas Barcelona-El Prat Airport", country: "ES", size: "large" },
  { code: "BCO", name: "Jinka Airport", country: "ET", size: "medium" },
  {
    code: "BCU",
    name: "Bauchi \u2013 Sir Abubakar Tafawa Balewa Bauchi State International Airport",
    country: "NG",
    size: "large"
  },
  { code: "BDA", name: "Hamilton \u2013 L.F. Wade International Airport", country: "BM", size: "large" },
  { code: "BDB", name: "Bundaberg Airport", country: "AU", size: "medium" },
  { code: "BDD", name: "Badu Island Airport", country: "AU", size: "small" },
  { code: "BDH", name: "Bandar Lengeh International Airport", country: "IR", size: "medium" },
  { code: "BDJ", name: "Banjarbaru \u2013 Syamsudin Noor International Airport", country: "ID", size: "large" },
  { code: "BDL", name: "Hartford \u2013 Bradley International Airport", country: "US", size: "large" },
  { code: "BDO", name: "Bandung \u2013 Husein Sastranegara International Airport", country: "ID", size: "medium" },
  { code: "BDP", name: "Bhadrapur Airport", country: "NP", size: "small" },
  { code: "BDQ", name: "Vadodara International Airport", country: "IN", size: "large" },
  { code: "BDS", name: "Brindisi Airport", country: "IT", size: "large" },
  { code: "BDT", name: "Gbadolite Airport", country: "CD", size: "medium" },
  { code: "BDU", name: "M\xE5lselv \u2013 Bardufoss Airport", country: "NO", size: "medium" },
  { code: "BEB", name: "Balivanich \u2013 Benbecula Airport", country: "GB", size: "medium" },
  { code: "BED", name: "Bedford \u2013 Laurence G Hanscom Field", country: "US", size: "medium" },
  { code: "BEF", name: "Bluefields Airport", country: "NI", size: "medium" },
  { code: "BEG", name: "Belgrade Nikola Tesla Airport", country: "RS", size: "large" },
  { code: "BEJ", name: "Tanjung Redeb - Borneo Island \u2013 Kalimarau Airport", country: "ID", size: "medium" },
  { code: "BEK", name: "Bareilly Air Force Station", country: "IN", size: "medium" },
  { code: "BEL", name: "Bel\xE9m \u2013 Val de Cans/J\xFAlio Cezar Ribeiro International Airport", country: "BR", size: "large" },
  { code: "BEM", name: "Oulad Yaich \u2013 Beni Mellal Airport", country: "MA", size: "large" },
  { code: "BEN", name: "Benina International Airport", country: "LY", size: "large" },
  { code: "BER", name: "Berlin Brandenburg Airport", country: "DE", size: "large" },
  { code: "BES", name: "Brest Bretagne airport", country: "FR", size: "large" },
  { code: "BET", name: "Bethel Airport", country: "US", size: "medium" },
  { code: "BEU", name: "Bedourie Airport", country: "AU", size: "medium" },
  { code: "BEW", name: "Beira International Airport", country: "MZ", size: "large" },
  { code: "BEY", name: "Beirut Rafic Hariri International Airport", country: "LB", size: "large" },
  { code: "BFD", name: "Bradford Regional Airport", country: "US", size: "medium" },
  { code: "BFF", name: "Scottsbluff \u2013 Western Neb. Rgnl/William B. Heilig Airport", country: "US", size: "medium" },
  { code: "BFI", name: "Seattle \u2013 King County International Airport - Boeing Field", country: "US", size: "medium" },
  { code: "BFJ", name: "Bijie Feixiong Airport", country: "CN", size: "medium" },
  { code: "BFL", name: "Bakersfield \u2013 Meadows Field", country: "US", size: "medium" },
  { code: "BFN", name: "Bloemfontein \u2013 Bram Fischer International Airport", country: "ZA", size: "large" },
  { code: "BFQ", name: "Puerto Pi\xF1a \u2013 Bahia Pi\xF1a Airport", country: "PA", size: "small" },
  { code: "BFS", name: "Belfast International Airport", country: "GB", size: "large" },
  { code: "BFV", name: "Buriram \u2013 Buri Ram Airport", country: "TH", size: "medium" },
  { code: "BFY", name: "Bengbu Tenghu Airport", country: "CN", size: "medium" },
  { code: "BGA", name: "Bucaramanga \u2013 Palonegro Airport", country: "CO", size: "medium" },
  { code: "BGC", name: "Bragan\xE7a Airport", country: "PT", size: "medium" },
  { code: "BGF", name: "Bangui M'Poko International Airport", country: "CF", size: "large" },
  { code: "BGG", name: "Bing\xF6l Airport", country: "TR", size: "medium" },
  { code: "BGI", name: "Bridgetown \u2013 Grantley Adams International Airport", country: "BB", size: "large" },
  { code: "BGK", name: "Big Creek Airport", country: "BZ", size: "small" },
  { code: "BGM", name: "Binghamton \u2013 Greater Binghamton/Edwin A Link field", country: "US", size: "medium" },
  { code: "BGO", name: "Bergen Airport, Flesland", country: "NO", size: "large" },
  { code: "BGR", name: "Bangor International Airport", country: "US", size: "medium" },
  { code: "BGW", name: "Baghdad International Airport / New Al Muthana Air Base", country: "IQ", size: "large" },
  { code: "BGY", name: "Orio al Serio (BG) \u2013 Il Caravaggio International Airport", country: "IT", size: "large" },
  { code: "BHB", name: "Bar Harbor \u2013 Hancock County-Bar Harbor Airport", country: "US", size: "medium" },
  { code: "BHD", name: "Belfast \u2013 George Best Belfast City Airport", country: "GB", size: "medium" },
  { code: "BHE", name: "Blenheim \u2013 Woodbourne Airport", country: "NZ", size: "medium" },
  { code: "BHH", name: "Bisha Airport", country: "SA", size: "medium" },
  { code: "BHI", name: "Bah\xEDa Blanca \u2013 Comandante Espora Airport", country: "AR", size: "medium" },
  { code: "BHJ", name: "Bhuj Airport", country: "IN", size: "medium" },
  { code: "BHK", name: "Bukhara International Airport", country: "UZ", size: "large" },
  { code: "BHM", name: "Birmingham-Shuttlesworth International Airport", country: "US", size: "large" },
  { code: "BHO", name: "Bhopal \u2013 Raja Bhoj International Airport", country: "IN", size: "large" },
  { code: "BHQ", name: "Broken Hill Airport", country: "AU", size: "medium" },
  { code: "BHR", name: "Bharatpur Airport", country: "NP", size: "small" },
  { code: "BHS", name: "Bathurst Airport", country: "AU", size: "medium" },
  { code: "BHU", name: "Bhavnagar Airport", country: "IN", size: "medium" },
  { code: "BHV", name: "Bahawalpur Airport", country: "PK", size: "medium" },
  { code: "BHX", name: "Birmingham, West Midlands \u2013 Birmingham Airport", country: "GB", size: "large" },
  { code: "BHY", name: "Beihai Fucheng Airport", country: "CN", size: "medium" },
  { code: "BIA", name: "Bastia-Poretta International airport", country: "FR", size: "large" },
  { code: "BID", name: "Block Island State Airport", country: "US", size: "small" },
  { code: "BIH", name: "Bishop \u2013 Eastern Sierra Regional Airport", country: "US", size: "medium" },
  { code: "BIK", name: "Biak \u2013 Frans Kaisiepo Airport", country: "ID", size: "medium" },
  { code: "BIL", name: "Billings Logan International Airport", country: "US", size: "medium" },
  { code: "BIM", name: "South Bimini Airport", country: "BS", size: "medium" },
  { code: "BIO", name: "Bilbao Airport", country: "ES", size: "large" },
  { code: "BIQ", name: "Biarritz Pays Basque airport", country: "FR", size: "medium" },
  { code: "BIR", name: "Biratnagar Airport", country: "NP", size: "medium" },
  { code: "BIS", name: "Bismarck Municipal Airport", country: "US", size: "medium" },
  { code: "BJA", name: "B\xE9ja\xEFa \u2013 Soummam\u2013Abane Ramdane Airport", country: "DZ", size: "large" },
  { code: "BJB", name: "Bojnord Airport", country: "IR", size: "medium" },
  { code: "BJC", name: "Denver \u2013 Rocky Mountain Metropolitan Airport", country: "US", size: "medium" },
  { code: "BJF", name: "B\xE5tsfjord Airport", country: "NO", size: "medium" },
  { code: "BJL", name: "Banjul (Yundum) \u2013 Banjul International Airport", country: "GM", size: "large" },
  { code: "BJM", name: "Bujumbura Melchior Ndadaye International Airport", country: "BI", size: "large" },
  { code: "BJR", name: "Bahir Dar Airport", country: "ET", size: "medium" },
  { code: "BJV", name: "Bodrum \u2013 Milas Bodrum International Airport", country: "TR", size: "large" },
  { code: "BJX", name: "Silao \u2013 Guanajuato International Airport", country: "MX", size: "large" },
  { code: "BJZ", name: "Badajoz Airport", country: "ES", size: "medium" },
  { code: "BKC", name: "Buckland Airport", country: "US", size: "small" },
  { code: "BKG", name: "Branson Airport", country: "US", size: "medium" },
  { code: "BKI", name: "Kota Kinabalu International Airport", country: "MY", size: "large" },
  { code: "BKK", name: "Bangkok \u2013 Suvarnabhumi Airport", country: "TH", size: "large" },
  { code: "BKM", name: "Bakalalan Airport", country: "MY", size: "small" },
  { code: "BKN", name: "Balkanabat International Airport", country: "TM", size: "medium" },
  { code: "BKO", name: "Bamako \u2013 Modibo Keita International Airport", country: "ML", size: "large" },
  { code: "BKQ", name: "Blackall Airport", country: "AU", size: "medium" },
  { code: "BKS", name: "Bengkulu \u2013 Fatmawati Soekarno Airport", country: "ID", size: "medium" },
  { code: "BKW", name: "Beaver \u2013 Raleigh County Memorial Airport", country: "US", size: "medium" },
  { code: "BKZ", name: "Bukoba Airport", country: "TZ", size: "small" },
  {
    code: "BLA",
    name: "Barcelona \u2013 General Jos\xE9 Antonio Anzoategui International Airport",
    country: "VE",
    size: "large"
  },
  { code: "BLB", name: "Panam\xE1 City \u2013 Panam\xE1 Pac\xEDfico International Airport", country: "PA", size: "small" },
  { code: "BLD", name: "Boulder City Municipal Airport", country: "US", size: "medium" },
  { code: "BLE", name: "Borlange \u2013 Dala Airport", country: "SE", size: "medium" },
  { code: "BLI", name: "Bellingham International Airport", country: "US", size: "medium" },
  { code: "BLJ", name: "Batna Mostefa Ben Boulaid Airport", country: "DZ", size: "large" },
  { code: "BLL", name: "Billund Airport", country: "DK", size: "large" },
  { code: "BLQ", name: "Bologna Guglielmo Marconi Airport", country: "IT", size: "large" },
  { code: "BLR", name: "Bengaluru \u2013 Kempegowda International Airport Bengaluru", country: "IN", size: "large" },
  { code: "BLV", name: "Belleville \u2013 Scott AFB/Midamerica Airport", country: "US", size: "medium" },
  { code: "BLW", name: "Beledweyne Airport", country: "SO", size: "small" },
  { code: "BLZ", name: "Blantyre \u2013 Chileka International Airport", country: "MW", size: "large" },
  { code: "BMA", name: "Stockholm-Bromma Airport", country: "SE", size: "medium" },
  { code: "BME", name: "Broome International Airport", country: "AU", size: "large" },
  {
    code: "BMI",
    name: "Bloomington/Normal \u2013 Central Illinois Regional Airport at Bloomington-Normal",
    country: "US",
    size: "medium"
  },
  { code: "BMK", name: "Borkum Airport", country: "DE", size: "small" },
  { code: "BMO", name: "Banmaw Airport", country: "MM", size: "small" },
  { code: "BMR", name: "Baltrum Airport", country: "DE", size: "small" },
  { code: "BMU", name: "Bima \u2013 Sultan Muhammad Salahuddin Airport", country: "ID", size: "medium" },
  { code: "BMV", name: "Buon Ma Thuot Airport", country: "VN", size: "medium" },
  { code: "BMW", name: "Bordj Badji Mokhtar Airport", country: "DZ", size: "medium" },
  { code: "BMY", name: "Waala \u2013 \xCEle Art - Waala Airport", country: "NC", size: "small" },
  { code: "BNA", name: "Nashville International Airport", country: "US", size: "large" },
  { code: "BNB", name: "Boende Airport", country: "CD", size: "small" },
  { code: "BND", name: "Bandar Abbas International Airport", country: "IR", size: "large" },
  { code: "BNE", name: "Brisbane International Airport", country: "AU", size: "large" },
  { code: "BNI", name: "Benin Airport", country: "NG", size: "medium" },
  { code: "BNK", name: "Ballina Byron Gateway Airport", country: "AU", size: "medium" },
  { code: "BNN", name: "Br\xF8nn\xF8ysund Airport, Br\xF8nn\xF8y", country: "NO", size: "medium" },
  { code: "BNS", name: "Barinas Airport", country: "VE", size: "medium" },
  { code: "BNX", name: "Mahovljani \u2013 Banja Luka International Airport", country: "BA", size: "large" },
  { code: "BNY", name: "Anua \u2013 Bellona/Anua Airport", country: "SB", size: "small" },
  { code: "BOB", name: "Motu Mute \u2013 Bora Bora Airport", country: "PF", size: "medium" },
  {
    code: "BOC",
    name: 'Isla Col\xF3n \u2013 Bocas del Toro "Isla Col\xF3n" International Airport',
    country: "PA",
    size: "medium"
  },
  { code: "BOD", name: "Bordeaux\u2013M\xE9rignac Airport", country: "FR", size: "large" },
  { code: "BOG", name: "Bogota \u2013 El Dorado International Airport", country: "CO", size: "large" },
  { code: "BOH", name: "Bournemouth Airport", country: "GB", size: "medium" },
  { code: "BOI", name: "Boise Air Terminal/Gowen Field", country: "US", size: "large" },
  { code: "BOJ", name: "Burgas Airport", country: "BG", size: "large" },
  { code: "BOM", name: "Mumbai \u2013 Chhatrapati Shivaji Maharaj International Airport", country: "IN", size: "large" },
  { code: "BON", name: "Kralendijk \u2013 Flamingo International Airport", country: "BQ", size: "large" },
  { code: "BOO", name: "Bod\xF8 Airport", country: "NO", size: "large" },
  { code: "BOR", name: "Ton Phueng \u2013 Bokeo International Airport", country: "LA", size: "medium" },
  { code: "BOS", name: "Boston Logan International Airport", country: "US", size: "large" },
  { code: "BOT", name: "Bosset Airport", country: "PG", size: "small" },
  { code: "BOY", name: "Bobo Dioulasso Airport", country: "BF", size: "large" },
  { code: "BPE", name: "Qinhuangdao (Changli) \u2013 Qinhuangdao Beidaihe Airport", country: "CN", size: "medium" },
  { code: "BPL", name: "Bole Alashankou Airport", country: "CN", size: "medium" },
  {
    code: "BPN",
    name: "Balikpapan \u2013 Sultan Aji Muhammad Sulaiman Sepinggan International Airport",
    country: "ID",
    size: "large"
  },
  { code: "BPS", name: "Porto Seguro International Airport", country: "BR", size: "large" },
  { code: "BPT", name: "Beaumont/Port Arthur \u2013 Jack Brooks Regional Airport", country: "US", size: "medium" },
  { code: "BPX", name: "Bangda \u2013 Qamdo Bangda Airport", country: "CN", size: "medium" },
  { code: "BPY", name: "Besalampy Airport", country: "MG", size: "medium" },
  { code: "BQB", name: "Busselton Margaret River Regional Airport", country: "AU", size: "small" },
  { code: "BQG", name: "Bogorodskoye Airport", country: "RU", size: "small" },
  { code: "BQJ", name: "Batagay Airport", country: "RU", size: "small" },
  { code: "BQK", name: "Brunswick Golden Isles Airport", country: "US", size: "medium" },
  { code: "BQL", name: "Boulia Airport", country: "AU", size: "medium" },
  { code: "BQN", name: "Aguadilla \u2013 Rafael Hern\xE1ndez International Airport", country: "PR", size: "medium" },
  { code: "BQS", name: "Blagoveschensk \u2013 Ignatyevo Airport", country: "RU", size: "medium" },
  { code: "BQT", name: "Brest International Airport", country: "BY", size: "large" },
  { code: "BQU", name: "Bequia \u2013 J F Mitchell Airport", country: "VC", size: "medium" },
  { code: "BRA", name: "Barreiras \u2013 Dom Ricardo Weberberger Airport", country: "BR", size: "small" },
  {
    code: "BRC",
    name: "San Carlos de Bariloche \u2013 Teniente Luis Candelaria International Airport",
    country: "AR",
    size: "large"
  },
  { code: "BRD", name: "Brainerd Lakes Regional Airport", country: "US", size: "medium" },
  { code: "BRE", name: "Bremen Airport", country: "DE", size: "large" },
  { code: "BRI", name: "Bari Karol Wojty\u0142a International Airport", country: "IT", size: "large" },
  { code: "BRK", name: "Bourke Airport", country: "AU", size: "medium" },
  { code: "BRL", name: "Burlington \u2013 Southeast Iowa Regional Airport", country: "US", size: "medium" },
  { code: "BRM", name: "Barquisimeto \u2013 Jacinto Lara International Airport", country: "VE", size: "large" },
  { code: "BRN", name: "Bern Airport", country: "CH", size: "medium" },
  { code: "BRO", name: "Brownsville South Padre Island International Airport", country: "US", size: "medium" },
  { code: "BRQ", name: "Brno-Tu\u0159any Airport", country: "CZ", size: "medium" },
  { code: "BRR", name: "Eoligarry \u2013 Barra Airport", country: "GB", size: "medium" },
  { code: "BRS", name: "Bristol Airport", country: "GB", size: "large" },
  { code: "BRU", name: "Zaventem \u2013 Brussels Airport", country: "BE", size: "large" },
  { code: "BRW", name: "Utqia\u0121vik \u2013 Wiley Post Will Rogers Memorial Airport", country: "US", size: "medium" },
  { code: "BRX", name: "Barahona \u2013 Maria Montez International Airport", country: "DO", size: "medium" },
  { code: "BSA", name: "Bosaso \u2013 Bender Qassim International Airport", country: "SO", size: "large" },
  {
    code: "BSB",
    name: "Bras\xEDlia \u2013 Presidente Juscelino Kubitschek International Airport",
    country: "BR",
    size: "large"
  },
  { code: "BSC", name: "Bah\xEDa Solano \u2013 Jos\xE9 Celestino Mutis Airport", country: "CO", size: "medium" },
  { code: "BSD", name: "Baoshan (Longyang) \u2013 Baoshan Yunrui Airport", country: "CN", size: "medium" },
  { code: "BSG", name: "Bata International Airport", country: "GQ", size: "large" },
  { code: "BSK", name: "Biskra - Mohamed Khider Airport", country: "DZ", size: "large" },
  { code: "BSL", name: "B\xE2le / Mulhouse \u2013 EuroAirport Basel\u2013Mulhouse\u2013Freiburg", country: "FR", size: "large" },
  { code: "BSO", name: "Basco Airport", country: "PH", size: "medium" },
  { code: "BSR", name: "Basra International Airport", country: "IQ", size: "large" },
  { code: "BSX", name: "Pathein Airport", country: "MM", size: "small" },
  { code: "BSZ", name: "Bishkek \u2013 Manas International Airport", country: "KG", size: "large" },
  { code: "BTC", name: "Batticaloa International Airport", country: "LK", size: "medium" },
  { code: "BTH", name: "Batam \u2013 Hang Nadim International Airport", country: "ID", size: "large" },
  { code: "BTI", name: "Barter Island Long Range Radar Station Airport", country: "US", size: "medium" },
  { code: "BTJ", name: "Banda Aceh \u2013 Sultan Iskandar Muda International Airport", country: "ID", size: "large" },
  { code: "BTK", name: "Bratsk Airport", country: "RU", size: "medium" },
  { code: "BTM", name: "Butte \u2013 Bert Mooney Airport", country: "US", size: "medium" },
  { code: "BTR", name: "Baton Rouge Metropolitan Airport", country: "US", size: "medium" },
  { code: "BTS", name: "Bratislava \u2013 M. R. \u0160tef\xE1nik Airport", country: "SK", size: "large" },
  { code: "BTT", name: "Bettles Airport", country: "US", size: "small" },
  { code: "BTU", name: "Bintulu Airport", country: "MY", size: "medium" },
  { code: "BTV", name: "Burlington \u2013 Patrick Leahy Burlington International Airport", country: "US", size: "medium" },
  { code: "BTW", name: "Batu Licin \u2013 Bersujud Airport", country: "ID", size: "small" },
  { code: "BUA", name: "Buka Island \u2013 Buka Airport", country: "PG", size: "medium" },
  { code: "BUC", name: "Burketown Airport", country: "AU", size: "small" },
  { code: "BUD", name: "Budapest Liszt Ferenc International Airport", country: "HU", size: "large" },
  { code: "BUF", name: "Buffalo Niagara International Airport", country: "US", size: "large" },
  { code: "BUI", name: "Bokondini Airport", country: "ID", size: "small" },
  { code: "BUN", name: "Buenaventura \u2013 Gerardo Tobar L\xF3pez Airport", country: "CO", size: "medium" },
  { code: "BUQ", name: "Bulawayo \u2013 Joshua Mqabuko Nkomo International Airport", country: "ZW", size: "large" },
  { code: "BUR", name: "Burbank \u2013 Hollywood Burbank/Bob Hope Airport", country: "US", size: "large" },
  { code: "BUS", name: "Batumi \u2013 Alexander Kartveli Batumi International Airport", country: "GE", size: "large" },
  { code: "BUT", name: "Jakar \u2013 Bathpalathang Airport", country: "BT", size: "small" },
  { code: "BUU", name: "Muara Bungo Airport", country: "ID", size: "small" },
  { code: "BUX", name: "Bunia Airport", country: "CD", size: "medium" },
  { code: "BUZ", name: "Bushehr Airport", country: "IR", size: "medium" },
  { code: "BVA", name: "Beauvais-Till\xE9 airport", country: "FR", size: "large" },
  { code: "BVB", name: "Boa Vista \u2013 Atlas Brasil Cantanhede International Airport", country: "BR", size: "large" },
  { code: "BVC", name: "Rabil \u2013 Aristides Pereira International Airport", country: "CV", size: "large" },
  { code: "BVE", name: "Brive Souillac airport", country: "FR", size: "medium" },
  { code: "BVG", name: "Berlev\xE5g Airport", country: "NO", size: "medium" },
  { code: "BVH", name: "Vilhena \u2013 Brigadeiro Camar\xE3o Airport", country: "BR", size: "medium" },
  { code: "BVI", name: "Birdsville Airport", country: "AU", size: "medium" },
  { code: "BVJ", name: "Bovanenkovo Airport", country: "RU", size: "medium" },
  { code: "BVS", name: "Breves Airport", country: "BR", size: "small" },
  {
    code: "BWA",
    name: "Siddharthanagar (Bhairahawa) \u2013 Gautam Buddha International Airport",
    country: "NP",
    size: "large"
  },
  { code: "BWI", name: "Baltimore/Washington International Thurgood Marshall Airport", country: "US", size: "large" },
  { code: "BWK", name: "Gornji Humac \u2013 Bra\u010D Airport", country: "HR", size: "medium" },
  { code: "BWN", name: "Bandar Seri Begawan \u2013 Brunei International Airport", country: "BN", size: "large" },
  { code: "BWO", name: "Balakovo Airport", country: "RU", size: "medium" },
  { code: "BWT", name: "Burnie \u2013 Wynyard Airport", country: "AU", size: "medium" },
  { code: "BWX", name: "Rogojampi, Banyuwangi \u2013 Banyuwangi Airport", country: "ID", size: "small" },
  { code: "BXG", name: "Bendigo Airport", country: "AU", size: "small" },
  { code: "BXH", name: "Balkhash Airport", country: "KZ", size: "medium" },
  { code: "BXR", name: "Bam Airport", country: "IR", size: "medium" },
  { code: "BXT", name: "Bontang-Borneo Island \u2013 LNG Badak Airport", country: "ID", size: "small" },
  { code: "BXU", name: "Butuan \u2013 Bancasi Airport", country: "PH", size: "medium" },
  { code: "BXY", name: "Baikonur Krayniy International Airport", country: "KZ", size: "large" },
  { code: "BYK", name: "Bouak\xE9 Airport", country: "CI", size: "medium" },
  { code: "BYM", name: "Bayamo \u2013 Carlos Manuel de Cespedes Airport", country: "CU", size: "medium" },
  { code: "BYN", name: "Bayankhongor Airport", country: "MN", size: "medium" },
  { code: "BYO", name: "Bonito Airport", country: "BR", size: "small" },
  { code: "BYR", name: "L\xE6s\xF8 Airport", country: "DK", size: "small" },
  { code: "BYW", name: "Blakely Island Airport", country: "US", size: "small" },
  { code: "BZE", name: "Belize City \u2013 Philip S. W. Goldson International Airport", country: "BZ", size: "large" },
  { code: "BZG", name: "Bydgoszcz \u2013 Szwederowo", country: "PL", size: "medium" },
  { code: "BZI", name: "Bal\u0131kesir Airport", country: "TR", size: "medium" },
  { code: "BZL", name: "Barisal Airport", country: "BD", size: "medium" },
  { code: "BZN", name: "Bozeman Yellowstone International Airport", country: "US", size: "medium" },
  { code: "BZO", name: "Bolzano (BZ) \u2013 Bolzano Airport", country: "IT", size: "medium" },
  { code: "BZR", name: "B\xE9ziers Vias airport", country: "FR", size: "medium" },
  { code: "BZV", name: "Brazzaville \u2013 Maya-Maya International Airport", country: "CG", size: "large" },
  { code: "BZX", name: "Bazhong Enyang Airport", country: "CN", size: "medium" },
  { code: "CAB", name: "Cabinda Airport", country: "AO", size: "medium" },
  { code: "CAC", name: "Cascavel \u2013 Coronel Adalberto Mendes da Silva Airport", country: "BR", size: "medium" },
  { code: "CAE", name: "Columbia Metropolitan Airport", country: "US", size: "medium" },
  { code: "CAF", name: "Carauari Airport", country: "BR", size: "small" },
  { code: "CAG", name: "Cagliari Elmas Airport", country: "IT", size: "large" },
  { code: "CAH", name: "Ca Mau City \u2013 C\xE0 Mau Airport", country: "VN", size: "medium" },
  { code: "CAI", name: "Cairo International Airport", country: "EG", size: "large" },
  { code: "CAJ", name: "Canaima Airport", country: "VE", size: "medium" },
  { code: "CAK", name: "Akron Canton Regional Airport", country: "US", size: "medium" },
  { code: "CAL", name: "Campbeltown Airport", country: "GB", size: "medium" },
  { code: "CAN", name: "Guangzhou (Huadu) \u2013 Guangzhou Baiyun International Airport", country: "CN", size: "large" },
  { code: "CAP", name: "Cap Haitien International Airport", country: "HT", size: "large" },
  { code: "CAT", name: "Cascais Airport", country: "PT", size: "medium" },
  { code: "CAU", name: "Caruaru Airport", country: "BR", size: "small" },
  { code: "CAW", name: "Campos dos Goytacazes \u2013 Bartolomeu Lisandro Airport", country: "BR", size: "medium" },
  { code: "CAY", name: "Matoury \u2013 Cayenne \u2013 F\xE9lix Ebou\xE9 Airport", country: "GF", size: "large" },
  { code: "CAZ", name: "Cobar Airport", country: "AU", size: "medium" },
  { code: "CBB", name: "Cochabamba \u2013 Jorge Wilsterman International Airport", country: "BO", size: "large" },
  { code: "CBH", name: "B\xE9char Boudghene Ben Ali Lotfi Airport", country: "DZ", size: "medium" },
  { code: "CBO", name: "Datu Odin Sinsuat \u2013 Cotabato (Awang) Airport", country: "PH", size: "medium" },
  { code: "CBQ", name: "Calabar \u2013 Margaret Ekpo International Airport", country: "NG", size: "medium" },
  { code: "CBR", name: "Canberra Airport", country: "AU", size: "medium" },
  { code: "CBT", name: "Catumbela Airport", country: "AO", size: "medium" },
  { code: "CCA", name: "Chimore Airport", country: "BO", size: "small" },
  { code: "CCC", name: "Cayo Coco \u2013 Jardines Del Rey Airport", country: "CU", size: "medium" },
  { code: "CCE", name: "New Cairo \u2013 Capital International Airport", country: "EG", size: "medium" },
  { code: "CCF", name: "Carcassonne Salvaza Airport", country: "FR", size: "medium" },
  { code: "CCJ", name: "Calicut International Airport", country: "IN", size: "large" },
  { code: "CCK", name: "West Island \u2013 Cocos (Keeling) Islands Airport", country: "CC", size: "large" },
  { code: "CCP", name: "Concepcion \u2013 Carriel Sur International Airport", country: "CL", size: "large" },
  { code: "CCR", name: "Concord \u2013 Buchanan Field", country: "US", size: "medium" },
  { code: "CCS", name: "Maiquet\xEDa Sim\xF3n Bol\xEDvar International Airport", country: "VE", size: "large" },
  { code: "CCU", name: "Kolkata \u2013 Netaji Subhash Chandra Bose International Airport", country: "IN", size: "large" },
  { code: "CCV", name: "Craig Cove Airport", country: "VU", size: "small" },
  { code: "CCZ", name: "Chub Cay Airport", country: "BS", size: "medium" },
  { code: "CDB", name: "Cold Bay Airport", country: "US", size: "medium" },
  { code: "CDC", name: "Cedar City Regional Airport", country: "US", size: "medium" },
  { code: "CDE", name: "Chengde Puning Airport", country: "CN", size: "medium" },
  {
    code: "CDG",
    name: "Paris (Roissy-en-France, Val-d'Oise) \u2013 Charles de Gaulle International Airport",
    country: "FR",
    size: "large"
  },
  { code: "CDP", name: "Kadapa Airport", country: "IN", size: "medium" },
  { code: "CDR", name: "Chadron Municipal Airport", country: "US", size: "medium" },
  { code: "CDT", name: "Castell\xF3n de la Plana \u2013 Castell\xF3n-Costa Azahar Airport", country: "ES", size: "medium" },
  { code: "CDV", name: "Cordova \u2013 Merle K (Mudhole) Smith Airport", country: "US", size: "medium" },
  { code: "CEB", name: "Cebu City/Lapu-Lapu City \u2013 Mactan Cebu International Airport", country: "PH", size: "large" },
  { code: "CEC", name: "Crescent City \u2013 Jack Mc Namara Field Airport", country: "US", size: "medium" },
  { code: "CED", name: "Ceduna Airport", country: "AU", size: "medium" },
  { code: "CEE", name: "Cherepovets Airport", country: "RU", size: "medium" },
  { code: "CEI", name: "Chiang Rai \u2013 Mae Fah Luang - Chiang Rai International Airport", country: "TH", size: "large" },
  { code: "CEK", name: "Chelyabinsk \u2013 Kurchatov Chelyabinsk International Airport", country: "RU", size: "large" },
  { code: "CEL", name: "Canela Airport", country: "BR", size: "small" },
  { code: "CEM", name: "Central Airport", country: "US", size: "small" },
  { code: "CEN", name: "Ciudad Obreg\xF3n International Airport", country: "MX", size: "medium" },
  { code: "CEZ", name: "Cortez Municipal Airport", country: "US", size: "medium" },
  { code: "CFB", name: "Cabo Frio Airport", country: "BR", size: "small" },
  { code: "CFE", name: "Clermont-Ferrand Auvergne airport", country: "FR", size: "large" },
  { code: "CFG", name: "Cienfuegos \u2013 Jaime Gonzalez Airport", country: "CU", size: "medium" },
  { code: "CFK", name: "Chlef Aboubakr Belkaid International Airport", country: "DZ", size: "large" },
  { code: "CFN", name: "Donegal Airport", country: "IE", size: "medium" },
  { code: "CFR", name: "Caen Carpiquet airport", country: "FR", size: "medium" },
  { code: "CFS", name: "Coffs Harbour Airport", country: "AU", size: "medium" },
  {
    code: "CFU",
    name: "Kerkyra (Corfu) \u2013 Corfu Ioannis Kapodistrias International Airport",
    country: "GR",
    size: "large"
  },
  { code: "CGB", name: "Cuiab\xE1 \u2013 V\xE1rzea Grande\u2013Marechal Rondon International Airport", country: "BR", size: "large" },
  { code: "CGD", name: "Changde (Dingcheng) \u2013 Changde Taohuayuan Airport", country: "CN", size: "medium" },
  { code: "CGH", name: "S\xE3o Paulo \u2013 Congonhas\u2013Deputado Freitas Nobre Airport", country: "BR", size: "large" },
  { code: "CGI", name: "Cape Girardeau Regional Airport", country: "US", size: "medium" },
  { code: "CGK", name: "Jakarta \u2013 Soekarno-Hatta International Airport", country: "ID", size: "large" },
  { code: "CGM", name: "Mambajao \u2013 Camiguin Airport", country: "PH", size: "medium" },
  { code: "CGN", name: "K\xF6ln (Cologne) \u2013 Cologne Bonn Airport", country: "DE", size: "large" },
  { code: "CGO", name: "Zhengzhou Xinzheng International Airport", country: "CN", size: "large" },
  { code: "CGP", name: "Chattogram (Chittagong) \u2013 Shah Amanat International Airport", country: "BD", size: "large" },
  { code: "CGQ", name: "Changchun Longjia International Airport", country: "CN", size: "large" },
  { code: "CGR", name: "Campo Grande Airport", country: "BR", size: "medium" },
  { code: "CGY", name: "Laguindingan International Airport", country: "PH", size: "large" },
  { code: "CHA", name: "Chattanooga Metropolitan Airport (Lovell Field)", country: "US", size: "medium" },
  { code: "CHC", name: "Christchurch International Airport", country: "NZ", size: "large" },
  { code: "CHG", name: "Shuangta, Chaoyang \u2013 Chaoyang Airport", country: "CN", size: "medium" },
  { code: "CHH", name: "Chachapoyas Airport", country: "PE", size: "medium" },
  {
    code: "CHM",
    name: "Chimbote \u2013 FAP Lieutenant Jaime Andres de Montreuil Morales Airport",
    country: "PE",
    size: "medium"
  },
  { code: "CHO", name: "Charlottesville Albemarle Airport", country: "US", size: "medium" },
  { code: "CHQ", name: "Souda \u2013 Chania International Airport", country: "GR", size: "large" },
  { code: "CHS", name: "Charleston International Airport", country: "US", size: "large" },
  { code: "CHT", name: "Te One \u2013 Inia William Tuuta Memorial Airport", country: "NZ", size: "medium" },
  { code: "CHU", name: "Chuathbaluk Airport", country: "US", size: "small" },
  { code: "CHX", name: "Changuinola Captain Manuel Ni\xF1o International Airport", country: "PA", size: "medium" },
  { code: "CHY", name: "Choiseul Bay Airport", country: "SB", size: "small" },
  { code: "CIA", name: "Rome \u2013 Ciampino\u2013G. B. Pastine International Airport", country: "IT", size: "large" },
  { code: "CID", name: "Cedar Rapids \u2013 The Eastern Iowa Airport", country: "US", size: "medium" },
  { code: "CIF", name: "Chifeng Yulong Airport", country: "CN", size: "medium" },
  { code: "CIH", name: "Changzhi Wangcun Airport", country: "CN", size: "small" },
  { code: "CIJ", name: "Cobija \u2013 Capit\xE1n An\xEDbal Arab Airport", country: "BO", size: "medium" },
  { code: "CIK", name: "Chalkyitsik Airport", country: "US", size: "small" },
  { code: "CIT", name: "Shymkent International Airport", country: "KZ", size: "large" },
  { code: "CIU", name: "Kincheloe \u2013 Chippewa County International Airport", country: "US", size: "medium" },
  { code: "CIW", name: "Canouan Airport", country: "VC", size: "medium" },
  {
    code: "CIX",
    name: "Chiclayo \u2013 Capit\xE1n FAP Jos\xE9 A. Qui\xF1ones Gonz\xE1lez International Airport",
    country: "PE",
    size: "large"
  },
  { code: "CIY", name: "Comiso Airport", country: "IT", size: "medium" },
  {
    code: "CJA",
    name: "Cajamarca \u2013 Mayor General FAP Armando Revoredo Iglesias Airport",
    country: "PE",
    size: "medium"
  },
  { code: "CJB", name: "Coimbatore International Airport", country: "IN", size: "large" },
  { code: "CJC", name: "Calama \u2013 El Loa Airport", country: "CL", size: "medium" },
  { code: "CJJ", name: "Cheongju International Airport/Cheongju Air Base (K-59/G-513)", country: "KR", size: "large" },
  { code: "CJL", name: "Chitral Airport", country: "PK", size: "medium" },
  { code: "CJM", name: "Chumphon Airport", country: "TH", size: "medium" },
  { code: "CJN", name: "Cijulang \u2013 Nusawiru Airport", country: "ID", size: "small" },
  { code: "CJS", name: "Ciudad Ju\xE1rez \u2013 Abraham Gonz\xE1lez International Airport", country: "MX", size: "large" },
  { code: "CJU", name: "Jeju City \u2013 Jeju International Airport", country: "KR", size: "large" },
  { code: "CJZ", name: "Cajazeiras \u2013 Pedro Vieira Moreira Airport", country: "BR", size: "small" },
  { code: "CKB", name: "Bridgeport \u2013 North Central West Virginia Airport", country: "US", size: "medium" },
  { code: "CKD", name: "Crooked Creek Airport", country: "US", size: "small" },
  { code: "CKG", name: "Chongqing Jiangbei International Airport", country: "CN", size: "large" },
  { code: "CKH", name: "Chokurdah \u2013 Chokurdakh Airport", country: "RU", size: "medium" },
  { code: "CKS", name: "Parauapebas \u2013 Caraj\xE1s Airport", country: "BR", size: "medium" },
  { code: "CKW", name: "Christmas Creek Mine \u2013 Christmas Creek Airport", country: "AU", size: "small" },
  { code: "CKX", name: "Chicken Airport", country: "US", size: "small" },
  { code: "CKY", name: "Conakry \u2013 Ahmed S\xE9kou Tour\xE9 International Airport", country: "GN", size: "large" },
  { code: "CKZ", name: "\xC7anakkale Airport", country: "TR", size: "medium" },
  { code: "CLD", name: "Carlsbad \u2013 McClellan-Palomar Airport", country: "US", size: "medium" },
  { code: "CLE", name: "Cleveland Hopkins International Airport", country: "US", size: "large" },
  { code: "CLJ", name: "Cluj-Napoca \u2013 Avram Iancu Cluj International Airport", country: "RO", size: "large" },
  { code: "CLL", name: "College Station \u2013 Easterwood Field", country: "US", size: "medium" },
  { code: "CLO", name: "Cali \u2013 Alfonso Bonilla Aragon International Airport", country: "CO", size: "large" },
  { code: "CLP", name: "Clarks Point Airport", country: "US", size: "small" },
  { code: "CLQ", name: "Colima \u2013 Licenciado Miguel de la Madrid International Airport", country: "MX", size: "medium" },
  { code: "CLT", name: "Charlotte Douglas International Airport", country: "US", size: "large" },
  { code: "CLV", name: "Caldas Novas \u2013 Nelson Ribeiro Guimar\xE3es Airport", country: "BR", size: "small" },
  { code: "CLY", name: "Calvi Sainte Catherine Airport", country: "FR", size: "medium" },
  { code: "CMA", name: "Cunnamulla Airport", country: "AU", size: "medium" },
  { code: "CMB", name: "Colombo \u2013 Bandaranaike International Colombo Airport", country: "LK", size: "large" },
  { code: "CME", name: "Ciudad del Carmen International Airport", country: "MX", size: "medium" },
  { code: "CMF", name: "Chamb\xE9ry Aix les Bains airport", country: "FR", size: "medium" },
  { code: "CMG", name: "Corumb\xE1 International Airport", country: "BR", size: "medium" },
  { code: "CMH", name: "Columbus \u2013 John Glenn Columbus International Airport", country: "US", size: "large" },
  { code: "CMI", name: "Savoy \u2013 University of Illinois Willard Airport", country: "US", size: "medium" },
  { code: "CMN", name: "Casablanca \u2013 Mohammed V International Airport", country: "MA", size: "large" },
  { code: "CMU", name: "Kundiawa \u2013 Chimbu Airport", country: "PG", size: "medium" },
  { code: "CMW", name: "Camaguey \u2013 Ignacio Agramonte International Airport", country: "CU", size: "large" },
  { code: "CMX", name: "Hancock \u2013 Houghton County Memorial Airport", country: "US", size: "medium" },
  { code: "CNB", name: "Coonamble Airport", country: "AU", size: "medium" },
  { code: "CNC", name: "Coconut Island Airport", country: "AU", size: "small" },
  { code: "CND", name: "Constan\u021Ba \u2013 Mihail Kog\u0103lniceanu International Airport", country: "RO", size: "large" },
  { code: "CNF", name: "Belo Horizonte \u2013 Tancredo Neves International Airport", country: "BR", size: "large" },
  { code: "CNI", name: "Dalian (Changhai) \u2013 Changhai Dachangshandao Airport", country: "CN", size: "small" },
  { code: "CNJ", name: "Cloncurry Airport", country: "AU", size: "medium" },
  { code: "CNM", name: "Carlsbad \u2013 Cavern City Air Terminal", country: "US", size: "medium" },
  { code: "CNN", name: "Kannur International Airport", country: "IN", size: "large" },
  { code: "CNP", name: "Neerlerit Inaat Airport", country: "GL", size: "medium" },
  { code: "CNQ", name: "Corrientes Airport", country: "AR", size: "medium" },
  { code: "CNS", name: "Cairns International Airport", country: "AU", size: "large" },
  { code: "CNX", name: "Chiang Mai International Airport", country: "TH", size: "large" },
  { code: "CNY", name: "Moab \u2013 Canyonlands Regional Airport", country: "US", size: "medium" },
  { code: "COD", name: "Cody \u2013 Yellowstone Regional Airport", country: "US", size: "medium" },
  { code: "COK", name: "Kochi \u2013 Cochin International Airport", country: "IN", size: "large" },
  { code: "COL", name: "Coll Island \u2013 Coll Airport", country: "GB", size: "small" },
  { code: "COO", name: "Cotonou Cadjehoun International Airport", country: "BJ", size: "large" },
  { code: "COQ", name: "Choibalsan Airport", country: "MN", size: "medium" },
  {
    code: "COR",
    name: "Cordoba \u2013 Ingeniero Aeron\xE1utico Ambrosio L.V. Taravella International Airport",
    country: "AR",
    size: "large"
  },
  { code: "COS", name: "Colorado Springs \u2013 City of Colorado Springs Municipal Airport", country: "US", size: "large" },
  { code: "COU", name: "Columbia Regional Airport", country: "US", size: "medium" },
  { code: "COV", name: "Tarsus \u2013 \xC7ukurova International Airport", country: "TR", size: "large" },
  { code: "CPC", name: "Chapelco/San Martin de los Andes \u2013 Aviador C. Campos Airport", country: "AR", size: "medium" },
  { code: "CPD", name: "Coober Pedy Airport", country: "AU", size: "medium" },
  {
    code: "CPE",
    name: "Campeche \u2013 Ingeniero Alberto Acu\xF1a Ongay International Airport",
    country: "MX",
    size: "medium"
  },
  { code: "CPH", name: "Copenhagen Kastrup Airport", country: "DK", size: "large" },
  { code: "CPO", name: "Copiapo \u2013 Desierto de Atacama Airport", country: "CL", size: "medium" },
  { code: "CPR", name: "Casper-Natrona County International Airport", country: "US", size: "medium" },
  { code: "CPT", name: "Cape Town International Airport", country: "ZA", size: "large" },
  { code: "CPV", name: "Campina Grande \u2013 Presidente Jo\xE3o Suassuna Airport", country: "BR", size: "medium" },
  { code: "CPX", name: "Culebra \u2013 Benjamin Rivera Noriega Airport", country: "PR", size: "medium" },
  { code: "CQW", name: "Wulong \u2013 Chongqing Xiann\xFCshan Airport", country: "CN", size: "medium" },
  { code: "CRA", name: "Craiova International Airport", country: "RO", size: "large" },
  {
    code: "CRD",
    name: "Comodoro Rivadavia \u2013 General Enrique Mosconi International Airport",
    country: "AR",
    size: "large"
  },
  { code: "CRI", name: "Colonel Hill Airport", country: "BS", size: "medium" },
  { code: "CRK", name: "Mabalacat \u2013 Clark International Airport / Clark Air Base", country: "PH", size: "large" },
  { code: "CRL", name: "Charleroi \u2013 Brussels South Charleroi Airport", country: "BE", size: "large" },
  { code: "CRM", name: "Catarman National Airport", country: "PH", size: "medium" },
  { code: "CRP", name: "Corpus Christi International Airport", country: "US", size: "medium" },
  { code: "CRU", name: "Carriacou Island \u2013 Lauriston Airport", country: "GD", size: "small" },
  {
    code: "CRV",
    name: "Isola di Capo Rizzuto (KR) \u2013 Crotone Sant'Anna Pythagoras Airport",
    country: "IT",
    size: "medium"
  },
  { code: "CRW", name: "Charleston \u2013 Yeager Airport", country: "US", size: "medium" },
  { code: "CRZ", name: "T\xFCrkmenabat International Airport", country: "TM", size: "large" },
  { code: "CSA", name: "Colonsay Airstrip", country: "GB", size: "small" },
  { code: "CSG", name: "Columbus Airport", country: "US", size: "medium" },
  { code: "CSH", name: "Solovetsky Islands \u2013 Solovki Airport", country: "RU", size: "small" },
  { code: "CSK", name: "Cap Skirring Airport", country: "SN", size: "medium" },
  { code: "CSW", name: "Cabo San Lucas International Airport", country: "MX", size: "medium" },
  { code: "CSX", name: "Changsha (Changsha) \u2013 Changsha Huanghua International Airport", country: "CN", size: "large" },
  { code: "CSY", name: "Cheboksary Airport", country: "RU", size: "medium" },
  { code: "CTA", name: "Catania-Fontanarossa Airport", country: "IT", size: "large" },
  { code: "CTC", name: "Catamarca \u2013 Coronel Felipe Varela International Airport", country: "AR", size: "medium" },
  { code: "CTD", name: "Chitr\xE9 \u2013 Alonso Valderrama Airport", country: "PA", size: "medium" },
  { code: "CTG", name: "Cartagena \u2013 Rafael Nu\xF1ez International Airport", country: "CO", size: "large" },
  { code: "CTL", name: "Charleville Airport", country: "AU", size: "medium" },
  { code: "CTM", name: "Chetumal International Airport", country: "MX", size: "medium" },
  { code: "CTN", name: "Cooktown Airport", country: "AU", size: "medium" },
  { code: "CTS", name: "Sapporo \u2013 New Chitose Airport", country: "JP", size: "large" },
  { code: "CTU", name: "Chengdu (Shuangliu) \u2013 Chengdu Shuangliu International Airport", country: "CN", size: "large" },
  { code: "CUA", name: "Comond\xFA \u2013 Ciudad Constituci\xF3n National Airport", country: "MX", size: "small" },
  { code: "CUC", name: "C\xFAcuta \u2013 Camilo Daza International Airport", country: "CO", size: "medium" },
  { code: "CUE", name: "Cuenca \u2013 Mariscal Lamar Airport", country: "EC", size: "medium" },
  { code: "CUF", name: "Levaldigi (CN) \u2013 Cuneo International Airport", country: "IT", size: "medium" },
  { code: "CUK", name: "Caye Caulker Airport", country: "BZ", size: "medium" },
  { code: "CUL", name: "Culiac\xE1n \u2013 Bachigualato Federal International Airport", country: "MX", size: "large" },
  { code: "CUM", name: "Cuman\xE1 \u2013 Antonio Jos\xE9 de Sucre Airport", country: "VE", size: "medium" },
  { code: "CUN", name: "Canc\xFAn International Airport", country: "MX", size: "large" },
  { code: "CUP", name: "Car\xFApano \u2013 General Francisco Berm\xFAdez Airport", country: "VE", size: "medium" },
  { code: "CUQ", name: "Coen Airport", country: "AU", size: "medium" },
  { code: "CUR", name: "Willemstad \u2013 Hato International Airport", country: "CW", size: "large" },
  {
    code: "CUU",
    name: "Chihuahua \u2013 General Roberto Fierro Villalobos International Airport",
    country: "MX",
    size: "large"
  },
  { code: "CUZ", name: "Cusco \u2013 Alejandro Velasco Astete International Airport", country: "PE", size: "large" },
  {
    code: "CVG",
    name: "Cincinnati / Covington \u2013 Cincinnati Northern Kentucky International Airport",
    country: "US",
    size: "large"
  },
  {
    code: "CVM",
    name: "Ciudad Victoria \u2013 General Pedro Jose Mendez International Airport",
    country: "MX",
    size: "medium"
  },
  { code: "CVN", name: "Clovis Municipal Airport", country: "US", size: "medium" },
  { code: "CVQ", name: "Carnarvon Airport", country: "AU", size: "medium" },
  { code: "CVU", name: "Corvo Airport", country: "PT", size: "small" },
  { code: "CWA", name: "Mosinee \u2013 Central Wisconsin Airport", country: "US", size: "medium" },
  { code: "CWB", name: "Curitiba-Afonso Pena International Airport", country: "BR", size: "large" },
  { code: "CWJ", name: "Lincang (Cangyuan) \u2013 Cangyuan Washan Airport", country: "CN", size: "medium" },
  { code: "CWL", name: "Cardiff International Airport", country: "GB", size: "large" },
  { code: "CWS", name: "Center Island Airport", country: "US", size: "small" },
  { code: "CXB", name: "Cox's Bazar Airport", country: "BD", size: "medium" },
  { code: "CXI", name: "Kiritimati \u2013 Cassidy International Airport", country: "KI", size: "large" },
  { code: "CXJ", name: "Caxias Do Sul \u2013 Hugo Cantergiani Regional Airport", country: "BR", size: "medium" },
  { code: "CXP", name: "Cilacap \u2013 Tunggul Wulung Airport", country: "ID", size: "medium" },
  {
    code: "CXR",
    name: "Nha Trang/nha Trang aiurportCam Ranh \u2013 Cam Ranh International Airport / Cam Ranh Air Base",
    country: "VN",
    size: "large"
  },
  { code: "CYA", name: "Les Cayes \u2013 Antoine-Simon International Airport", country: "HT", size: "medium" },
  { code: "CYB", name: "West End \u2013 Charles Kirkconnell International Airport", country: "KY", size: "medium" },
  { code: "CYC", name: "Caye Chapel Airport", country: "BZ", size: "medium" },
  { code: "CYF", name: "Chefornak Airport", country: "US", size: "small" },
  { code: "CYI", name: "Shuishang \u2013 Chiayi Airport", country: "TW", size: "medium" },
  { code: "CYO", name: "Cayo Largo del Sur \u2013 Vilo Acu\xF1a International Airport", country: "CU", size: "medium" },
  { code: "CYP", name: "Calbayog City \u2013 Calbayog Airport", country: "PH", size: "medium" },
  { code: "CYS", name: "Cheyenne Regional Jerry Olson Field", country: "US", size: "medium" },
  { code: "CYT", name: "Yakataga Airport", country: "US", size: "small" },
  { code: "CYU", name: "Cuyo Airport", country: "PH", size: "small" },
  { code: "CYX", name: "Cherskiy Airport", country: "RU", size: "medium" },
  { code: "CYZ", name: "Cauayan City \u2013 Cauayan Airport", country: "PH", size: "medium" },
  { code: "CZE", name: "Coro \u2013 Jos\xE9 Leonardo Chirinos Airport", country: "VE", size: "medium" },
  { code: "CZH", name: "Corozal Airport", country: "BZ", size: "medium" },
  { code: "CZL", name: "Constantine \u2013 Mohamed Boudiaf International Airport", country: "DZ", size: "large" },
  { code: "CZM", name: "Cozumel International Airport", country: "MX", size: "large" },
  { code: "CZS", name: "Cruzeiro do Sul Airport", country: "BR", size: "medium" },
  { code: "CZU", name: "Corozal \u2013 Las Brujas Airport", country: "CO", size: "medium" },
  { code: "CZX", name: "Changzhou Benniu International Airport", country: "CN", size: "medium" },
  { code: "DAB", name: "Daytona Beach International Airport", country: "US", size: "medium" },
  { code: "DAC", name: "Dhaka \u2013 Hazrat Shahjalal International Airport", country: "BD", size: "large" },
  { code: "DAD", name: "Da Nang International Airport", country: "VN", size: "large" },
  { code: "DAL", name: "Dallas Love Field", country: "US", size: "large" },
  { code: "DAM", name: "Damascus International Airport", country: "SY", size: "large" },
  { code: "DAR", name: "Dar es Salaam \u2013 Julius Nyerere International Airport", country: "TZ", size: "large" },
  { code: "DAT", name: "Datong Yungang International Airport", country: "CN", size: "large" },
  { code: "DAU", name: "Daru Airport", country: "PG", size: "medium" },
  { code: "DAV", name: "David \u2013 Enrique Malek International Airport", country: "PA", size: "medium" },
  { code: "DAX", name: "Dazhou (Daxian) \u2013 Dachuan Airport", country: "CN", size: "small" },
  { code: "DAY", name: "Dayton \u2013 James M. Cox Dayton International Airport", country: "US", size: "medium" },
  { code: "DBA", name: "Dalbandin Airport", country: "PK", size: "small" },
  { code: "DBB", name: "El Alamein International Airport", country: "EG", size: "large" },
  { code: "DBC", name: "Baicheng Chang'an Airport", country: "CN", size: "medium" },
  { code: "DBM", name: "Debre Markos Airport", country: "ET", size: "small" },
  { code: "DBO", name: "Dubbo City Regional Airport", country: "AU", size: "medium" },
  { code: "DBQ", name: "Dubuque Regional Airport", country: "US", size: "medium" },
  { code: "DBR", name: "Darbhanga Airport", country: "IN", size: "medium" },
  { code: "DBV", name: "Dubrovnik Ru\u0111er Bo\u0161kovi\u0107 Airport", country: "HR", size: "large" },
  { code: "DCA", name: "Washington \u2013 Ronald Reagan Washington National Airport", country: "US", size: "large" },
  { code: "DCF", name: "Canefield Airport", country: "DM", size: "medium" },
  { code: "DCM", name: "Castres Mazamet Airport", country: "FR", size: "medium" },
  { code: "DCY", name: "Garz\xEA (Daocheng) \u2013 Daocheng Yading Airport", country: "CN", size: "medium" },
  { code: "DDC", name: "Dodge City Regional Airport", country: "US", size: "medium" },
  { code: "DDG", name: "Dandong (Zhenxing) \u2013 Dandong Langtou International Airport", country: "CN", size: "medium" },
  { code: "DDR", name: "Xigaz\xEA (Dingri) \u2013 Shigatse Tingri Airport", country: "CN", size: "medium" },
  { code: "DEA", name: "Dera Ghazi Khan Airport", country: "PK", size: "medium" },
  { code: "DEB", name: "Debrecen International Airport", country: "HU", size: "large" },
  { code: "DEC", name: "Decatur Airport", country: "US", size: "medium" },
  { code: "DED", name: "Dehradun (Jauligrant) \u2013 Dehradun Jolly Grant Airport", country: "IN", size: "medium" },
  { code: "DEE", name: "Yuzhno-Kurilsk Mendeleyevo Airport", country: "RU", size: "small" },
  { code: "DEF", name: "Dezful Airport", country: "IR", size: "medium" },
  { code: "DEL", name: "New Delhi \u2013 Indira Gandhi International Airport", country: "IN", size: "large" },
  { code: "DEM", name: "Dembidollo Airport", country: "ET", size: "small" },
  { code: "DEN", name: "Denver International Airport", country: "US", size: "large" },
  { code: "DEX", name: "Dekai \u2013 Nop Goliat Dekai Airport", country: "ID", size: "small" },
  { code: "DFW", name: "Dallas-Fort Worth \u2013 Dallas Fort Worth International Airport", country: "US", size: "large" },
  { code: "DGA", name: "Dangriga Airport", country: "BZ", size: "medium" },
  { code: "DGH", name: "Deoghar Airport", country: "IN", size: "small" },
  { code: "DGO", name: "Durango \u2013 General Guadalupe Victoria International Airport", country: "MX", size: "medium" },
  { code: "DGT", name: "Dumaguete City \u2013 Sibulan Airport", country: "PH", size: "medium" },
  { code: "DHM", name: "Kangra Airport", country: "IN", size: "medium" },
  { code: "DHN", name: "Dothan Regional Airport", country: "US", size: "medium" },
  { code: "DHX", name: "Kediri \u2013 Dhoho International Airport", country: "ID", size: "medium" },
  { code: "DIA", name: "Doha International Airport", country: "QA", size: "large" },
  { code: "DIB", name: "Dibrugarh Airport", country: "IN", size: "medium" },
  { code: "DIE", name: "Antisiranana \u2013 Arrachart Airport", country: "MG", size: "medium" },
  { code: "DIG", name: "Diqing (Shangri-La) \u2013 Diqing Shangri-La Airport", country: "CN", size: "medium" },
  { code: "DIK", name: "Dickinson Theodore Roosevelt Regional Airport", country: "US", size: "medium" },
  { code: "DIL", name: "Dili \u2013 Presidente Nicolau Lobato International Airport", country: "TL", size: "large" },
  { code: "DIN", name: "Dien Bien Phu Airport", country: "VN", size: "medium" },
  { code: "DIR", name: "Dire Dawa \u2013 Aba Tenna Dejazmach Yilma International Airport", country: "ET", size: "large" },
  { code: "DIU", name: "Diu Airport", country: "IN", size: "small" },
  { code: "DIY", name: "Diyarbak\u0131r Airport", country: "TR", size: "medium" },
  { code: "DJB", name: "Jambi \u2013 Sultan Thaha Airport", country: "ID", size: "small" },
  { code: "DJE", name: "Mellita \u2013 Djerba Zarzis International Airport", country: "TN", size: "large" },
  { code: "DJG", name: "Djanet \u2013 Tiska Djanet Airport", country: "DZ", size: "large" },
  { code: "DJJ", name: "Sentani \u2013 Dortheys Hiyo Eluay International Airport", country: "ID", size: "large" },
  {
    code: "DJT",
    name: "West Palm Beach \u2013 President Donald J. Trump International Airport",
    country: "US",
    size: "large"
  },
  { code: "DKA", name: "Katsina \u2013 Umaru Musa Yar'adua Airport", country: "NG", size: "medium" },
  { code: "DLA", name: "Douala International Airport", country: "CM", size: "large" },
  { code: "DLC", name: "Dalian (Ganjingzi) \u2013 Dalian Zhoushuizi International Airport", country: "CN", size: "large" },
  { code: "DLE", name: "Dole Tavaux Airport", country: "FR", size: "medium" },
  { code: "DLG", name: "Dillingham Airport", country: "US", size: "medium" },
  { code: "DLH", name: "Duluth International Airport", country: "US", size: "medium" },
  { code: "DLI", name: "Da Lat \u2013 Lien Khuong Airport", country: "VN", size: "medium" },
  { code: "DLM", name: "Dalaman International Airport", country: "TR", size: "large" },
  { code: "DLR", name: "Dalnerechensk Airport", country: "RU", size: "small" },
  { code: "DLU", name: "Dali (Xiaguan) \u2013 Dali Fengyi Airport", country: "CN", size: "medium" },
  { code: "DLZ", name: "Dalanzadgad Airport", country: "MN", size: "medium" },
  { code: "DMB", name: "Taraz International Airport", country: "KZ", size: "large" },
  { code: "DMD", name: "Doomadgee Airport", country: "AU", size: "small" },
  { code: "DME", name: "Moscow \u2013 Domodedovo International Airport", country: "RU", size: "large" },
  { code: "DMK", name: "Bangkok \u2013 Don Mueang International Airport", country: "TH", size: "large" },
  { code: "DMM", name: "Ad Dammam \u2013 King Fahd International Airport", country: "SA", size: "large" },
  { code: "DMU", name: "Dimapur Airport", country: "IN", size: "medium" },
  { code: "DND", name: "Dundee Airport", country: "GB", size: "medium" },
  { code: "DNH", name: "Dunhuang Mogao International Airport", country: "CN", size: "large" },
  { code: "DNZ", name: "Denizli \u2013 \xC7ardak Airport", country: "TR", size: "medium" },
  { code: "DOD", name: "Dodoma Airport", country: "TZ", size: "medium" },
  { code: "DOG", name: "Dongola Airport", country: "SD", size: "medium" },
  { code: "DOH", name: "Doha \u2013 Hamad International Airport", country: "QA", size: "large" },
  { code: "DOL", name: "Deauville Normandie airport", country: "FR", size: "medium" },
  { code: "DOM", name: "Marigot \u2013 Douglas-Charles Airport", country: "DM", size: "medium" },
  { code: "DOP", name: "Dolpa Airport", country: "NP", size: "small" },
  { code: "DOV", name: "Dover Civil Air Terminal/Dover Air Force Base", country: "US", size: "medium" },
  { code: "DOY", name: "Dongying (Kenli) \u2013 Dongying Shengli Airport", country: "CN", size: "medium" },
  { code: "DPL", name: "Dipolog Airport", country: "PH", size: "medium" },
  { code: "DPO", name: "Devonport Airport", country: "AU", size: "medium" },
  {
    code: "DPS",
    name: "Kuta, Badung \u2013 Denpasar I Gusti Ngurah Rai International Airport",
    country: "ID",
    size: "large"
  },
  { code: "DPT", name: "Deputatskiy Airport", country: "RU", size: "small" },
  { code: "DQA", name: "Daqing Sartu Airport", country: "CN", size: "small" },
  { code: "DQM", name: "Duqm International Airport", country: "OM", size: "large" },
  { code: "DRG", name: "Deering Airport", country: "US", size: "medium" },
  { code: "DRJ", name: "Drietabbetje Airport", country: "SR", size: "small" },
  { code: "DRO", name: "Durango La Plata County Airport", country: "US", size: "medium" },
  { code: "DRP", name: "Legazpi \u2013 Bicol International Airport", country: "PH", size: "large" },
  { code: "DRS", name: "Dresden Airport", country: "DE", size: "large" },
  { code: "DRV", name: "Baa Atoll \u2013 Dharavandhoo Airport", country: "MV", size: "small" },
  { code: "DRW", name: "Darwin International Airport / RAAF Darwin", country: "AU", size: "large" },
  { code: "DSD", name: "Grande Anse \u2013 La D\xE9sirade Airport", country: "GP", size: "small" },
  { code: "DSE", name: "Dessie \u2013 Kombolcha Airport", country: "ET", size: "small" },
  { code: "DSI", name: "Destin Executive Airport", country: "US", size: "medium" },
  { code: "DSM", name: "Des Moines International Airport", country: "US", size: "large" },
  { code: "DSN", name: "Ordos Ejin Horo International Airport", country: "CN", size: "large" },
  { code: "DSO", name: "S\u014Fnd\u014Fng-ni \u2013 Sondok Airport", country: "KP", size: "medium" },
  { code: "DSS", name: "Dakar \u2013 Blaise Diagne International Airport", country: "SN", size: "large" },
  { code: "DSY", name: "Ta Noun \u2013 Dara Sakor International Airport", country: "KH", size: "large" },
  { code: "DTB", name: "Siborong-Borong \u2013 Silangit Airport", country: "ID", size: "small" },
  { code: "DTD", name: "Datadawai-Borneo Island \u2013 Datadawai Airport", country: "ID", size: "small" },
  { code: "DTM", name: "Dortmund Airport", country: "DE", size: "large" },
  { code: "DTR", name: "Decatur Shores Airport", country: "US", size: "small" },
  { code: "DTU", name: "Heihe \u2013 Wudalianchi Dedu Airport", country: "CN", size: "medium" },
  { code: "DTW", name: "Detroit Metropolitan Wayne County Airport", country: "US", size: "large" },
  { code: "DUB", name: "Dublin Airport", country: "IE", size: "large" },
  { code: "DUD", name: "Dunedin International Airport", country: "NZ", size: "medium" },
  { code: "DUE", name: "Chitato \u2013 Dundo Airport", country: "AO", size: "medium" },
  { code: "DUJ", name: "DuBois Regional Airport", country: "US", size: "medium" },
  { code: "DUM", name: "Dumai \u2013 Pinang Kampai Airport", country: "ID", size: "medium" },
  { code: "DUR", name: "Durban \u2013 King Shaka International Airport", country: "ZA", size: "large" },
  { code: "DUS", name: "D\xFCsseldorf Airport", country: "DE", size: "large" },
  { code: "DUT", name: "Unalaska \u2013 Tom Madsen (Dutch Harbor) Airport", country: "US", size: "medium" },
  { code: "DVL", name: "Devils Lake Regional Airport", country: "US", size: "medium" },
  { code: "DVO", name: "Davao \u2013 Francisco Bangoy International Airport", country: "PH", size: "large" },
  { code: "DWB", name: "Soalala Airport", country: "MG", size: "small" },
  { code: "DWC", name: "Dubai(Jebel Ali) \u2013 Al Maktoum International Airport", country: "AE", size: "large" },
  { code: "DWD", name: "Dawadmi Domestic Airport", country: "SA", size: "medium" },
  { code: "DXB", name: "Dubai International Airport", country: "AE", size: "large" },
  { code: "DXJ", name: "Xiangxi Biancheng Airport", country: "CN", size: "small" },
  { code: "DXN", name: "Gautam Buddha Nagar \u2013 Noida International Airport", country: "IN", size: "large" },
  {
    code: "DYG",
    name: "Zhangjiajie (Yongding) \u2013 Zhangjiajie Hehua International Airport",
    country: "CN",
    size: "large"
  },
  { code: "DYR", name: "Anadyr \u2013 Ugolny Yuri Ryktheu Airport", country: "RU", size: "medium" },
  { code: "DYU", name: "Dushanbe International Airport", country: "TJ", size: "large" },
  { code: "DZA", name: "Dzaoudzi Pamandzi International Airport", country: "YT", size: "large" },
  { code: "DZH", name: "Dazhou (Dachuan) \u2013 Dazhou Jinya Airport", country: "CN", size: "medium" },
  { code: "DZN", name: "Zhezkazgan National Airport", country: "KZ", size: "large" },
  { code: "EAA", name: "Eagle Airport", country: "US", size: "small" },
  { code: "EAM", name: "Najran Domestic Airport", country: "SA", size: "medium" },
  { code: "EAR", name: "Kearney Regional Airport", country: "US", size: "medium" },
  { code: "EAS", name: "Hondarribia \u2013 San Sebasti\xE1n Airport", country: "ES", size: "medium" },
  { code: "EAT", name: "Wenatchee \u2013 Pangborn Memorial Airport", country: "US", size: "medium" },
  { code: "EAU", name: "Eau Claire \u2013 Chippewa Valley Regional Airport", country: "US", size: "medium" },
  { code: "EAX", name: "Kwatta \u2013 Eduard Alexander Gummels Airport", country: "SR", size: "small" },
  { code: "EBA", name: "Campo nell'Elba (LI) \u2013 Marina di Campo Airport", country: "IT", size: "medium" },
  { code: "EBB", name: "Entebbe International Airport", country: "UG", size: "large" },
  { code: "EBD", name: "El-Obeid Airport", country: "SD", size: "medium" },
  { code: "EBH", name: "El Bayadh Airport", country: "DZ", size: "small" },
  { code: "EBJ", name: "Esbjerg Airport", country: "DK", size: "medium" },
  { code: "EBL", name: "Arbil \u2013 Erbil International Airport", country: "IQ", size: "large" },
  { code: "ECN", name: "Tymbou (Kirklar) \u2013 Ercan International Airport", country: "CY", size: "large" },
  {
    code: "ECP",
    name: "Panama City Beach \u2013 Northwest Florida Beaches International Airport",
    country: "US",
    size: "medium"
  },
  { code: "EDI", name: "Ingliston, Edinburgh \u2013 Edinburgh Airport", country: "GB", size: "large" },
  { code: "EDL", name: "Eldoret International Airport", country: "KE", size: "large" },
  { code: "EDO", name: "Edremit \u2013 Bal\u0131kesir Koca Seyit Airport", country: "TR", size: "large" },
  { code: "EDR", name: "Pormpuraaw Airport", country: "AU", size: "small" },
  { code: "EEK", name: "Eek Airport", country: "US", size: "small" },
  { code: "EFL", name: "Kefallinia Island \u2013 Kefallinia Airport", country: "GR", size: "medium" },
  { code: "EGC", name: "Bergerac Dordogne-P\xE9rigord airport", country: "FR", size: "medium" },
  { code: "EGE", name: "Eagle County Regional Airport", country: "US", size: "medium" },
  { code: "EGS", name: "Egilssta\xF0ir Airport", country: "IS", size: "medium" },
  { code: "EGX", name: "Egegik Airport", country: "US", size: "medium" },
  { code: "EHU", name: "Ezhou Huahu International Airport", country: "CN", size: "large" },
  { code: "EIE", name: "Yeniseysk Airport", country: "RU", size: "medium" },
  { code: "EIN", name: "Eindhoven Airport", country: "NL", size: "large" },
  { code: "EIS", name: "Beef Island \u2013 Terrance B. Lettsome International Airport", country: "VG", size: "large" },
  { code: "EJA", name: "Barrancabermeja \u2013 Yarigu\xEDes Airport", country: "CO", size: "medium" },
  { code: "EJH", name: "Al Wajh Domestic Airport", country: "SA", size: "medium" },
  { code: "EJT", name: "Enejit Island \u2013 Enejit Airport", country: "MH", size: "small" },
  { code: "EKO", name: "Elko Regional Airport", country: "US", size: "medium" },
  { code: "EKS", name: "Shakhtyorsk Airport", country: "RU", size: "small" },
  { code: "ELC", name: "Elcho Island Airport", country: "AU", size: "medium" },
  { code: "ELD", name: "El Dorado \u2013 South Arkansas Regional Airport at Goodwin Field", country: "US", size: "medium" },
  { code: "ELF", name: "El Fasher Airport", country: "SD", size: "medium" },
  { code: "ELG", name: "El Menia \u2013 El Golea Airport", country: "DZ", size: "medium" },
  { code: "ELH", name: "North Eleuthera Airport", country: "BS", size: "medium" },
  { code: "ELI", name: "Elim Airport", country: "US", size: "small" },
  { code: "ELM", name: "Elmira/Corning \u2013 Elmira Corning Regional Airport", country: "US", size: "medium" },
  { code: "ELP", name: "El Paso International Airport", country: "US", size: "large" },
  { code: "ELQ", name: "Qassim \u2013 Prince Naif bin Abdulaziz International Airport", country: "SA", size: "large" },
  { code: "ELS", name: "East London \u2013 King Phalo Airport", country: "ZA", size: "large" },
  { code: "ELU", name: "Guemar Airport - \u0645\u0637\u0627\u0631 \u0642\u0645\u0627\u0631 \u0628\u0627\u0644\u0648\u0627\u062F\u064A", country: "DZ", size: "medium" },
  { code: "EMA", name: "Nottingham, Leicestershire \u2013 East Midlands Airport", country: "GB", size: "large" },
  { code: "EMD", name: "Emerald Airport", country: "AU", size: "medium" },
  { code: "EME", name: "Emden Airport", country: "DE", size: "small" },
  { code: "EMK", name: "Emmonak Airport", country: "US", size: "medium" },
  { code: "ENA", name: "Kenai Municipal Airport", country: "US", size: "medium" },
  { code: "ENE", name: "Ende \u2013 H. Hasan Aroeboesman (Ende) Airport", country: "ID", size: "small" },
  { code: "ENF", name: "Enontekio Airport", country: "FI", size: "medium" },
  { code: "ENH", name: "Enshi (Enshi) \u2013 Enshi Xujiaping Airport", country: "CN", size: "medium" },
  { code: "ENI", name: "El Nido Airport", country: "PH", size: "small" },
  {
    code: "ENO",
    name: "Encarnaci\xF3n \u2013 Teniente Ramon A. Ayub Gonzalez International Airport",
    country: "PY",
    size: "large"
  },
  { code: "ENT", name: "Eniwetok Atoll \u2013 Eniwetok Airport", country: "MH", size: "small" },
  { code: "ENU", name: "Enegu \u2013 Akanu Ibiam International Airport", country: "NG", size: "large" },
  { code: "ENY", name: "Yan'an (Baota) \u2013 Yan'an Nanniwan Airport", country: "CN", size: "medium" },
  { code: "EOH", name: "Medell\xEDn \u2013 Enrique Olaya Herrera Airport", country: "CO", size: "medium" },
  { code: "EOI", name: "Eday Airport", country: "GB", size: "medium" },
  { code: "EPR", name: "Esperance Airport", country: "AU", size: "medium" },
  { code: "EPU", name: "P\xE4rnu Airport", country: "EE", size: "medium" },
  { code: "EQS", name: "Esquel Brigadier Antonio Parodi International Airport", country: "AR", size: "medium" },
  { code: "ERC", name: "Erzincan Airport", country: "TR", size: "medium" },
  { code: "ERF", name: "Erfurt-Weimar Airport", country: "DE", size: "large" },
  { code: "ERH", name: "Errachidia \u2013 Moulay Ali Cherif Airport", country: "MA", size: "medium" },
  { code: "ERI", name: "Erie International Tom Ridge Field", country: "US", size: "medium" },
  { code: "ERL", name: "Erenhot Saiwusu International Airport", country: "CN", size: "medium" },
  { code: "ERS", name: "Windhoek \u2013 Eros Airport", country: "NA", size: "medium" },
  { code: "ERZ", name: "Erzurum International Airport", country: "TR", size: "medium" },
  { code: "ESB", name: "Ankara \u2013 Esenbo\u011Fa International Airport", country: "TR", size: "large" },
  { code: "ESC", name: "Escanaba \u2013 Delta County Airport", country: "US", size: "medium" },
  { code: "ESD", name: "Eastsound \u2013 Orcas Island Airport", country: "US", size: "medium" },
  { code: "ESL", name: "Elista Airport", country: "RU", size: "medium" },
  { code: "ESM", name: "Tachina \u2013 Carlos Concha Torres International Airport", country: "EC", size: "large" },
  { code: "ESR", name: "El Salvador \u2013 Ricardo Garc\xEDa Posada Airport", country: "CL", size: "medium" },
  { code: "ESU", name: "Essaouira-Mogador Airport", country: "MA", size: "medium" },
  { code: "ETM", name: "Eilat \u2013 Ramon International Airport", country: "IL", size: "large" },
  {
    code: "ETR",
    name: "Santa Rosa - Artillery Colonel Victor Larrea International Airport",
    country: "EC",
    size: "medium"
  },
  { code: "ETZ", name: "Goin \u2013 Metz-Nancy-Lorraine Airport", country: "FR", size: "medium" },
  { code: "EUA", name: "Eua Island \u2013 Kaufana Airport", country: "TO", size: "small" },
  { code: "EUG", name: "Eugene Airport", country: "US", size: "medium" },
  { code: "EUN", name: "El Aai\xFAn \u2013 Laayoune Hassan I International Airport", country: "EH", size: "large" },
  { code: "EUX", name: "Oranjestad \u2013 F. D. Roosevelt Airport", country: "BQ", size: "medium" },
  { code: "EVE", name: "Evenes \u2013 Harstad/Narvik Airport", country: "NO", size: "large" },
  { code: "EVG", name: "Sveg Airport", country: "SE", size: "small" },
  { code: "EVN", name: "Yerevan \u2013 Zvartnots International Airport", country: "AM", size: "large" },
  { code: "EVV", name: "Evansville Regional Airport", country: "US", size: "medium" },
  { code: "EWB", name: "New Bedford Regional Airport", country: "US", size: "medium" },
  { code: "EWN", name: "New Bern \u2013 Coastal Carolina Regional Airport", country: "US", size: "medium" },
  { code: "EWR", name: "Newark Liberty International Airport", country: "US", size: "large" },
  { code: "EXT", name: "Exeter, Devon \u2013 Exeter International Airport", country: "GB", size: "medium" },
  { code: "EYK", name: "Beloyarskiy Airport", country: "RU", size: "medium" },
  { code: "EYP", name: "Yopal \u2013 El Alcaravan - Yopal Airport", country: "CO", size: "medium" },
  { code: "EYW", name: "Key West International Airport", country: "US", size: "medium" },
  {
    code: "EZE",
    name: "Buenos Aires (Ezeiza) \u2013 Ezeiza International Airport - Ministro Pistarini",
    country: "AR",
    size: "large"
  },
  { code: "EZS", name: "Elaz\u0131\u011F Airport", country: "TR", size: "medium" },
  { code: "FAE", name: "V\xE1gar Airport", country: "FO", size: "large" },
  { code: "FAI", name: "Fairbanks International Airport", country: "US", size: "medium" },
  { code: "FAO", name: "Faro - Gago Coutinho International Airport", country: "PT", size: "large" },
  { code: "FAR", name: "Fargo \u2013 Hector International Airport", country: "US", size: "medium" },
  { code: "FAT", name: "Fresno Yosemite International Airport", country: "US", size: "large" },
  { code: "FAV", name: "Fakarava Airport", country: "PF", size: "medium" },
  { code: "FAY", name: "Fayetteville Regional Airport - Grannis Field", country: "US", size: "medium" },
  { code: "FBD", name: "Fayzabad Airport", country: "AF", size: "small" },
  { code: "FBE", name: "Francisco Beltr\xE3o \u2013 Paulo Abdala Airport", country: "BR", size: "small" },
  { code: "FBM", name: "Lubumbashi International Airport", country: "CD", size: "large" },
  { code: "FCA", name: "Kalispell \u2013 Glacier Park International Airport", country: "US", size: "medium" },
  {
    code: "FCN",
    name: "Wurster Nordseek\xFCste \u2013 Sea-Airport Cuxhaven/Nordholz / Nordholz Naval Airbase",
    country: "DE",
    size: "medium"
  },
  { code: "FCO", name: "Rome\u2013Fiumicino Leonardo da Vinci International Airport", country: "IT", size: "large" },
  { code: "FDE", name: "F\xF8rde Airport, Bringeland", country: "NO", size: "small" },
  { code: "FDF", name: "Fort-de-France \u2013 Martinique Aim\xE9 C\xE9saire International Airport", country: "MQ", size: "large" },
  { code: "FDH", name: "Friedrichshafen \u2013 Bodensee Airport Friedrichshafen", country: "DE", size: "large" },
  { code: "FDU", name: "Bandundu Airport", country: "CD", size: "medium" },
  { code: "FEG", name: "Fergana International Airport", country: "UZ", size: "medium" },
  { code: "FEN", name: "Fernando de Noronha Airport", country: "BR", size: "medium" },
  { code: "FEZ", name: "Sa\xEFss \u2013 Fes Sa\xEFss International Airport", country: "MA", size: "large" },
  { code: "FGU", name: "Fangatau Airport", country: "PF", size: "medium" },
  { code: "FHZ", name: "Fakahina Airport", country: "PF", size: "small" },
  { code: "FIE", name: "Fair Isle Airport", country: "GB", size: "small" },
  { code: "FIH", name: "Kinshasa \u2013 Ndjili International Airport", country: "CD", size: "large" },
  { code: "FIZ", name: "Fitzroy Crossing Airport", country: "AU", size: "medium" },
  { code: "FJR", name: "Fujairah International Airport", country: "AE", size: "large" },
  { code: "FKB", name: "Rheinm\xFCnster \u2013 Karlsruhe Baden-Baden Airport", country: "DE", size: "large" },
  { code: "FKI", name: "Kisangani \u2013 Bangoka International Airport", country: "CD", size: "large" },
  { code: "FKQ", name: "Fakfak Airport", country: "ID", size: "medium" },
  { code: "FKS", name: "Sukagawa \u2013 Fukushima Airport", country: "JP", size: "medium" },
  { code: "FLA", name: "Florencia \u2013 Gustavo Artunduaga Paredes Airport", country: "CO", size: "medium" },
  { code: "FLG", name: "Flagstaff Pulliam Airport", country: "US", size: "medium" },
  { code: "FLL", name: "Fort Lauderdale Hollywood International Airport", country: "US", size: "large" },
  { code: "FLN", name: "Florian\xF3polis \u2013 Herc\xEDlio Luz International Airport", country: "BR", size: "large" },
  { code: "FLO", name: "Florence Regional Airport", country: "US", size: "medium" },
  { code: "FLR", name: "Firenze (FI) \u2013 Florence Airport, Peretola", country: "IT", size: "large" },
  { code: "FLS", name: "Whitemark \u2013 Flinders Island Airport", country: "AU", size: "small" },
  { code: "FLW", name: "Santa Cruz das Flores \u2013 Flores Airport", country: "PT", size: "medium" },
  { code: "FLZ", name: "Sibolga (Pinangsori) \u2013 Dr. Ferdinand Lumban Tobing Airport", country: "ID", size: "medium" },
  { code: "FMA", name: "Formosa National Airport", country: "AR", size: "medium" },
  { code: "FMI", name: "Kalemie Airport", country: "CD", size: "medium" },
  { code: "FMM", name: "Memmingen Allgau Airport", country: "DE", size: "large" },
  { code: "FMO", name: "Greven \u2013 M\xFCnster Osnabr\xFCck Airport", country: "DE", size: "large" },
  { code: "FMT", name: "Faresmaathodaa \u2013 Faresmaathoda Airport", country: "MV", size: "small" },
  { code: "FNA", name: "Freetown (Lungi-Town) \u2013 Lungi International Airport", country: "SL", size: "large" },
  { code: "FNC", name: "Funchal \u2013 Cristiano Ronaldo International Airport", country: "PT", size: "large" },
  { code: "FND", name: "Funadhoo Airport", country: "MV", size: "small" },
  { code: "FNI", name: "N\xEEmes/Garons \u2013 N\xEEmes-Arles-Camargue Airport", country: "FR", size: "medium" },
  { code: "FNJ", name: "Pyongyang Sunan International Airport", country: "KP", size: "large" },
  { code: "FNT", name: "Flint \u2013 Bishop International Airport", country: "US", size: "medium" },
  { code: "FOA", name: "Foula Airfield", country: "GB", size: "small" },
  { code: "FOC", name: "Fuzhou (Changle) \u2013 Fuzhou Changle International Airport", country: "CN", size: "large" },
  { code: "FOD", name: "Fort Dodge Regional Airport", country: "US", size: "medium" },
  { code: "FOG", name: "Foggia (FG) \u2013 Foggia Gino Lisa Airport", country: "IT", size: "medium" },
  { code: "FON", name: "La Fortuna Arenal Airport", country: "CR", size: "medium" },
  { code: "FOR", name: "Fortaleza \u2013 Pinto Martins International Airport", country: "BR", size: "large" },
  { code: "FPO", name: "Freeport \u2013 Grand Bahama International Airport", country: "BS", size: "large" },
  { code: "FRA", name: "Frankfurt am Main \u2013 Frankfurt Main Airport", country: "DE", size: "large" },
  { code: "FRD", name: "Friday Harbor Airport", country: "US", size: "medium" },
  { code: "FRE", name: "Fera Island \u2013 Fera/Maringe Airport", country: "SB", size: "small" },
  { code: "FRL", name: "Forl\xEC (FC) \u2013 Forl\xEC-Luigi Ridolfi International Airport", country: "IT", size: "medium" },
  { code: "FRO", name: "Flor\xF8 Airport", country: "NO", size: "medium" },
  { code: "FRS", name: "San Benito \u2013 Mundo Maya International Airport", country: "GT", size: "medium" },
  { code: "FRW", name: "Francistown \u2013 Phillip Gaonwe Matante International Airport", country: "BW", size: "large" },
  { code: "FSC", name: "Figari Sud-Corse Airport", country: "FR", size: "large" },
  { code: "FSD", name: "Sioux Falls Regional Airport", country: "US", size: "medium" },
  { code: "FSH", name: "Singkil \u2013 Syekh Hamzah Fansyuri Airport", country: "ID", size: "small" },
  { code: "FSM", name: "Fort Smith Regional Airport", country: "US", size: "medium" },
  { code: "FSP", name: "Saint-Pierre Pointe-Blanche Airport", country: "PM", size: "medium" },
  { code: "FSZ", name: "Makinohara / Shimada \u2013 Mount Fuji Shizuoka Airport", country: "JP", size: "large" },
  { code: "FTA", name: "Futuna Island \u2013 Futuna Airport", country: "VU", size: "small" },
  { code: "FTE", name: "El Calafate - Commander Armando Tola International Airport", country: "AR", size: "medium" },
  { code: "FTI", name: "Fitiuta Village \u2013 Fitiuta Airport", country: "AS", size: "small" },
  { code: "FTU", name: "T\xF4lanaro Airport", country: "MG", size: "medium" },
  { code: "FTW", name: "Fort Worth Meacham International Airport", country: "US", size: "medium" },
  { code: "FUE", name: "El Matorral \u2013 Fuerteventura Airport", country: "ES", size: "large" },
  { code: "FUG", name: "Yingzhou, Fuyang \u2013 Fuyang Xiguan Airport", country: "CN", size: "medium" },
  { code: "FUJ", name: "Goto \u2013 Fukue Airport", country: "JP", size: "medium" },
  { code: "FUK", name: "Fukuoka Airport", country: "JP", size: "large" },
  { code: "FUN", name: "Funafuti International Airport", country: "TV", size: "medium" },
  { code: "FUO", name: "Foshan (Nanhai) \u2013 Foshan Shadi Airport", country: "CN", size: "medium" },
  { code: "FUT", name: "Futuna Island \u2013 Pointe Vele Airport", country: "WF", size: "small" },
  { code: "FWA", name: "Fort Wayne International Airport", country: "US", size: "medium" },
  { code: "FYJ", name: "Fuyuan Dongji Airport", country: "CN", size: "medium" },
  { code: "FYN", name: "Fuyun Koktokay Airport", country: "CN", size: "medium" },
  { code: "FYU", name: "Fort Yukon Airport", country: "US", size: "medium" },
  { code: "GAJ", name: "Higashine \u2013 Yamagata Airport", country: "JP", size: "medium" },
  { code: "GAL", name: "Galena \u2013 Edward G. Pitka Sr Airport", country: "US", size: "medium" },
  { code: "GAM", name: "Gambell Airport", country: "US", size: "medium" },
  { code: "GAN", name: "Gan International Airport", country: "MV", size: "large" },
  { code: "GAQ", name: "Gao International Airport", country: "ML", size: "medium" },
  { code: "GAU", name: "Guwahati \u2013 Lokpriya Gopinath Bordoloi International Airport", country: "IN", size: "large" },
  { code: "GAX", name: "Gamba Airport", country: "GA", size: "small" },
  { code: "GAY", name: "Gaya Airport", country: "IN", size: "medium" },
  { code: "GBB", name: "Gabala International Airport", country: "AZ", size: "medium" },
  { code: "GBE", name: "Gaborone \u2013 Sir Seretse Khama International Airport", country: "BW", size: "large" },
  { code: "GBI", name: "Kalaburagi Airport", country: "IN", size: "small" },
  { code: "GBJ", name: "Grand-Bourg \u2013 Marie-Galante Airport", country: "GP", size: "medium" },
  { code: "GBZ", name: "Claris \u2013 Great Barrier Aerodrome", country: "NZ", size: "small" },
  { code: "GCC", name: "Gillette \u2013 Northeast Wyoming Regional Airport", country: "US", size: "medium" },
  { code: "GCH", name: "Gachsaran Airport", country: "IR", size: "medium" },
  { code: "GCI", name: "Saint Peter Port \u2013 Guernsey Airport", country: "GG", size: "medium" },
  { code: "GCK", name: "Garden City Regional Airport", country: "US", size: "medium" },
  { code: "GCM", name: "George Town \u2013 Owen Roberts International Airport", country: "KY", size: "large" },
  { code: "GCN", name: "Grand Canyon - Tusayan \u2013 Grand Canyon National Park Airport", country: "US", size: "medium" },
  { code: "GDB", name: "Gondia Airport", country: "IN", size: "medium" },
  { code: "GDE", name: "Gode Airport", country: "ET", size: "medium" },
  { code: "GDL", name: "Guadalajara International Airport", country: "MX", size: "large" },
  { code: "GDN", name: "Gda\u0144sk \u2013 R\u0119biechowo", country: "PL", size: "large" },
  { code: "GDQ", name: "Azezo \u2013 Gondar Airport", country: "ET", size: "medium" },
  { code: "GDT", name: "Cockburn Town \u2013 JAGS McCartney International Airport", country: "TC", size: "medium" },
  { code: "GDV", name: "Glendive \u2013 Dawson Community Airport", country: "US", size: "medium" },
  { code: "GDX", name: "Magadan \u2013 Sokol Airport", country: "RU", size: "medium" },
  { code: "GDZ", name: "Gelendzhik Airport", country: "RU", size: "medium" },
  { code: "GEA", name: "Noum\xE9a Magenta Airport", country: "NC", size: "medium" },
  { code: "GEG", name: "Spokane International Airport", country: "US", size: "large" },
  { code: "GEL", name: "Santo \xC2ngelo Airport", country: "BR", size: "medium" },
  { code: "GEM", name: "Mengomey\xE9n \u2013 President Obiang Nguema International Airport", country: "GQ", size: "medium" },
  { code: "GEO", name: "Georgetown \u2013 Cheddi Jagan International Airport", country: "GY", size: "large" },
  { code: "GER", name: "Nueva Gerona \u2013 Rafael Cabrera Airport", country: "CU", size: "medium" },
  { code: "GES", name: "General Santos International Airport", country: "PH", size: "large" },
  { code: "GET", name: "Moonyoonooka \u2013 Geraldton Airport", country: "AU", size: "medium" },
  { code: "GEV", name: "G\xE4llivare Airport", country: "SE", size: "medium" },
  { code: "GFF", name: "Griffith Airport", country: "AU", size: "medium" },
  { code: "GFK", name: "Grand Forks International Airport", country: "US", size: "medium" },
  { code: "GGF", name: "Almeirim Airport", country: "BR", size: "small" },
  { code: "GGG", name: "Longview \u2013 East Texas Regional Airport", country: "US", size: "medium" },
  { code: "GGJ", name: "Gua\xEDra Airport", country: "BR", size: "small" },
  { code: "GGR", name: "Garowe Airport", country: "SO", size: "small" },
  { code: "GGS", name: "Gobernador Gregores Airport", country: "AR", size: "small" },
  { code: "GGT", name: "Moss Town \u2013 Exuma International Airport", country: "BS", size: "medium" },
  { code: "GGW", name: "Glasgow Valley County Airport Wokal Field", country: "US", size: "medium" },
  { code: "GHA", name: "El Atteuf \u2013 Noum\xE9rat - Moufdi Zakaria Airport", country: "DZ", size: "medium" },
  { code: "GHB", name: "Governor's Harbour Airport", country: "BS", size: "medium" },
  { code: "GHV", name: "Bra\u0219ov (Ghimbav) \u2013 Bra\u0219ov-Ghimbav International Airport", country: "RO", size: "large" },
  { code: "GIB", name: "Gibraltar Airport", country: "GI", size: "large" },
  { code: "GIC", name: "Boigu Island Airport", country: "AU", size: "small" },
  { code: "GID", name: "Gitega Airport", country: "BI", size: "medium" },
  { code: "GIG", name: "Rio de Janeiro Gale\xE3o \u2013 Tom Jobim International Airport", country: "BR", size: "large" },
  { code: "GIL", name: "Gilgit Airport", country: "PK", size: "medium" },
  { code: "GIS", name: "Gisborne Airport", country: "NZ", size: "medium" },
  { code: "GIZ", name: "Jizan Regional Airport / King Abdullah bin Abdulaziz Airport", country: "SA", size: "medium" },
  { code: "GJA", name: "Guanaja \u2013 La Laguna Airport", country: "HN", size: "medium" },
  { code: "GJL", name: "Tahir \u2013 Jijel Ferhat Abbas Airport", country: "DZ", size: "large" },
  { code: "GJT", name: "Grand Junction Regional Airport", country: "US", size: "medium" },
  { code: "GKA", name: "Goronka \u2013 Goroka Airport", country: "PG", size: "medium" },
  { code: "GKK", name: "Huvadhu Atoll \u2013 Kooddoo Airport", country: "MV", size: "small" },
  { code: "GKN", name: "Gulkana Airport", country: "US", size: "medium" },
  { code: "GLA", name: "Glasgow Airport", country: "GB", size: "large" },
  { code: "GLF", name: "Golfito Airport", country: "CR", size: "medium" },
  { code: "GLH", name: "Greenville \u2013 Mid Delta Regional Airport", country: "US", size: "medium" },
  { code: "GLK", name: "Galcaio Airport", country: "SO", size: "small" },
  { code: "GLT", name: "Gladstone Airport", country: "AU", size: "medium" },
  { code: "GLV", name: "Golovin Airport", country: "US", size: "small" },
  { code: "GMA", name: "Gemena Airport", country: "CD", size: "medium" },
  { code: "GMB", name: "Gambela Airport", country: "ET", size: "medium" },
  { code: "GME", name: "Gomel Airport", country: "BY", size: "medium" },
  { code: "GMI", name: "Gasmata Island Airport", country: "PG", size: "small" },
  { code: "GMO", name: "Gombe Lawanti International Airport", country: "NG", size: "medium" },
  { code: "GMP", name: "Seoul Gimpo International Airport", country: "KR", size: "large" },
  { code: "GMQ", name: "Golog (Maq\xEAn) \u2013 Golog Maq\xEAn Airport", country: "CN", size: "medium" },
  { code: "GMR", name: "Totegegie Airport", country: "PF", size: "medium" },
  { code: "GMZ", name: "Alajero, La Gomera Island \u2013 La Gomera Airport", country: "ES", size: "small" },
  { code: "GNB", name: "Grenoble Alpes Is\xE8re Airport", country: "FR", size: "medium" },
  { code: "GND", name: "Saint George's \u2013 Maurice Bishop International Airport", country: "GD", size: "large" },
  { code: "GNJ", name: "Ganja International Airport", country: "AZ", size: "large" },
  { code: "GNS", name: "Gunungsitoli \u2013 Binaka Airport", country: "ID", size: "medium" },
  { code: "GNU", name: "Goodnews Airport", country: "US", size: "small" },
  { code: "GNV", name: "Gainesville Regional Airport", country: "US", size: "medium" },
  { code: "GNY", name: "\u015Eanl\u0131urfa GAP Airport", country: "TR", size: "large" },
  { code: "GOA", name: "Genova (GE) \u2013 Genoa Cristoforo Colombo Airport", country: "IT", size: "large" },
  { code: "GOH", name: "Nuuk International Airport", country: "GL", size: "large" },
  { code: "GOI", name: "Vasco da Gama \u2013 Goa Dabolim International Airport", country: "IN", size: "large" },
  { code: "GOJ", name: "Nizhny Novgorod / Strigino International Airport", country: "RU", size: "large" },
  { code: "GOM", name: "Goma International Airport", country: "CD", size: "large" },
  { code: "GOP", name: "Gorakhpur Airport", country: "IN", size: "medium" },
  { code: "GOQ", name: "Golmud Airport", country: "CN", size: "medium" },
  { code: "GOT", name: "G\xF6teborg Landvetter Airport", country: "SE", size: "large" },
  { code: "GOU", name: "Garoua International Airport", country: "CM", size: "large" },
  { code: "GOV", name: "Nhulunbuy \u2013 Gove Airport", country: "AU", size: "medium" },
  { code: "GOX", name: "Mopa \u2013 Manohar International Airport", country: "IN", size: "large" },
  { code: "GOY", name: "Tura Mountain Airport", country: "RU", size: "small" },
  { code: "GPA", name: "Patras Araxos Agamemnon Airport", country: "GR", size: "medium" },
  { code: "GPI", name: "Guapi Airport", country: "CO", size: "medium" },
  { code: "GPS", name: "Isla Baltra \u2013 Seymour Galapagos Ecological Airport", country: "EC", size: "medium" },
  { code: "GPT", name: "Gulfport Biloxi International Airport", country: "US", size: "medium" },
  { code: "GRB", name: "Green Bay \u2013 Austin Straubel International Airport", country: "US", size: "medium" },
  { code: "GRI", name: "Grand Island \u2013 Central Nebraska Regional Airport", country: "US", size: "medium" },
  { code: "GRJ", name: "George Airport", country: "ZA", size: "large" },
  {
    code: "GRK",
    name: "Fort Cavazos \u2013 Killeen Regional Airport / Robert Gray Army Airfield",
    country: "US",
    size: "medium"
  },
  { code: "GRO", name: "Girona-Costa Brava Airport", country: "ES", size: "large" },
  { code: "GRQ", name: "Groningen Airport Eelde", country: "NL", size: "large" },
  { code: "GRR", name: "Grand Rapids \u2013 Gerald R. Ford International Airport", country: "US", size: "large" },
  {
    code: "GRU",
    name: "S\xE3o Paulo/Guarulhos\u2013Governor Andr\xE9 Franco Montoro International Airport",
    country: "BR",
    size: "large"
  },
  { code: "GRV", name: "Grozny \u2013 Akhmat Kadyrov Grozny International Airport", country: "RU", size: "large" },
  { code: "GRW", name: "Santa Cruz da Graciosa \u2013 Graciosa Airport", country: "PT", size: "medium" },
  { code: "GRX", name: "Granada \u2013 F.G.L. Airport Granada-Ja\xE9n Airport", country: "ES", size: "medium" },
  { code: "GRY", name: "Gr\xEDmsey/Sandv\xEDk \u2013 Gr\xEDmsey Airport", country: "IS", size: "medium" },
  { code: "GRZ", name: "Feldkirchen bei Graz \u2013 Graz Airport", country: "AT", size: "large" },
  { code: "GSM", name: "Qeshm(Dayrestan) \u2013 Qeshm International Airport", country: "IR", size: "large" },
  { code: "GSO", name: "Greensboro \u2013 Piedmont Triad International Airport", country: "US", size: "large" },
  {
    code: "GSP",
    name: "Greenville/Greer/Spartanburg \u2013 Greenville-Spartanburg International Airport",
    country: "US",
    size: "medium"
  },
  { code: "GST", name: "Gustavus Airport", country: "US", size: "medium" },
  { code: "GSV", name: "Saratov \u2013 Gagarin International Airport", country: "RU", size: "large" },
  { code: "GTA", name: "Gatokae Aerodrome", country: "SB", size: "small" },
  { code: "GTE", name: "Groote Eylandt Airport", country: "AU", size: "medium" },
  { code: "GTF", name: "Great Falls International Airport", country: "US", size: "medium" },
  { code: "GTO", name: "Gorontalo \u2013 Jalaluddin Airport", country: "ID", size: "small" },
  {
    code: "GTR",
    name: "Columbus/W Point/Starkville \u2013 Golden Triangle Regional Airport",
    country: "US",
    size: "medium"
  },
  { code: "GUA", name: "Guatemala City \u2013 La Aurora International Airport", country: "GT", size: "large" },
  { code: "GUB", name: "San Quint\xEDn \u2013 Guerrero Negro Airport", country: "MX", size: "small" },
  { code: "GUC", name: "Gunnison Crested Butte Regional Airport", country: "US", size: "medium" },
  { code: "GUM", name: "Hag\xE5t\xF1a \u2013 Antonio B. Won Pat International Airport", country: "GU", size: "large" },
  { code: "GUP", name: "Gallup Municipal Airport", country: "US", size: "medium" },
  { code: "GUR", name: "Gurney Airport", country: "PG", size: "medium" },
  { code: "GUW", name: "Atyrau International Airport", country: "KZ", size: "large" },
  { code: "GUZ", name: "Guarapari Airport", country: "BR", size: "small" },
  { code: "GVA", name: "Geneva International Airport", country: "CH", size: "large" },
  { code: "GVR", name: "Governador Valadares \u2013 Coronel Altino Machado Airport", country: "BR", size: "medium" },
  { code: "GWD", name: "Gurandani \u2013 New Gwadar International Airport", country: "PK", size: "large" },
  { code: "GWL", name: "Gwalior Airport", country: "IN", size: "medium" },
  { code: "GWT", name: "Sylt \u2013 Westerland Sylt Airport", country: "DE", size: "medium" },
  { code: "GXF", name: "Seiyun Hadhramaut International Airport", country: "YE", size: "large" },
  { code: "GXG", name: "Negage Airport", country: "AO", size: "medium" },
  { code: "GXH", name: "Gannan (Xiahe) \u2013 Gannan Xiahe Airport", country: "CN", size: "medium" },
  { code: "GYA", name: "Guayaramer\xEDn Airport", country: "BO", size: "medium" },
  { code: "GYD", name: "Baku \u2013 Heydar Aliyev International Airport", country: "AZ", size: "large" },
  { code: "GYE", name: "Guayaquil \u2013 Jos\xE9 Joaqu\xEDn de Olmedo International Airport", country: "EC", size: "large" },
  { code: "GYM", name: "Guaymas \u2013 General Jos\xE9 Mar\xEDa Y\xE1\xF1ez International Airport", country: "MX", size: "medium" },
  { code: "GYN", name: "Goi\xE2nia \u2013 Santa Genoveva International Airport", country: "BR", size: "large" },
  { code: "GYS", name: "Guangyuan (Lizhou) \u2013 Guangyuan Panlong Airport", country: "CN", size: "medium" },
  { code: "GYU", name: "Guyuan (Yuanzhou) \u2013 Guyuan Liupanshan Airport", country: "CN", size: "medium" },
  { code: "GYY", name: "Gary/Chicago International Airport", country: "US", size: "medium" },
  { code: "GYZ", name: "Cosmo Newbery \u2013 Gruyere Airport", country: "AU", size: "small" },
  { code: "GZG", name: "Garz\xEA (Garz\xEA) \u2013 Garze Gesar Airport", country: "CN", size: "small" },
  { code: "GZO", name: "Gizo \u2013 Nusatupe Airport", country: "SB", size: "small" },
  { code: "GZP", name: "Gazipa\u015Fa-Alanya Airport", country: "TR", size: "medium" },
  { code: "GZT", name: "Gaziantep O\u011Fuzeli International Airport", country: "TR", size: "large" },
  { code: "HAA", name: "Hasvik Airport", country: "NO", size: "small" },
  { code: "HAC", name: "Hachijojima Airport", country: "JP", size: "medium" },
  { code: "HAD", name: "Halmstad Airport", country: "SE", size: "medium" },
  { code: "HAH", name: "Moroni \u2013 Prince Said Ibrahim International Airport", country: "KM", size: "large" },
  { code: "HAJ", name: "Hannover Airport", country: "DE", size: "large" },
  { code: "HAK", name: "Haikou (Meilan) \u2013 Haikou Meilan International Airport", country: "CN", size: "large" },
  { code: "HAL", name: "Halali Airport", country: "NA", size: "small" },
  { code: "HAM", name: "Hamburg Helmut Schmidt Airport", country: "DE", size: "large" },
  { code: "HAN", name: "Hanoi (Soc Son) \u2013 Noi Bai International Airport", country: "VN", size: "large" },
  { code: "HAQ", name: "Haa Dhaalu Atoll \u2013 Hanimaadhoo International Airport", country: "MV", size: "large" },
  { code: "HAS", name: "Hail International Airport", country: "SA", size: "large" },
  { code: "HAU", name: "Karm\xF8y \u2013 Haugesund Airport, Karm\xF8y", country: "NO", size: "medium" },
  { code: "HAV", name: "Havana \u2013 Jos\xE9 Mart\xED International Airport", country: "CU", size: "large" },
  { code: "HBA", name: "Hobart (Cambridge) \u2013 Hobart International Airport", country: "AU", size: "large" },
  { code: "HBE", name: "Alexandria International Airport", country: "EG", size: "large" },
  { code: "HBQ", name: "Haibei (Qilian) \u2013 Haibei Qilian Airport", country: "CN", size: "small" },
  { code: "HBX", name: "Hubballi Airport", country: "IN", size: "medium" },
  { code: "HCJ", name: "Hechi (Jinchengjiang) \u2013 Hechi Jinchengjiang Airport", country: "CN", size: "medium" },
  { code: "HCR", name: "Holy Cross Airport", country: "US", size: "medium" },
  { code: "HCZ", name: "Chenzhou Beihu Airport", country: "CN", size: "medium" },
  { code: "HDD", name: "Hyderabad Airport", country: "PK", size: "small" },
  { code: "HDF", name: "Zirchow \u2013 Heringsdorf Airport", country: "DE", size: "medium" },
  { code: "HDG", name: "Handan Airport", country: "CN", size: "medium" },
  { code: "HDK", name: "Kulhudhuffushi Airport", country: "MV", size: "small" },
  { code: "HDM", name: "Hamadan Airport", country: "IR", size: "medium" },
  { code: "HDN", name: "Hayden \u2013 Yampa Valley Airport", country: "US", size: "medium" },
  { code: "HDO", name: "Ghaziabad \u2013 Hindon Airport / Hindon Air Force Station", country: "IN", size: "small" },
  { code: "HDS", name: "Hoedspruit \u2013 Eastgate Airport / Air Force Base Hoedspruit", country: "ZA", size: "medium" },
  { code: "HDY", name: "Hat Yai International Airport", country: "TH", size: "large" },
  { code: "HEA", name: "Guzara \u2013 Herat - Khwaja Abdullah Ansari International Airport", country: "AF", size: "large" },
  { code: "HEH", name: "Heho Airport", country: "MM", size: "medium" },
  { code: "HEI", name: "Oesterdeichstrich \u2013 Heide-B\xFCsum Airport", country: "DE", size: "small" },
  { code: "HEK", name: "Heihe Aihui Airport", country: "CN", size: "medium" },
  { code: "HEL", name: "Helsinki (Vantaa) \u2013 Helsinki Vantaa Airport", country: "FI", size: "large" },
  { code: "HER", name: "Heraklion International Nikos Kazantzakis Airport", country: "GR", size: "large" },
  { code: "HET", name: "Hohhot Baita International Airport", country: "CN", size: "large" },
  { code: "HFA", name: "Haifa \u2013 Uri Michaeli Haifa International Airport", country: "IL", size: "medium" },
  { code: "HFE", name: "Hefei Xinqiao International Airport", country: "CN", size: "large" },
  { code: "HFN", name: "H\xF6fn \u2013 Hornafj\xF6r\xF0ur Airport", country: "IS", size: "medium" },
  { code: "HFS", name: "R\xE5da \u2013 Hagfors Airport", country: "SE", size: "small" },
  { code: "HFT", name: "Hammerfest Airport", country: "NO", size: "medium" },
  { code: "HGA", name: "Hargeisa \u2013 Egal International Airport", country: "SO", size: "large" },
  { code: "HGD", name: "Hughenden Airport", country: "AU", size: "small" },
  { code: "HGH", name: "Hangzhou Xiaoshan International Airport", country: "CN", size: "large" },
  { code: "HGI", name: "Hollongi \u2013 Itanagar Donyi Polo Hollongi Airport", country: "IN", size: "medium" },
  { code: "HGL", name: "Helgoland-D\xFCne Airport", country: "DE", size: "small" },
  { code: "HGN", name: "Mae Hong Son Airport", country: "TH", size: "medium" },
  { code: "HGO", name: "Korhogo Airport", country: "CI", size: "medium" },
  { code: "HGR", name: "Hagerstown Regional Richard A Henson Field", country: "US", size: "medium" },
  { code: "HGU", name: "Mount Hagen Kagamuga Airport", country: "PG", size: "medium" },
  { code: "HHH", name: "Hilton Head Island \u2013 Hilton Head Airport", country: "US", size: "medium" },
  { code: "HHN", name: "Frankfurt am Main (Lautzenhausen) \u2013 Frankfurt-Hahn Airport", country: "DE", size: "large" },
  { code: "HHQ", name: "Hua Hin Airport", country: "TH", size: "medium" },
  { code: "HHR", name: "Hawthorne \u2013 Jack Northrop Field Hawthorne Municipal Airport", country: "US", size: "medium" },
  { code: "HHZ", name: "Hikueru Airport", country: "PF", size: "small" },
  { code: "HIA", name: "Huai'an Lianshui Airport", country: "CN", size: "large" },
  { code: "HIB", name: "Hibbing \u2013 Range Regional Airport", country: "US", size: "medium" },
  { code: "HID", name: "Horn Island Airport", country: "AU", size: "medium" },
  { code: "HII", name: "Lake Havasu City Airport", country: "US", size: "medium" },
  { code: "HIJ", name: "Hiroshima Airport", country: "JP", size: "large" },
  { code: "HIL", name: "Shilavo Airport", country: "ET", size: "small" },
  { code: "HIN", name: "Sacheon Airport / Sacheon Air Base", country: "KR", size: "medium" },
  { code: "HIR", name: "Honiara International Airport", country: "SB", size: "large" },
  { code: "HJB", name: "Hejing Bayinbuluke Airport", country: "CN", size: "small" },
  { code: "HJJ", name: "Huaihua Zhijiang Airport", country: "CN", size: "medium" },
  { code: "HJR", name: "Khajuraho Airport", country: "IN", size: "medium" },
  { code: "HKD", name: "Hakodate Airport", country: "JP", size: "large" },
  { code: "HKG", name: "Hong Kong International Airport", country: "HK", size: "large" },
  { code: "HKK", name: "Hokitika Airfield", country: "NZ", size: "medium" },
  { code: "HKN", name: "Kimbe \u2013 Hoskins Airport", country: "PG", size: "medium" },
  { code: "HKT", name: "Phuket International Airport", country: "TH", size: "large" },
  { code: "HLA", name: "Johannesburg \u2013 Lanseria International Airport", country: "ZA", size: "large" },
  { code: "HLD", name: "Hailar \u2013 Hulunbuir Hailar Airport", country: "CN", size: "large" },
  { code: "HLE", name: "Jamestown \u2013 Saint Helena International Airport", country: "SH", size: "medium" },
  { code: "HLH", name: "Ulanhot Yilelite Airport", country: "CN", size: "small" },
  { code: "HLN", name: "Helena Regional Airport", country: "US", size: "medium" },
  { code: "HLP", name: "Jakarta \u2013 Halim Perdanakusuma International Airport", country: "ID", size: "large" },
  { code: "HLZ", name: "Hamilton International Airport", country: "NZ", size: "medium" },
  { code: "HMA", name: "Khanty-Mansiysk \u2013 Khanty Mansiysk Airport", country: "RU", size: "medium" },
  { code: "HMB", name: "Suhaj International Airport", country: "EG", size: "large" },
  { code: "HME", name: "Hassi Messaoud-Oued Irara Krim Belkacem Airport", country: "DZ", size: "medium" },
  { code: "HMI", name: "Hami Airport", country: "CN", size: "medium" },
  {
    code: "HMO",
    name: "Hermosillo \u2013 General Ignacio L. Pesqueira International Airport",
    country: "MX",
    size: "large"
  },
  { code: "HMS", name: "Muara Teweh \u2013 Haji Muhammad Sidik Airport", country: "ID", size: "small" },
  { code: "HMV", name: "Hemavan Airport", country: "SE", size: "small" },
  { code: "HNA", name: "Hanamaki \u2013 Iwate Hanamaki Airport", country: "JP", size: "medium" },
  { code: "HND", name: "Tokyo Haneda International Airport", country: "JP", size: "large" },
  { code: "HNH", name: "Hoonah Airport", country: "US", size: "small" },
  { code: "HNL", name: "Honolulu, Oahu \u2013 Daniel K. Inouye International Airport", country: "US", size: "large" },
  { code: "HNM", name: "Hana Airport", country: "US", size: "medium" },
  { code: "HNS", name: "Haines Airport", country: "US", size: "medium" },
  { code: "HNY", name: "Hengyang Nanyue Airport", country: "CN", size: "small" },
  { code: "HOB", name: "Hobbs \u2013 Lea County Regional Airport", country: "US", size: "medium" },
  { code: "HOF", name: "Hofuf \u2013 Al-Ahsa International Airport", country: "SA", size: "large" },
  { code: "HOG", name: "Holguin \u2013 Frank Pais International Airport", country: "CU", size: "large" },
  { code: "HOI", name: "Otepa \u2013 Hao Airport", country: "PF", size: "medium" },
  { code: "HOK", name: "Lajamanu \u2013 Hooker Creek Airport", country: "AU", size: "small" },
  { code: "HOM", name: "Homer Airport", country: "US", size: "medium" },
  { code: "HOR", name: "Horta Airport", country: "PT", size: "medium" },
  { code: "HOT", name: "Hot Springs \u2013 Memorial Field Airport", country: "US", size: "medium" },
  { code: "HOU", name: "Houston \u2013 William P. Hobby Airport", country: "US", size: "large" },
  { code: "HOV", name: "\xD8rsta-Volda Airport, Hovden", country: "NO", size: "medium" },
  { code: "HPA", name: "Lifuka Island Airport", country: "TO", size: "medium" },
  { code: "HPB", name: "Hooper Bay Airport", country: "US", size: "small" },
  { code: "HPG", name: "Shennongjia (Hongping) \u2013 Shennongjia Hongping Airport", country: "CN", size: "medium" },
  { code: "HPH", name: "Haiphong (Hai An) \u2013 Cat Bi International Airport", country: "VN", size: "large" },
  { code: "HPN", name: "White Plains \u2013 Westchester County Airport", country: "US", size: "medium" },
  { code: "HQL", name: "Tashikuergan Hongqilafu Airport", country: "CN", size: "medium" },
  { code: "HQQ", name: "Anyang Hongqiqu Airport", country: "CN", size: "small" },
  { code: "HRB", name: "Harbin Taiping International Airport", country: "CN", size: "large" },
  { code: "HRE", name: "Harare \u2013 Robert Gabriel Mugabe International Airport", country: "ZW", size: "large" },
  { code: "HRF", name: "Hoarafushi Airport", country: "MV", size: "small" },
  { code: "HRG", name: "Hurghada International Airport", country: "EG", size: "large" },
  { code: "HRI", name: "Mattala Rajapaksa International Airport", country: "LK", size: "medium" },
  { code: "HRL", name: "Harlingen \u2013 Valley International Airport", country: "US", size: "medium" },
  { code: "HRO", name: "Harrison \u2013 Boone County Airport", country: "US", size: "medium" },
  { code: "HSA", name: "Turk\u0131stan \u2013 Hazrat Sultan International Airport", country: "KZ", size: "large" },
  { code: "HSC", name: "Shaoguan Danxia Airport", country: "CN", size: "medium" },
  { code: "HSG", name: "Saga \u2013 Kyushu Saga International Airport", country: "JP", size: "large" },
  { code: "HSL", name: "Huslia Airport", country: "US", size: "medium" },
  { code: "HSN", name: "Zhoushan Putuoshan International Airport", country: "CN", size: "large" },
  { code: "HSR", name: "Rajkot International Airport", country: "IN", size: "large" },
  { code: "HSS", name: "Hisar \u2013 Maharaja Agrasen International Airport", country: "IN", size: "large" },
  { code: "HSV", name: "Huntsville International Airport", country: "US", size: "medium" },
  { code: "HTA", name: "Chita-Kadala International Airport", country: "RU", size: "large" },
  { code: "HTG", name: "Khatanga Airport", country: "RU", size: "medium" },
  { code: "HTI", name: "Hamilton Island Airport", country: "AU", size: "medium" },
  { code: "HTN", name: "Hotan Airport", country: "CN", size: "medium" },
  { code: "HTS", name: "Huntington \u2013 Tri-State Airport / Milton J. Ferguson Field", country: "US", size: "medium" },
  { code: "HTT", name: "Mengnai \u2013 Huatugou Airport", country: "CN", size: "medium" },
  { code: "HTY", name: "Antakya \u2013 Hatay Airport", country: "TR", size: "medium" },
  { code: "HUE", name: "Akwi \u2013 Humera Airport", country: "ET", size: "small" },
  { code: "HUG", name: "Huehuetenango Airport", country: "GT", size: "small" },
  { code: "HUH", name: "Fare \u2013 Huahine-Fare Airport", country: "PF", size: "medium" },
  { code: "HUI", name: "Hu\u1EBF \u2013 Phu Bai International Airport", country: "VN", size: "large" },
  { code: "HUN", name: "Hualien City \u2013 Hualien Chiashan Airport", country: "TW", size: "large" },
  { code: "HUO", name: "Holingol Huolinhe Airport", country: "CN", size: "medium" },
  { code: "HUS", name: "Hughes Airport", country: "US", size: "small" },
  { code: "HUU", name: "Hu\xE1nuco \u2013 Alferez Fap David Figueroa Fernandini Airport", country: "PE", size: "medium" },
  { code: "HUX", name: "Huatulco \u2013 Bah\xEDas de Huatulco International Airport", country: "MX", size: "large" },
  { code: "HUY", name: "Grimsby, Lincolnshire \u2013 Humberside Airport", country: "GB", size: "medium" },
  { code: "HUZ", name: "Huizhou (Pingtan) \u2013 Huizhou Pingtan Airport", country: "CN", size: "medium" },
  { code: "HVB", name: "Hervey Bay Airport", country: "AU", size: "medium" },
  { code: "HVD", name: "Khovd Airport", country: "MN", size: "medium" },
  { code: "HVG", name: "Honningsv\xE5g Airport, Valan", country: "NO", size: "medium" },
  { code: "HVN", name: "New Haven \u2013 Tweed New Haven Airport", country: "US", size: "medium" },
  { code: "HVR", name: "Havre City County Airport", country: "US", size: "medium" },
  { code: "HWR", name: "Halwara International Airport", country: "IN", size: "large" },
  { code: "HXD", name: "Delingha \u2013 Haixi Delingha Airport", country: "CN", size: "medium" },
  { code: "HYA", name: "Hyannis \u2013 Cape Cod Gateway Airport", country: "US", size: "medium" },
  { code: "HYD", name: "Hyderabad \u2013 Rajiv Gandhi International Airport", country: "IN", size: "large" },
  { code: "HYN", name: "Taizhou (Luqiao) \u2013 Taizhou Luqiao Airport", country: "CN", size: "medium" },
  { code: "HYS", name: "Hays Regional Airport", country: "US", size: "medium" },
  { code: "HZA", name: "Heze (Dingtao) \u2013 Heze Mudan Airport", country: "CN", size: "medium" },
  { code: "HZG", name: "Hanzhong (Chenggu) \u2013 Hanzhong Chenggu Airport", country: "CN", size: "medium" },
  { code: "HZH", name: "Liping Airport", country: "CN", size: "medium" },
  { code: "IAA", name: "Igarka Airport", country: "RU", size: "medium" },
  { code: "IAD", name: "Dulles \u2013 Washington Dulles International Airport", country: "US", size: "large" },
  { code: "IAG", name: "Niagara Falls International Airport", country: "US", size: "medium" },
  { code: "IAH", name: "Houston \u2013 George Bush Intercontinental Airport", country: "US", size: "large" },
  { code: "IAM", name: "In Am\xE9nas \u2013 Zarzaitine - In Am\xE9nas Airport", country: "DZ", size: "medium" },
  { code: "IAN", name: "Kiana \u2013 Bob Baker Memorial Airport", country: "US", size: "medium" },
  { code: "IAO", name: "Del Carmen \u2013 Siargao Airport", country: "PH", size: "small" },
  { code: "IAR", name: "Tunoshna \u2013 Golden Ring Yaroslavl International Airport", country: "RU", size: "large" },
  { code: "IAS", name: "Ia\u015Fi International Airport", country: "RO", size: "large" },
  { code: "IBA", name: "Ibadan Airport", country: "NG", size: "medium" },
  { code: "IBB", name: "Puerto Villamil \u2013 General Villamil Airport", country: "EC", size: "small" },
  { code: "IBE", name: "Ibagu\xE9 \u2013 Perales Airport", country: "CO", size: "medium" },
  { code: "IBR", name: "Omitama \u2013 Ibaraki Airport", country: "JP", size: "large" },
  { code: "IBZ", name: "Ibiza (Eivissa) \u2013 Ibiza Airport", country: "ES", size: "large" },
  { code: "ICC", name: "Isla de Coche \u2013 Andr\xE9s Miguel Salazar Marcano Airport", country: "VE", size: "small" },
  { code: "ICI", name: "Cicia Airport", country: "FJ", size: "small" },
  { code: "ICN", name: "Seoul \u2013 Incheon International Airport", country: "KR", size: "large" },
  { code: "ICT", name: "Wichita Dwight D. Eisenhower National Airport", country: "US", size: "medium" },
  { code: "IDA", name: "Idaho Falls Regional Airport", country: "US", size: "medium" },
  { code: "IDR", name: "Indore \u2013 Devi Ahilya Bai Holkar International Airport", country: "IN", size: "large" },
  { code: "IEG", name: "Zielona G\xF3ra \u2013 Babimost", country: "PL", size: "medium" },
  { code: "IFJ", name: "\xCDsafj\xF6r\xF0ur Airport", country: "IS", size: "medium" },
  { code: "IFN", name: "Isfahan Shahid Beheshti International Airport", country: "IR", size: "large" },
  { code: "IGA", name: "Matthew Town \u2013 Inagua Airport", country: "BS", size: "medium" },
  { code: "IGD", name: "I\u011Fd\u0131r Airport", country: "TR", size: "medium" },
  { code: "IGG", name: "Igiugig Airport", country: "US", size: "small" },
  { code: "IGR", name: "Puerto Iguazu \u2013 Cataratas Del Iguaz\xFA International Airport", country: "AR", size: "medium" },
  { code: "IGT", name: "Sunzha \u2013 Magas Airport", country: "RU", size: "medium" },
  { code: "IGU", name: "Foz do Igua\xE7u \u2013 Cataratas International Airport", country: "BR", size: "large" },
  { code: "IIA", name: "Inis Me\xE1in \u2013 Inishmaan Aerodrome", country: "IE", size: "small" },
  { code: "IJK", name: "Izhevsk Airport", country: "RU", size: "medium" },
  { code: "IKA", name: "Tehran \u2013 Imam Khomeini International Airport", country: "IR", size: "large" },
  { code: "IKG", name: "Karakol International Airport", country: "KG", size: "medium" },
  { code: "IKI", name: "Iki Airport", country: "JP", size: "medium" },
  { code: "IKO", name: "Nikolski Air Station", country: "US", size: "small" },
  { code: "IKS", name: "Tiksi Airport", country: "RU", size: "medium" },
  { code: "IKT", name: "Irkutsk International Airport", country: "RU", size: "large" },
  { code: "IKU", name: "Tamchy \u2013 Issyk-Kul International Airport", country: "KG", size: "large" },
  { code: "ILD", name: "Lleida-Alguaire Airport", country: "ES", size: "medium" },
  { code: "ILF", name: "Ilford Airport", country: "CA", size: "small" },
  { code: "ILG", name: "Wilmington Airport", country: "US", size: "medium" },
  { code: "ILI", name: "Iliamna Airport", country: "US", size: "medium" },
  { code: "ILM", name: "Wilmington International Airport", country: "US", size: "medium" },
  { code: "ILO", name: "Cabatuan \u2013 Iloilo International Airport", country: "PH", size: "large" },
  { code: "ILP", name: "\xCEle des Pins Airport", country: "NC", size: "medium" },
  { code: "ILQ", name: "Ilo \u2013 General Jorge Fernandez Maldon Airport", country: "PE", size: "medium" },
  {
    code: "ILR",
    name: "Ilorin/Ogbomosho \u2013 General Tunde Idiagbon International Airport",
    country: "NG",
    size: "large"
  },
  { code: "ILS", name: "San Salvador \u2013 Ilopango International Airport", country: "SV", size: "medium" },
  { code: "ILY", name: "Isle of Islay, Argyll and Bute \u2013 Islay Airport", country: "GB", size: "medium" },
  { code: "IMF", name: "Imphal \u2013 Bir Tikendrajit International Airport", country: "IN", size: "large" },
  { code: "IMK", name: "Simikot Airport", country: "NP", size: "small" },
  { code: "IMP", name: "Imperatriz \u2013 Prefeito Renato Moreira Airport", country: "BR", size: "medium" },
  { code: "IMT", name: "Kingsford \u2013 Ford Airport", country: "US", size: "medium" },
  { code: "INB", name: "Independence Airport", country: "BZ", size: "small" },
  { code: "INC", name: "Yinchuan Hedong International Airport", country: "CN", size: "large" },
  { code: "IND", name: "Indianapolis International Airport", country: "US", size: "large" },
  { code: "INH", name: "Inhambane Airport", country: "MZ", size: "medium" },
  { code: "INI", name: "Ni\u0161 Constantine the Great Airport", country: "RS", size: "large" },
  { code: "INL", name: "International Falls \u2013 Falls International Airport", country: "US", size: "medium" },
  { code: "INN", name: "Innsbruck Airport", country: "AT", size: "large" },
  { code: "INO", name: "Inongo Airport", country: "CD", size: "small" },
  { code: "INQ", name: "Inis O\xEDrr \u2013 Inisheer Aerodrome", country: "IE", size: "small" },
  { code: "INU", name: "Yaren \u2013 Nauru International Airport", country: "NR", size: "medium" },
  { code: "INV", name: "Inverness Airport", country: "GB", size: "medium" },
  { code: "INZ", name: "In Salah Airport", country: "DZ", size: "medium" },
  { code: "IOA", name: "Ioannina King Pyrrhus National Airport", country: "GR", size: "medium" },
  { code: "IOM", name: "Castletown \u2013 Isle of Man Airport", country: "IM", size: "large" },
  { code: "IOR", name: "Inis M\xF3r \u2013 Inishmore Aerodrome", country: "IE", size: "small" },
  { code: "IOS", name: "Ilh\xE9us \u2013 Bahia - Jorge Amado Airport", country: "BR", size: "medium" },
  { code: "IPC", name: "Isla De Pascua \u2013 Mataveri International Airport", country: "CL", size: "large" },
  { code: "IPH", name: "Ipoh \u2013 Sultan Azlan Shah Airport", country: "MY", size: "large" },
  { code: "IPI", name: "Ipiales \u2013 San Luis Airport", country: "CO", size: "medium" },
  { code: "IPL", name: "Imperial County Airport", country: "US", size: "medium" },
  { code: "IPN", name: "Ipatinga \u2013 Usiminas Airport", country: "BR", size: "medium" },
  { code: "IPT", name: "Williamsport Regional Airport", country: "US", size: "medium" },
  { code: "IQM", name: "Qiemo Yudu Airport", country: "CN", size: "medium" },
  { code: "IQN", name: "Qingyang (Xifeng) \u2013 Qingyang Xifeng Airport", country: "CN", size: "medium" },
  { code: "IQQ", name: "Iquique \u2013 Diego Aracena International Airport", country: "CL", size: "large" },
  {
    code: "IQT",
    name: "Iquitos \u2013 Coronel FAP Francisco Secada Vignetta International Airport",
    country: "PE",
    size: "large"
  },
  { code: "IRA", name: "Kirakira \u2013 Ngorangora Airport", country: "SB", size: "small" },
  { code: "IRC", name: "Circle City (New) Airport", country: "US", size: "small" },
  { code: "IRG", name: "Lockhart River Airport", country: "AU", size: "medium" },
  { code: "IRJ", name: "La Rioja \u2013 Capitan V A Almonacid Airport", country: "AR", size: "medium" },
  { code: "IRK", name: "Kirksville Regional Airport", country: "US", size: "medium" },
  { code: "IRP", name: "Isiro \u2013 Matari Airport", country: "CD", size: "medium" },
  { code: "IRZ", name: "Santa Isabel do Rio Negro \u2013 Tapuruquara Airport", country: "BR", size: "small" },
  { code: "ISA", name: "Mount Isa Airport", country: "AU", size: "medium" },
  { code: "ISB", name: "Attock \u2013 Islamabad International Airport", country: "PK", size: "large" },
  { code: "ISC", name: "St. Mary's, Isles of Scilly \u2013 St. Mary's Airport", country: "GB", size: "small" },
  { code: "ISE", name: "Isparta \u2013 S\xFCleyman Demirel International Airport", country: "TR", size: "medium" },
  { code: "ISG", name: "Ishigaki \u2013 New Ishigaki Airport", country: "JP", size: "medium" },
  { code: "ISK", name: "Nashik International Airport", country: "IN", size: "large" },
  { code: "ISP", name: "Islip \u2013 Long Island MacArthur Airport", country: "US", size: "medium" },
  { code: "IST", name: "Istanbul \u2013 \u0130stanbul Airport", country: "TR", size: "large" },
  { code: "ISU", name: "Sulaymaniyah \u2013 Jalal Talabani International Airport", country: "IQ", size: "medium" },
  { code: "ITB", name: "Itaituba Airport", country: "BR", size: "medium" },
  { code: "ITH", name: "Ithaca Tompkins Regional Airport", country: "US", size: "medium" },
  { code: "ITM", name: "Osaka Itami International Airport", country: "JP", size: "large" },
  { code: "ITO", name: "Hilo International Airport", country: "US", size: "medium" },
  { code: "ITU", name: "Kurilsk \u2013 Iturup Airport", country: "RU", size: "small" },
  { code: "IUE", name: "Alofi \u2013 Niue International Airport", country: "NU", size: "medium" },
  { code: "IVC", name: "Invercargill Airport", country: "NZ", size: "medium" },
  { code: "IVL", name: "Ivalo Airport", country: "FI", size: "large" },
  { code: "IWA", name: "Ivanovo South Airport", country: "RU", size: "medium" },
  { code: "IWD", name: "Ironwood \u2013 Gogebic Iron County Airport", country: "US", size: "small" },
  { code: "IWJ", name: "Masuda \u2013 Iwami Airport", country: "JP", size: "medium" },
  { code: "IWK", name: "Iwakuni Kintaikyo Airport", country: "JP", size: "medium" },
  { code: "IXA", name: "Agartala - Maharaja Bir Bikram Airport", country: "IN", size: "medium" },
  { code: "IXB", name: "Siliguri \u2013 Bagdogra Airport", country: "IN", size: "large" },
  { code: "IXC", name: "Chandigarh \u2013 Shaheed Bhagat Singh International Airport", country: "IN", size: "large" },
  { code: "IXD", name: "Allahabad \u2013 Prayagraj Airport", country: "IN", size: "medium" },
  { code: "IXE", name: "Mangaluru International Airport", country: "IN", size: "large" },
  { code: "IXG", name: "Belgaum \u2013 Belagavi Airport", country: "IN", size: "medium" },
  { code: "IXI", name: "Lilabari North Lakhimpur Airport", country: "IN", size: "medium" },
  { code: "IXJ", name: "Jammu Airport", country: "IN", size: "medium" },
  { code: "IXK", name: "Keshod Airport", country: "IN", size: "medium" },
  { code: "IXL", name: "Leh Kushok Bakula Rimpochee Airport", country: "IN", size: "medium" },
  { code: "IXM", name: "Madurai Airport", country: "IN", size: "medium" },
  { code: "IXP", name: "Pathankot Airport", country: "IN", size: "medium" },
  { code: "IXR", name: "Ranchi \u2013 Birsa Munda Airport", country: "IN", size: "medium" },
  { code: "IXS", name: "Silchar Airport", country: "IN", size: "medium" },
  { code: "IXU", name: "Aurangabad Airport", country: "IN", size: "medium" },
  { code: "IXY", name: "Kandla Airport", country: "IN", size: "medium" },
  { code: "IXZ", name: "Port Blair \u2013 Veer Savarkar International Airport / INS Utkrosh", country: "IN", size: "large" },
  { code: "IZA", name: "Juiz de Fora \u2013 Presidente Itamar Franco Airport", country: "BR", size: "medium" },
  { code: "IZO", name: "Izumo Enmusubi Airport", country: "JP", size: "medium" },
  {
    code: "IZT",
    name: "Ixtepec \u2013 General Antonio C\xE1rdenas Rodr\xEDguez National Airport / Ixtepec Air Base",
    country: "MX",
    size: "medium"
  },
  { code: "JAC", name: "Jackson Hole Airport", country: "US", size: "medium" },
  { code: "JAE", name: "Ja\xE9n \u2013 Shumba Airport", country: "PE", size: "medium" },
  { code: "JAF", name: "Jaffna International Airport", country: "LK", size: "large" },
  { code: "JAI", name: "Jaipur International Airport", country: "IN", size: "large" },
  { code: "JAN", name: "Jackson-Medgar Wiley Evers International Airport", country: "US", size: "medium" },
  { code: "JAU", name: "Jauja \u2013 Francisco Carle Airport", country: "PE", size: "medium" },
  { code: "JAV", name: "Ilulissat Airport", country: "GL", size: "medium" },
  { code: "JAX", name: "Jacksonville International Airport", country: "US", size: "large" },
  { code: "JBB", name: "Jember \u2013 Notohadinegoro Airport", country: "ID", size: "small" },
  { code: "JBK", name: "Qitai Jiangbulake Airport", country: "CN", size: "small" },
  { code: "JBQ", name: "La Isabela International Airport", country: "DO", size: "medium" },
  { code: "JBR", name: "Jonesboro Municipal Airport", country: "US", size: "medium" },
  { code: "JCK", name: "Julia Creek Airport", country: "AU", size: "small" },
  { code: "JCL", name: "\u010Cesk\xE9 Bud\u011Bjovice South Bohemian Airport", country: "CZ", size: "large" },
  { code: "JDF", name: "Juiz de Fora \u2013 Francisco de Assis Airport", country: "BR", size: "medium" },
  { code: "JDH", name: "Jodhpur Airport", country: "IN", size: "medium" },
  { code: "JDO", name: "Juazeiro do Norte \u2013 Orlando Bezerra de Menezes Airport", country: "BR", size: "small" },
  { code: "JDZ", name: "Jingdezhen Luojia Airport", country: "CN", size: "medium" },
  { code: "JED", name: "Jeddah \u2013 King Abdulaziz International Airport", country: "SA", size: "large" },
  { code: "JEE", name: "Carrefour Sanon \u2013 J\xE9r\xE9mie Airport", country: "HT", size: "medium" },
  { code: "JEG", name: "Aasiaat Airport", country: "GL", size: "medium" },
  { code: "JEJ", name: "Ailinglapalap Atoll \u2013 Jeh Airport", country: "MH", size: "small" },
  { code: "JER", name: "St. Peter \u2013 Jersey Airport", country: "JE", size: "medium" },
  { code: "JFK", name: "New York \u2013 John F. Kennedy International Airport", country: "US", size: "large" },
  { code: "JFR", name: "Paamiut Airport", country: "GL", size: "small" },
  { code: "JGA", name: "Jamnagar Airport", country: "IN", size: "medium" },
  { code: "JGB", name: "Jagdalpur Airport", country: "IN", size: "small" },
  { code: "JGD", name: "Jiagedaqi \u2013 Daxing'anling Elunchun Airport", country: "CN", size: "medium" },
  { code: "JGN", name: "Jiayuguan International Airport", country: "CN", size: "large" },
  { code: "JGS", name: "Ji'an \u2013 Jinggangshan Airport", country: "CN", size: "medium" },
  { code: "JHB", name: "Johor Bahru \u2013 Senai International Airport", country: "MY", size: "large" },
  { code: "JHG", name: "Jinghong (Gasa) \u2013 Xishuangbanna Gasa International Airport", country: "CN", size: "large" },
  { code: "JHM", name: "Lahaina \u2013 Kapalua Airport", country: "US", size: "medium" },
  { code: "JHS", name: "Sisimiut Airport", country: "GL", size: "medium" },
  { code: "JIB", name: "Djibouti City \u2013 Djibouti-Ambouli Airport", country: "DJ", size: "large" },
  { code: "JIC", name: "Jinchang Jinchuan Airport", country: "CN", size: "medium" },
  { code: "JIJ", name: "Jijiga \u2013 Gerad Wilwal International Airport", country: "ET", size: "large" },
  { code: "JIK", name: "Ikaria Island \u2013 Ikaria Airport", country: "GR", size: "small" },
  { code: "JIM", name: "Jimma Airport", country: "ET", size: "medium" },
  { code: "JIO", name: "Tiakur \u2013 Jos Orno Imsula Airport", country: "ID", size: "small" },
  { code: "JIQ", name: "Qianjiang Wulingshan Airport", country: "CN", size: "medium" },
  { code: "JIU", name: "Jiujiang Lushan Airport", country: "CN", size: "small" },
  { code: "JJD", name: "Cruz \u2013 Comandante Ariston Pessoa Airport", country: "BR", size: "medium" },
  { code: "JJG", name: "Jaguaruna \u2013 Humberto Ghizzo Bortoluzzi Regional Airport", country: "BR", size: "small" },
  { code: "JJM", name: "Meru-Kinna \u2013 Mulika Lodge Airport", country: "KE", size: "small" },
  { code: "JJN", name: "Quanzhou Jinjiang International Airport", country: "CN", size: "large" },
  { code: "JJU", name: "Qaqortoq Airport", country: "GL", size: "medium" },
  { code: "JKG", name: "J\xF6nk\xF6ping Airport", country: "SE", size: "medium" },
  { code: "JKH", name: "Chios Island National Airport", country: "GR", size: "medium" },
  { code: "JKL", name: "Kalymnos Island \u2013 Kalymnos Airport", country: "GR", size: "small" },
  { code: "JKR", name: "Janakpur Airport", country: "NP", size: "medium" },
  { code: "JLG", name: "Jalgaon Airport", country: "IN", size: "small" },
  { code: "JLN", name: "Joplin Regional Airport", country: "US", size: "medium" },
  { code: "JLR", name: "Jabalpur Airport", country: "IN", size: "medium" },
  { code: "JMJ", name: "Pu'er (Lancang) \u2013 Lancang Jingmai Airport", country: "CN", size: "medium" },
  { code: "JMK", name: "Mykonos Island National Airport", country: "GR", size: "medium" },
  { code: "JMO", name: "Jomsom Airport", country: "NP", size: "small" },
  { code: "JMS", name: "Jamestown Regional Airport", country: "US", size: "medium" },
  { code: "JMU", name: "Jiamusi Songjiang International Airport", country: "CN", size: "medium" },
  { code: "JNB", name: "Johannesburg \u2013 O.R. Tambo International Airport", country: "ZA", size: "large" },
  { code: "JNG", name: "Jining Da'an Airport", country: "CN", size: "medium" },
  { code: "JNH", name: "Xiuzhou, Hangzhou \u2013 Jiaxing Nanhu Airport", country: "CN", size: "medium" },
  { code: "JNU", name: "Juneau International Airport", country: "US", size: "medium" },
  { code: "JNX", name: "Naxos Island National Airport", country: "GR", size: "small" },
  { code: "JNZ", name: "Jinzhou (Linghai) \u2013 Jinzhou Bay Airport", country: "CN", size: "medium" },
  { code: "JOE", name: "Joensuu Airport", country: "FI", size: "medium" },
  { code: "JOG", name: "Yogyakarta \u2013 Adisutjipto International Airport", country: "ID", size: "medium" },
  { code: "JOI", name: "Joinville \u2013 Lauro Carneiro de Loyola Airport", country: "BR", size: "medium" },
  { code: "JOL", name: "Jolo Airport", country: "PH", size: "medium" },
  { code: "JOS", name: "Jos \u2013 Yakubu Gowon Airport", country: "NG", size: "medium" },
  { code: "JPA", name: "Jo\xE3o Pessoa \u2013 Presidente Castro Pinto International Airport", country: "BR", size: "large" },
  { code: "JPE", name: "Paragominas \u2013 Nagib Demachki Airport", country: "BR", size: "small" },
  { code: "JPR", name: "Ji-Paran\xE1 Airport", country: "BR", size: "small" },
  { code: "JQA", name: "Uummannaq \u2013 Qaarsut Airport", country: "GL", size: "small" },
  { code: "JRG", name: "Jharsuguda Airport", country: "IN", size: "small" },
  { code: "JRH", name: "Jorhat Airport", country: "IN", size: "medium" },
  { code: "JRO", name: "Arusha \u2013 Kilimanjaro International Airport", country: "TZ", size: "large" },
  { code: "JSA", name: "Jaisalmer Airport", country: "IN", size: "medium" },
  { code: "JSH", name: "Crete Island \u2013 Sitia Airport", country: "GR", size: "medium" },
  { code: "JSI", name: "Skiathos Island National Airport", country: "GR", size: "medium" },
  { code: "JSJ", name: "Jiansanjiang Shidi Airport", country: "CN", size: "medium" },
  { code: "JSK", name: "Bandar-e-Jask \u2013 Jask Airport", country: "IR", size: "small" },
  { code: "JSR", name: "Jashore (Jessore) \u2013 Jessore Airport", country: "BD", size: "medium" },
  { code: "JST", name: "Johnstown \u2013 John Murtha Johnstown Cambria County Airport", country: "US", size: "medium" },
  { code: "JSU", name: "Maniitsoq Airport", country: "GL", size: "small" },
  { code: "JSY", name: "Syros Island \u2013 Syros Airport", country: "GR", size: "small" },
  { code: "JTC", name: "Bauru/Arealva\u2013Moussa Nakhal Tobias State Airport", country: "BR", size: "medium" },
  { code: "JTR", name: "Santorini Island \u2013 Santorini International Airport", country: "GR", size: "large" },
  { code: "JTY", name: "Astypalaia Island \u2013 Astypalaia Airport", country: "GR", size: "small" },
  { code: "JUB", name: "Juba International Airport", country: "SS", size: "large" },
  { code: "JUH", name: "Chizhou Jiuhuashan Airport", country: "CN", size: "small" },
  { code: "JUI", name: "Juist Airport", country: "DE", size: "small" },
  {
    code: "JUJ",
    name: "San Salvador de Jujuy \u2013 Gobernador Horacio Guzman International Airport",
    country: "AR",
    size: "large"
  },
  { code: "JUL", name: "Juliaca \u2013 Inca Manco Capac International Airport", country: "PE", size: "large" },
  { code: "JUM", name: "Jumla Airport", country: "NP", size: "small" },
  { code: "JUV", name: "Upernavik Airport", country: "GL", size: "small" },
  { code: "JUZ", name: "Quzhou (Kezheng) \u2013 Quzhou Airport", country: "CN", size: "medium" },
  { code: "JXA", name: "Jixi Xingkaihu Airport", country: "CN", size: "medium" },
  { code: "JYV", name: "Jyv\xE4skyl\xE4n Maalaiskunta \u2013 Jyv\xE4skyl\xE4 Airport", country: "FI", size: "medium" },
  { code: "JZH", name: "Ngawa (Songpan) \u2013 Jiuzhai Huanglong Airport", country: "CN", size: "medium" },
  { code: "KAA", name: "Kasama Airport", country: "ZM", size: "small" },
  { code: "KAB", name: "Kariba Airport", country: "ZW", size: "medium" },
  { code: "KAC", name: "Qamishli International Airport", country: "SY", size: "medium" },
  { code: "KAD", name: "Kaduna International Airport", country: "NG", size: "large" },
  { code: "KAI", name: "Kaieteur Falls \u2013 Kaieteur Airport", country: "GY", size: "medium" },
  { code: "KAJ", name: "Kajaani Airport", country: "FI", size: "medium" },
  { code: "KAL", name: "Kaltag Airport", country: "US", size: "small" },
  { code: "KAN", name: "Kano \u2013 Mallam Aminu Kano International Airport", country: "NG", size: "large" },
  { code: "KAO", name: "Kuusamo Airport", country: "FI", size: "medium" },
  { code: "KAT", name: "Awanui \u2013 Kaitaia Airport", country: "NZ", size: "medium" },
  { code: "KAW", name: "Kawthoung Airport", country: "MM", size: "medium" },
  { code: "KAX", name: "Kalbarri Airport", country: "AU", size: "small" },
  { code: "KBC", name: "Birch Creek Airport", country: "US", size: "small" },
  { code: "KBL", name: "Kabul International Airport", country: "AF", size: "large" },
  { code: "KBR", name: "Kota Baharu \u2013 Sultan Ismail Petra Airport", country: "MY", size: "medium" },
  { code: "KBU", name: "Stagen \u2013 Gusti Syamsir Alam Airport", country: "ID", size: "small" },
  { code: "KBV", name: "Krabi International Airport", country: "TH", size: "large" },
  { code: "KCA", name: "Kuqa Qiuci Airport", country: "CN", size: "small" },
  { code: "KCG", name: "Chignik Airport", country: "US", size: "small" },
  { code: "KCH", name: "Kuching International Airport", country: "MY", size: "large" },
  { code: "KCM", name: "Kahramanmara\u015F Airport", country: "TR", size: "medium" },
  { code: "KCQ", name: "Chignik Lake Airport", country: "US", size: "small" },
  { code: "KCT", name: "Galle \u2013 Koggala Airport", country: "LK", size: "medium" },
  { code: "KCY", name: "Krasnoyarsk Cheremshanka Airport", country: "RU", size: "medium" },
  { code: "KCZ", name: "Nankoku \u2013 Kochi Ryoma Airport", country: "JP", size: "large" },
  { code: "KDD", name: "Khuzdar Airport", country: "PK", size: "small" },
  { code: "KDH", name: "Kandahar \u2013 Ahmad Shah Baba International Airport", country: "AF", size: "large" },
  { code: "KDI", name: "Kendari \u2013 Haluoleo Airport", country: "ID", size: "small" },
  { code: "KDL", name: "K\xE4rdla Airport", country: "EE", size: "medium" },
  { code: "KDM", name: "Huvadhu Atoll \u2013 Kaadedhdhoo Airport", country: "MV", size: "medium" },
  { code: "KDO", name: "Kadhdhoo Airport", country: "MV", size: "medium" },
  { code: "KDU", name: "Skardu International Airport", country: "PK", size: "large" },
  { code: "KDV", name: "Vunisea Airport", country: "FJ", size: "small" },
  { code: "KEB", name: "Nanwalek Airport", country: "US", size: "small" },
  { code: "KEF", name: "Reykjav\xEDk \u2013 Keflavik International Airport", country: "IS", size: "large" },
  { code: "KEJ", name: "Kemerovo \u2013 Alexei Leonov Kemerovo International Airport", country: "RU", size: "large" },
  { code: "KEM", name: "Kemi / Tornio \u2013 Kemi-Tornio Airport", country: "FI", size: "medium" },
  { code: "KEP", name: "Nepalgunj Airport", country: "NP", size: "medium" },
  { code: "KER", name: "Kerman \u2013 Ayatollah Hashemi Rafsanjani International Airport", country: "IR", size: "large" },
  { code: "KET", name: "Kengtung Airport", country: "MM", size: "medium" },
  { code: "KEW", name: "Keewaywin Airport", country: "CA", size: "small" },
  { code: "KFG", name: "Kalkgurung Airport", country: "AU", size: "small" },
  { code: "KFP", name: "False Pass Airport", country: "US", size: "small" },
  { code: "KFS", name: "Kastamonu Airport", country: "TR", size: "medium" },
  { code: "KGA", name: "Kananga Airport", country: "CD", size: "medium" },
  { code: "KGC", name: "Kingscote Airport", country: "AU", size: "medium" },
  { code: "KGD", name: "Kaliningrad \u2013 Khrabrovo Airport", country: "RU", size: "large" },
  { code: "KGE", name: "Kagau Island \u2013 Kaghau Airport", country: "SB", size: "small" },
  { code: "KGF", name: "Karaganda \u2013 Sary-Arka Airport", country: "KZ", size: "large" },
  { code: "KGI", name: "Broadwood \u2013 Kalgoorlie Boulder Airport", country: "AU", size: "medium" },
  { code: "KGK", name: "Koliganek Airport", country: "US", size: "small" },
  { code: "KGL", name: "Kigali International Airport", country: "RW", size: "large" },
  { code: "KGP", name: "Kogalym International Airport", country: "RU", size: "medium" },
  { code: "KGS", name: 'Kos Island \u2013 Kos International Airport "Ippokratis"', country: "GR", size: "large" },
  { code: "KGT", name: "Garz\xEA (Kangding) \u2013 Kangding Airport", country: "CN", size: "medium" },
  { code: "KGX", name: "Grayling Airport", country: "US", size: "small" },
  { code: "KHD", name: "Khoram Abad Airport", country: "IR", size: "medium" },
  { code: "KHG", name: "Kashgar Laining International Airport", country: "CN", size: "large" },
  { code: "KHH", name: "Kaohsiung (Xiaogang) \u2013 Kaohsiung International Airport", country: "TW", size: "large" },
  { code: "KHI", name: "Karachi \u2013 Jinnah International Airport", country: "PK", size: "large" },
  { code: "KHK", name: "Khark Airport", country: "IR", size: "medium" },
  { code: "KHM", name: "Kanti Airport", country: "MM", size: "small" },
  { code: "KHN", name: "Nanchang Changbei International Airport", country: "CN", size: "large" },
  { code: "KHS", name: "Khasab Airport", country: "OM", size: "medium" },
  { code: "KHT", name: "Khost International Airport", country: "AF", size: "medium" },
  { code: "KHV", name: "Khabarovsk Novy Airport", country: "RU", size: "medium" },
  { code: "KHX", name: "Kihihi \u2013 Savannah Airstrip", country: "UG", size: "medium" },
  { code: "KHZ", name: "Kauehi Airport", country: "PF", size: "small" },
  { code: "KIE", name: "Kieta \u2013 Aropa Airport", country: "PG", size: "small" },
  { code: "KIF", name: "Kingfisher Lake Airport", country: "CA", size: "small" },
  { code: "KIH", name: "Kish Island \u2013 Kish International Airport", country: "IR", size: "large" },
  { code: "KIJ", name: "Niigata Airport", country: "JP", size: "large" },
  { code: "KIK", name: "Kirkuk International Airport", country: "IQ", size: "large" },
  { code: "KIM", name: "Kimberley Airport", country: "ZA", size: "large" },
  { code: "KIN", name: "Kingston \u2013 Norman Manley International Airport", country: "JM", size: "large" },
  { code: "KIO", name: "Kili Island \u2013 Kili Airport", country: "MH", size: "small" },
  { code: "KIR", name: "Farranfore \u2013 Kerry Airport", country: "IE", size: "medium" },
  { code: "KIS", name: "Kisumu International Airport", country: "KE", size: "large" },
  { code: "KIT", name: "Kithira Island \u2013 Kithira Airport", country: "GR", size: "small" },
  { code: "KIX", name: "Osaka \u2013 Kansai International Airport", country: "JP", size: "large" },
  { code: "KJA", name: "Krasnoyarsk International Airport", country: "RU", size: "large" },
  { code: "KJB", name: "Orvakal \u2013 Kurnool Airport", country: "IN", size: "medium" },
  { code: "KJH", name: "Kaili  (Huangping) \u2013 Kaili Huangping Airport", country: "CN", size: "medium" },
  { code: "KJI", name: "Burqin Kanas Airport", country: "CN", size: "medium" },
  { code: "KJT", name: "Kertajati International Airport", country: "ID", size: "medium" },
  { code: "KKA", name: "Koyuk Alfred Adams Airport", country: "US", size: "small" },
  { code: "KKC", name: "Khon Kaen Airport", country: "TH", size: "medium" },
  { code: "KKE", name: "Kerikeri Airport", country: "NZ", size: "medium" },
  { code: "KKH", name: "Kongiganak Airport", country: "US", size: "small" },
  { code: "KKI", name: "Akiachak Airport", country: "US", size: "small" },
  { code: "KKJ", name: "Kitakyushu Airport", country: "JP", size: "large" },
  { code: "KKN", name: "Kirkenes Airport, H\xF8ybuktmoen", country: "NO", size: "medium" },
  { code: "KKR", name: "Raitahiti \u2013 Kaukura Airport", country: "PF", size: "medium" },
  { code: "KKS", name: "Kashan Airport", country: "IR", size: "medium" },
  { code: "KKW", name: "Kikwit Airport", country: "CD", size: "medium" },
  { code: "KKX", name: "Kikai Airport", country: "JP", size: "medium" },
  { code: "KLG", name: "Kalskag Airport", country: "US", size: "small" },
  { code: "KLH", name: "Kolhapur Airport", country: "IN", size: "medium" },
  { code: "KLN", name: "Larsen Bay Airport", country: "US", size: "small" },
  { code: "KLO", name: "Kalibo International Airport", country: "PH", size: "large" },
  { code: "KLP", name: "Seruyan Kuala Pembuang Airport", country: "ID", size: "small" },
  { code: "KLR", name: "Kalmar Airport", country: "SE", size: "medium" },
  { code: "KLU", name: "Klagenfurt am W\xF6rthersee \u2013 Klagenfurt Airport", country: "AT", size: "large" },
  { code: "KLV", name: "Karlovy Vary Airport", country: "CZ", size: "large" },
  { code: "KLW", name: "Klawock Airport", country: "US", size: "medium" },
  { code: "KLX", name: "Kalamata Airport", country: "GR", size: "medium" },
  { code: "KMA", name: "Kerema Airport", country: "PG", size: "medium" },
  { code: "KMC", name: "King Khaled Military City Airport", country: "SA", size: "medium" },
  { code: "KME", name: "Kamembe Airport", country: "RW", size: "medium" },
  { code: "KMG", name: "Kunming Changshui International Airport", country: "CN", size: "large" },
  { code: "KMI", name: "Miyazaki Airport", country: "JP", size: "large" },
  { code: "KMJ", name: "Kumamoto Airport", country: "JP", size: "large" },
  { code: "KMN", name: "Kamina City Airport", country: "CD", size: "small" },
  { code: "KMO", name: "Manokotak Airport", country: "US", size: "small" },
  { code: "KMQ", name: "Kanazawa \u2013 Komatsu Airport / JASDF Komatsu Air Base", country: "JP", size: "large" },
  { code: "KMS", name: "Kumasi \u2013 Prempeh I International Airport", country: "GH", size: "large" },
  { code: "KMW", name: "Kostroma Sokerkino Airport", country: "RU", size: "medium" },
  { code: "KND", name: "Kindu Airport", country: "CD", size: "medium" },
  { code: "KNG", name: "Kaimana \u2013 Utarom Airport", country: "ID", size: "medium" },
  { code: "KNH", name: "Shang-I \u2013 Kinmen Airport", country: "TW", size: "medium" },
  { code: "KNK", name: "Kokhanok Airport", country: "US", size: "small" },
  { code: "KNO", name: "Beringin \u2013 Kualanamu International Airport", country: "ID", size: "large" },
  { code: "KNQ", name: "Kon\xE9 Airport", country: "NC", size: "medium" },
  { code: "KNS", name: "King Island Airport", country: "AU", size: "medium" },
  { code: "KNU", name: "Kanpur Airport", country: "IN", size: "medium" },
  { code: "KNW", name: "New Stuyahok Airport", country: "US", size: "small" },
  { code: "KNX", name: "Kununurra \u2013 East Kimberley Regional (Kununurra) Airport", country: "AU", size: "medium" },
  {
    code: "KOA",
    name: "Kailua-Kona \u2013 Ellison Onizuka Kona International Airport at Ke\u0101hole",
    country: "US",
    size: "large"
  },
  { code: "KOC", name: "Koumac Airport", country: "NC", size: "small" },
  { code: "KOE", name: "Kupang \u2013 El Tari Airport", country: "ID", size: "medium" },
  { code: "KOI", name: "Kirkwall, Orkney Islands \u2013 Kirkwall Airport", country: "GB", size: "medium" },
  { code: "KOJ", name: "Kagoshima Airport", country: "JP", size: "large" },
  { code: "KOK", name: "Kokkola / Kruunupyy \u2013 Kokkola-Pietarsaari Airport", country: "FI", size: "medium" },
  { code: "KOP", name: "Nakhon Phanom Airport", country: "TH", size: "medium" },
  { code: "KOS", name: "Preah Sihanouk \u2013 Sihanouk International Airport", country: "KH", size: "large" },
  { code: "KOT", name: "Kotlik Airport", country: "US", size: "small" },
  { code: "KOV", name: "Kokshetau International Airport", country: "KZ", size: "large" },
  { code: "KOW", name: "Ganzhou Huangjin Airport", country: "CN", size: "small" },
  { code: "KOZ", name: "Ouzinkie Airport", country: "US", size: "small" },
  { code: "KPN", name: "Kipnuk Airport", country: "US", size: "small" },
  { code: "KPO", name: "Pohang Airport (G-815/K-3)", country: "KR", size: "medium" },
  { code: "KPV", name: "Perryville Airport", country: "US", size: "small" },
  { code: "KPW", name: "Keperveem Airport", country: "RU", size: "medium" },
  { code: "KQA", name: "Akutan Airport", country: "US", size: "small" },
  { code: "KQH", name: "Ajmer (Kishangarh) \u2013 Kishangarh Airport Ajmer", country: "IN", size: "medium" },
  { code: "KQR", name: "Karara Airport", country: "AU", size: "small" },
  { code: "KQT", name: "Bokhtar International Airport", country: "TJ", size: "large" },
  { code: "KRB", name: "Karumba Airport", country: "AU", size: "small" },
  { code: "KRC", name: "Sungai Penuh \u2013 Departi Parbo Airport", country: "ID", size: "small" },
  { code: "KRE", name: "Kirundo Airport", country: "BI", size: "small" },
  { code: "KRF", name: "Nyland \u2013 Kramfors-Sollefte\xE5 H\xF6ga Kusten Airport", country: "SE", size: "medium" },
  { code: "KRI", name: "Kikori Airport", country: "PG", size: "small" },
  { code: "KRK", name: "Krak\xF3w \u2013 Balice", country: "PL", size: "large" },
  { code: "KRL", name: "Korla Licheng Airport", country: "CN", size: "medium" },
  { code: "KRN", name: "Kiruna Airport", country: "SE", size: "large" },
  { code: "KRO", name: "Kurgan Airport", country: "RU", size: "medium" },
  { code: "KRP", name: "Karup \u2013 Midtjyllands Airport / Air Base Karup", country: "DK", size: "medium" },
  { code: "KRR", name: "Krasnodar Pashkovsky International Airport", country: "RU", size: "large" },
  { code: "KRS", name: "Kristiansand(Kjevik) \u2013 Kristiansand Airport", country: "NO", size: "large" },
  { code: "KRT", name: "Khartoum International Airport", country: "SD", size: "large" },
  { code: "KRW", name: "Turkmenba\u015Fy International Airport", country: "TM", size: "medium" },
  { code: "KRY", name: "Karamay Airport", country: "CN", size: "small" },
  { code: "KSA", name: "Okat \u2013 Kosrae International Airport", country: "FM", size: "large" },
  { code: "KSC", name: "Ko\u0161ice International Airport", country: "SK", size: "medium" },
  { code: "KSD", name: "Karlstad Airport", country: "SE", size: "medium" },
  { code: "KSF", name: "Calden \u2013 Kassel Airport", country: "DE", size: "large" },
  { code: "KSH", name: "Kermanshah \u2013 Shahid Ashrafi Esfahani Airport", country: "IR", size: "medium" },
  { code: "KSJ", name: "Kasos Island \u2013 Kasos Airport", country: "GR", size: "small" },
  { code: "KSL", name: "Kassala Airport", country: "SD", size: "medium" },
  { code: "KSM", name: "St Mary's Airport", country: "US", size: "small" },
  { code: "KSN", name: "Kostanay International Airport", country: "KZ", size: "large" },
  { code: "KSO", name: "Argos Orestiko \u2013 Kastoria National Airport Aristotle", country: "GR", size: "small" },
  { code: "KSQ", name: "Karshi Airport", country: "UZ", size: "small" },
  { code: "KSR", name: "Benteng \u2013 Selayar - Haji Aroeppala Airport", country: "ID", size: "small" },
  { code: "KSU", name: "Kvernberget \u2013 Kristiansund Airport, Kvernberget", country: "NO", size: "medium" },
  { code: "KSY", name: "Kars Airport", country: "TR", size: "medium" },
  { code: "KSZ", name: "Kotlas Airport", country: "RU", size: "medium" },
  { code: "KTA", name: "Karratha Airport", country: "AU", size: "medium" },
  { code: "KTD", name: "Kitadait\u014Djima \u2013 Kitadaito Airport", country: "JP", size: "medium" },
  { code: "KTG", name: "Ketapang \u2013 Rahadi Osman Airport", country: "ID", size: "medium" },
  { code: "KTI", name: "Phnom Penh (Boeng Khyang) \u2013 Techo International Airport", country: "KH", size: "large" },
  { code: "KTM", name: "Kathmandu \u2013 Tribhuvan International Airport", country: "NP", size: "large" },
  { code: "KTN", name: "Ketchikan International Airport", country: "US", size: "medium" },
  { code: "KTP", name: "Tinson Pen Airport", country: "JM", size: "medium" },
  { code: "KTS", name: "Brevig Mission Airport", country: "US", size: "small" },
  { code: "KTT", name: "Kittil\xE4 International Airport", country: "FI", size: "large" },
  { code: "KTW", name: "Katowice \u2013 Pyrzowice", country: "PL", size: "large" },
  { code: "KUA", name: "Kuantan Airport", country: "MY", size: "medium" },
  { code: "KUD", name: "Kudat Airport", country: "MY", size: "small" },
  { code: "KUF", name: "Samara \u2013 Kurumoch International Airport", country: "RU", size: "large" },
  { code: "KUG", name: "Kubin Island Airport", country: "AU", size: "small" },
  { code: "KUH", name: "Kushiro Airport", country: "JP", size: "medium" },
  { code: "KUK", name: "Kasigluk Airport", country: "US", size: "small" },
  { code: "KUL", name: "Sepang \u2013 Kuala Lumpur International Airport", country: "MY", size: "large" },
  { code: "KUM", name: "Yakushima Airport", country: "JP", size: "medium" },
  { code: "KUN", name: "Kaunas International Airport", country: "LT", size: "large" },
  { code: "KUO", name: "Kuopio / Siilinj\xE4rvi \u2013 Kuopio Airport", country: "FI", size: "large" },
  { code: "KUS", name: "Kulusuk Airport", country: "GL", size: "medium" },
  { code: "KUT", name: "Kopitnari \u2013 David the Builder Kutaisi International Airport", country: "GE", size: "large" },
  { code: "KUU", name: "Bhuntar \u2013 Kullu Manali Airport", country: "IN", size: "medium" },
  { code: "KUV", name: "Gunsan Airport / Gunsan Air Base", country: "KR", size: "medium" },
  { code: "KVA", name: "Kavala Alexander the Great International Airport", country: "GR", size: "large" },
  { code: "KVC", name: "King Cove Airport", country: "US", size: "small" },
  { code: "KVG", name: "Kavieng Airport", country: "PG", size: "medium" },
  { code: "KVK", name: "Apatity \u2013 Kirovsk-Apatity Airport", country: "RU", size: "small" },
  { code: "KVL", name: "Kivalina Airport", country: "US", size: "small" },
  { code: "KVM", name: "Markovo Airport", country: "RU", size: "small" },
  { code: "KVO", name: "Kraljevo \u2013 Morava Airport", country: "RS", size: "medium" },
  { code: "KVX", name: "Kirov \u2013 Pobedilovo Airport", country: "RU", size: "medium" },
  { code: "KWA", name: "Kwajalein \u2013 Bucholz Army Air Field", country: "MH", size: "medium" },
  { code: "KWB", name: "Karimunjawa \u2013 Dewadaru Airport", country: "ID", size: "small" },
  { code: "KWE", name: "Guiyang (Nanming) \u2013 Guiyang Longdongbao International Airport", country: "CN", size: "large" },
  { code: "KWI", name: "Kuwait City \u2013 Kuwait International Airport", country: "KW", size: "large" },
  { code: "KWJ", name: "Gwangju Airport", country: "KR", size: "medium" },
  { code: "KWK", name: "Kwigillingok Airport", country: "US", size: "small" },
  { code: "KWL", name: "Guilin (Lingui) \u2013 Guilin Liangjiang International Airport", country: "CN", size: "large" },
  { code: "KWM", name: "Kowanyama Airport", country: "AU", size: "medium" },
  { code: "KWN", name: "Quinhagak Airport", country: "US", size: "small" },
  { code: "KWT", name: "Kwethluk Airport", country: "US", size: "small" },
  { code: "KWZ", name: "Kolwezi Airport", country: "CD", size: "medium" },
  { code: "KXB", name: "Kolaka \u2013 Sangia Nibandera Airport", country: "ID", size: "medium" },
  { code: "KXF", name: "Koro Island Airport", country: "FJ", size: "small" },
  { code: "KXK", name: "Komsomolsk-on-Amur Airport", country: "RU", size: "medium" },
  { code: "KXO", name: "Kisoro Airport", country: "UG", size: "small" },
  { code: "KYA", name: "Konya Airport", country: "TR", size: "large" },
  { code: "KYD", name: "Orchid Island \u2013 Lanyu Airport", country: "TW", size: "medium" },
  { code: "KYK", name: "Karluk Airport", country: "US", size: "small" },
  { code: "KYP", name: "Kyaukpyu Airport", country: "MM", size: "medium" },
  { code: "KYS", name: "Kayes Dag Dag Airport", country: "ML", size: "medium" },
  { code: "KYU", name: "Koyukuk Airport", country: "US", size: "small" },
  { code: "KYZ", name: "Kyzyl Airport", country: "RU", size: "medium" },
  { code: "KZI", name: "Kozani National Airport Filippos", country: "GR", size: "medium" },
  { code: "KZN", name: "Kazan International Airport", country: "RU", size: "large" },
  { code: "KZO", name: "Kyzylorda \u2013 Korkyt Ata International Airport", country: "KZ", size: "large" },
  { code: "KZR", name: "Alt\u0131nta\u015F \u2013 Zafer Airport", country: "TR", size: "medium" },
  { code: "KZS", name: "Kastelorizo Island \u2013 Kastelorizo Airport", country: "GR", size: "small" },
  { code: "LAD", name: "Luanda \u2013 Quatro de Fevereiro International Airport", country: "AO", size: "large" },
  { code: "LAE", name: "Lae \u2013 Nadzab Tomodachi International Airport", country: "PG", size: "large" },
  { code: "LAF", name: "West Lafayette \u2013 Purdue University Airport", country: "US", size: "medium" },
  { code: "LAJ", name: "Lages Airport", country: "BR", size: "medium" },
  { code: "LAK", name: "Aklavik/Freddie Carmichael Airport", country: "CA", size: "small" },
  { code: "LAL", name: "Lakeland Linder International Airport", country: "US", size: "medium" },
  { code: "LAN", name: "Lansing \u2013 Capital Region International Airport", country: "US", size: "medium" },
  { code: "LAO", name: "Laoag City \u2013 Laoag International Airport", country: "PH", size: "large" },
  { code: "LAP", name: "La Paz \u2013 Manuel M\xE1rquez de Le\xF3n International Airport", country: "MX", size: "medium" },
  { code: "LAQ", name: "Al Albraq \u2013 Al Abraq International Airport", country: "LY", size: "large" },
  { code: "LAR", name: "Laramie Regional Airport", country: "US", size: "medium" },
  { code: "LAS", name: "Las Vegas \u2013 Harry Reid International Airport", country: "US", size: "large" },
  { code: "LAU", name: "Lamu \u2013 Manda Airport", country: "KE", size: "medium" },
  { code: "LAW", name: "Lawton Fort Sill Regional Airport", country: "US", size: "medium" },
  { code: "LAX", name: "Los Angeles International Airport", country: "US", size: "large" },
  { code: "LBA", name: "Leeds, West Yorkshire \u2013 Leeds Bradford Airport", country: "GB", size: "large" },
  { code: "LBB", name: "Lubbock Preston Smith International Airport", country: "US", size: "medium" },
  { code: "LBC", name: "L\xFCbeck Blankensee Airport", country: "DE", size: "medium" },
  { code: "LBD", name: "Khujand International Airport", country: "TJ", size: "large" },
  { code: "LBE", name: "Latrobe \u2013 Arnold Palmer Regional Airport", country: "US", size: "medium" },
  { code: "LBF", name: "North Platte Regional Airport Lee Bird Field", country: "US", size: "medium" },
  { code: "LBJ", name: "Labuan Bajo, Manggarai Barat \u2013 Komodo Airport", country: "ID", size: "small" },
  { code: "LBL", name: "Liberal Mid-America Regional Airport", country: "US", size: "medium" },
  { code: "LBP", name: "Long Banga Airport", country: "MY", size: "small" },
  { code: "LBR", name: "L\xE1brea Airport", country: "BR", size: "small" },
  { code: "LBS", name: "Labasa Airport", country: "FJ", size: "medium" },
  { code: "LBU", name: "Labuan Airport", country: "MY", size: "medium" },
  { code: "LBV", name: "Libreville Leon M'ba International Airport", country: "GA", size: "large" },
  { code: "LBW", name: "Long Bawan \u2013 Yuvai Semaring Airport", country: "ID", size: "small" },
  { code: "LCA", name: "Larnaca International Airport", country: "CY", size: "large" },
  { code: "LCE", name: "La Ceiba \u2013 Golos\xF3n International Airport", country: "HN", size: "medium" },
  { code: "LCG", name: "Culleredo \u2013 A Coru\xF1a Airport", country: "ES", size: "medium" },
  { code: "LCH", name: "Lake Charles Regional Airport", country: "US", size: "medium" },
  { code: "LCJ", name: "\u0141\xF3d\u017A \u2013 Lublinek", country: "PL", size: "large" },
  { code: "LCK", name: "Columbus \u2013 Rickenbacker International Airport", country: "US", size: "medium" },
  { code: "LCR", name: "La Chorrera \u2013 Virgilio Barco Vargas (La Chorrera) Airport", country: "CO", size: "small" },
  { code: "LCX", name: "Longyan (Liancheng) \u2013 Liancheng Guanzhishan Airport", country: "CN", size: "medium" },
  { code: "LCY", name: "London City Airport", country: "GB", size: "medium" },
  { code: "LDB", name: "Londrina \u2013 Governor Jos\xE9 Richa Airport", country: "BR", size: "medium" },
  { code: "LDE", name: "Tarbes/Lourdes/Pyr\xE9n\xE9es \u2013 Tarbes-Lourdes-Pyr\xE9n\xE9es Airport", country: "FR", size: "medium" },
  { code: "LDG", name: "Leshukonskoye Airport", country: "RU", size: "small" },
  { code: "LDH", name: "Lord Howe Island Airport", country: "AU", size: "small" },
  { code: "LDS", name: "Yichun Lindu Airport", country: "CN", size: "medium" },
  { code: "LDU", name: "Lahad Datu Airport", country: "MY", size: "medium" },
  { code: "LDX", name: "Saint-Laurent-du-Maroni Airport", country: "GF", size: "medium" },
  { code: "LDY", name: "Derry, Derry and Strabane \u2013 City of Derry Airport", country: "GB", size: "medium" },
  { code: "LEA", name: "Exmouth \u2013 Learmonth Airport", country: "AU", size: "medium" },
  { code: "LEB", name: "Lebanon Municipal Airport", country: "US", size: "medium" },
  { code: "LEC", name: "Len\xE7\xF3is \u2013 Coronel Hor\xE1cio de Mattos Airport", country: "BR", size: "small" },
  { code: "LED", name: "St. Petersburg \u2013 Pulkovo Airport", country: "RU", size: "large" },
  { code: "LEI", name: "Almer\xEDa Airport", country: "ES", size: "medium" },
  { code: "LEJ", name: "Schkeuditz \u2013 Leipzig/Halle Airport", country: "DE", size: "large" },
  { code: "LEL", name: "Lake Evella Airport", country: "AU", size: "small" },
  { code: "LEN", name: "La Virgen del Camino \u2013 Le\xF3n Airport", country: "ES", size: "medium" },
  { code: "LEQ", name: "Land's End, Cornwall \u2013 Land's End Airport", country: "GB", size: "small" },
  { code: "LER", name: "Leinster Airport", country: "AU", size: "medium" },
  { code: "LET", name: "Leticia \u2013 Alfredo V\xE1squez Cobo International Airport", country: "CO", size: "medium" },
  {
    code: "LEU",
    name: "La Seu d'Urgell Pyrenees and Andorra \u2013 Pirineus - la Seu d'Urgel Airport",
    country: "ES",
    size: "medium"
  },
  { code: "LEV", name: "Bureta \u2013 Levuka Airfield", country: "FJ", size: "small" },
  { code: "LEX", name: "Lexington \u2013 Blue Grass Airport", country: "US", size: "medium" },
  { code: "LFM", name: "Lamerd Airport", country: "IR", size: "medium" },
  { code: "LFQ", name: "Linfen (Yaodu) \u2013 Linfen Yaodu Airport", country: "CN", size: "medium" },
  { code: "LFT", name: "Lafayette Regional Airport", country: "US", size: "medium" },
  { code: "LFW", name: "Lom\xE9\u2013Tokoin International Airport", country: "TG", size: "large" },
  { code: "LGA", name: "New York \u2013 LaGuardia Airport", country: "US", size: "large" },
  { code: "LGB", name: "Long Beach International Airport", country: "US", size: "large" },
  { code: "LGG", name: "Gr\xE2ce-Hollogne \u2013 Li\xE8ge Airport", country: "BE", size: "medium" },
  { code: "LGI", name: "Deadman's Cay Airport", country: "BS", size: "medium" },
  { code: "LGK", name: "Langkawi International Airport", country: "MY", size: "large" },
  { code: "LGL", name: "Long Datih \u2013 Long Lellang Airport", country: "MY", size: "small" },
  { code: "LGW", name: "London Gatwick Airport", country: "GB", size: "large" },
  { code: "LGZ", name: "Shannan Longzi Airport", country: "CN", size: "small" },
  { code: "LHE", name: "Lahore \u2013 Allama Iqbal International Airport", country: "PK", size: "large" },
  { code: "LHG", name: "Lightning Ridge Airport", country: "AU", size: "medium" },
  { code: "LHL", name: "Lachin International Airport", country: "AZ", size: "medium" },
  { code: "LHR", name: "London Heathrow Airport", country: "GB", size: "large" },
  { code: "LHS", name: "Las Heras Airport", country: "AR", size: "medium" },
  { code: "LHW", name: "Lanzhou (Yongdeng) \u2013 Lanzhou Zhongchuan International Airport", country: "CN", size: "large" },
  { code: "LIF", name: "Lifou Airport", country: "NC", size: "medium" },
  { code: "LIG", name: "Limoges/Bellegarde \u2013 Limoges Airport", country: "FR", size: "medium" },
  { code: "LIH", name: "Lihue, Kauai \u2013 Lihue Airport", country: "US", size: "large" },
  { code: "LIK", name: "Likiep Island \u2013 Likiep Airport", country: "MH", size: "small" },
  { code: "LIL", name: "Lesquin \u2013 Lille Airport", country: "FR", size: "large" },
  { code: "LIM", name: "Lima \u2013 Jorge Ch\xE1vez International Airport", country: "PE", size: "large" },
  { code: "LIN", name: "Segrate (MI) \u2013 Milano Linate Airport", country: "IT", size: "large" },
  { code: "LIO", name: "Lim\xF3n International Airport", country: "CR", size: "medium" },
  { code: "LIR", name: "Liberia \u2013 Daniel Oduber Quir\xF3s International Airport", country: "CR", size: "large" },
  { code: "LIS", name: "Lisbon Humberto Delgado Airport", country: "PT", size: "large" },
  {
    code: "LIT",
    name: "Little Rock \u2013 Bill & Hillary Clinton National Airport/Adams Field",
    country: "US",
    size: "medium"
  },
  { code: "LIW", name: "Loikaw Airport", country: "MM", size: "medium" },
  { code: "LJG", name: "Lijiang Sanyi International Airport", country: "CN", size: "large" },
  { code: "LJU", name: "Zgornji Brnik \u2013 Ljubljana Jo\u017Ee Pu\u010Dnik Airport", country: "SI", size: "large" },
  { code: "LKA", name: "Tiwatobi \u2013 Larantuka Gewayentana Airport", country: "ID", size: "small" },
  { code: "LKB", name: "Lakeba Island Airport", country: "FJ", size: "small" },
  { code: "LKI", name: "Lubang \u2013 Lasikin Airport", country: "ID", size: "small" },
  { code: "LKL", name: "Lakselv Airport, Banak", country: "NO", size: "medium" },
  { code: "LKM", name: "Lolak \u2013 Bolaang Mongondow Airport", country: "ID", size: "small" },
  { code: "LKN", name: "Leknes Airport", country: "NO", size: "medium" },
  { code: "LKO", name: "Lucknow \u2013 Chaudhary Charan Singh International Airport", country: "IN", size: "large" },
  { code: "LLA", name: "Lule\xE5 Airport", country: "SE", size: "large" },
  { code: "LLB", name: "Qiannan (Libo) \u2013 Libo Airport", country: "CN", size: "small" },
  { code: "LLF", name: "Yongzhou Lingling Airport", country: "CN", size: "medium" },
  { code: "LLK", name: "Lankaran International Airport", country: "AZ", size: "small" },
  { code: "LLO", name: "Palopo \u2013 Bua - Palopo Lagaligo Airport", country: "ID", size: "small" },
  { code: "LLV", name: "L\xFCliang Dawu Airport", country: "CN", size: "medium" },
  { code: "LLW", name: "Lumbadzi \u2013 Kamuzu International Airport", country: "MW", size: "large" },
  { code: "LMA", name: "Minchumina Airport", country: "US", size: "small" },
  { code: "LMC", name: "La Macarena Airport", country: "CO", size: "small" },
  { code: "LMM", name: "Los Mochis \u2013 Valle del Fuerte International Airport", country: "MX", size: "medium" },
  { code: "LMN", name: "Limbang Airport", country: "MY", size: "medium" },
  { code: "LMP", name: "Lampedusa Airport", country: "IT", size: "medium" },
  { code: "LMY", name: "Lake Murray Airport", country: "PG", size: "small" },
  { code: "LNB", name: "Lamen Bay Airport", country: "VU", size: "small" },
  { code: "LNE", name: "Lonorore Airport", country: "VU", size: "small" },
  { code: "LNJ", name: "Lincang Boshang Airport", country: "CN", size: "medium" },
  { code: "LNK", name: "Lincoln Airport", country: "US", size: "medium" },
  { code: "LNL", name: "Longnan (Cheng) \u2013 Longnan Chengzhou Airport", country: "CN", size: "medium" },
  { code: "LNO", name: "Leonora Airport", country: "AU", size: "medium" },
  { code: "LNS", name: "Lancaster Airport", country: "US", size: "medium" },
  { code: "LNU", name: "Malinau \u2013 Robert Atty Bessing", country: "ID", size: "small" },
  { code: "LNV", name: "Londolovit Airport", country: "PG", size: "small" },
  { code: "LNY", name: "Lanai City \u2013 Lanai Airport", country: "US", size: "medium" },
  { code: "LNZ", name: "Linz-H\xF6rsching Airport", country: "AT", size: "large" },
  { code: "LOD", name: "Longana Airport", country: "VU", size: "small" },
  { code: "LOE", name: "Loei Airport", country: "TH", size: "medium" },
  { code: "LOH", name: "La Toma (Catamayo) \u2013 Ciudad de Catamayo Airport", country: "EC", size: "small" },
  { code: "LOO", name: "Laghouat - Molay Ahmed Medeghri Airport", country: "DZ", size: "medium" },
  { code: "LOP", name: "Mataram (Pujut, Lombok Tengah) \u2013 Lombok International Airport", country: "ID", size: "large" },
  { code: "LOS", name: "Lagos \u2013 Murtala Muhammed International Airport", country: "NG", size: "large" },
  { code: "LPA", name: "Gran Canaria Island \u2013 Gran Canaria Airport", country: "ES", size: "large" },
  { code: "LPB", name: "La Paz / El Alto \u2013 El Alto International Airport", country: "BO", size: "large" },
  { code: "LPD", name: "La Pedrera Airport", country: "CO", size: "small" },
  { code: "LPF", name: "Liupanshui (Zhongshan) \u2013 Liupanshui Yuezhao Airport", country: "CN", size: "medium" },
  { code: "LPI", name: "Link\xF6ping City Airport", country: "SE", size: "large" },
  { code: "LPL", name: "Liverpool John Lennon Airport", country: "GB", size: "large" },
  { code: "LPM", name: "Lamap Airport", country: "VU", size: "small" },
  { code: "LPP", name: "Lappeenranta Airport", country: "FI", size: "large" },
  { code: "LPQ", name: "Luang Phabang International Airport", country: "LA", size: "large" },
  { code: "LPS", name: "Lopez Island Airport", country: "US", size: "small" },
  { code: "LPT", name: "Lampang Airport", country: "TH", size: "medium" },
  { code: "LPU", name: "Long Apung-Borneo Island \u2013 Long Apung Airport", country: "ID", size: "small" },
  { code: "LPY", name: "Chaspuzac, Haute-Loire \u2013 Le Puy-Loudes Airfield", country: "FR", size: "small" },
  { code: "LQM", name: "Puerto Legu\xEDzamo \u2013 Caucaya Airport", country: "CO", size: "small" },
  { code: "LRD", name: "Laredo International Airport", country: "US", size: "medium" },
  { code: "LRE", name: "Longreach Airport", country: "AU", size: "medium" },
  { code: "LRH", name: "La Rochelle \xCEle de R\xE9 Airport", country: "FR", size: "medium" },
  { code: "LRM", name: "La Romana \u2013 Casa De Campo International Airport", country: "DO", size: "large" },
  { code: "LRR", name: "Lar Airport", country: "IR", size: "medium" },
  { code: "LRS", name: "Leros Island \u2013 Leros Airport", country: "GR", size: "small" },
  { code: "LRU", name: "Las Cruces International Airport", country: "US", size: "medium" },
  { code: "LRV", name: "Gran Roque Island \u2013 Los Roques Airport", country: "VE", size: "small" },
  { code: "LSA", name: "Losuia Airport", country: "PG", size: "small" },
  { code: "LSC", name: "La Serena-Coquimbo \u2013 La Florida Airport", country: "CL", size: "medium" },
  { code: "LSE", name: "La Crosse Regional Airport", country: "US", size: "medium" },
  { code: "LSG", name: "Leshan (Wutongqiao) \u2013 Leshan Airport", country: "CN", size: "medium" },
  { code: "LSH", name: "Lashio Airport", country: "MM", size: "medium" },
  { code: "LSI", name: "Lerwick, Shetland \u2013 Sumburgh Airport", country: "GB", size: "medium" },
  { code: "LSP", name: "Paraguan\xE1 \u2013 Josefa Camejo International Airport", country: "VE", size: "medium" },
  { code: "LSR", name: "Kutacane \u2013 Alas Leuser Airport", country: "ID", size: "medium" },
  { code: "LST", name: "Launceston (Western Junction) \u2013 Launceston Airport", country: "AU", size: "medium" },
  { code: "LSW", name: "Lhok Seumawe-Sumatra Island \u2013 Malikus Saleh Airport", country: "ID", size: "small" },
  { code: "LSY", name: "Lismore Airport", country: "AU", size: "medium" },
  { code: "LTD", name: "Ghadames Airport", country: "LY", size: "medium" },
  { code: "LTI", name: "Altai Airport", country: "MN", size: "medium" },
  { code: "LTK", name: "Latakia International Airport", country: "SY", size: "medium" },
  { code: "LTM", name: "Lethem Airport", country: "GY", size: "medium" },
  { code: "LTN", name: "Luton, Luton \u2013 London Luton Airport", country: "GB", size: "large" },
  { code: "LTO", name: "Loreto International Airport", country: "MX", size: "large" },
  { code: "LTT", name: "Saint-Tropez \u2013 La M\xF4le Airport", country: "FR", size: "small" },
  { code: "LTU", name: "Latur \u2013 Murod Kond Airport", country: "IN", size: "medium" },
  { code: "LTX", name: "Latacunga \u2013 Cotopaxi International Airport", country: "EC", size: "medium" },
  { code: "LUA", name: "Lukla \u2013 Tenzing-Hillary Airport", country: "NP", size: "medium" },
  { code: "LUD", name: "Luderitz Airport", country: "NA", size: "medium" },
  { code: "LUG", name: "Agno \u2013 Lugano Airport", country: "CH", size: "medium" },
  { code: "LUK", name: "Cincinnati Municipal Airport Lunken Field", country: "US", size: "medium" },
  { code: "LUM", name: "Dehong (Mangshi) \u2013 Dehong Mangshi International Airport", country: "CN", size: "medium" },
  { code: "LUN", name: "Lusaka \u2013 Kenneth Kaunda International Airport", country: "ZM", size: "large" },
  { code: "LUP", name: "Kalaupapa Airport", country: "US", size: "small" },
  { code: "LUQ", name: "San Luis \u2013 Brigadier Mayor D Cesar Raul Ojeda Airport", country: "AR", size: "medium" },
  { code: "LUR", name: "Cape Lisburne LRRS Airport", country: "US", size: "medium" },
  { code: "LUV", name: "Langgur \u2013 Karel Sadsuitubun Airport", country: "ID", size: "medium" },
  { code: "LUX", name: "Luxembourg-Findel International Airport", country: "LU", size: "large" },
  { code: "LUZ", name: "Lublin \u2013 \u015Awidnik", country: "PL", size: "large" },
  { code: "LVI", name: "Livingstone \u2013 Harry Mwanga Nkumbula International Airport", country: "ZM", size: "large" },
  { code: "LVO", name: "Laverton Airport", country: "AU", size: "small" },
  { code: "LWB", name: "Lewisburg \u2013 Greenbrier Valley Airport", country: "US", size: "medium" },
  { code: "LWK", name: "Lerwick, Shetland Islands \u2013 Lerwick / Tingwall Airport", country: "GB", size: "small" },
  { code: "LWN", name: "Gyumri \u2013 Shirak International Airport", country: "AM", size: "large" },
  { code: "LWS", name: "Lewiston Nez Perce County Airport", country: "US", size: "medium" },
  { code: "LWY", name: "Lawas Airport", country: "MY", size: "small" },
  { code: "LXA", name: "Shannan (Gonggar) \u2013 Lhasa Gonggar International Airport", country: "CN", size: "large" },
  { code: "LXG", name: "Luang Namtha Airport", country: "LA", size: "small" },
  { code: "LXR", name: "Luxor International Airport", country: "EG", size: "large" },
  { code: "LXS", name: "Limnos Island \u2013 Limnos Airport", country: "GR", size: "small" },
  { code: "LYA", name: "Luoyang (Laocheng) \u2013 Luoyang Beijiao Airport", country: "CN", size: "large" },
  { code: "LYC", name: "Lycksele Airport", country: "SE", size: "medium" },
  { code: "LYG", name: "Lianyungang Huaguoshan International Airport", country: "CN", size: "large" },
  { code: "LYH", name: "Lynchburg Regional Airport - Preston Glenn Field", country: "US", size: "medium" },
  { code: "LYI", name: "Linyi (Hedong) \u2013 Linyi Qiyang Airport", country: "CN", size: "medium" },
  { code: "LYP", name: "Faisalabad International Airport", country: "PK", size: "large" },
  { code: "LYR", name: "Longyearbyen \u2013 Svalbard Airport, Longyear", country: "NO", size: "medium" },
  { code: "LYS", name: "Colombier-Saugnieu, Rh\xF4ne \u2013 Lyon Saint-Exup\xE9ry Airport", country: "FR", size: "large" },
  { code: "LZG", name: "Nanchong (Langzhong) \u2013 Langzhong Gucheng Airport", country: "CN", size: "medium" },
  {
    code: "LZH",
    name: "Liuzhou (Liujiang) \u2013 Liuzhou Bailian Airport / Bailian Air Base",
    country: "CN",
    size: "medium"
  },
  { code: "LZN", name: "Matsu (Nangan) \u2013 Matsu Nangan Airport", country: "TW", size: "medium" },
  { code: "LZO", name: "Luzhou (Yunlong) \u2013 Luzhou Yunlong Airport", country: "CN", size: "medium" },
  { code: "LZY", name: "Nyingchi (Mainling) \u2013 Nyingchi Mainling Airport", country: "CN", size: "medium" },
  { code: "MAA", name: "Chennai International Airport", country: "IN", size: "large" },
  { code: "MAB", name: "Marab\xE1 \u2013 Jo\xE3o Correa da Rocha Airport", country: "BR", size: "medium" },
  { code: "MAD", name: "Madrid \u2013 Adolfo Su\xE1rez Madrid\u2013Barajas Airport", country: "ES", size: "large" },
  { code: "MAF", name: "Midland International Air and Space Port", country: "US", size: "medium" },
  { code: "MAG", name: "Madang Airport", country: "PG", size: "medium" },
  { code: "MAH", name: "Mah\xF3n (Ma\xF3) \u2013 Menorca Airport", country: "ES", size: "large" },
  { code: "MAJ", name: "Majuro Atoll \u2013 Marshall Islands International Airport", country: "MH", size: "large" },
  { code: "MAK", name: "Malakal International Airport", country: "SS", size: "medium" },
  { code: "MAM", name: "Matamoros \u2013 General Servando Canales International Airport", country: "MX", size: "medium" },
  { code: "MAN", name: "Manchester, Greater Manchester \u2013 Manchester Airport", country: "GB", size: "large" },
  { code: "MAO", name: "Manaus \u2013 Eduardo Gomes International Airport", country: "BR", size: "large" },
  { code: "MAQ", name: "Mae Sot Airport", country: "TH", size: "medium" },
  { code: "MAR", name: "Maracaibo \u2013 La Chinita International Airport", country: "VE", size: "large" },
  { code: "MAS", name: "Manus Island \u2013 Momote Airport", country: "PG", size: "medium" },
  { code: "MAU", name: "Maupiti Airport", country: "PF", size: "medium" },
  { code: "MAZ", name: "Mayaguez \u2013 Eugenio Maria De Hostos Airport", country: "PR", size: "medium" },
  { code: "MBA", name: "Mombasa \u2013 Moi International Airport", country: "KE", size: "large" },
  { code: "MBD", name: "Mafeking \u2013 Mmabatho International Airport", country: "ZA", size: "medium" },
  { code: "MBE", name: "Monbetsu Airport", country: "JP", size: "medium" },
  { code: "MBI", name: "Mbeya \u2013 Songwe Airport", country: "TZ", size: "medium" },
  { code: "MBJ", name: "Montego Bay \u2013 Sangster International Airport", country: "JM", size: "large" },
  { code: "MBL", name: "Manistee County Blacker Airport", country: "US", size: "small" },
  { code: "MBS", name: "Freeland \u2013 MBS International Airport", country: "US", size: "medium" },
  { code: "MBT", name: "Masbate \u2013 Moises R. Espinosa Airport", country: "PH", size: "medium" },
  { code: "MBW", name: "Melbourne Moorabbin Airport", country: "AU", size: "medium" },
  { code: "MBX", name: "Maribor Edvard Rusjan Airport", country: "SI", size: "medium" },
  { code: "MCE", name: "Merced Regional Macready Field", country: "US", size: "medium" },
  { code: "MCG", name: "McGrath Airport", country: "US", size: "medium" },
  { code: "MCI", name: "Kansas City International Airport", country: "US", size: "large" },
  { code: "MCK", name: "McCook Ben Nelson Regional Airport", country: "US", size: "medium" },
  { code: "MCN", name: "Macon \u2013 Middle Georgia Regional Airport", country: "US", size: "medium" },
  { code: "MCO", name: "Orlando International Airport", country: "US", size: "large" },
  { code: "MCP", name: "Macap\xE1 - Alberto Alcolumbre International Airport", country: "BR", size: "medium" },
  { code: "MCT", name: "Muscat/Seeb \u2013 Muscat International Airport", country: "OM", size: "large" },
  { code: "MCV", name: "McArthur River Mine Airport", country: "AU", size: "small" },
  { code: "MCW", name: "Mason City Municipal Airport", country: "US", size: "medium" },
  { code: "MCX", name: "Makhachkala Uytash International Airport", country: "RU", size: "large" },
  { code: "MCY", name: "Maroochydore \u2013 Sunshine Coast Airport", country: "AU", size: "large" },
  { code: "MCZ", name: "Macei\xF3 \u2013 Zumbi dos Palmares International Airport", country: "BR", size: "large" },
  { code: "MDC", name: "Manado \u2013 Sam Ratulangi International Airport", country: "ID", size: "large" },
  { code: "MDE", name: "Medell\xEDn \u2013 Jose Maria C\xF3rdova International Airport", country: "CO", size: "large" },
  { code: "MDG", name: "Mudanjiang Hailang International Airport", country: "CN", size: "medium" },
  { code: "MDI", name: "Makurdi Airport", country: "NG", size: "medium" },
  { code: "MDK", name: "Mbandaka Airport", country: "CD", size: "medium" },
  { code: "MDL", name: "Mandalay International Airport", country: "MM", size: "large" },
  { code: "MDQ", name: "Mar del Plata \u2013 \xC1stor Piazzola International Airport", country: "AR", size: "medium" },
  { code: "MDT", name: "Harrisburg International Airport", country: "US", size: "medium" },
  { code: "MDU", name: "Mendi Airport", country: "PG", size: "medium" },
  { code: "MDW", name: "Chicago Midway International Airport", country: "US", size: "large" },
  { code: "MDZ", name: "Mendoza \u2013 Governor Francisco Gabrielli International Airport", country: "AR", size: "large" },
  { code: "MEB", name: "Essendon Fields \u2013 Melbourne Essendon Airport", country: "AU", size: "medium" },
  { code: "MEC", name: "Manta \u2013 Eloy Alfaro International Airport", country: "EC", size: "medium" },
  { code: "MED", name: "Medina \u2013 Prince Mohammad Bin Abdulaziz Airport", country: "SA", size: "large" },
  { code: "MEE", name: "Mar\xE9 Airport", country: "NC", size: "medium" },
  { code: "MEG", name: "Malanje Airport", country: "AO", size: "medium" },
  { code: "MEH", name: "Mehamn Airport", country: "NO", size: "medium" },
  { code: "MEI", name: "Meridian \u2013 Key Field / Meridian Regional Airport", country: "US", size: "medium" },
  { code: "MEL", name: "Melbourne Airport", country: "AU", size: "large" },
  { code: "MEM", name: "Memphis \u2013 Frederick W. Smith International Airport", country: "US", size: "large" },
  { code: "MEQ", name: "Kuala Pesisir \u2013 Cut Nyak Dhien Airport", country: "ID", size: "medium" },
  { code: "MEX", name: "Mexico City Benito Ju\xE1rez International Airport", country: "MX", size: "large" },
  { code: "MFA", name: "Kilindoni \u2013 Mafia Airport", country: "TZ", size: "small" },
  { code: "MFE", name: "McAllen Miller International Airport", country: "US", size: "medium" },
  { code: "MFG", name: "Muzaffarabad Airport", country: "PK", size: "small" },
  { code: "MFJ", name: "Moala Airport", country: "FJ", size: "small" },
  { code: "MFK", name: "Matsu (Beigan) \u2013 Matsu Beigan Airport", country: "TW", size: "medium" },
  { code: "MFM", name: "Nossa Senhora do Carmo \u2013 Macau International Airport", country: "MO", size: "large" },
  { code: "MFR", name: "Medford \u2013 Rogue Valley International-Medford Airport", country: "US", size: "medium" },
  { code: "MFU", name: "Mfuwe International Airport", country: "ZM", size: "large" },
  { code: "MGA", name: "Managua \u2013 Augusto C. Sandino (Managua) International Airport", country: "NI", size: "large" },
  { code: "MGB", name: "Mount Gambier Airport", country: "AU", size: "medium" },
  { code: "MGC", name: "Michigan City Municipal Airport", country: "US", size: "medium" },
  { code: "MGF", name: "Maring\xE1 \u2013 Regional de Maring\xE1 - S\xEDlvio Name J\xFAnior Airport", country: "BR", size: "medium" },
  { code: "MGH", name: "Margate Airport", country: "ZA", size: "medium" },
  { code: "MGM", name: "Montgomery Regional (Dannelly Field) Airport", country: "US", size: "medium" },
  { code: "MGQ", name: "Mogadishu \u2013 Aden Adde International Airport", country: "SO", size: "large" },
  { code: "MGT", name: "Milingimbi Island \u2013 Milingimbi Airport", country: "AU", size: "small" },
  { code: "MGW", name: "Morgantown Municipal Airport Walter L. (Bill) Hart Field", country: "US", size: "medium" },
  { code: "MGZ", name: "Mkeik \u2013 Myeik Airport", country: "MM", size: "medium" },
  { code: "MHC", name: "Dalcahue \u2013 Mocopulli Airport", country: "CL", size: "small" },
  { code: "MHD", name: "Mashhad International Airport", country: "IR", size: "large" },
  { code: "MHG", name: "Mannheim-City Airport", country: "DE", size: "medium" },
  { code: "MHH", name: "Marsh Harbour \u2013 Leonard M. Thompson International Airport", country: "BS", size: "medium" },
  { code: "MHK", name: "Manhattan Regional Airport", country: "US", size: "medium" },
  { code: "MHM", name: "Manaoba Airport", country: "SB", size: "small" },
  { code: "MHQ", name: "Mariehamn Airport", country: "FI", size: "medium" },
  { code: "MHT", name: "Manchester-Boston Regional Airport", country: "US", size: "medium" },
  { code: "MHU", name: "Mount Hotham Airport", country: "AU", size: "medium" },
  { code: "MHX", name: "Manihiki Island Airport", country: "CK", size: "small" },
  { code: "MIA", name: "Miami International Airport", country: "US", size: "large" },
  { code: "MID", name: "M\xE9rida \u2013 Manuel Crescencio Rej\xF3n International Airport", country: "MX", size: "large" },
  { code: "MIG", name: "Mianyang (Fucheng) \u2013 Mianyang Nanjiao Airport", country: "CN", size: "medium" },
  { code: "MII", name: "Mar\xEDlia \u2013 Frank Miloye Milenkowichi\u2013Mar\xEDlia State Airport", country: "BR", size: "medium" },
  { code: "MIJ", name: "Mili Island Airport", country: "MH", size: "small" },
  { code: "MIM", name: "Merimbula Airport", country: "AU", size: "medium" },
  { code: "MIR", name: "Monastir Habib Bourguiba International Airport", country: "TN", size: "medium" },
  { code: "MIS", name: "Misima Island Airport", country: "PG", size: "small" },
  { code: "MIU", name: "Maiduguri International Airport", country: "NG", size: "large" },
  { code: "MJE", name: "Majkin Airport", country: "MH", size: "small" },
  { code: "MJF", name: "Mosj\xF8en Airport, Kj\xE6rstad", country: "NO", size: "medium" },
  { code: "MJI", name: "Tripoli \u2013 Mitiga International Airport", country: "LY", size: "large" },
  { code: "MJK", name: "Denham \u2013 Shark Bay Airport", country: "AU", size: "medium" },
  { code: "MJM", name: "Mbuji Mayi Airport", country: "CD", size: "medium" },
  { code: "MJN", name: "Mahajanga \u2013 Amborovy Airport", country: "MG", size: "large" },
  { code: "MJT", name: "Mytilene International Airport", country: "GR", size: "medium" },
  { code: "MJY", name: "Motygino Airport", country: "RU", size: "small" },
  { code: "MJZ", name: "Mirny Airport", country: "RU", size: "medium" },
  { code: "MKE", name: "Milwaukee \u2013 General Mitchell International Airport", country: "US", size: "large" },
  { code: "MKG", name: "Muskegon County Airport", country: "US", size: "medium" },
  { code: "MKK", name: "Kaunakakai \u2013 Molokai Airport", country: "US", size: "medium" },
  { code: "MKL", name: "Jackson \u2013 McKellar-Sipes Regional Airport", country: "US", size: "medium" },
  { code: "MKM", name: "Mukah Airport", country: "MY", size: "medium" },
  { code: "MKP", name: "Makemo Airport", country: "PF", size: "medium" },
  { code: "MKQ", name: "Merauke \u2013 Mopah International Airport", country: "ID", size: "medium" },
  { code: "MKR", name: "Meekatharra Airport", country: "AU", size: "medium" },
  { code: "MKU", name: "Makokou Airport", country: "GA", size: "medium" },
  { code: "MKW", name: "Manokwari \u2013 Rendani Airport", country: "ID", size: "medium" },
  { code: "MKY", name: "Mackay Airport", country: "AU", size: "medium" },
  { code: "MKZ", name: "Malacca International Airport", country: "MY", size: "medium" },
  { code: "MLA", name: "Valletta \u2013 Malta International Airport", country: "MT", size: "large" },
  { code: "MLB", name: "Melbourne Orlando International Airport", country: "US", size: "medium" },
  { code: "MLE", name: "Mal\xE9 \u2013 Velana International Airport", country: "MV", size: "large" },
  { code: "MLG", name: "Malang \u2013 Abdul Rachman Saleh Airport", country: "ID", size: "medium" },
  { code: "MLI", name: "Moline \u2013 Quad City International Airport", country: "US", size: "medium" },
  { code: "MLL", name: "Marshall Don Hunter Sr Airport", country: "US", size: "small" },
  { code: "MLM", name: "Morelia \u2013 General Francisco J. Mujica International Airport", country: "MX", size: "large" },
  { code: "MLN", name: "Melilla Airport", country: "ES", size: "medium" },
  { code: "MLO", name: "Milos Island \u2013 Milos Airport", country: "GR", size: "small" },
  { code: "MLU", name: "Monroe Regional Airport", country: "US", size: "medium" },
  { code: "MLW", name: "Monrovia \u2013 Spriggs Payne Airport", country: "LR", size: "medium" },
  { code: "MLX", name: "Malatya Erha\xE7 Airport", country: "TR", size: "medium" },
  { code: "MLY", name: "Manley Hot Springs Airport", country: "US", size: "small" },
  { code: "MMB", name: "\u014Czora \u2013 Memanbetsu Airport", country: "JP", size: "medium" },
  { code: "MMD", name: "Minamidaito Airport", country: "JP", size: "medium" },
  { code: "MME", name: "Darlington, Durham \u2013 Teesside International Airport", country: "GB", size: "medium" },
  { code: "MMG", name: "Mount Magnet Airport", country: "AU", size: "medium" },
  { code: "MMH", name: "Mammoth Lakes \u2013 Mammoth Yosemite Airport", country: "US", size: "medium" },
  { code: "MMJ", name: "Matsumoto \u2013 Shinshu-Matsumoto Airport", country: "JP", size: "medium" },
  { code: "MMK", name: "Murmansk \u2013 Emperor Nicholas II Murmansk Airport", country: "RU", size: "large" },
  { code: "MMO", name: "Vila do Maio \u2013 Maio Airport", country: "CV", size: "medium" },
  { code: "MMX", name: "Malm\xF6 Sturup Airport", country: "SE", size: "large" },
  { code: "MMY", name: "Miyakojima \u2013 Miyako Airport", country: "JP", size: "medium" },
  { code: "MNC", name: "Nacala International Airport", country: "MZ", size: "medium" },
  { code: "MNF", name: "Mana Island Airport", country: "FJ", size: "small" },
  { code: "MNG", name: "Maningrida Airport", country: "AU", size: "medium" },
  { code: "MNI", name: "Gerald's Park \u2013 John A. Osborne Airport", country: "MS", size: "large" },
  { code: "MNJ", name: "Mananjary Airport", country: "MG", size: "medium" },
  { code: "MNL", name: "Manila (Pasay) \u2013 Ninoy Aquino International Airport", country: "PH", size: "large" },
  { code: "MNT", name: "Minto Al Wright Airport", country: "US", size: "small" },
  { code: "MNU", name: "Mawlamyine Airport", country: "MM", size: "small" },
  { code: "MNX", name: "Manicor\xE9 Airport", country: "BR", size: "medium" },
  { code: "MNY", name: "Stirling Island \u2013 Mono Airport", country: "SB", size: "small" },
  { code: "MOB", name: "Mobile Regional Airport", country: "US", size: "medium" },
  { code: "MOC", name: "Montes Claros \u2013 M\xE1rio Ribeiro Airport", country: "BR", size: "medium" },
  { code: "MOF", name: "Waioti \u2013 Frans Xavier Seda Airport", country: "ID", size: "small" },
  { code: "MOG", name: "Mong Hsat Airport", country: "MM", size: "medium" },
  { code: "MOH", name: "Morowali \u2013 Maleo Airport", country: "ID", size: "small" },
  { code: "MOI", name: "Mitiaro Island Airport", country: "CK", size: "small" },
  { code: "MOJ", name: "Moengo Airstrip", country: "SR", size: "small" },
  { code: "MOL", name: "\xC5r\xF8 \u2013 Molde Airport, \xC5r\xF8", country: "NO", size: "medium" },
  { code: "MOQ", name: "Morondava Airport", country: "MG", size: "medium" },
  { code: "MOT", name: "Minot International Airport", country: "US", size: "medium" },
  { code: "MOU", name: "Mountain Village Airport", country: "US", size: "small" },
  { code: "MOV", name: "Moranbah Airport", country: "AU", size: "medium" },
  { code: "MOZ", name: "Moorea-Maiao \u2013 Moorea Temae Airport", country: "PF", size: "medium" },
  { code: "MPA", name: "Mpacha \u2013 Katima Mulilo Airport", country: "NA", size: "medium" },
  { code: "MPC", name: "Muko Muko Airport", country: "ID", size: "small" },
  { code: "MPH", name: "Caticlan \u2013 Godofredo P. Ramos Airport", country: "PH", size: "medium" },
  { code: "MPL", name: "Montpellier/M\xE9diterran\xE9e \u2013 Montpellier-M\xE9diterran\xE9e Airport", country: "FR", size: "large" },
  { code: "MPM", name: "Maputo Airport", country: "MZ", size: "large" },
  { code: "MPN", name: "Mount Pleasant Airport / RAF Mount Pleasant", country: "FK", size: "medium" },
  { code: "MPY", name: "Maripasoula Airport", country: "GF", size: "medium" },
  { code: "MQC", name: "Miquelon Airport", country: "PM", size: "small" },
  { code: "MQF", name: "Magnitogorsk International Airport", country: "RU", size: "large" },
  { code: "MQJ", name: "Khonuu \u2013 Moma Airport", country: "RU", size: "medium" },
  { code: "MQL", name: "Mildura Airport", country: "AU", size: "medium" },
  { code: "MQM", name: "Mardin Airport", country: "TR", size: "medium" },
  { code: "MQN", name: "Mo i Rana Airport, R\xF8ssvoll", country: "NO", size: "medium" },
  { code: "MQP", name: "Mbombela \u2013 Kruger Mpumalanga International Airport", country: "ZA", size: "large" },
  { code: "MQS", name: "Lovell \u2013 Mustique Airport", country: "VC", size: "medium" },
  { code: "MQT", name: "Gwinn \u2013 Marquette Sawyer Regional Airport", country: "US", size: "medium" },
  { code: "MQX", name: "Mekele Alula Aba Nega Airport", country: "ET", size: "medium" },
  { code: "MRA", name: "Misrata International Airport", country: "LY", size: "small" },
  { code: "MRE", name: "Serena \u2013 Mara Serena Lodge Airstrip", country: "KE", size: "medium" },
  { code: "MRI", name: "Anchorage \u2013 Merrill Field", country: "US", size: "medium" },
  { code: "MRS", name: "Marignane, Bouches-du-Rh\xF4ne \u2013 Marseille Provence Airport", country: "FR", size: "large" },
  {
    code: "MRU",
    name: "Plaine Magnien \u2013 Sir Seewoosagur Ramgoolam International Airport",
    country: "MU",
    size: "large"
  },
  { code: "MRV", name: "Mineralnyye Vody \u2013 Mineralnye Vody Airport", country: "RU", size: "large" },
  { code: "MRX", name: "Mahshahr Airport", country: "IR", size: "medium" },
  { code: "MRY", name: "Monterey Regional Airport", country: "US", size: "medium" },
  { code: "MRZ", name: "Moree Airport", country: "AU", size: "medium" },
  { code: "MSA", name: "Muskrat Dam Airport", country: "CA", size: "small" },
  { code: "MSJ", name: "Misawa Airport / Misawa Air Base", country: "JP", size: "medium" },
  { code: "MSL", name: "Muscle Shoals \u2013 Northwest Alabama Regional Airport", country: "US", size: "medium" },
  { code: "MSN", name: "Madison \u2013 Dane County Regional Truax Field", country: "US", size: "medium" },
  { code: "MSO", name: "Missoula Montana Airport", country: "US", size: "medium" },
  {
    code: "MSP",
    name: "Minneapolis\u2013Saint Paul International Airport / Wold\u2013Chamberlain Field",
    country: "US",
    size: "large"
  },
  { code: "MSQ", name: "Minsk National Airport", country: "BY", size: "large" },
  { code: "MSR", name: "Mu\u015F Airport", country: "TR", size: "medium" },
  { code: "MSS", name: "Massena International Airport Richards Field", country: "US", size: "medium" },
  { code: "MST", name: "Maastricht Aachen Airport", country: "NL", size: "large" },
  { code: "MSU", name: "Maseru(Mazenod) \u2013 Moshoeshoe I International Airport", country: "LS", size: "large" },
  {
    code: "MSY",
    name: "New Orleans \u2013 Louis Armstrong New Orleans International Airport",
    country: "US",
    size: "large"
  },
  { code: "MSZ", name: "Mo\xE7\xE2medes \u2013 Welwitschia Mirabilis International Airport", country: "AO", size: "medium" },
  { code: "MTF", name: "Mizan Teferi Airport", country: "ET", size: "small" },
  { code: "MTJ", name: "Montrose Regional Airport", country: "US", size: "medium" },
  { code: "MTP", name: "Montauk Airport", country: "US", size: "small" },
  { code: "MTR", name: "Monter\xEDa \u2013 Los Garzones Airport", country: "CO", size: "medium" },
  { code: "MTT", name: "Cosoleacaque \u2013 Minatitl\xE1n/Coatzacoalcos International Airport", country: "MX", size: "medium" },
  { code: "MTY", name: "Monterrey International Airport", country: "MX", size: "large" },
  { code: "MUA", name: "Munda Airport", country: "SB", size: "medium" },
  { code: "MUB", name: "Maun International Airport", country: "BW", size: "large" },
  { code: "MUC", name: "Munich Airport", country: "DE", size: "large" },
  { code: "MUE", name: "Waimea (Kamuela) \u2013 Waimea Kohala Airport", country: "US", size: "medium" },
  { code: "MUH", name: "Marsa Matruh \u2013 Mersa Matruh International Airport", country: "EG", size: "large" },
  { code: "MUK", name: "Mauke Island \u2013 Mauke Airport", country: "CK", size: "small" },
  { code: "MUN", name: "Matur\xEDn \u2013 Jos\xE9 Tadeo Monagas International Airport", country: "VE", size: "medium" },
  { code: "MUR", name: "Marudi Airport", country: "MY", size: "medium" },
  { code: "MUX", name: "Multan International Airport", country: "PK", size: "large" },
  { code: "MUZ", name: "Musoma Airport", country: "TZ", size: "small" },
  {
    code: "MVB",
    name: "Franceville \u2013 M'Vengue El Hadj Omar Bongo Ondimba International Airport",
    country: "GA",
    size: "medium"
  },
  {
    code: "MVD",
    name: "Ciudad de la Costa \u2013 Carrasco General Ces\xE1reo L. Berisso International Airport",
    country: "UY",
    size: "large"
  },
  { code: "MVF", name: "Mossor\xF3 \u2013 Dix-Sept Rosado Airport", country: "BR", size: "medium" },
  { code: "MVP", name: "Mit\xFA \u2013 Fabio Alberto Leon Bentley Airport", country: "CO", size: "medium" },
  { code: "MVQ", name: "Mogilev Airport", country: "BY", size: "medium" },
  { code: "MVR", name: "Maroua \u2013 Salak Airport", country: "CM", size: "medium" },
  { code: "MVT", name: "Mataiva Airport", country: "PF", size: "medium" },
  { code: "MVY", name: "Martha's Vineyard Airport", country: "US", size: "small" },
  { code: "MWA", name: "Marion \u2013 Veterans Airport of Southern Illinois", country: "US", size: "medium" },
  { code: "MWL", name: "Mineral Wells Regional Airport", country: "US", size: "medium" },
  { code: "MWQ", name: "Magway Airport", country: "MM", size: "small" },
  { code: "MWX", name: "Muan (Piseo-ri) \u2013 Muan International Airport", country: "KR", size: "large" },
  { code: "MWZ", name: "Mwanza International Airport", country: "TZ", size: "large" },
  { code: "MXH", name: "Moro Airport", country: "PG", size: "small" },
  {
    code: "MXL",
    name: "Mexicali \u2013 General Rodolfo S\xE1nchez Taboada International Airport",
    country: "MX",
    size: "medium"
  },
  { code: "MXP", name: "Ferno (VA) \u2013 Milan Malpensa International Airport", country: "IT", size: "large" },
  { code: "MXV", name: "M\xF6r\xF6n Airport", country: "MN", size: "medium" },
  { code: "MXW", name: "Mandalgobi Airport", country: "MN", size: "small" },
  { code: "MXX", name: "Mora Airport", country: "SE", size: "medium" },
  {
    code: "MXZ",
    name: "Meizhou (Meixian) \u2013 Meizhou Meixian Changgangji International Airport",
    country: "CN",
    size: "small"
  },
  { code: "MYA", name: "Moruya Airport", country: "AU", size: "medium" },
  { code: "MYD", name: "Malindi International Airport", country: "KE", size: "medium" },
  { code: "MYE", name: "Miyakejima Airport", country: "JP", size: "medium" },
  { code: "MYG", name: "Abraham Bay Settlement \u2013 Mayaguana Airport", country: "BS", size: "medium" },
  { code: "MYI", name: "Murray Island Airport", country: "AU", size: "small" },
  { code: "MYJ", name: "Matsuyama Airport", country: "JP", size: "large" },
  { code: "MYK", name: "May Creek Airport", country: "US", size: "small" },
  { code: "MYL", name: "McCall Municipal Airport", country: "US", size: "medium" },
  { code: "MYP", name: "Mary International Airport", country: "TM", size: "medium" },
  { code: "MYQ", name: "Mysore Airport", country: "IN", size: "medium" },
  { code: "MYR", name: "Myrtle Beach International Airport", country: "US", size: "large" },
  { code: "MYT", name: "Myitkyina Airport", country: "MM", size: "medium" },
  { code: "MYU", name: "Mekoryuk Airport", country: "US", size: "medium" },
  { code: "MYW", name: "Mtwara Airport", country: "TZ", size: "medium" },
  { code: "MYY", name: "Miri Airport", country: "MY", size: "medium" },
  { code: "MZG", name: "Huxi \u2013 Penghu Magong Airport", country: "TW", size: "large" },
  { code: "MZH", name: "Amasya Merzifon Airport", country: "TR", size: "medium" },
  { code: "MZI", name: "S\xE9var\xE9 \u2013 Mopti Airport", country: "ML", size: "medium" },
  { code: "MZL", name: "Manizales \u2013 La Nubia Airport", country: "CO", size: "medium" },
  { code: "MZO", name: "Manzanillo \u2013 Sierra Maestra International Airport", country: "CU", size: "medium" },
  { code: "MZQ", name: "Mkuze Airport", country: "ZA", size: "medium" },
  { code: "MZR", name: "Mazar-i-Sharif International Airport", country: "AF", size: "large" },
  { code: "MZS", name: "Moradabad Airport", country: "IN", size: "medium" },
  { code: "MZT", name: "Mazatl\xE0n \u2013 General Rafael Buelna International Airport", country: "MX", size: "large" },
  { code: "MZV", name: "Mulu Airport", country: "MY", size: "medium" },
  { code: "MZW", name: "Mecheria Airport", country: "DZ", size: "medium" },
  { code: "NAA", name: "Narrabri Airport", country: "AU", size: "medium" },
  { code: "NAG", name: "Nagpur \u2013 Dr. Babasaheb Ambedkar International Airport", country: "IN", size: "large" },
  { code: "NAH", name: "Tabukan Utara, Sangihe Islands \u2013 Naha Airport", country: "ID", size: "medium" },
  { code: "NAJ", name: "Nakhchivan International Airport", country: "AZ", size: "large" },
  { code: "NAL", name: "Nalchik Airport", country: "RU", size: "medium" },
  { code: "NAM", name: "Namniwel Airport", country: "ID", size: "medium" },
  { code: "NAN", name: "Nadi International Airport", country: "FJ", size: "large" },
  { code: "NAO", name: "Nanchong (Gaoping) \u2013 Nanchong Gaoping Airport", country: "CN", size: "small" },
  { code: "NAP", name: "Napoli \u2013 Naples International Airport", country: "IT", size: "large" },
  { code: "NAQ", name: "Qaanaaq Airport", country: "GL", size: "medium" },
  { code: "NAS", name: "Nassau \u2013 Lynden Pindling International Airport", country: "BS", size: "large" },
  {
    code: "NAT",
    name: "Natal \u2013 Rio Grande do Norte/S\xE3o Gon\xE7alo do Amarante\u2013Governador Alu\xEDzio Alves International Airport",
    country: "BR",
    size: "large"
  },
  { code: "NAU", name: "Napuka Island Airport", country: "PF", size: "small" },
  { code: "NAV", name: "Nev\u015Fehir Kapadokya Airport", country: "TR", size: "large" },
  { code: "NAW", name: "Narathiwat Airport", country: "TH", size: "medium" },
  { code: "NBC", name: "Nizhnekamsk \u2013 Begishevo Airport", country: "RU", size: "medium" },
  { code: "NBE", name: "Enfidha - Hammamet International Airport", country: "TN", size: "medium" },
  {
    code: "NBJ",
    name: "Luanda (\xCDcolo e Bengo) \u2013 Dr. Antonio Agostinho Neto International Airport",
    country: "AO",
    size: "large"
  },
  { code: "NBN", name: "San Antonio de Pal\xE9 \u2013 Annob\xF3n Airport", country: "GQ", size: "small" },
  { code: "NBO", name: "Nairobi \u2013 Jomo Kenyatta International Airport", country: "KE", size: "large" },
  { code: "NBS", name: "Baishan \u2013 Changbaishan Airport", country: "CN", size: "medium" },
  { code: "NCA", name: "North Caicos Airport", country: "TC", size: "medium" },
  { code: "NCE", name: "Nice, Alpes-Maritimes \u2013 Nice-C\xF4te d'Azur Airport", country: "FR", size: "large" },
  {
    code: "NCL",
    name: "Newcastle upon Tyne, Tyne and Wear \u2013 Newcastle International Airport",
    country: "GB",
    size: "large"
  },
  { code: "NCN", name: "Chenega Bay Airport", country: "US", size: "small" },
  { code: "NCU", name: "Nukus International Airport", country: "UZ", size: "large" },
  { code: "NCY", name: "Annecy Meythet airport", country: "FR", size: "medium" },
  { code: "NDB", name: "Nouadhibou International Airport", country: "MR", size: "large" },
  { code: "NDC", name: "Nanded Airport", country: "IN", size: "medium" },
  { code: "NDG", name: "Qiqihar Sanjiazi Airport", country: "CN", size: "large" },
  { code: "NDJ", name: "N'Djamena International Airport", country: "TD", size: "large" },
  { code: "NDR", name: "Al Aaroui \u2013 Nador Al Aaroui International Airport", country: "MA", size: "large" },
  { code: "NDU", name: "Rundu Airport", country: "NA", size: "medium" },
  { code: "NDY", name: "Sanday Airport", country: "GB", size: "small" },
  { code: "NEC", name: "Necochea Airport", country: "AR", size: "medium" },
  { code: "NER", name: "Neryungri \u2013 Chulman Airport", country: "RU", size: "medium" },
  { code: "NEV", name: "Charlestown \u2013 Vance W. Amory International Airport", country: "KN", size: "medium" },
  { code: "NGB", name: "Ningbo Lishe International Airport", country: "CN", size: "large" },
  { code: "NGE", name: "N'Gaound\xE9r\xE9 Airport", country: "CM", size: "medium" },
  { code: "NGI", name: "Ngau Airport", country: "FJ", size: "small" },
  { code: "NGK", name: "Nogliki Airport", country: "RU", size: "small" },
  { code: "NGO", name: "Tokoname \u2013 Chubu Centrair International Airport", country: "JP", size: "large" },
  { code: "NGQ", name: "Shiquanhe \u2013 Ngari Gunsa Airport", country: "CN", size: "medium" },
  { code: "NGS", name: "Nagasaki Airport", country: "JP", size: "large" },
  { code: "NHV", name: "Nuku Hiva Airport", country: "PF", size: "medium" },
  { code: "NIB", name: "Nikolai Airport", country: "US", size: "small" },
  { code: "NIM", name: "Niamey \u2013 Diori Hamani International Airport", country: "NE", size: "large" },
  { code: "NIU", name: "Naiu Atoll \u2013 Naiu Airport", country: "PF", size: "small" },
  { code: "NJC", name: "Nizhnevartovsk Airport", country: "RU", size: "large" },
  { code: "NJF", name: "Najaf \u2013 Al Najaf International Airport", country: "IQ", size: "large" },
  { code: "NKC", name: "Nouakchott\u2013Oumtounsy International Airport", country: "MR", size: "large" },
  { code: "NKG", name: "Nanjing Lukou International Airport", country: "CN", size: "large" },
  { code: "NKM", name: "Nagoya Airport / JASDF Komaki Air Base", country: "JP", size: "medium" },
  { code: "NKT", name: "\u015E\u0131rnak \u015Eerafettin El\xE7i Airport", country: "TR", size: "medium" },
  { code: "NLA", name: "Ndola \u2013 Simon Mwansa Kapwepwe International Airport", country: "ZM", size: "large" },
  { code: "NLD", name: "Nuevo Laredo \u2013 Quetzalc\xF3atl International Airport", country: "MX", size: "medium" },
  { code: "NLF", name: "Darnley Island Airport", country: "AU", size: "small" },
  { code: "NLG", name: "Nelson Lagoon Airport", country: "US", size: "small" },
  { code: "NLH", name: "Ninglang Luguhu Airport", country: "CN", size: "medium" },
  { code: "NLI", name: "Nikolayevsk-na-Amure Airport", country: "RU", size: "medium" },
  { code: "NLK", name: "Burnt Pine \u2013 Norfolk Island International Airport", country: "NF", size: "medium" },
  { code: "NLT", name: "Xinyuan Nalati Airport", country: "CN", size: "medium" },
  { code: "NLU", name: "Mexico City \u2013 Felipe \xC1ngeles International Airport", country: "MX", size: "large" },
  { code: "NMA", name: "Namangan International Airport", country: "UZ", size: "large" },
  { code: "NME", name: "Nightmute Airport", country: "US", size: "small" },
  { code: "NMF", name: "Noonu Atoll \u2013 Maafaru International Airport", country: "MV", size: "medium" },
  { code: "NMI", name: "Navi Mumbai International Airport", country: "IN", size: "large" },
  { code: "NNB", name: "Santa Ana Island \u2013 Santa Ana Airport", country: "SB", size: "small" },
  { code: "NNG", name: "Nanning (Jiangnan) \u2013 Nanning Wuxu International Airport", country: "CN", size: "large" },
  { code: "NNM", name: "Naryan Mar Airport", country: "RU", size: "medium" },
  { code: "NNR", name: "Inverin \u2013 Connemara Regional Airport", country: "IE", size: "small" },
  { code: "NNT", name: "Nan Airport", country: "TH", size: "medium" },
  { code: "NNY", name: "Nanyang (Wancheng) \u2013 Nanyang Jiangying Airport", country: "CN", size: "small" },
  { code: "NOB", name: "Nicoya \u2013 Nosara Airport", country: "CR", size: "medium" },
  { code: "NOC", name: "Charlestown \u2013 Ireland West Airport Knock", country: "IE", size: "large" },
  { code: "NOD", name: "Norddeich \u2013 Norden-Norddeich Airport", country: "DE", size: "small" },
  { code: "NOJ", name: "Noyabrsk Airport", country: "RU", size: "medium" },
  { code: "NOP", name: "Sinop Airport", country: "TR", size: "medium" },
  { code: "NOS", name: "Nosy Be International Airport", country: "MG", size: "large" },
  { code: "NOU", name: "Noum\xE9a (La Tontouta) \u2013 La Tontouta International Airport", country: "NC", size: "large" },
  { code: "NOV", name: "Huambo \u2013 Albano Machado Airport", country: "AO", size: "medium" },
  { code: "NOZ", name: "Novokuznetsk \u2013 Spichenkovo Airport", country: "RU", size: "medium" },
  { code: "NPE", name: "Napier \u2013 Hawke's Bay Airport", country: "NZ", size: "medium" },
  { code: "NPL", name: "New Plymouth Airport", country: "NZ", size: "medium" },
  { code: "NPO", name: "Nanga Pinoh-Borneo Island \u2013 Nanga Pinoh Airport", country: "ID", size: "medium" },
  { code: "NPT", name: "Newport State Airport", country: "US", size: "medium" },
  { code: "NQN", name: "Neuqu\xE9n \u2013 Presidente Per\xF3n International Airport", country: "AR", size: "large" },
  { code: "NQU", name: "Nuqu\xED \u2013 Reyes Murillo Airport", country: "CO", size: "small" },
  { code: "NQY", name: "Newquay \u2013 Cornwall Airport Newquay", country: "GB", size: "medium" },
  { code: "NQZ", name: "Astana \u2013 Nursultan Nazarbayev International Airport", country: "KZ", size: "large" },
  { code: "NRA", name: "Narrandera Airport", country: "AU", size: "medium" },
  { code: "NRD", name: "Norderney Airport", country: "DE", size: "small" },
  { code: "NRK", name: "Norrk\xF6ping Airport", country: "SE", size: "medium" },
  { code: "NRL", name: "North Ronaldsay Airport", country: "GB", size: "small" },
  { code: "NRN", name: "Weeze (Niederrhein) Airport", country: "DE", size: "large" },
  { code: "NRR", name: "Ceiba \u2013 Jos\xE9 Aponte de la Torre Airport", country: "PR", size: "medium" },
  { code: "NRT", name: "Narita International Airport", country: "JP", size: "large" },
  { code: "NSH", name: "Nowshahr Airport", country: "IR", size: "medium" },
  { code: "NSI", name: "Yaound\xE9 Nsimalen International Airport", country: "CM", size: "large" },
  { code: "NSK", name: "Norilsk \u2013 Alykel International Airport", country: "RU", size: "large" },
  { code: "NSN", name: "Nelson Airport", country: "NZ", size: "medium" },
  { code: "NST", name: "Nakhon Si Thammarat Airport", country: "TH", size: "medium" },
  { code: "NTE", name: "Nantes Atlantique Airport", country: "FR", size: "large" },
  { code: "NTG", name: "Nantong (Tongzhou) \u2013 Nantong Xingdong International Airport", country: "CN", size: "medium" },
  { code: "NTL", name: "Williamtown \u2013 Newcastle Airport", country: "AU", size: "large" },
  { code: "NTN", name: "Normanton Airport", country: "AU", size: "medium" },
  { code: "NTQ", name: "Wajima \u2013 Noto Satoyama Airport", country: "JP", size: "medium" },
  { code: "NTT", name: "Niuatoputapu \u2013 Kuini Lavenia Airport", country: "TO", size: "small" },
  { code: "NTX", name: "Ranai-Natuna Besar Island \u2013 Ranai Airport", country: "ID", size: "medium" },
  { code: "NUE", name: "Nuremberg Airport", country: "DE", size: "large" },
  { code: "NUI", name: "Nuiqsut Airport", country: "US", size: "medium" },
  { code: "NUL", name: "Nulato Airport", country: "US", size: "small" },
  { code: "NUM", name: "Sharma \u2013 Neom Bay Airport", country: "SA", size: "large" },
  { code: "NUP", name: "Nunapitchuk Airport", country: "US", size: "small" },
  { code: "NUS", name: "Norsup Airport", country: "VU", size: "small" },
  { code: "NUX", name: "Novy Urengoy Airport", country: "RU", size: "medium" },
  { code: "NVA", name: "Neiva \u2013 Benito Salas Airport", country: "CO", size: "medium" },
  { code: "NVI", name: "Navoi International Airport", country: "UZ", size: "medium" },
  { code: "NVT", name: "Navegantes \u2013 Ministro Victor Konder International Airport", country: "BR", size: "large" },
  { code: "NWI", name: "Norwich, Norfolk \u2013 Norwich Airport", country: "GB", size: "medium" },
  { code: "NYA", name: "Nyagan Airport", country: "RU", size: "medium" },
  { code: "NYI", name: "Sunyani Airport", country: "GH", size: "medium" },
  { code: "NYK", name: "Gathiuru \u2013 Nanyuki Civil Airport", country: "KE", size: "medium" },
  { code: "NYM", name: "Nadym Airport", country: "RU", size: "medium" },
  { code: "NYO", name: "Nyk\xF6ping \u2013 Stockholm Skavsta Airport", country: "SE", size: "large" },
  { code: "NYT", name: "Naypyitaw \u2013 Nay Pyi Taw International Airport", country: "MM", size: "large" },
  { code: "NYU", name: "Nyaung U \u2013 Bagan Airport", country: "MM", size: "small" },
  { code: "NZC", name: "Nazca \u2013 Maria Reiche Neuman Airport", country: "PE", size: "medium" },
  { code: "NZG", name: "Nizhneangarsk International Airport", country: "RU", size: "small" },
  { code: "NZH", name: "Manzhouli Xijiao Airport", country: "CN", size: "medium" },
  { code: "NZL", name: "Zhalantun Genghis Khan Airport", country: "CN", size: "medium" },
  { code: "OAJ", name: "Richlands \u2013 Albert J Ellis Airport", country: "US", size: "medium" },
  { code: "OAK", name: "Oakland San Francisco Bay Airport", country: "US", size: "large" },
  { code: "OAL", name: "Cacoal Airport", country: "BR", size: "small" },
  { code: "OAX", name: "Oaxaca \u2013 Xoxocotl\xE1n International Airport", country: "MX", size: "large" },
  { code: "OBN", name: "North Connel \u2013 Oban Airport", country: "GB", size: "small" },
  { code: "OBO", name: "Obihiro \u2013 Tokachi-Obihiro Airport", country: "JP", size: "medium" },
  { code: "OBU", name: "Kobuk Airport", country: "US", size: "small" },
  { code: "OBX", name: "Obo Airport", country: "PG", size: "small" },
  { code: "OCC", name: "Coca \u2013 Francisco De Orellana Airport", country: "EC", size: "medium" },
  { code: "OCE", name: "Ocean City Municipal Airport", country: "US", size: "medium" },
  { code: "OCJ", name: "Boscobel \u2013 Ian Fleming International Airport", country: "JM", size: "medium" },
  { code: "OCS", name: "Corisco Island \u2013 Corisco International Airport", country: "GQ", size: "large" },
  { code: "ODB", name: "C\xF3rdoba Airport", country: "ES", size: "medium" },
  { code: "ODE", name: "Odense Hans Christian Andersen Airport", country: "DK", size: "large" },
  { code: "ODN", name: "Long Seridan Airport", country: "MY", size: "small" },
  { code: "ODO", name: "Bodaybo Airport", country: "RU", size: "small" },
  { code: "ODY", name: "Oudomsay Airport", country: "LA", size: "small" },
  {
    code: "OEC",
    name: "Oecussi-Ambeno \u2013 Oecusse Route of the Sandalwood International Airport",
    country: "TL",
    size: "large"
  },
  { code: "OER", name: "\xD6rnsk\xF6ldsvik Airport", country: "SE", size: "medium" },
  { code: "OES", name: "San Antonio Oeste \u2013 Antoine de Saint Exup\xE9ry Airport", country: "AR", size: "small" },
  { code: "OFU", name: "Ofu Airport", country: "AS", size: "small" },
  { code: "OGD", name: "Ogden Hinckley Airport", country: "US", size: "medium" },
  { code: "OGG", name: "Kahului International Airport", country: "US", size: "large" },
  { code: "OGL", name: "Ogle \u2013 Eugene F. Correia International Airport", country: "GY", size: "medium" },
  { code: "OGN", name: "Yonaguni Airport", country: "JP", size: "medium" },
  { code: "OGS", name: "Ogdensburg International Airport", country: "US", size: "medium" },
  { code: "OGU", name: "Ordu\u2013Giresun Airport", country: "TR", size: "medium" },
  { code: "OGX", name: "Ouargla \u2013 Ain Beida Airport", country: "DZ", size: "medium" },
  { code: "OGZ", name: "Beslan \u2013 Vladikavkaz Beslan International Airport", country: "RU", size: "medium" },
  { code: "OHD", name: "Ohrid St. Paul the Apostle Airport", country: "MK", size: "large" },
  { code: "OHE", name: "Mohe Gulian Airport", country: "CN", size: "medium" },
  { code: "OHO", name: "Okhotsk Airport", country: "RU", size: "medium" },
  { code: "OHS", name: "Suhar International Airport", country: "OM", size: "large" },
  { code: "OIM", name: "Izu Oshima \u2013 Oshima Airport", country: "JP", size: "medium" },
  { code: "OIR", name: "Okushiri Island \u2013 Okushiri Airport", country: "JP", size: "medium" },
  { code: "OIT", name: "Oita Airport", country: "JP", size: "medium" },
  { code: "OJU", name: "Tojo Una-Una \u2013 Tanjung Api Airport", country: "ID", size: "small" },
  { code: "OKA", name: "Naha International Airport", country: "JP", size: "large" },
  { code: "OKC", name: "Oklahoma City \u2013 OKC Will Rogers World Airport", country: "US", size: "large" },
  { code: "OKD", name: "Sapporo Okadama Airport", country: "JP", size: "medium" },
  { code: "OKE", name: "Wadomari \u2013 Okinoerabu Airport", country: "JP", size: "medium" },
  { code: "OKI", name: "Okinoshima \u2013 Oki Global Geopark Airport", country: "JP", size: "medium" },
  { code: "OKJ", name: "Okayama Momotaro Airport", country: "JP", size: "large" },
  { code: "OKL", name: "Oksibil Airport", country: "ID", size: "medium" },
  { code: "OKR", name: "Yorke Island Airport", country: "AU", size: "small" },
  { code: "OKY", name: "Oakey Army Aviation Centre", country: "AU", size: "medium" },
  { code: "OLA", name: "\xD8rland Airport", country: "NO", size: "medium" },
  { code: "OLB", name: "Olbia (SS) \u2013 Olbia Costa Smeralda Airport", country: "IT", size: "large" },
  { code: "OLF", name: "Wolf Point \u2013 L M Clayton Airport", country: "US", size: "medium" },
  { code: "OLH", name: "Old Harbor Airport", country: "US", size: "small" },
  { code: "OLJ", name: "Olpoi \u2013 North West Santo Airport", country: "VU", size: "small" },
  { code: "OLM", name: "Olympia Regional Airport", country: "US", size: "medium" },
  { code: "OLP", name: "Olympic Dam Airport", country: "AU", size: "small" },
  { code: "OLZ", name: "Olyokminsk Airport", country: "RU", size: "medium" },
  { code: "OMA", name: "Omaha \u2013 Eppley Airfield", country: "US", size: "large" },
  { code: "OMD", name: "Oranjemund Airport", country: "NA", size: "medium" },
  { code: "OME", name: "Nome Airport", country: "US", size: "medium" },
  { code: "OMH", name: "Urmia Airport", country: "IR", size: "medium" },
  { code: "OMN", name: "Zomin Airport", country: "UZ", size: "medium" },
  { code: "OMO", name: "Mostar International Airport", country: "BA", size: "large" },
  { code: "OMR", name: "Oradea International Airport", country: "RO", size: "large" },
  { code: "OMS", name: "Omsk Central Airport", country: "RU", size: "large" },
  { code: "OND", name: "Ondangwa Airport", country: "NA", size: "medium" },
  { code: "ONG", name: "Mornington Island Airport", country: "AU", size: "small" },
  { code: "ONJ", name: "Kitaakita \u2013 Odate Noshiro Airport", country: "JP", size: "medium" },
  { code: "ONQ", name: "Zonguldak \xC7aycuma Airport", country: "TR", size: "medium" },
  { code: "ONT", name: "Ontario International Airport", country: "US", size: "large" },
  { code: "ONX", name: "Col\xF3n \u2013 Enrique Adolfo Jimenez Airport", country: "PA", size: "medium" },
  { code: "OOK", name: "Toksook Bay Airport", country: "US", size: "small" },
  { code: "OOL", name: "Gold Coast Airport", country: "AU", size: "large" },
  { code: "OOM", name: "Cooma Snowy Mountains Airport", country: "AU", size: "medium" },
  { code: "OPF", name: "Miami-Opa Locka Executive Airport", country: "US", size: "medium" },
  { code: "OPO", name: "Porto \u2013 Francisco de S\xE1 Carneiro Airport", country: "PT", size: "large" },
  { code: "OPP", name: "Salin\xF3polis Airport", country: "BR", size: "small" },
  { code: "OPS", name: "Sinop \u2013 Presidente Jo\xE3o Batista Figueiredo Airport", country: "BR", size: "small" },
  { code: "OPU", name: "Balimo Airport", country: "PG", size: "medium" },
  { code: "ORB", name: "\xD6rebro Airport", country: "SE", size: "medium" },
  { code: "ORD", name: "Chicago O'Hare International Airport", country: "US", size: "large" },
  { code: "ORF", name: "Norfolk International Airport", country: "US", size: "large" },
  { code: "ORG", name: "Paramaribo \u2013 Zorg en Hoop Airport", country: "SR", size: "small" },
  { code: "ORH", name: "Worcester Regional Airport", country: "US", size: "medium" },
  { code: "ORI", name: "Port Lions Airport", country: "US", size: "small" },
  { code: "ORK", name: "Cork International Airport", country: "IE", size: "large" },
  {
    code: "ORN",
    name: "Es-S\xE9nia \u2013 Oran Es-S\xE9nia (Ahmed Ben Bella) International Airport",
    country: "DZ",
    size: "large"
  },
  { code: "ORT", name: "Northway Airport", country: "US", size: "medium" },
  { code: "ORU", name: "Oruro \u2013 Juan Mendoza International Airport", country: "BO", size: "large" },
  { code: "ORV", name: "Noorvik \u2013 Robert (Bob) Curtis Memorial Airport", country: "US", size: "small" },
  { code: "ORY", name: "Paris (Orly, Val-de-Marne) \u2013 Paris-Orly Airport", country: "FR", size: "large" },
  { code: "ORZ", name: "Orange Walk \u2013 H.E Alfredo Martinez (Tower Hill) Airstrip", country: "BZ", size: "small" },
  { code: "OSD", name: "\xD6stersund \u2013 \xC5re \xD6stersund Airport", country: "SE", size: "medium" },
  { code: "OSI", name: "Osijek(Klisa) \u2013 Osijek Airport", country: "HR", size: "medium" },
  { code: "OSL", name: "Oslo (Gardermoen) \u2013 Oslo-Gardermoen International Airport", country: "NO", size: "large" },
  { code: "OSR", name: "Mo\u0161nov \u2013 Leo\u0161 Jan\xE1\u010Dek Airport Ostrava", country: "CZ", size: "large" },
  { code: "OSS", name: "Osh International Airport", country: "KG", size: "large" },
  { code: "OST", name: "Oostende \u2013 Ostend-Bruges International Airport", country: "BE", size: "large" },
  { code: "OSW", name: "Orsk Airport", country: "RU", size: "medium" },
  { code: "OSY", name: "Namsos Airport", country: "NO", size: "small" },
  { code: "OTD", name: "Contadora Island \u2013 Raul Arias Espinoza Airport", country: "PA", size: "small" },
  { code: "OTH", name: "North Bend \u2013 Southwest Oregon Regional Airport", country: "US", size: "medium" },
  { code: "OTP", name: "Otopeni \u2013 Bucharest Henri Coand\u0103 International Airport", country: "RO", size: "large" },
  { code: "OTS", name: "Anacortes Airport", country: "US", size: "small" },
  { code: "OTZ", name: "Kotzebue \u2013 Ralph Wien Memorial Airport", country: "US", size: "medium" },
  { code: "OUA", name: "Ouagadougou Thomas Sankara International Airport", country: "BF", size: "large" },
  { code: "OUD", name: "Ahl Angad \u2013 Oujda Angads Airport", country: "MA", size: "large" },
  { code: "OUI", name: "Ushant \u2013 Ouessant Airport", country: "FR", size: "small" },
  { code: "OUL", name: "Oulu / Oulunsalo \u2013 Oulu Airport", country: "FI", size: "large" },
  { code: "OUZ", name: "Zou\xE9rate \u2013 Tazadit Airport", country: "MR", size: "medium" },
  { code: "OVB", name: "Novosibirsk Tolmachevo Airport", country: "RU", size: "large" },
  { code: "OVD", name: "Ran\xF3n \u2013 Asturias Airport", country: "ES", size: "large" },
  { code: "OVS", name: "Sovetskiy Airport", country: "RU", size: "medium" },
  { code: "OWB", name: "Owensboro Daviess County Airport", country: "US", size: "medium" },
  { code: "OXB", name: "Bissau \u2013 Osvaldo Vieira International Airport", country: "GW", size: "large" },
  { code: "OYE", name: "Oyem Airport", country: "GA", size: "medium" },
  { code: "OZC", name: "Ozamiz \u2013 Labo Airport", country: "PH", size: "medium" },
  { code: "OZG", name: "Zagora Airport", country: "MA", size: "large" },
  { code: "OZZ", name: "Ouarzazate International Airport", country: "MA", size: "large" },
  { code: "PAB", name: "Bilaspur Airport", country: "IN", size: "medium" },
  { code: "PAC", name: "Albrook \u2013 Marcos A. Gelabert International Airport", country: "PA", size: "medium" },
  { code: "PAD", name: "B\xFCren \u2013 Paderborn Lippstadt Airport", country: "DE", size: "large" },
  { code: "PAE", name: "Everett \u2013 Seattle Paine Field International Airport", country: "US", size: "medium" },
  { code: "PAG", name: "Pagadian Airport", country: "PH", size: "medium" },
  { code: "PAH", name: "Paducah \u2013 Barkley Regional Airport", country: "US", size: "medium" },
  { code: "PAP", name: "Port-au-Prince \u2013 Toussaint Louverture International Airport", country: "HT", size: "large" },
  { code: "PAS", name: "Paros National Airport", country: "GR", size: "small" },
  { code: "PAT", name: "Patna \u2013 Jay Prakash Narayan Airport", country: "IN", size: "medium" },
  { code: "PAV", name: "Paulo Afonso Airport", country: "BR", size: "medium" },
  { code: "PAZ", name: "Poza Rica \u2013 El Taj\xEDn National Airport", country: "MX", size: "medium" },
  { code: "PBC", name: "Puebla \u2013 Hermanos Serd\xE1n International Airport", country: "MX", size: "large" },
  { code: "PBD", name: "Porbandar Airport", country: "IN", size: "medium" },
  { code: "PBG", name: "Plattsburgh International Airport", country: "US", size: "medium" },
  { code: "PBH", name: "Paro International Airport", country: "BT", size: "large" },
  { code: "PBJ", name: "Paama Island \u2013 Tavie Airport", country: "VU", size: "small" },
  { code: "PBM", name: "Paramaribo \u2013 Johan Adolf Pengel International Airport", country: "SR", size: "large" },
  { code: "PBO", name: "Paraburdoo Airport", country: "AU", size: "medium" },
  { code: "PBR", name: "Puerto Barrios Airport", country: "GT", size: "medium" },
  { code: "PBU", name: "Putao Airport", country: "MM", size: "medium" },
  { code: "PCL", name: "Pucallpa \u2013 Cap FAP David Abenzur Rengifo International Airport", country: "PE", size: "large" },
  { code: "PCN", name: "Koromiko \u2013 Picton Aerodrome", country: "NZ", size: "small" },
  { code: "PCP", name: "S\xE3o Tom\xE9 & Pr\xEDncipe \u2013 Principe Airport", country: "ST", size: "medium" },
  { code: "PCR", name: "Puerto Carre\xF1o \u2013 German Olano Airport", country: "CO", size: "medium" },
  { code: "PDA", name: "Puerto In\xEDrida \u2013 Obando Cesar Gaviria Trujillo Airport", country: "CO", size: "medium" },
  { code: "PDB", name: "Pedro Bay Airport", country: "US", size: "small" },
  { code: "PDG", name: "Padang (Katapiang) \u2013 Minangkabau International Airport", country: "ID", size: "large" },
  { code: "PDK", name: "Atlanta \u2013 DeKalb Peachtree Airport", country: "US", size: "medium" },
  { code: "PDL", name: "Ponta Delgada \u2013 Jo\xE3o Paulo II Airport", country: "PT", size: "large" },
  { code: "PDM", name: "Pedas\xED \u2013 Capt. J. Montenegro Airport", country: "PA", size: "small" },
  { code: "PDO", name: "Talang Gudang-Sumatra Island \u2013 Pendopo Airport", country: "ID", size: "medium" },
  {
    code: "PDP",
    name: "Punta del Este \u2013 Capitan Corbeta CA Curbelo International Airport",
    country: "UY",
    size: "medium"
  },
  { code: "PDS", name: "Piedras Negras International Airport", country: "MX", size: "medium" },
  { code: "PDT", name: "Pendleton \u2013 Eastern Oregon Regional Airport at Pendleton", country: "US", size: "medium" },
  { code: "PDV", name: "Plovdiv International Airport", country: "BG", size: "large" },
  { code: "PDX", name: "Portland International Airport", country: "US", size: "large" },
  { code: "PED", name: "Pardubice Airport", country: "CZ", size: "large" },
  { code: "PEE", name: "Perm International Airport", country: "RU", size: "large" },
  {
    code: "PEG",
    name: "Perugia (PG) \u2013 Perugia San Francesco d'Assisi \u2013 Umbria International Airport",
    country: "IT",
    size: "large"
  },
  { code: "PEI", name: "Pereira \u2013 Mateca\xF1a International Airport", country: "CO", size: "medium" },
  { code: "PEK", name: "Beijing Capital International Airport", country: "CN", size: "large" },
  { code: "PEM", name: "Puerto Maldonado \u2013 Padre Aldamiz International Airport", country: "PE", size: "medium" },
  { code: "PEN", name: "Penang International Airport", country: "MY", size: "large" },
  { code: "PER", name: "Perth International Airport", country: "AU", size: "large" },
  { code: "PES", name: "Petrozavodsk Airport", country: "RU", size: "medium" },
  { code: "PET", name: "Pelotas \u2013 Jo\xE3o Sim\xF5es Lopes Neto International Airport", country: "BR", size: "medium" },
  { code: "PEU", name: "Puerto Lempira Airport", country: "HN", size: "small" },
  { code: "PEV", name: "P\xE9cs-Pog\xE1ny International Airport", country: "HU", size: "large" },
  { code: "PEW", name: "Peshawar \u2013 Bacha Khan International Airport", country: "PK", size: "large" },
  { code: "PEX", name: "Pechora Airport", country: "RU", size: "medium" },
  { code: "PEZ", name: "Penza Airport", country: "RU", size: "medium" },
  { code: "PFB", name: "Passo Fundo \u2013 Lauro Kurtz Airport", country: "BR", size: "medium" },
  { code: "PFO", name: "Paphos International Airport", country: "CY", size: "large" },
  { code: "PFQ", name: "Parsabad-Moghan Airport", country: "IR", size: "small" },
  { code: "PFR", name: "Ilebo Airport", country: "CD", size: "small" },
  { code: "PGA", name: "Page Municipal Airport", country: "US", size: "medium" },
  { code: "PGD", name: "Punta Gorda Airport", country: "US", size: "medium" },
  {
    code: "PGF",
    name: "Perpignan/Rivesaltes \u2013 Perpignan-Rivesaltes (Llaban\xE8re) Airport",
    country: "FR",
    size: "medium"
  },
  { code: "PGH", name: "Pantnagar Airport", country: "IN", size: "medium" },
  { code: "PGK", name: "Pangkal Pinang \u2013 Depati Amir Airport", country: "ID", size: "medium" },
  { code: "PGM", name: "Port Graham Airport", country: "US", size: "small" },
  { code: "PGU", name: "Khiyaroo \u2013 Persian Gulf International Airport", country: "IR", size: "medium" },
  { code: "PGV", name: "Greenville \u2013 Pitt-Greenville Airport", country: "US", size: "medium" },
  { code: "PGZ", name: "Ponta Grossa Airport - Comandante Antonio Amilton Beraldo", country: "BR", size: "medium" },
  {
    code: "PHB",
    name: "Parna\xEDba - Prefeito Doutor Jo\xE3o Silva Filho International Airport",
    country: "BR",
    size: "medium"
  },
  { code: "PHC", name: "Port Harcourt International Airport", country: "NG", size: "large" },
  { code: "PHE", name: "Port Hedland International Airport", country: "AU", size: "large" },
  { code: "PHF", name: "Newport News Williamsburg International Airport", country: "US", size: "medium" },
  { code: "PHG", name: "Port Harcourt City Airport / Port Harcourt Air Force Base", country: "NG", size: "medium" },
  { code: "PHH", name: "Pokhara International Airport", country: "NP", size: "large" },
  { code: "PHL", name: "Philadelphia International Airport", country: "US", size: "large" },
  { code: "PHO", name: "Point Hope Airport", country: "US", size: "small" },
  { code: "PHS", name: "Phitsanulok Airport", country: "TH", size: "medium" },
  { code: "PHW", name: "Phalaborwa \u2013 Hendrik Van Eck Airport", country: "ZA", size: "medium" },
  { code: "PHX", name: "Phoenix Sky Harbor International Airport", country: "US", size: "large" },
  { code: "PHY", name: "Phetchabun Airport", country: "TH", size: "medium" },
  {
    code: "PIA",
    name: "Peoria \u2013 General Wayne A. Downing Peoria International Airport",
    country: "US",
    size: "medium"
  },
  { code: "PIB", name: "Moselle \u2013 Hattiesburg Laurel Regional Airport", country: "US", size: "medium" },
  {
    code: "PIE",
    name: "Pinellas Park \u2013 St. Petersburg Clearwater International Airport",
    country: "US",
    size: "large"
  },
  { code: "PIH", name: "Pocatello Regional Airport", country: "US", size: "medium" },
  { code: "PIK", name: "Prestwick, South Ayrshire \u2013 Glasgow Prestwick Airport", country: "GB", size: "large" },
  { code: "PIP", name: "Pilot Point Airport", country: "US", size: "small" },
  { code: "PIR", name: "Pierre Regional Airport", country: "US", size: "medium" },
  { code: "PIS", name: "Poitiers/Biard \u2013 Poitiers-Biard Airport", country: "FR", size: "medium" },
  { code: "PIT", name: "Pittsburgh International Airport", country: "US", size: "large" },
  {
    code: "PIU",
    name: "Piura \u2013 PAF Captain Guillermo Concha Iberico International Airport",
    country: "PE",
    size: "medium"
  },
  { code: "PIX", name: "Pico Island \u2013 Pico Airport", country: "PT", size: "medium" },
  { code: "PIZ", name: "Point Lay LRRS Airport", country: "US", size: "medium" },
  { code: "PJA", name: "Pajala Airport", country: "SE", size: "small" },
  { code: "PJM", name: "Puerto Jimenez Airport", country: "CR", size: "medium" },
  { code: "PKA", name: "Napaskiak Airport", country: "US", size: "small" },
  { code: "PKB", name: "Parkersburg (Williamstown) \u2013 Mid Ohio Valley Regional Airport", country: "US", size: "medium" },
  { code: "PKC", name: "Petropavlovsk-Kamchatsky \u2013 Yelizovo Airport", country: "RU", size: "large" },
  { code: "PKE", name: "Parkes Airport", country: "AU", size: "medium" },
  { code: "PKG", name: "Pangkor Island \u2013 Pulau Pangkor Airport", country: "MY", size: "small" },
  { code: "PKN", name: "Pangkalanbun \u2013 Iskandar Airport", country: "ID", size: "small" },
  { code: "PKP", name: "Puka Puka Airport", country: "PF", size: "small" },
  { code: "PKR", name: "Pokhara Domestic Airport", country: "NP", size: "medium" },
  {
    code: "PKU",
    name: "Pekanbaru \u2013 Sultan Syarif Kasim II International Airport / Roesmin Nurjadin AFB",
    country: "ID",
    size: "medium"
  },
  { code: "PKV", name: "Pskov \u2013 Princess Olga Pskov International Airport", country: "RU", size: "medium" },
  { code: "PKX", name: "Beijing Daxing International Airport", country: "CN", size: "large" },
  { code: "PKY", name: "Palangkaraya \u2013 Tjilik Riwut Airport", country: "ID", size: "medium" },
  { code: "PKZ", name: "Pakse International Airport", country: "LA", size: "large" },
  { code: "PLJ", name: "Placencia Airport", country: "BZ", size: "medium" },
  { code: "PLM", name: "Palembang \u2013 Sultan Mahmud Badaruddin II Airport", country: "ID", size: "medium" },
  { code: "PLN", name: "Pellston Regional Airport of Emmet County Airport", country: "US", size: "medium" },
  { code: "PLO", name: "Port Lincoln Airport", country: "AU", size: "medium" },
  { code: "PLQ", name: "Palanga International Airport", country: "LT", size: "large" },
  { code: "PLS", name: "Providenciales International Airport", country: "TC", size: "large" },
  { code: "PLW", name: "Palu \u2013 Mutiara - SIS Al-Jufrie Airport", country: "ID", size: "medium" },
  { code: "PLX", name: "Semey \u2013 Semei International Airport", country: "KZ", size: "large" },
  {
    code: "PLZ",
    name: "Gqeberha (Port Elizabeth) \u2013 Chief Dawid Stuurman International Airport",
    country: "ZA",
    size: "large"
  },
  { code: "PMC", name: "Puerto Montt \u2013 El Tepual International Airport", country: "CL", size: "large" },
  { code: "PMF", name: "Parma (PR) \u2013 Parma Airport", country: "IT", size: "medium" },
  { code: "PMG", name: "Ponta Por\xE3 Airport", country: "BR", size: "medium" },
  { code: "PMI", name: "Palma de Mallorca Airport", country: "ES", size: "large" },
  { code: "PMK", name: "Palm Island Airport", country: "AU", size: "small" },
  { code: "PMO", name: "Palermo \u2013 Falcone\u2013Borsellino Airport", country: "IT", size: "large" },
  { code: "PMQ", name: "Perito Moreno Jalil Hamer Airport", country: "AR", size: "medium" },
  { code: "PMR", name: "Palmerston North Airport", country: "NZ", size: "medium" },
  {
    code: "PMV",
    name: "Isla Margarita \u2013 Del Caribe Santiago Mari\xF1o International Airport",
    country: "VE",
    size: "large"
  },
  { code: "PMW", name: "Palmas \u2013 Brigadeiro Lysias Rodrigues Airport", country: "BR", size: "medium" },
  { code: "PMY", name: "Puerto Madryn \u2013 El Tehuelche Airport", country: "AR", size: "medium" },
  { code: "PNA", name: "Pamplona Airport", country: "ES", size: "medium" },
  { code: "PND", name: "Punta Gorda Airport", country: "BZ", size: "small" },
  { code: "PNI", name: "Pohnpei Island \u2013 Pohnpei International Airport", country: "FM", size: "medium" },
  { code: "PNK", name: "Pontianak \u2013 Supadio International Airport", country: "ID", size: "large" },
  { code: "PNL", name: "Pantelleria (TP) \u2013 Pantelleria Airport", country: "IT", size: "medium" },
  { code: "PNP", name: "Popondetta \u2013 Girua Airport", country: "PG", size: "medium" },
  { code: "PNQ", name: "Pune International Airport", country: "IN", size: "large" },
  { code: "PNR", name: "Pointe Noire \u2013 Antonio Agostinho-Neto International Airport", country: "CG", size: "large" },
  { code: "PNS", name: "Pensacola International Airport", country: "US", size: "large" },
  { code: "PNT", name: "Puerto Natales \u2013 Lieutenant Julio Gallardo Airport", country: "CL", size: "medium" },
  { code: "PNY", name: "Puducherry (Pondicherry) \u2013 Pondicherry Airport", country: "IN", size: "medium" },
  { code: "PNZ", name: "Petrolina \u2013 Senador Nilo Coelho Airport", country: "BR", size: "medium" },
  { code: "POA", name: "Porto Alegre-Salgado Filho International Airport", country: "BR", size: "large" },
  { code: "POG", name: "Port Gentil International Airport", country: "GA", size: "large" },
  { code: "POL", name: "Pemba Airport", country: "MZ", size: "medium" },
  { code: "POM", name: "Port Moresby Jacksons International Airport", country: "PG", size: "large" },
  { code: "POP", name: "Puerto Plata \u2013 Gregorio Luperon International Airport", country: "DO", size: "medium" },
  { code: "POR", name: "Pori Airport", country: "FI", size: "medium" },
  { code: "POS", name: "Port of Spain \u2013 Piarco International Airport", country: "TT", size: "large" },
  { code: "POZ", name: "Pozna\u0144 \u2013 \u0141awica", country: "PL", size: "large" },
  { code: "PPB", name: "Presidente Prudente Airport", country: "BR", size: "medium" },
  { code: "PPE", name: "Puerto Pe\xF1asco \u2013 Mar de Cort\xE9s International Airport", country: "MX", size: "small" },
  { code: "PPG", name: "Pago Pago International Airport", country: "AS", size: "large" },
  { code: "PPK", name: "Petropavl International Airport", country: "KZ", size: "large" },
  { code: "PPN", name: "Popay\xE1n \u2013 Guillermo Le\xF3n Valencia Airport", country: "CO", size: "medium" },
  { code: "PPP", name: "Proserpine Whitsunday Coast Airport", country: "AU", size: "medium" },
  {
    code: "PPS",
    name: "Puerto Princesa International Airport / PAF Antonio Bautista Air Base",
    country: "PH",
    size: "large"
  },
  { code: "PPT", name: "Papeete \u2013 Fa'a'\u0101 International Airport", country: "PF", size: "large" },
  { code: "PPW", name: "Papa Westray, Orkney Islands \u2013 Papa Westray Airport", country: "GB", size: "small" },
  { code: "PQC", name: "Phu Quoc Island \u2013 Ph\xFA Qu\u1ED1c International Airport", country: "VN", size: "large" },
  { code: "PQI", name: "Presque Isle International Airport", country: "US", size: "medium" },
  { code: "PQQ", name: "Port Macquarie Airport", country: "AU", size: "medium" },
  { code: "PQS", name: "Pilot Station Airport", country: "US", size: "small" },
  { code: "PRA", name: "Parana \u2013 General Urquiza Airport", country: "AR", size: "medium" },
  { code: "PRC", name: "Prescott Regional Airport - Ernest A. Love Field", country: "US", size: "medium" },
  { code: "PRG", name: "Prague \u2013 V\xE1clav Havel Airport Prague", country: "CZ", size: "large" },
  { code: "PRI", name: "Praslin Island Airport", country: "SC", size: "medium" },
  { code: "PRM", name: "Portim\xE3o Airport", country: "PT", size: "medium" },
  { code: "PRN", name: "Prishtina \u2013 Pri\u0161tina Adem Jashari International Airport", country: "XK", size: "large" },
  { code: "PSA", name: "Pisa (PI) \u2013 Pisa International Airport", country: "IT", size: "large" },
  { code: "PSC", name: "Pasco \u2013 Tri Cities Airport", country: "US", size: "medium" },
  { code: "PSD", name: "Port Said International Airport", country: "EG", size: "large" },
  { code: "PSE", name: "Ponce \u2013 Mercedita International Airport", country: "PR", size: "medium" },
  { code: "PSG", name: "Petersburg James A Johnson Airport", country: "US", size: "medium" },
  { code: "PSM", name: "Portsmouth International Airport at Pease", country: "US", size: "medium" },
  { code: "PSO", name: "Chachag\xFC\xED \u2013 Antonio Nari\xF1o Airport", country: "CO", size: "medium" },
  { code: "PSP", name: "Palm Springs International Airport", country: "US", size: "large" },
  { code: "PSR", name: "Pescara \u2013 Abruzzo Airport", country: "IT", size: "large" },
  { code: "PSS", name: "Posadas \u2013 Libertador Gral D Jose De San Martin Airport", country: "AR", size: "medium" },
  { code: "PSU", name: "Putussibau-Borneo Island \u2013 Pangsuma Airport", country: "ID", size: "medium" },
  { code: "PSY", name: "Stanley \u2013 Port Stanley Airport", country: "FK", size: "small" },
  { code: "PSZ", name: "Puerto Su\xE1rez \u2013 Capit\xE1n Av. Salvador Ogaya G. airport", country: "BO", size: "medium" },
  { code: "PTA", name: "Port Alsworth Airport", country: "US", size: "small" },
  { code: "PTF", name: "Malolo Lailai Island Airport", country: "FJ", size: "small" },
  { code: "PTG", name: "Polokwane International Airport", country: "ZA", size: "large" },
  { code: "PTH", name: "Port Heiden Airport", country: "US", size: "medium" },
  { code: "PTJ", name: "Portland Airport", country: "AU", size: "medium" },
  { code: "PTO", name: "Pato Branco \u2013 Juvenal Loureiro Cardoso Airport", country: "BR", size: "small" },
  { code: "PTP", name: "Pointe-\xE0-Pitre \u2013 Maryse Cond\xE9 International Airport", country: "GP", size: "large" },
  { code: "PTU", name: "Platinum Airport", country: "US", size: "medium" },
  { code: "PTY", name: "Tocumen International Airport", country: "PA", size: "large" },
  { code: "PUB", name: "Pueblo Memorial Airport", country: "US", size: "medium" },
  { code: "PUD", name: "Puerto Deseado Airport", country: "AR", size: "medium" },
  { code: "PUF", name: "Pau/Pyr\xE9n\xE9es (Uzein) \u2013 Pau Pyr\xE9n\xE9es Airport", country: "FR", size: "medium" },
  { code: "PUG", name: "Port Augusta Airport", country: "AU", size: "medium" },
  { code: "PUJ", name: "Punta Cana International Airport", country: "DO", size: "large" },
  { code: "PUQ", name: "Punta Arenas \u2013 President Carlos Ib\xE1\xF1ez International Airport", country: "CL", size: "large" },
  { code: "PUR", name: "Puerto Rico/Manuripi \u2013 Puerto Rico Airport", country: "BO", size: "small" },
  { code: "PUS", name: "Busan \u2013 Gimhae International Airport", country: "KR", size: "large" },
  { code: "PUU", name: "Puerto As\xEDs \u2013 Tres De Mayo Airport", country: "CO", size: "medium" },
  { code: "PUW", name: "Pullman-Moscow Regional Airport", country: "US", size: "medium" },
  { code: "PUY", name: "Pula Airport", country: "HR", size: "large" },
  { code: "PUZ", name: "Puerto Cabezas Airport", country: "NI", size: "medium" },
  { code: "PVA", name: "Providencia \u2013 El Embrujo Airport", country: "CO", size: "medium" },
  {
    code: "PVD",
    name: "Providence/Warwick \u2013 Rhode Island T. F. Green International Airport",
    country: "US",
    size: "large"
  },
  { code: "PVG", name: "Shanghai (Pudong) \u2013 Shanghai Pudong International Airport", country: "CN", size: "large" },
  {
    code: "PVH",
    name: "Porto Velho \u2013 Governador Jorge Teixeira de Oliveira International Airport",
    country: "BR",
    size: "large"
  },
  { code: "PVK", name: "Preveza \u2013 Aktion National Airport", country: "GR", size: "medium" },
  { code: "PVR", name: "Puerto Vallarta International Airport", country: "MX", size: "large" },
  { code: "PVU", name: "Provo Municipal Airport", country: "US", size: "medium" },
  { code: "PWE", name: "Apapelgino \u2013 Pevek Airport", country: "RU", size: "medium" },
  { code: "PWM", name: "Portland International Jetport", country: "US", size: "large" },
  { code: "PWQ", name: "Pavlodar International Airport", country: "KZ", size: "large" },
  { code: "PXH", name: "Mount Eba \u2013 Prominent Hill Airport", country: "AU", size: "small" },
  { code: "PXM", name: "Puerto Escondido International Airport", country: "MX", size: "medium" },
  { code: "PXO", name: "Vila Baleira \u2013 Porto Santo Airport", country: "PT", size: "medium" },
  { code: "PXR", name: "Surin Airport", country: "TH", size: "medium" },
  { code: "PXU", name: "Pleiku Airport", country: "VN", size: "medium" },
  { code: "PYJ", name: "Yakutia \u2013 Polyarny Airport", country: "RU", size: "medium" },
  { code: "PYK", name: "Karaj \u2013 Payam International Airport", country: "IR", size: "large" },
  { code: "PYT", name: "Paracatu \u2013 Pedro Rabelo de Souza Airport", country: "BR", size: "small" },
  { code: "PZB", name: "Pietermaritzburg Airport", country: "ZA", size: "medium" },
  { code: "PZH", name: "Fort Sandeman \u2013 Zhob Airport", country: "PK", size: "medium" },
  { code: "PZI", name: "Panzhihua (Renhe) \u2013 Panzhihua Bao'anying Airport", country: "CN", size: "medium" },
  { code: "PZO", name: "Guyana City \u2013 General Manuel Carlos Piar International Airport", country: "VE", size: "large" },
  { code: "PZU", name: "Port Sudan New International Airport", country: "SD", size: "large" },
  { code: "QBC", name: "Bella Coola Airport", country: "CA", size: "medium" },
  { code: "QOW", name: "Owerri \u2013 Sam Mbakwe International Cargo Airport", country: "NG", size: "medium" },
  { code: "QRO", name: "Quer\xE9taro Intercontinental Airport", country: "MX", size: "large" },
  { code: "QRW", name: "Okpe \u2013 Warri Airport", country: "NG", size: "medium" },
  { code: "QSF", name: "S\xE9tif \u2013 Ain Arnat Airport", country: "DZ", size: "medium" },
  { code: "QSR", name: "Salerno Costa d'Amalfi Airport", country: "IT", size: "medium" },
  { code: "QSZ", name: "Shache Airport", country: "CN", size: "medium" },
  { code: "QUO", name: "Uyo \u2013 Akwa Ibom International Airport", country: "NG", size: "medium" },
  { code: "RAB", name: "Kokopo \u2013 Tokua Airport", country: "PG", size: "medium" },
  { code: "RAE", name: "Arar Domestic Airport", country: "SA", size: "medium" },
  { code: "RAH", name: "Rafha Domestic Airport", country: "SA", size: "medium" },
  { code: "RAI", name: "Praia \u2013 Nelson Mandela International Airport", country: "CV", size: "large" },
  { code: "RAK", name: "Marrakesh Menara Airport", country: "MA", size: "large" },
  { code: "RAM", name: "Ramingining Airport", country: "AU", size: "small" },
  { code: "RAO", name: "Ribeir\xE3o Preto \u2013 Leite Lopes Airport", country: "BR", size: "medium" },
  { code: "RAP", name: "Rapid City Regional Airport", country: "US", size: "medium" },
  { code: "RAR", name: "Avarua \u2013 Rarotonga International Airport", country: "CK", size: "large" },
  { code: "RAS", name: "Rasht \u2013 Sardar-e-Jangal Airport", country: "IR", size: "medium" },
  { code: "RBA", name: "Rabat-Sal\xE9 Airport", country: "MA", size: "large" },
  { code: "RBB", name: "Borba Airport", country: "BR", size: "small" },
  { code: "RBQ", name: "Rurrenabaque Airport", country: "BO", size: "small" },
  { code: "RBR", name: "Rio Branco-Pl\xE1cido de Castro International Airport", country: "BR", size: "large" },
  { code: "RBV", name: "Ramata Airport", country: "SB", size: "small" },
  { code: "RBY", name: "Ruby Airport", country: "US", size: "medium" },
  { code: "RCB", name: "Richards Bay Airport", country: "ZA", size: "medium" },
  { code: "RCE", name: "Roche Harbor Airport", country: "US", size: "small" },
  { code: "RCH", name: "Riohacha \u2013 Almirante Padilla Airport", country: "CO", size: "medium" },
  { code: "RCM", name: "Richmond Airport", country: "AU", size: "small" },
  { code: "RDD", name: "Redding Municipal Airport", country: "US", size: "medium" },
  { code: "RDM", name: "Redmond \u2013 Roberts Field", country: "US", size: "medium" },
  { code: "RDO", name: "Warszawa \u2013 Radom", country: "PL", size: "medium" },
  { code: "RDP", name: "Durgapur \u2013 Kazi Nazrul Islam Airport", country: "IN", size: "medium" },
  { code: "RDU", name: "Raleigh/Durham \u2013 Raleigh-Durham International Airport", country: "US", size: "large" },
  { code: "RDV", name: "Red Devil Airport", country: "US", size: "small" },
  { code: "RDZ", name: "Rodez/Marcillac \u2013 Rodez\u2013Aveyron Airport", country: "FR", size: "medium" },
  { code: "REC", name: "Recife/Guararapes - Gilberto Freyre International Airport", country: "BR", size: "large" },
  { code: "REG", name: "Reggio Calabria Airport", country: "IT", size: "medium" },
  { code: "REL", name: "Rawson \u2013 Almirante Marco Andres Zar Airport", country: "AR", size: "medium" },
  { code: "REN", name: "Orenburg Central Airport", country: "RU", size: "medium" },
  { code: "RER", name: "Retalhuleu Airport", country: "GT", size: "medium" },
  { code: "RES", name: "Resistencia International Airport", country: "AR", size: "large" },
  { code: "RET", name: "R\xF8st Airport", country: "NO", size: "small" },
  { code: "REU", name: "Reus Airport", country: "ES", size: "large" },
  { code: "REW", name: "Rewa Airport, Chorhata, REWA", country: "IN", size: "medium" },
  { code: "REX", name: "Reynosa \u2013 General Lucio Blanco International Airport", country: "MX", size: "medium" },
  { code: "RFD", name: "Chicago/Rockford \u2013 Chicago Rockford International Airport", country: "US", size: "medium" },
  { code: "RFP", name: "Uturoa \u2013 Raiatea Airport", country: "PF", size: "medium" },
  {
    code: "RGA",
    name: "Rio Grande \u2013 Gobernador Ram\xF3n Trejo Noel International Airport",
    country: "AR",
    size: "medium"
  },
  { code: "RGI", name: "Rangiroa Airport", country: "PF", size: "medium" },
  {
    code: "RGL",
    name: "Rio Gallegos \u2013 Piloto Civil Norberto Fern\xE1ndez International Airport",
    country: "AR",
    size: "large"
  },
  { code: "RGN", name: "Yangon International Airport", country: "MM", size: "large" },
  { code: "RGO", name: "Hoemun-ri \u2013 Orang (Chongjin) Airport", country: "KP", size: "medium" },
  { code: "RHD", name: "Termas de R\xEDo Hondo international Airport", country: "AR", size: "medium" },
  { code: "RHI", name: "Rhinelander Oneida County Airport", country: "US", size: "medium" },
  { code: "RHO", name: 'Rhodes International Airport "Diagoras"', country: "GR", size: "large" },
  { code: "RHT", name: "Badanjilin \u2013 Alxa Right Banner Badanjilin Airport", country: "CN", size: "small" },
  { code: "RIA", name: "Santa Maria Airport", country: "BR", size: "medium" },
  { code: "RIB", name: "Riberalta \u2013 Capit\xE1n Av. Selin Zeitun Lopez Airport", country: "BO", size: "medium" },
  { code: "RIC", name: "Richmond International Airport", country: "US", size: "large" },
  { code: "RIH", name: "R\xEDo Hato \u2013 Scarlett Martinez International Airport", country: "PA", size: "small" },
  { code: "RIS", name: "Rishiri Airport", country: "JP", size: "medium" },
  { code: "RIW", name: "Riverton \u2013 Central Wyoming Regional Airport", country: "US", size: "medium" },
  { code: "RIX", name: "Riga International Airport", country: "LV", size: "large" },
  { code: "RIY", name: "Mukalla(Riyan) \u2013 Riyan International Airport", country: "YE", size: "large" },
  { code: "RIZ", name: "Rizhao (Donggang) \u2013 Rizhao Shanzihe Airport", country: "CN", size: "medium" },
  { code: "RJA", name: "Madhurapudi \u2013 Rajahmundry Airport", country: "IN", size: "medium" },
  { code: "RJH", name: "Rajshahi \u2013 Shah Makhdum Airport", country: "BD", size: "medium" },
  { code: "RJK", name: "Rijeka(Omi\u0161alj) \u2013 Rijeka Airport", country: "HR", size: "large" },
  { code: "RJL", name: "Logro\xF1o-Agoncillo Airport", country: "ES", size: "medium" },
  { code: "RJM", name: "Waisai \u2013 Marinda Airport", country: "ID", size: "small" },
  { code: "RJN", name: "Rafsanjan Airport", country: "IR", size: "medium" },
  { code: "RKD", name: "Rockland \u2013 Knox County Regional Airport", country: "US", size: "medium" },
  { code: "RKE", name: "Roskilde \u2013 Copenhagen Roskilde Airport", country: "DK", size: "medium" },
  { code: "RKI", name: "Sipura Island \u2013 Mentawai Airport", country: "ID", size: "small" },
  { code: "RKS", name: "Rock Springs \u2013 Southwest Wyoming Regional Airport", country: "US", size: "medium" },
  { code: "RKT", name: "Ras Al Khaimah International Airport", country: "AE", size: "large" },
  { code: "RKV", name: "Reykjav\xEDk Domestic Airport", country: "IS", size: "medium" },
  { code: "RKZ", name: "Xigaz\xEA (Samzhubz\xEA) \u2013 Xigaze Peace Airport / Shigatse Air Base", country: "CN", size: "large" },
  { code: "RLG", name: "Laage \u2013 Rostock-Laage Airport", country: "DE", size: "medium" },
  { code: "RLK", name: "Bayannur Tianjitai Airport", country: "CN", size: "medium" },
  { code: "RMA", name: "Roma Airport", country: "AU", size: "medium" },
  { code: "RMF", name: "Marsa Alam International Airport", country: "EG", size: "large" },
  { code: "RMI", name: "Rimini (RN) \u2013 Federico Fellini International Airport", country: "IT", size: "large" },
  { code: "RML", name: "Colombo Ratmalana International Airport", country: "LK", size: "large" },
  { code: "RMO", name: "Chi\u015Fin\u0103u International Airport", country: "MD", size: "large" },
  { code: "RMP", name: "Rampart Airport", country: "US", size: "small" },
  {
    code: "RMQ",
    name: "Taichung (Qingshui) \u2013 Taichung International Airport / Ching Chuang Kang Air Base",
    country: "TW",
    size: "large"
  },
  { code: "RMT", name: "Rimatara Island \u2013 Rimatara Airport", country: "PF", size: "small" },
  { code: "RMU", name: "Corvera \u2013 Region of Murcia International Airport", country: "ES", size: "large" },
  { code: "RMZ", name: "Tobolsk Remezov Airport", country: "RU", size: "medium" },
  { code: "RNB", name: "Ronneby Airport", country: "SE", size: "medium" },
  { code: "RNI", name: "Corn Island Airport", country: "NI", size: "small" },
  { code: "RNJ", name: "Yoron Airport", country: "JP", size: "medium" },
  { code: "RNL", name: "Rennell Island \u2013 Rennell/Tingoa Airport", country: "SB", size: "small" },
  { code: "RNN", name: "R\xF8nne \u2013 Bornholm Airport", country: "DK", size: "medium" },
  { code: "RNO", name: "Reno Tahoe International Airport", country: "US", size: "large" },
  { code: "RNP", name: "Rongelap Island Airport", country: "MH", size: "small" },
  {
    code: "RNS",
    name: "Saint-Jacques-de-la-Lande, Ille-et-Vilaine \u2013 Rennes-Saint-Jacques Airport",
    country: "FR",
    size: "medium"
  },
  { code: "ROA", name: "Roanoke\u2013Blacksburg Regional Airport", country: "US", size: "medium" },
  { code: "ROB", name: "Monrovia \u2013 Roberts International Airport", country: "LR", size: "large" },
  {
    code: "ROC",
    name: "Rochester \u2013 Frederick Douglass Greater Rochester International Airport",
    country: "US",
    size: "large"
  },
  { code: "ROI", name: "Roi Et Airport", country: "TH", size: "medium" },
  { code: "ROK", name: "Rockhampton Airport", country: "AU", size: "medium" },
  { code: "ROO", name: "Rondon\xF3polis \u2013 Maestro Marinho Franco Airport", country: "BR", size: "medium" },
  { code: "ROP", name: "Rota Island \u2013 Rota International Airport", country: "MP", size: "large" },
  { code: "ROR", name: "Babelthuap Island \u2013 Roman Tmetuchl International Airport", country: "PW", size: "large" },
  { code: "ROS", name: "Rosario Islas Malvinas International Airport", country: "AR", size: "large" },
  { code: "ROT", name: "Rotorua Regional Airport", country: "NZ", size: "medium" },
  { code: "ROW", name: "Roswell Air Center Airport", country: "US", size: "medium" },
  { code: "RPR", name: "Raipur \u2013 Swami Vivekananda Airport", country: "IN", size: "medium" },
  { code: "RQA", name: "Ruoqiang Town \u2013 Ruoqiang Loulan Airport", country: "CN", size: "medium" },
  { code: "RRG", name: "Port Mathurin \u2013 Sir Charles Gaetan Duval Airport", country: "MU", size: "medium" },
  { code: "RRR", name: "Raroia Airport", country: "PF", size: "small" },
  { code: "RRS", name: "R\xF8ros Airport", country: "NO", size: "medium" },
  { code: "RSA", name: "Santa Rosa Airport", country: "AR", size: "medium" },
  { code: "RSD", name: "Rock Sound International Airport", country: "BS", size: "medium" },
  { code: "RSH", name: "Russian Mission Airport", country: "US", size: "small" },
  { code: "RSI", name: "Hanak \u2013 Red Sea International Airport", country: "SA", size: "large" },
  { code: "RST", name: "Rochester International Airport", country: "US", size: "medium" },
  { code: "RSU", name: "Yeosu Airport", country: "KR", size: "medium" },
  { code: "RSW", name: "Fort Myers \u2013 Southwest Florida International Airport", country: "US", size: "large" },
  { code: "RTA", name: "Rotuma Airport", country: "FJ", size: "small" },
  { code: "RTB", name: "Coxen Hole \u2013 Juan Manuel G\xE1lvez International Airport", country: "HN", size: "large" },
  { code: "RTG", name: "Satar Tacik, Manggarai \u2013 Frans Sales Lega Airport", country: "ID", size: "small" },
  { code: "RTI", name: "Ba'a - Rote Island \u2013 David Constantijn Saudale Airport", country: "ID", size: "small" },
  { code: "RTM", name: "Rotterdam The Hague Airport", country: "NL", size: "large" },
  { code: "RUA", name: "Arua Airport", country: "UG", size: "medium" },
  { code: "RUH", name: "Riyadh \u2013 King Khalid International Airport", country: "SA", size: "large" },
  { code: "RUL", name: "Maavaarulu \u2013 Maavaarulaa Airport", country: "MV", size: "small" },
  { code: "RUN", name: "Sainte-Marie \u2013 Roland Garros Airport", country: "RE", size: "large" },
  { code: "RUR", name: "Rurutu Airport", country: "PF", size: "medium" },
  { code: "RUS", name: "Marau Airport", country: "SB", size: "small" },
  { code: "RUT", name: "Rutland - Southern Vermont Regional Airport", country: "US", size: "medium" },
  { code: "RVE", name: "Saravena \u2013 Los Colonizadores Airport", country: "CO", size: "small" },
  { code: "RVK", name: "R\xF8rvik Airport, Ryum", country: "NO", size: "medium" },
  { code: "RVN", name: "Rovaniemi Airport", country: "FI", size: "large" },
  { code: "RVV", name: "Raivavae Airport", country: "PF", size: "small" },
  {
    code: "RVY",
    name: "Rivera/Santana do Livramento \u2013 Pres. Gral. \xD3scar D. Gestido Binational Airport",
    country: "UY",
    size: "medium"
  },
  { code: "RXS", name: "Roxas City \u2013 Roxas Airport", country: "PH", size: "medium" },
  { code: "RYK", name: "Rahim Yar Khan \u2013 Shaikh Zaid Airport", country: "PK", size: "medium" },
  { code: "RYO", name: "Rio Turbio \u2013 28 de Noviembre Airport", country: "AR", size: "small" },
  { code: "RZE", name: "Rzesz\xF3w \u2013 Jasionka", country: "PL", size: "large" },
  { code: "RZR", name: "Ramsar Airport", country: "IR", size: "medium" },
  { code: "RZV", name: "Rize\u2013Artvin Airport", country: "TR", size: "medium" },
  { code: "SAB", name: "Zion's Hill \u2013 Juancho E. Yrausquin Airport", country: "BQ", size: "medium" },
  { code: "SAF", name: "Santa Fe Municipal Airport", country: "US", size: "medium" },
  { code: "SAG", name: "Kakadi \u2013 Shirdi International Airport", country: "IN", size: "large" },
  { code: "SAH", name: "Sanaa International Airport", country: "YE", size: "large" },
  { code: "SAI", name: "Siem Reap-Angkor International Airport", country: "KH", size: "large" },
  {
    code: "SAL",
    name: "San Salvador (San Luis Talpa) \u2013 El Salvador International Airport Saint \xD3scar Arnulfo Romero y Gald\xE1mez",
    country: "SV",
    size: "large"
  },
  { code: "SAN", name: "San Diego International Airport", country: "US", size: "large" },
  { code: "SAP", name: "San Pedro Sula \u2013 Ram\xF3n Villeda Morales International Airport", country: "HN", size: "large" },
  { code: "SAQ", name: "Andros Island \u2013 San Andros Airport", country: "BS", size: "medium" },
  { code: "SAT", name: "San Antonio International Airport", country: "US", size: "large" },
  { code: "SAV", name: "Savannah Hilton Head International Airport", country: "US", size: "large" },
  {
    code: "SAW",
    name: "Pendik, Istanbul \u2013 Istanbul Sabiha G\xF6k\xE7en International Airport",
    country: "TR",
    size: "large"
  },
  { code: "SBA", name: "Santa Barbara Municipal Airport", country: "US", size: "medium" },
  { code: "SBD", name: "San Bernardino International Airport", country: "US", size: "large" },
  { code: "SBH", name: "Gustavia \u2013 St. Jean Airport", country: "BL", size: "medium" },
  { code: "SBN", name: "South Bend International Airport", country: "US", size: "medium" },
  { code: "SBP", name: "San Luis Obispo \u2013 San Luis County Regional Airport", country: "US", size: "medium" },
  { code: "SBR", name: "Saibai Island Airport", country: "AU", size: "small" },
  { code: "SBT", name: "Sabetta International Airport", country: "RU", size: "medium" },
  { code: "SBW", name: "Sibu Airport", country: "MY", size: "medium" },
  { code: "SBY", name: "Salisbury Ocean City Wicomico Regional Airport", country: "US", size: "medium" },
  { code: "SBZ", name: "Sibiu International Airport", country: "RO", size: "large" },
  { code: "SCC", name: "Deadhorse Airport", country: "US", size: "medium" },
  { code: "SCE", name: "State College Regional Airport", country: "US", size: "medium" },
  { code: "SCK", name: "Stockton Metropolitan Airport", country: "US", size: "medium" },
  {
    code: "SCL",
    name: "Santiago \u2013 Comodoro Arturo Merino Ben\xEDtez International Airport",
    country: "CL",
    size: "large"
  },
  { code: "SCM", name: "Scammon Bay Airport", country: "US", size: "small" },
  { code: "SCN", name: "Saarbr\xFCcken Airport", country: "DE", size: "medium" },
  { code: "SCO", name: "Aktau International Airport", country: "KZ", size: "large" },
  { code: "SCQ", name: "Santiago de Compostela \u2013 Santiago-Rosal\xEDa de Castro Airport", country: "ES", size: "large" },
  { code: "SCR", name: "Malung-S\xE4len \u2013 Scandinavian Mountains Airport", country: "SE", size: "large" },
  { code: "SCT", name: "Mori \u2013 Socotra Airport", country: "YE", size: "medium" },
  { code: "SCU", name: "Santiago \u2013 Antonio Maceo International Airport", country: "CU", size: "large" },
  { code: "SCV", name: "Suceava \u0218tefan cel Mare International Airport", country: "RO", size: "large" },
  { code: "SCW", name: "Syktyvkar Airport", country: "RU", size: "medium" },
  { code: "SCY", name: "Puerto Baquerizo Moreno \u2013 San Crist\xF3bal Airport", country: "EC", size: "small" },
  { code: "SCZ", name: "Santa Cruz/Graciosa Bay/Luova Airport", country: "SB", size: "small" },
  { code: "SDD", name: "Lubango Mukanka International Airport", country: "AO", size: "medium" },
  {
    code: "SDE",
    name: "Santiago del Estero \u2013 Vicecomodoro Angel D. La Paz Aragon\xE9s Airport",
    country: "AR",
    size: "medium"
  },
  { code: "SDF", name: "Louisville Muhammad Ali International Airport", country: "US", size: "large" },
  { code: "SDG", name: "Sanandaj Airport", country: "IR", size: "medium" },
  { code: "SDJ", name: "Natori \u2013 Sendai Airport", country: "JP", size: "large" },
  { code: "SDK", name: "Sandakan Airport", country: "MY", size: "medium" },
  { code: "SDL", name: "Sundsvall/ H\xE4rn\xF6sand \u2013 Sundsvall-H\xE4rn\xF6sand Airport", country: "SE", size: "medium" },
  { code: "SDN", name: "Sandane Airport, Anda", country: "NO", size: "small" },
  { code: "SDP", name: "Sand Point Airport", country: "US", size: "medium" },
  { code: "SDQ", name: "Santo Domingo \u2013 Las Am\xE9ricas International Airport", country: "DO", size: "large" },
  { code: "SDR", name: "Santander \u2013 Seve Ballesteros-Santander Airport", country: "ES", size: "medium" },
  { code: "SDS", name: "Sado Airport", country: "JP", size: "medium" },
  { code: "SDU", name: "Rio de Janeiro \u2013 Santos Dumont Airport", country: "BR", size: "large" },
  { code: "SDW", name: "Chipi \u2013 Sindhudurg Airport", country: "IN", size: "medium" },
  { code: "SDY", name: "Sidney - Richland Regional Airport", country: "US", size: "medium" },
  { code: "SEA", name: "Seattle\u2013Tacoma International Airport", country: "US", size: "large" },
  { code: "SEB", name: "Sabha Airport", country: "LY", size: "medium" },
  { code: "SEK", name: "Srednekolymsk Airport", country: "RU", size: "medium" },
  { code: "SEN", name: "Southend-on-Sea, Essex \u2013 London Southend Airport", country: "GB", size: "medium" },
  { code: "SET", name: "Serra Talhada \u2013 Santa Magalh\xE3es Airport", country: "BR", size: "small" },
  { code: "SEU", name: "Seronera Airport", country: "TZ", size: "small" },
  { code: "SEZ", name: "Victoria \u2013 Seychelles International Airport", country: "SC", size: "large" },
  { code: "SFA", name: "Sfax Thyna International Airport", country: "TN", size: "medium" },
  { code: "SFB", name: "Orlando Sanford International Airport", country: "US", size: "large" },
  { code: "SFC", name: "St-Fran\xE7ois Airport", country: "GP", size: "small" },
  { code: "SFG", name: "Grand Case-l'Esp\xE9rance Airport", country: "MF", size: "medium" },
  { code: "SFJ", name: "Kangerlussuaq International Airport", country: "GL", size: "medium" },
  { code: "SFL", name: "S\xE3o Filipe Airport", country: "CV", size: "small" },
  { code: "SFN", name: "Santa Fe \u2013 Sauce Viejo Airport", country: "AR", size: "medium" },
  { code: "SFO", name: "San Francisco International Airport", country: "US", size: "large" },
  {
    code: "SFS",
    name: "Olongapo \u2013 Subic Bay International Airport / Naval Air Station Cubi Point",
    country: "PH",
    size: "large"
  },
  { code: "SFT", name: "Skellefte\xE5 Airport", country: "SE", size: "medium" },
  { code: "SGC", name: "Surgut International Airport", country: "RU", size: "large" },
  { code: "SGD", name: "S\xF8nderborg Airport", country: "DK", size: "medium" },
  { code: "SGF", name: "Springfield Branson National Airport", country: "US", size: "medium" },
  { code: "SGN", name: "Ho Chi Minh City \u2013 Tan Son Nhat International Airport", country: "VN", size: "large" },
  { code: "SGO", name: "St George Airport", country: "AU", size: "small" },
  { code: "SGU", name: "St George Regional Airport", country: "US", size: "medium" },
  { code: "SGY", name: "Skagway Airport", country: "US", size: "small" },
  { code: "SHA", name: "Shanghai (Minhang) \u2013 Shanghai Hongqiao International Airport", country: "CN", size: "large" },
  { code: "SHB", name: "Nakashibetsu Airport", country: "JP", size: "medium" },
  { code: "SHC", name: "Shire Inda Selassie Airport", country: "ET", size: "small" },
  { code: "SHD", name: "Weyers Cave \u2013 Shenandoah Valley Regional Airport", country: "US", size: "medium" },
  { code: "SHE", name: "Shenyang Taoxian International Airport", country: "CN", size: "large" },
  { code: "SHF", name: "Shihezi Huayuan Airport", country: "CN", size: "small" },
  { code: "SHG", name: "Shungnak Airport", country: "US", size: "small" },
  { code: "SHH", name: "Shishmaref Airport", country: "US", size: "small" },
  { code: "SHI", name: "Miyakojima \u2013 Shimojishima Airport", country: "JP", size: "medium" },
  { code: "SHJ", name: "Sharjah International Airport", country: "AE", size: "large" },
  { code: "SHL", name: "Shillong Airport", country: "IN", size: "medium" },
  { code: "SHM", name: "Shirahama \u2013 Nanki Shirahama Airport", country: "JP", size: "medium" },
  { code: "SHO", name: "Mpaka \u2013 King Mswati III International Airport", country: "SZ", size: "large" },
  { code: "SHR", name: "Sheridan County Airport", country: "US", size: "medium" },
  { code: "SHS", name: "Jingzhou (Shashi) \u2013 Jingzhou Shashi Airport", country: "CN", size: "medium" },
  { code: "SHV", name: "Shreveport Regional Airport", country: "US", size: "medium" },
  { code: "SHW", name: "Sharurah Domestic Airport", country: "SA", size: "medium" },
  { code: "SHY", name: "Shinyanga Airport", country: "TZ", size: "small" },
  { code: "SID", name: "Espargos \u2013 Am\xEDlcar Cabral International Airport", country: "CV", size: "large" },
  { code: "SIF", name: "Simara Airport", country: "NP", size: "small" },
  { code: "SIG", name: "San Juan \u2013 Fernando Luis Ribas Dominicci Airport", country: "PR", size: "medium" },
  { code: "SIH", name: "Silgadi Doti Airport", country: "NP", size: "small" },
  { code: "SIN", name: "Singapore Changi Airport", country: "SG", size: "large" },
  { code: "SIS", name: "Sishen Airport", country: "ZA", size: "medium" },
  { code: "SIT", name: "Sitka Rocky Gutierrez Airport", country: "US", size: "medium" },
  { code: "SJC", name: "San Jose \u2013 Mineta San Jose International Airport", country: "US", size: "large" },
  { code: "SJD", name: "San Jos\xE9 del Cabo \u2013 Los Cabos International Airport", country: "MX", size: "large" },
  { code: "SJE", name: "San Jos\xE9 Del Guaviare \u2013 Jorge E. Gonzalez Torres Airport", country: "CO", size: "medium" },
  { code: "SJI", name: "San Jose Airport", country: "PH", size: "medium" },
  { code: "SJJ", name: "Sarajevo International Airport", country: "BA", size: "large" },
  { code: "SJK", name: "S\xE3o Jos\xE9 Dos Campos \u2013 Professor Urbano Ernesto Stumpf Airport", country: "BR", size: "medium" },
  { code: "SJL", name: "S\xE3o Gabriel da Cachoeira Airport", country: "BR", size: "medium" },
  { code: "SJO", name: "San Jos\xE9 (Alajuela) \u2013 Juan Santamar\xEDa International Airport", country: "CR", size: "large" },
  {
    code: "SJP",
    name: "S\xE3o Jos\xE9 do Rio Preto \u2013 Prof. Eribelto Manoel Reino State Airport",
    country: "BR",
    size: "medium"
  },
  { code: "SJT", name: "San Angelo Regional Mathis Field", country: "US", size: "medium" },
  { code: "SJU", name: "San Juan \u2013 Luis Munoz Marin International Airport", country: "PR", size: "large" },
  { code: "SJW", name: "Shijiazhuang Zhengding International Airport", country: "CN", size: "large" },
  { code: "SJZ", name: "Velas \u2013 S\xE3o Jorge Airport", country: "PT", size: "medium" },
  { code: "SKB", name: "Basseterre \u2013 Robert L. Bradshaw International Airport", country: "KN", size: "large" },
  { code: "SKD", name: "Samarkand International Airport", country: "UZ", size: "large" },
  { code: "SKG", name: "Thessaloniki Macedonia International Airport", country: "GR", size: "large" },
  { code: "SKH", name: "Surkhet Airport", country: "NP", size: "small" },
  { code: "SKK", name: "Shaktoolik Airport", country: "US", size: "small" },
  { code: "SKN", name: "Hadsel \u2013 Stokmarknes Airport, Skagen", country: "NO", size: "medium" },
  { code: "SKO", name: "Sokoto \u2013 Sadiq Abubakar III International Airport", country: "NG", size: "large" },
  { code: "SKP", name: "Ilinden \u2013 Skopje International Airport", country: "MK", size: "large" },
  { code: "SKT", name: "Sialkot International Airport", country: "PK", size: "large" },
  { code: "SKU", name: "Skiros Island \u2013 Skiros Airport", country: "GR", size: "small" },
  { code: "SKX", name: "Saransk International Airport", country: "RU", size: "large" },
  { code: "SKZ", name: "Sukkur \u2013 Begum Nusrat Bhutto International Airport Sukkur", country: "PK", size: "medium" },
  { code: "SLA", name: "Salta \u2013 Mart\xEDn Miguel de G\xFCemes International Airport", country: "AR", size: "large" },
  { code: "SLC", name: "Salt Lake City International Airport", country: "US", size: "large" },
  { code: "SLD", name: "Slia\u010D Airport", country: "SK", size: "medium" },
  { code: "SLE", name: "Salem-Willamette Valley Airport/McNary Field", country: "US", size: "medium" },
  { code: "SLH", name: "Sola Airport", country: "VU", size: "small" },
  { code: "SLI", name: "Solwesi Airport", country: "ZM", size: "small" },
  { code: "SLK", name: "Saranac Lake \u2013 Adirondack Regional Airport", country: "US", size: "medium" },
  { code: "SLL", name: "Salalah International Airport", country: "OM", size: "large" },
  { code: "SLM", name: "Salamanca Airport", country: "ES", size: "medium" },
  { code: "SLN", name: "Salina Municipal Airport", country: "US", size: "medium" },
  { code: "SLP", name: "San Luis Potos\xED \u2013 Ponciano Arriaga International Airport", country: "MX", size: "medium" },
  { code: "SLQ", name: "Sleetmute Airport", country: "US", size: "small" },
  { code: "SLU", name: "Castries \u2013 George F. L. Charles Airport", country: "LC", size: "medium" },
  { code: "SLW", name: "Saltillo \u2013 Plan de Guadalupe International Airport", country: "MX", size: "medium" },
  { code: "SLX", name: "Salt Cay Airport", country: "TC", size: "small" },
  { code: "SLY", name: "Salekhard Airport", country: "RU", size: "medium" },
  { code: "SLZ", name: "S\xE3o Lu\xEDs \u2013 Marechal Cunha Machado International Airport", country: "BR", size: "large" },
  { code: "SMA", name: "Vila do Porto \u2013 Santa Maria Airport", country: "PT", size: "medium" },
  { code: "SMF", name: "Sacramento International Airport", country: "US", size: "large" },
  { code: "SMI", name: "Samos Island \u2013 Samos Airport", country: "GR", size: "medium" },
  { code: "SMK", name: "St Michael Airport", country: "US", size: "small" },
  { code: "SML", name: "Stella Maris Airport", country: "BS", size: "medium" },
  { code: "SMN", name: "Salmon \u2013 Lemhi County Airport", country: "US", size: "medium" },
  { code: "SMQ", name: "Sampit (H.Asan) Airport", country: "ID", size: "small" },
  { code: "SMR", name: "Santa Marta \u2013 Sim\xF3n Bol\xEDvar International Airport", country: "CO", size: "medium" },
  { code: "SMS", name: "Vohilava \u2013 Sainte Marie Airport", country: "MG", size: "medium" },
  { code: "SMT", name: "Sorriso \u2013 Adolino Bedin Regional Airport", country: "BR", size: "small" },
  { code: "SMW", name: "Smara Airport", country: "EH", size: "medium" },
  { code: "SMX", name: "Santa Maria Public Airport Captain G Allan Hancock Field", country: "US", size: "medium" },
  { code: "SNA", name: "Santa Ana \u2013 John Wayne Orange County International Airport", country: "US", size: "large" },
  { code: "SNB", name: "Milikapiti \u2013 Snake Bay Airport", country: "AU", size: "medium" },
  {
    code: "SNC",
    name: "Salinas/La Libertad \u2013 General Ulpiano Paez International Airport",
    country: "EC",
    size: "large"
  },
  { code: "SNE", name: "Pregui\xE7a Airport", country: "CV", size: "medium" },
  { code: "SNN", name: "Shannon Airport", country: "IE", size: "large" },
  { code: "SNO", name: "Sakon Nakhon Airport", country: "TH", size: "medium" },
  { code: "SNP", name: "St Paul Island Airport", country: "US", size: "medium" },
  { code: "SNU", name: "Santa Clara \u2013 Abel Santamaria International Airport", country: "CU", size: "large" },
  { code: "SNV", name: "Santa Elena de Uair\xE9n Airport", country: "VE", size: "medium" },
  { code: "SNW", name: "Thandwe Airport", country: "MM", size: "medium" },
  { code: "SNX", name: "Semnan Municipal Airport", country: "IR", size: "small" },
  { code: "SOB", name: "S\xE1rmell\xE9k \u2013 H\xE9v\xEDz\u2013Balaton Airport", country: "HU", size: "medium" },
  { code: "SOC", name: "Surakarta \u2013 Adisoemarmo International Airport", country: "ID", size: "large" },
  { code: "SOD", name: "Sorocaba Airport", country: "BR", size: "small" },
  { code: "SOF", name: "Sofia Airport", country: "BG", size: "large" },
  { code: "SOG", name: "Sogndal Airport, Hauk\xE5sen", country: "NO", size: "small" },
  { code: "SOJ", name: "S\xF8rkjosen Airport", country: "NO", size: "medium" },
  { code: "SOM", name: "El Tigre \u2013 San Tom\xE9 Airport", country: "VE", size: "medium" },
  { code: "SON", name: "Luganville \u2013 Santo Pekoa International Airport", country: "VU", size: "medium" },
  { code: "SOQ", name: "Sorong \u2013 Domine Eduard Osok Airport", country: "ID", size: "medium" },
  { code: "SOU", name: "Southampton Airport", country: "GB", size: "medium" },
  { code: "SOV", name: "Seldovia Airport", country: "US", size: "small" },
  { code: "SOW", name: "Show Low Regional Airport", country: "US", size: "medium" },
  { code: "SOY", name: "Stronsay Airport", country: "GB", size: "small" },
  { code: "SPC", name: "Sta Cruz de la Palma, La Palma Island \u2013 La Palma Airport", country: "ES", size: "medium" },
  { code: "SPD", name: "Saidpur Airport", country: "BD", size: "medium" },
  { code: "SPI", name: "Springfield \u2013 Abraham Lincoln Capital Airport", country: "US", size: "medium" },
  { code: "SPN", name: "I Fadang, Saipan \u2013 Saipan International Airport", country: "MP", size: "medium" },
  { code: "SPP", name: "Menongue Airport", country: "AO", size: "medium" },
  { code: "SPR", name: "San Pedro \u2013 John Greif II Airport", country: "BZ", size: "medium" },
  { code: "SPS", name: "Wichita Falls Municipal Airport / Sheppard Air Force Base", country: "US", size: "medium" },
  { code: "SPU", name: "Split Saint Jerome Airport", country: "HR", size: "large" },
  { code: "SPX", name: "Al Jiza \u2013 Sphinx International Airport", country: "EG", size: "large" },
  { code: "SPY", name: "San Pedro Airport", country: "CI", size: "medium" },
  { code: "SQD", name: "Shangrao (Hengfeng) \u2013 Shangrao Sanqingshan Airport", country: "CN", size: "medium" },
  { code: "SQG", name: "Sintang \u2013 Tebelian Airport", country: "ID", size: "medium" },
  { code: "SQJ", name: "Sanming (Sha) \u2013 Sanming Shaxian Airport", country: "CN", size: "medium" },
  { code: "SQL", name: "San Carlos Airport", country: "US", size: "medium" },
  { code: "SRA", name: "Santa Rosa \u2013 Luis Alberto Lehr Airport", country: "BR", size: "small" },
  { code: "SRE", name: "Sucre \u2013 Alcantar\xED International Airport", country: "BO", size: "large" },
  { code: "SRG", name: "Semarang \u2013 Jenderal Ahmad Yani Airport", country: "ID", size: "large" },
  { code: "SRL", name: "Muleg\xE9 \u2013 Palo Verde Airport", country: "MX", size: "small" },
  { code: "SRP", name: "Leirvik \u2013 Stord Airport, S\xF8rstokken", country: "NO", size: "medium" },
  { code: "SRQ", name: "Sarasota/Bradenton \u2013 Sarasota Bradenton International Airport", country: "US", size: "large" },
  { code: "SRT", name: "Soroti Airport", country: "UG", size: "medium" },
  { code: "SRV", name: "Stony River 2 Airport", country: "US", size: "small" },
  { code: "SRY", name: "Sari Dasht-e Naz International Airport", country: "IR", size: "medium" },
  { code: "SRZ", name: "Santa Cruz \u2013 El Trompillo Airport", country: "BO", size: "medium" },
  {
    code: "SSA",
    name: "Salvador \u2013 Deputado Luiz Eduardo Magalh\xE3es International Airport",
    country: "BR",
    size: "large"
  },
  { code: "SSG", name: "Malabo International Airport", country: "GQ", size: "large" },
  { code: "SSH", name: "Sharm El Sheikh International Airport", country: "EG", size: "large" },
  { code: "SSJ", name: "Alstahaug \u2013 Sandnessj\xF8en Airport, Stokka", country: "NO", size: "medium" },
  { code: "SSR", name: "Pentecost Island \u2013 Sara Airport", country: "VU", size: "small" },
  { code: "SST", name: "Santa Teresita Airport", country: "AR", size: "medium" },
  { code: "SSW", name: "Friday Harbor \u2013 Stuart Island Airpark", country: "US", size: "small" },
  { code: "SSY", name: "Mbanza Congo Airport", country: "AO", size: "medium" },
  { code: "STC", name: "Saint Cloud Regional Airport", country: "US", size: "medium" },
  {
    code: "STD",
    name: "Santo Domingo \u2013 Mayor Buenaventura Vivas International Airport",
    country: "VE",
    size: "medium"
  },
  { code: "STG", name: "St George Airport", country: "US", size: "medium" },
  { code: "STI", name: "Santiago \u2013 Cibao International Airport", country: "DO", size: "large" },
  { code: "STL", name: "St Louis \u2013 St. Louis Lambert International Airport", country: "US", size: "large" },
  { code: "STM", name: "Santar\xE9m - Maestro Wilson Fonseca International Airport", country: "BR", size: "medium" },
  { code: "STN", name: "London, Essex \u2013 London Stansted Airport", country: "GB", size: "large" },
  { code: "STR", name: "Stuttgart Airport", country: "DE", size: "large" },
  { code: "STS", name: "Santa Rosa \u2013 Charles M. Schulz Sonoma County Airport", country: "US", size: "medium" },
  { code: "STT", name: "Charlotte Amalie \u2013 Cyril E. King Airport", country: "VI", size: "large" },
  { code: "STV", name: "Surat International Airport", country: "IN", size: "large" },
  { code: "STW", name: "Stavropol Shpakovskoye Airport", country: "RU", size: "medium" },
  { code: "STX", name: "Christiansted \u2013 Henry E. Rohlsen Airport", country: "VI", size: "medium" },
  { code: "SUB", name: "Surabaya \u2013 Juanda International Airport", country: "ID", size: "large" },
  {
    code: "SUF",
    name: "Lamezia Terme (CZ) \u2013 Lamezia Terme Sant'Eufemia International Airport",
    country: "IT",
    size: "large"
  },
  { code: "SUG", name: "Surigao City \u2013 Surigao Airport", country: "PH", size: "medium" },
  { code: "SUI", name: "Sukhumi \u2013 Vladislav Ardzinba Sukhum International Airport", country: "GE", size: "large" },
  { code: "SUJ", name: "Satu Mare International Airport", country: "RO", size: "medium" },
  { code: "SUK", name: "Batagay-Alyta \u2013 Sakkyryr Airport", country: "RU", size: "small" },
  { code: "SUN", name: "Hailey \u2013 Friedman Memorial Airport", country: "US", size: "medium" },
  { code: "SUR", name: "Summer Beaver Airport", country: "CA", size: "small" },
  { code: "SUV", name: "Nausori International Airport", country: "FJ", size: "large" },
  {
    code: "SUX",
    name: "Sioux City \u2013 Sioux Gateway Airport / Brigadier General Bud Day Field",
    country: "US",
    size: "medium"
  },
  { code: "SUY", name: "Suntar Airport", country: "RU", size: "small" },
  { code: "SVA", name: "Savoonga Airport", country: "US", size: "medium" },
  { code: "SVB", name: "Sambava Airport", country: "MG", size: "medium" },
  { code: "SVC", name: "Silver City \u2013 Grant County Airport", country: "US", size: "medium" },
  { code: "SVD", name: "Kingstown \u2013 Argyle International Airport", country: "VC", size: "large" },
  { code: "SVG", name: "Stavanger Airport, Sola", country: "NO", size: "large" },
  { code: "SVI", name: "San Vicente Del Cagu\xE1n \u2013 Eduardo Falla Solano Airport", country: "CO", size: "medium" },
  { code: "SVJ", name: "Svolv\xE6r Airport, Helle", country: "NO", size: "medium" },
  { code: "SVL", name: "Savonlinna Airport", country: "FI", size: "medium" },
  { code: "SVO", name: "Moscow \u2013 Sheremetyevo International Airport", country: "RU", size: "large" },
  { code: "SVQ", name: "Seville Airport", country: "ES", size: "large" },
  { code: "SVS", name: "Stevens Village Airport", country: "US", size: "small" },
  { code: "SVU", name: "Savusavu Airport", country: "FJ", size: "small" },
  { code: "SVX", name: "Yekaterinburg \u2013 Koltsovo Airport", country: "RU", size: "large" },
  {
    code: "SVZ",
    name: "San Antonio del Tachira \u2013 Juan Vicente G\xF3mez International Airport",
    country: "VE",
    size: "medium"
  },
  { code: "SWA", name: "Jieyang (Rongcheng) \u2013 Jieyang Chaoshan International Airport", country: "CN", size: "large" },
  { code: "SWF", name: "Newburgh \u2013 New York Stewart International Airport", country: "US", size: "medium" },
  { code: "SWL", name: "San Vicente Airport", country: "PH", size: "small" },
  { code: "SWO", name: "Stillwater Regional Airport", country: "US", size: "medium" },
  { code: "SWQ", name: "Sumbawa Besar \u2013 Sultan Muhammad Kaharuddin III Airport", country: "ID", size: "small" },
  { code: "SWX", name: "Shakawe Airport", country: "BW", size: "small" },
  { code: "SXB", name: "Strasbourg Airport", country: "FR", size: "large" },
  { code: "SXK", name: "Saumlaki-Yamdena Island \u2013 Mathilda Batlayeri Airport", country: "ID", size: "small" },
  { code: "SXM", name: "Sint Maarten \u2013 Princess Juliana International Airport", country: "SX", size: "large" },
  { code: "SXP", name: "Nunam Iqua Airport", country: "US", size: "small" },
  { code: "SXR", name: "Srinagar International Airport", country: "IN", size: "large" },
  { code: "SYD", name: "Sydney (Mascot) \u2013 Sydney Kingsford Smith International Airport", country: "AU", size: "large" },
  { code: "SYM", name: "Pu'er Simao Airport", country: "CN", size: "small" },
  { code: "SYO", name: "Shonai Airport", country: "JP", size: "medium" },
  { code: "SYQ", name: "San Jose \u2013 Tob\xEDas Bola\xF1os International Airport", country: "CR", size: "medium" },
  { code: "SYR", name: "Syracuse Hancock International Airport", country: "US", size: "large" },
  { code: "SYS", name: "Saskylakh Airport", country: "RU", size: "medium" },
  { code: "SYU", name: "Sue Islet \u2013 Warraber Island Airport", country: "AU", size: "small" },
  { code: "SYX", name: "Sanya (Tianya) \u2013 Sanya Phoenix International Airport", country: "CN", size: "large" },
  { code: "SYY", name: "Stornoway, Western Isles \u2013 Stornoway Airport", country: "GB", size: "medium" },
  { code: "SYZ", name: "Shiraz Shahid Dastghaib International Airport", country: "IR", size: "large" },
  { code: "SZA", name: "Soyo Airport", country: "AO", size: "medium" },
  { code: "SZB", name: "Subang \u2013 Sultan Abdul Aziz Shah International Airport", country: "MY", size: "large" },
  { code: "SZE", name: "Semera Airport", country: "ET", size: "small" },
  { code: "SZF", name: "Samsun-\xC7ar\u015Famba Airport", country: "TR", size: "medium" },
  { code: "SZG", name: "Salzburg Airport", country: "AT", size: "large" },
  { code: "SZH", name: "Shuozhou Zirun Airport", country: "CN", size: "medium" },
  { code: "SZI", name: "Zaysan Airport", country: "KZ", size: "small" },
  { code: "SZK", name: "Skukuza Airport", country: "ZA", size: "medium" },
  { code: "SZX", name: "Shenzhen Bao'an International Airport", country: "CN", size: "large" },
  { code: "SZY", name: "Olsztyn \u2013 Mazury (Szymany)", country: "PL", size: "medium" },
  { code: "SZZ", name: "Szczecin \u2013 Goleni\xF3w", country: "PL", size: "large" },
  { code: "TAB", name: "Scarborough \u2013 A.N.R. Robinson International Airport", country: "TT", size: "large" },
  { code: "TAC", name: "Tacloban City \u2013 Daniel Z. Romualdez Airport", country: "PH", size: "medium" },
  { code: "TAE", name: "Daegu International Airport", country: "KR", size: "large" },
  { code: "TAG", name: "Panglao \u2013 Bohol-Panglao International Airport", country: "PH", size: "large" },
  { code: "TAH", name: "Tanna Island \u2013 Whitegrass Airport", country: "VU", size: "medium" },
  { code: "TAI", name: "Taiz International Airport", country: "YE", size: "medium" },
  { code: "TAK", name: "Takamatsu Airport", country: "JP", size: "large" },
  { code: "TAL", name: "Tanana \u2013 Ralph M Calhoun Memorial Airport", country: "US", size: "small" },
  {
    code: "TAM",
    name: "Ciudad Madero \u2013 General Francisco Javier Mina International Airport",
    country: "MX",
    size: "medium"
  },
  { code: "TAO", name: "Qingdao (Jiaozhou) \u2013 Qingdao Jiaodong International Airport", country: "CN", size: "large" },
  { code: "TAP", name: "Tapachula International Airport", country: "MX", size: "medium" },
  { code: "TAS", name: "Tashkent International Airport", country: "UZ", size: "large" },
  { code: "TAT", name: "Poprad-Tatry Airport", country: "SK", size: "medium" },
  { code: "TAY", name: "Tartu Airport", country: "EE", size: "medium" },
  { code: "TAZ", name: "Da\u015Foguz \u2013 Dashoguz International Airport", country: "TM", size: "large" },
  { code: "TBB", name: "Tuy Hoa \u2013 Dong Tac Airport", country: "VN", size: "medium" },
  { code: "TBG", name: "Tabubil Airport", country: "PG", size: "small" },
  { code: "TBH", name: "Tablas Island \u2013 Tugdan Airport", country: "PH", size: "medium" },
  { code: "TBI", name: "Cat Island \u2013 New Bight Airport", country: "BS", size: "medium" },
  { code: "TBJ", name: "Tabarka-A\xEFn Draham International Airport", country: "TN", size: "medium" },
  { code: "TBM", name: "Tumbang Samba-Borneo Island \u2013 Tumbang Samba Airport", country: "ID", size: "small" },
  {
    code: "TBN",
    name: "Fort Leonard Wood \u2013 Waynesville-St. Robert Regional Airport-Forney Field",
    country: "US",
    size: "medium"
  },
  { code: "TBO", name: "Tabora Airport", country: "TZ", size: "small" },
  { code: "TBP", name: "Tumbes \u2013 Captain Pedro Canga Rodr\xEDguez International Airport", country: "PE", size: "medium" },
  { code: "TBS", name: "Tbilisi International Airport", country: "GE", size: "large" },
  { code: "TBT", name: "Tabatinga International Airport", country: "BR", size: "medium" },
  { code: "TBU", name: "Nuku'alofa \u2013 Fua'amotu International Airport", country: "TO", size: "large" },
  { code: "TBZ", name: "Tabriz International Airport", country: "IR", size: "large" },
  { code: "TCA", name: "Tennant Creek Airport", country: "AU", size: "medium" },
  { code: "TCB", name: "Treasure Cay Airport", country: "BS", size: "medium" },
  { code: "TCD", name: "Tarapac\xE1 Airport", country: "CO", size: "small" },
  { code: "TCG", name: "Tacheng Qianquan Airport", country: "CN", size: "small" },
  { code: "TCO", name: "Tumaco \u2013 La Florida Airport", country: "CO", size: "medium" },
  { code: "TCP", name: "Taba International Airport", country: "EG", size: "medium" },
  {
    code: "TCQ",
    name: "Tacna \u2013 Coronel FAP Carlos Ciriani Santa Rosa International Airport",
    country: "PE",
    size: "medium"
  },
  { code: "TCR", name: "Vagaikulam \u2013 Tuticorin Airport", country: "IN", size: "small" },
  { code: "TCT", name: "Takotna Airport", country: "US", size: "small" },
  { code: "TCZ", name: "Baoshan (Tengchong) \u2013 Tengchong Tuofeng Airport", country: "CN", size: "medium" },
  { code: "TDD", name: "Trinidad \u2013 Teniente Av. Jorge Henrich Arauz Airport", country: "BO", size: "medium" },
  { code: "TDK", name: "Taldykorgan Airport", country: "KZ", size: "medium" },
  { code: "TDS", name: "Sasereme Airport", country: "PG", size: "small" },
  { code: "TDX", name: "Laem Ngop \u2013 Trat Airport", country: "TH", size: "medium" },
  { code: "TEB", name: "Teterboro Airport", country: "US", size: "medium" },
  { code: "TEE", name: "T\xE9bessi \u2013 Cheikh Larbi T\xE9bessi Airport", country: "DZ", size: "medium" },
  { code: "TEK", name: "Tatitlek Airport", country: "US", size: "small" },
  { code: "TEN", name: "Tongren (Daxing) \u2013 Tongren Fenghuang Airport", country: "CN", size: "medium" },
  { code: "TEQ", name: "\xC7orlu \u2013 Tekirda\u011F \xC7orlu Airport", country: "TR", size: "medium" },
  { code: "TER", name: "Praia da Vit\xF3ria \u2013 Lajes Airport", country: "PT", size: "medium" },
  { code: "TET", name: "Tete Airport", country: "MZ", size: "large" },
  { code: "TEX", name: "Telluride Regional Airport", country: "US", size: "medium" },
  { code: "TEZ", name: "Tezpur Airport", country: "IN", size: "medium" },
  { code: "TFF", name: "Tef\xE9 Airport", country: "BR", size: "medium" },
  { code: "TFI", name: "Tufi Airport", country: "PG", size: "small" },
  { code: "TFN", name: "Tenerife Norte-Ciudad de La Laguna Airport", country: "ES", size: "large" },
  { code: "TFS", name: "Tenerife Sur Airport", country: "ES", size: "large" },
  { code: "TFU", name: "Chengdu (Jianyang) \u2013 Chengdu Tianfu International Airport", country: "CN", size: "large" },
  { code: "TGD", name: "Podgorica Airport / Podgorica Golubovci Airbase", country: "ME", size: "large" },
  { code: "TGG", name: "Kuala Terengganu \u2013 Sultan Mahmud Airport", country: "MY", size: "medium" },
  { code: "TGH", name: "Tongoa Island \u2013 Tongoa Airport", country: "VU", size: "small" },
  { code: "TGJ", name: "Tiga Airport", country: "NC", size: "medium" },
  { code: "TGM", name: "Recea \u2013 T\xE2rgu Mure\u015F Transilvania International Airport", country: "RO", size: "medium" },
  { code: "TGO", name: "Tongliao Airport", country: "CN", size: "medium" },
  { code: "TGQ", name: "Tangar\xE1 da Serra Airport", country: "BR", size: "small" },
  { code: "TGR", name: "Touggourt Sidi Madhi Airport", country: "DZ", size: "medium" },
  { code: "TGT", name: "Tanga Airport", country: "TZ", size: "medium" },
  { code: "TGU", name: "Tegucigalpa \u2013 Toncont\xEDn Airport", country: "HN", size: "medium" },
  { code: "TGZ", name: "Tuxtla Guti\xE9rrez \u2013 Angel Albino Corzo International Airport", country: "MX", size: "medium" },
  { code: "THD", name: "Thanh H\xF3a \u2013 Tho Xuan Airport", country: "VN", size: "small" },
  { code: "THE", name: "Teresina \u2013 Senador Petr\xF4nio Portela Airport", country: "BR", size: "medium" },
  { code: "THG", name: "Biloela \u2013 Thangool Airport", country: "AU", size: "medium" },
  { code: "THL", name: "Tachileik Airport", country: "MM", size: "medium" },
  { code: "THN", name: "Trollh\xE4ttan-V\xE4nersborg Airport", country: "SE", size: "medium" },
  { code: "THO", name: "\xDE\xF3rsh\xF6fn Airport", country: "IS", size: "small" },
  { code: "THQ", name: "Tianshui (Maiji) \u2013 Tianshui Maijishan Airport", country: "CN", size: "medium" },
  { code: "THR", name: "Tehran \u2013 Mehrabad International Airport", country: "IR", size: "large" },
  { code: "THS", name: "Sukhothai Airport", country: "TH", size: "medium" },
  { code: "THX", name: "Turukhansk Airport", country: "RU", size: "small" },
  { code: "TIA", name: "Rinas \u2013 Tirana International Airport Mother Teresa", country: "AL", size: "large" },
  { code: "TIE", name: "Tippi Airport", country: "ET", size: "small" },
  { code: "TIF", name: "Taif International Airport", country: "SA", size: "large" },
  { code: "TIH", name: "Tuherahera \u2013 Tikehau Airport", country: "PF", size: "medium" },
  { code: "TIJ", name: "Tijuana \u2013 General Abelardo L. Rodriguez International Airport", country: "MX", size: "large" },
  { code: "TIM", name: "Timika \u2013 Mozes Kilangin Airport", country: "ID", size: "medium" },
  { code: "TIN", name: "Tindouf Airport", country: "DZ", size: "medium" },
  {
    code: "TIQ",
    name: "Tinian Island \u2013 Francisco Manglona Borja / Tinian International Airport",
    country: "MP",
    size: "medium"
  },
  { code: "TIR", name: "Tirupati International Airport", country: "IN", size: "large" },
  { code: "TIU", name: "Timaru Airport", country: "NZ", size: "medium" },
  { code: "TIV", name: "Tivat Airport", country: "ME", size: "medium" },
  { code: "TIW", name: "Tacoma Narrows Airport", country: "US", size: "medium" },
  { code: "TIZ", name: "Tari Airport", country: "PG", size: "small" },
  { code: "TJA", name: "Tarija \u2013 Capitan Oriel Lea Plaza Airport", country: "BO", size: "medium" },
  { code: "TJG", name: "Tanta-Tabalong \u2013 Warukin Airport", country: "ID", size: "medium" },
  { code: "TJH", name: "Toyooka \u2013 Konotori Tajima Airport", country: "JP", size: "medium" },
  { code: "TJK", name: "Tokat Airport", country: "TR", size: "medium" },
  { code: "TJL", name: "Tr\xEAs Lagoas \u2013 Pl\xEDnio Alarcom Airport", country: "BR", size: "small" },
  { code: "TJM", name: "Tyumen \u2013 Roshchino International Airport", country: "RU", size: "large" },
  { code: "TJQ", name: "Tanjung Pandan \u2013 H A S Hanandjoeddin International Airport", country: "ID", size: "small" },
  { code: "TJS", name: "Tanjung Selor-Borneo Island \u2013 Tanjung Harapan Airport", country: "ID", size: "small" },
  { code: "TJU", name: "Kulob International Airport", country: "TJ", size: "large" },
  { code: "TKD", name: "Sekondi-Takoradi \u2013 Takoradi Airport", country: "GH", size: "medium" },
  { code: "TKF", name: "Truckee Tahoe Airport", country: "US", size: "medium" },
  { code: "TKG", name: "Bandar Lampung \u2013 Radin Inten II International Airport", country: "ID", size: "medium" },
  { code: "TKJ", name: "Tok Junction Airport", country: "US", size: "small" },
  { code: "TKK", name: "Weno Island \u2013 Chuuk International Airport", country: "FM", size: "large" },
  { code: "TKM", name: "Taksimo Airport", country: "RU", size: "small" },
  { code: "TKN", name: "Amagi \u2013 Tokunoshima Airport", country: "JP", size: "medium" },
  { code: "TKP", name: "Takapoto Airport", country: "PF", size: "medium" },
  { code: "TKQ", name: "Kigoma Airport", country: "TZ", size: "small" },
  { code: "TKS", name: "Tokushima Awaodori Airport / JMSDF Tokushima Air Base", country: "JP", size: "large" },
  { code: "TKU", name: "Turku Airport", country: "FI", size: "large" },
  { code: "TKV", name: "Tatakoto Airport", country: "PF", size: "small" },
  { code: "TKX", name: "Takaroa Airport", country: "PF", size: "medium" },
  { code: "TLA", name: "Teller Airport", country: "US", size: "small" },
  { code: "TLC", name: "Toluca \u2013 Adolfo L\xF3pez Mateos International Airport", country: "MX", size: "large" },
  { code: "TLE", name: "Toliara Airport", country: "MG", size: "medium" },
  { code: "TLH", name: "Tallahassee International Airport", country: "US", size: "medium" },
  { code: "TLI", name: "Toli Toli-Celebes Island \u2013 Sultan Bantilan Airport", country: "ID", size: "small" },
  { code: "TLL", name: "Tallinn \u2013 Lennart Meri Tallinn Airport", country: "EE", size: "large" },
  { code: "TLM", name: "Zenata \u2013 Messali El Hadj Airport", country: "DZ", size: "large" },
  { code: "TLN", name: "Hy\xE8res, Var \u2013 Toulon-Hy\xE8res Airport", country: "FR", size: "medium" },
  { code: "TLQ", name: "Turpan Jiaohe Airport", country: "CN", size: "medium" },
  { code: "TLS", name: "Toulouse/Blagnac \u2013 Toulouse-Blagnac Airport", country: "FR", size: "large" },
  { code: "TLT", name: "Tuluksak Airport", country: "US", size: "small" },
  { code: "TLU", name: "Santiago de Tol\xFA \u2013 Golfo de Morrosquillo Airport", country: "CO", size: "small" },
  { code: "TLV", name: "Tel Aviv \u2013 Ben Gurion International Airport", country: "IL", size: "large" },
  { code: "TLY", name: "Plastun Airport", country: "RU", size: "small" },
  { code: "TMC", name: "Radamata \u2013 Tambolaka Airport", country: "ID", size: "small" },
  { code: "TME", name: "Tame \u2013 Gustavo Vargas Airport", country: "CO", size: "medium" },
  { code: "TMF", name: "Thimarafushi Airport", country: "MV", size: "small" },
  { code: "TMG", name: "Tomanggong Airport", country: "MY", size: "small" },
  { code: "TMH", name: "Tanah Merah Airport", country: "ID", size: "medium" },
  { code: "TMI", name: "Tumling Tar Airport", country: "NP", size: "small" },
  { code: "TMJ", name: "Termez Airport", country: "UZ", size: "medium" },
  { code: "TML", name: "Tamale \u2013 Yakubu Tali International Airport", country: "GH", size: "large" },
  { code: "TMM", name: "Toamasina Ambalamanasy Airport", country: "MG", size: "large" },
  { code: "TMP", name: "Tampere / Pirkkala \u2013 Tampere-Pirkkala Airport", country: "FI", size: "large" },
  { code: "TMR", name: "Tamanrasset \u2013 Aguenar \u2013 Hadj Bey Akhamok Airport", country: "DZ", size: "large" },
  { code: "TMS", name: "S\xE3o Tom\xE9 International Airport", country: "ST", size: "large" },
  { code: "TMT", name: "Oriximin\xE1 \u2013 Trombetas Airport", country: "BR", size: "medium" },
  { code: "TMW", name: "Tamworth Airport", country: "AU", size: "medium" },
  { code: "TMX", name: "Timimoun Airport", country: "DZ", size: "medium" },
  { code: "TNA", name: "Jinan (Licheng) \u2013 Jinan Yaoqiang International Airport", country: "CN", size: "large" },
  { code: "TNC", name: "Tin City Long Range Radar Station Airport", country: "US", size: "small" },
  { code: "TND", name: "Trinidad \u2013 Alberto Delgado Airport", country: "CU", size: "medium" },
  { code: "TNE", name: "Tanegashima \u2013 New Tanegashima Airport", country: "JP", size: "medium" },
  { code: "TNG", name: "Tangier Ibn Battuta Airport", country: "MA", size: "large" },
  { code: "TNH", name: "Tonghua Sanyuanpu Airport", country: "CN", size: "medium" },
  {
    code: "TNJ",
    name: "Tanjung Pinang-Bintan Island \u2013 Raja Haji Fisabilillah International Airport",
    country: "ID",
    size: "medium"
  },
  { code: "TNK", name: "Tununak Airport", country: "US", size: "small" },
  {
    code: "TNN",
    name: "Tainan (Rende) \u2013 Tainan International Airport / Tainan Air Base",
    country: "TW",
    size: "large"
  },
  { code: "TNR", name: "Antananarivo \u2013 Ivato International Airport", country: "MG", size: "large" },
  { code: "TOD", name: "Tioman Island \u2013 Tioman Airport", country: "MY", size: "medium" },
  { code: "TOE", name: "Tozeur Nefta International Airport", country: "TN", size: "medium" },
  { code: "TOF", name: "Tomsk Kamov Airport", country: "RU", size: "large" },
  { code: "TOG", name: "Togiak Village \u2013 Togiak Airport", country: "US", size: "small" },
  { code: "TOL", name: "Toledo \u2013 Eugene F. Kranz Toledo Express Airport", country: "US", size: "medium" },
  { code: "TOM", name: "Timbuktu \u2013 Tombouktou Airport", country: "ML", size: "large" },
  { code: "TOS", name: "Troms\xF8 Airport", country: "NO", size: "large" },
  { code: "TOU", name: "Touho Airport", country: "NC", size: "medium" },
  { code: "TOW", name: "Toledo - Luiz Dalcanale Filho Municipal Airport", country: "BR", size: "small" },
  { code: "TOY", name: "Toyama Kitokito Airport", country: "JP", size: "medium" },
  { code: "TPA", name: "Tampa International Airport", country: "US", size: "large" },
  { code: "TPE", name: "Taoyuan \u2013 Taiwan Taoyuan International Airport", country: "TW", size: "large" },
  { code: "TPI", name: "Tapini Airport", country: "PG", size: "small" },
  { code: "TPJ", name: "Taplejung Airport", country: "NP", size: "medium" },
  { code: "TPP", name: "Tarapoto \u2013 Cadete FAP Guillermo Del Castillo Paredes Airport", country: "PE", size: "medium" },
  { code: "TPQ", name: "Tepic \u2013 Amado Nervo National Airport", country: "MX", size: "medium" },
  { code: "TPS", name: "Trapani (TP) \u2013 Vincenzo Florio Airport Trapani-Birgi", country: "IT", size: "medium" },
  { code: "TQO", name: "Tulum \u2013 Felipe Carrillo Puerto International Airport Tulum", country: "MX", size: "large" },
  { code: "TRA", name: "Tarama Airport", country: "JP", size: "medium" },
  { code: "TRC", name: "Torre\xF3n \u2013 Francisco Sarabia Tinoco International Airport", country: "MX", size: "medium" },
  { code: "TRD", name: "Trondheim Airport, V\xE6rnes", country: "NO", size: "large" },
  { code: "TRE", name: "Balemartine, Argyll and Bute \u2013 Tiree Airport", country: "GB", size: "medium" },
  { code: "TRF", name: "Sandefjord(Torp) \u2013 Sandefjord Airport, Torp", country: "NO", size: "large" },
  { code: "TRG", name: "Tauranga Airport", country: "NZ", size: "medium" },
  { code: "TRI", name: "Blountville \u2013 Tri-Cities Regional TN/VA Airport", country: "US", size: "medium" },
  {
    code: "TRK",
    name: "Tarakan \u2013 Juwata International Airport / Suharnoko Harbani AFB",
    country: "ID",
    size: "medium"
  },
  { code: "TRN", name: "Caselle Torinese (TO) \u2013 Turin Airport", country: "IT", size: "large" },
  { code: "TRR", name: "Trincomalee \u2013 China Bay Airport", country: "LK", size: "medium" },
  { code: "TRS", name: "Ronchi dei Legionari/Trieste \u2013 Trieste Airport", country: "IT", size: "large" },
  { code: "TRT", name: "Toraja Airport", country: "ID", size: "medium" },
  {
    code: "TRU",
    name: "Trujillo \u2013 Capit\xE1n FAP Carlos Mart\xEDnez de Pinillos International Airport",
    country: "PE",
    size: "large"
  },
  { code: "TRV", name: "Thiruvananthapuram International Airport", country: "IN", size: "large" },
  { code: "TRW", name: "South Tarawa \u2013 Bonriki International Airport", country: "KI", size: "large" },
  { code: "TRZ", name: "Tiruchirappalli International Airport", country: "IN", size: "large" },
  { code: "TSA", name: "Taipei (Songshan) \u2013 Taipei Songshan International Airport", country: "TW", size: "large" },
  { code: "TSF", name: "Treviso (TV) \u2013 Treviso Airport", country: "IT", size: "large" },
  { code: "TSJ", name: "Tsushima Airport", country: "JP", size: "medium" },
  { code: "TSM", name: "Taos Regional Airport", country: "US", size: "medium" },
  { code: "TSN", name: "Tianjin Binhai International Airport", country: "CN", size: "large" },
  { code: "TSR", name: "Timi\u015Foara \u2013 Timi\u0219oara Traian Vuia International Airport", country: "RO", size: "large" },
  { code: "TST", name: "Trang Airport", country: "TH", size: "medium" },
  { code: "TSV", name: "Townsville Airport / RAAF Base Townsville", country: "AU", size: "medium" },
  { code: "TTA", name: "Tan Tan Airport", country: "MA", size: "medium" },
  { code: "TTE", name: "Ternate \u2013 Sultan Babullah Airport", country: "ID", size: "medium" },
  { code: "TTJ", name: "Tottori Sand Dunes Conan Airport", country: "JP", size: "medium" },
  { code: "TTN", name: "Ewing Township \u2013 Trenton Mercer Airport", country: "US", size: "medium" },
  { code: "TTS", name: "Tsaratanana Airport", country: "MG", size: "small" },
  { code: "TTT", name: "Taitung City \u2013 Taitung Airport", country: "TW", size: "medium" },
  { code: "TTU", name: "T\xE9touan \u2013 Sania Ramel Airport", country: "MA", size: "large" },
  {
    code: "TUA",
    name: "Tulc\xE1n \u2013 Lieutenant Colonel Luis A. Mantilla International Airport",
    country: "EC",
    size: "medium"
  },
  { code: "TUB", name: "Tubuai Airport", country: "PF", size: "medium" },
  {
    code: "TUC",
    name: "San Miguel de Tucum\xE1n \u2013 Teniente Benjam\xEDn Matienzo International Airport",
    country: "AR",
    size: "large"
  },
  { code: "TUF", name: "Tours, Indre-et-Loire \u2013 Tours Val de Loire Airport", country: "FR", size: "medium" },
  { code: "TUG", name: "Tuguegarao City \u2013 Tuguegarao Airport", country: "PH", size: "medium" },
  { code: "TUI", name: "Turaif Domestic Airport", country: "SA", size: "medium" },
  { code: "TUK", name: "Turbat International Airport", country: "PK", size: "large" },
  { code: "TUL", name: "Tulsa International Airport", country: "US", size: "large" },
  { code: "TUN", name: "Tunis Carthage International Airport", country: "TN", size: "large" },
  { code: "TUO", name: "Taupo Airport", country: "NZ", size: "medium" },
  { code: "TUP", name: "Tupelo Regional Airport", country: "US", size: "medium" },
  { code: "TUR", name: "Tucuru\xED Airport", country: "BR", size: "medium" },
  { code: "TUS", name: "Tucson International Airport", country: "US", size: "large" },
  { code: "TUU", name: "Tabuk \u2013 Prince Sultan bin Abdulaziz International Airport", country: "SA", size: "large" },
  { code: "TVC", name: "Traverse City \u2013 Cherry Capital Airport", country: "US", size: "medium" },
  { code: "TVF", name: "Thief River Falls Regional Airport", country: "US", size: "medium" },
  { code: "TVS", name: "Tangshan (Fengrun) \u2013 Tangshan Sann\xFChe Airport", country: "CN", size: "small" },
  { code: "TVT", name: "Tashkent-Khumo International Airport", country: "UZ", size: "medium" },
  { code: "TVU", name: "Matei Airport", country: "FJ", size: "small" },
  { code: "TVY", name: "Dawei Airport", country: "MM", size: "medium" },
  { code: "TWA", name: "Twin Hills Airport", country: "US", size: "small" },
  { code: "TWC", name: "Tumxuk Tangwangcheng Airport", country: "CN", size: "small" },
  { code: "TWF", name: "Twin Falls \u2013 Joslin Field Magic Valley Regional Airport", country: "US", size: "medium" },
  { code: "TWT", name: "Bongao \u2013 Sanga Sanga Airport", country: "PH", size: "medium" },
  { code: "TWU", name: "Tawau Airport", country: "MY", size: "medium" },
  { code: "TXE", name: "Takengon \u2013 Rembele Airport", country: "ID", size: "medium" },
  { code: "TXK", name: "Texarkana Regional Airport (Webb Field)", country: "US", size: "medium" },
  { code: "TXN", name: "Huangshan Tunxi International Airport", country: "CN", size: "large" },
  { code: "TYF", name: "Torsby Airport", country: "SE", size: "medium" },
  { code: "TYL", name: "Talara \u2013 Captain Victor Montes Arias International Airport", country: "PE", size: "medium" },
  { code: "TYN", name: "Taiyuan Wusu International Airport", country: "CN", size: "large" },
  { code: "TYR", name: "Tyler Pounds Regional Airport", country: "US", size: "medium" },
  { code: "TYS", name: "Knoxville/Maryville \u2013 McGhee Tyson Airport", country: "US", size: "large" },
  { code: "TZA", name: "Belize City \u2013 Sir Barry Bowen Municipal Airport", country: "BZ", size: "medium" },
  { code: "TZL", name: "Dubrave Gornje \u2013 Tuzla International Airport", country: "BA", size: "large" },
  { code: "TZN", name: "Andros \u2013 Congo Town Airport", country: "BS", size: "medium" },
  { code: "TZX", name: "Trabzon International Airport", country: "TR", size: "medium" },
  { code: "UAH", name: "Ua Huka Airport", country: "PF", size: "small" },
  {
    code: "UAI",
    name: "Suai \u2013 Commander in Chief of FALINTIL, Kay Rala Xanana Gusm\xE3o, International Airport",
    country: "TL",
    size: "medium"
  },
  { code: "UAP", name: "Ua Pou Airport", country: "PF", size: "small" },
  { code: "UAQ", name: "San Juan \u2013 Domingo Faustino Sarmiento Airport", country: "AR", size: "medium" },
  { code: "UAR", name: "Bouarfa Airport", country: "MA", size: "medium" },
  { code: "UBA", name: "Uberaba \u2013 M\xE1rio de Almeida Franco Airport", country: "BR", size: "medium" },
  { code: "UBB", name: "Mabuiag Island Airport", country: "AU", size: "small" },
  { code: "UBJ", name: "Ube \u2013 Yamaguchi Ube Airport", country: "JP", size: "medium" },
  {
    code: "UBN",
    name: "Ulaanbaatar (Sergelen) \u2013 Ulaanbaatar Chinggis Khaan International Airport",
    country: "MN",
    size: "large"
  },
  { code: "UBP", name: "Ubon Ratchathani Airport", country: "TH", size: "medium" },
  { code: "UCB", name: "Ulanqab Jining Airport", country: "CN", size: "medium" },
  { code: "UCT", name: "Ukhta Airport", country: "RU", size: "medium" },
  { code: "UDI", name: "Uberl\xE2ndia \u2013 Ten. Cel. Aviador C\xE9sar Bombonato Airport", country: "BR", size: "medium" },
  { code: "UDR", name: "Udaipur \u2013 Maharana Pratap Airport", country: "IN", size: "medium" },
  { code: "UEL", name: "Quelimane Airport", country: "MZ", size: "medium" },
  { code: "UEO", name: "Kumejima Airport", country: "JP", size: "medium" },
  { code: "UET", name: "Quetta International Airport", country: "PK", size: "large" },
  { code: "UFA", name: "Ufa International Airport", country: "RU", size: "large" },
  { code: "UGA", name: "Bulgan Airport", country: "MN", size: "medium" },
  { code: "UGC", name: "Urgench International Airport", country: "UZ", size: "large" },
  { code: "UGU", name: "Bilogai \u2013 Bilorai Airport", country: "ID", size: "medium" },
  { code: "UIB", name: "Quibd\xF3 \u2013 El Cara\xF1o Airport", country: "CO", size: "medium" },
  { code: "UIH", name: "Quy Nohn \u2013 Phu Cat Airport", country: "VN", size: "medium" },
  { code: "UII", name: "Utila Island \u2013 Utila Airport", country: "HN", size: "small" },
  { code: "UIN", name: "Quincy Regional Airport Baldwin Field", country: "US", size: "medium" },
  { code: "UIO", name: "Quito \u2013 Mariscal Sucre International Airport", country: "EC", size: "large" },
  { code: "UIT", name: "Jabor Jaluit Atoll \u2013 Jaluit Airport", country: "MH", size: "small" },
  { code: "UJE", name: "Ujae Atoll Airport", country: "MH", size: "small" },
  { code: "UKB", name: "Kobe Airport", country: "JP", size: "large" },
  { code: "UKE", name: "Bhawanipatna \u2013 Utkela Airport", country: "IN", size: "medium" },
  { code: "UKG", name: "Ust-Kuyga Airport", country: "RU", size: "small" },
  { code: "UKK", name: "Ust-Kamenogorsk (Oskemen) \u2013 Oskemen International Airport", country: "KZ", size: "large" },
  { code: "UKX", name: "Ust-Kut Airport", country: "RU", size: "medium" },
  { code: "ULG", name: "\xD6lgii Mongolei International Airport", country: "MN", size: "medium" },
  { code: "ULH", name: "Al-Ula International Airport", country: "SA", size: "large" },
  { code: "ULK", name: "Lensk Airport", country: "RU", size: "medium" },
  { code: "ULO", name: "Ulaangom Airport", country: "MN", size: "medium" },
  { code: "ULP", name: "Quilpie Airport", country: "AU", size: "medium" },
  { code: "ULU", name: "Gulu Airport", country: "UG", size: "medium" },
  { code: "ULV", name: "Ulyanovsk Baratayevka Airport", country: "RU", size: "medium" },
  { code: "ULY", name: "Cherdakly \u2013 Ulyanovsk Vostochny Airport", country: "RU", size: "medium" },
  { code: "ULZ", name: "Uliastai \u2013 Donoi Airport", country: "MN", size: "small" },
  { code: "UME", name: "Ume\xE5 Airport", country: "SE", size: "large" },
  { code: "UMS", name: "Ust-Maya Airport", country: "RU", size: "small" },
  { code: "UMU", name: "Umuarama \u2013 Orlando de Carvalho Airport", country: "BR", size: "small" },
  { code: "UNA", name: "Una-Comandatuba Airport", country: "BR", size: "small" },
  { code: "UNG", name: "Kiunga Airport", country: "PG", size: "small" },
  { code: "UNI", name: "Union Island International Airport", country: "VC", size: "medium" },
  { code: "UNK", name: "Unalakleet Airport", country: "US", size: "medium" },
  { code: "UNN", name: "Ranong Airport", country: "TH", size: "medium" },
  { code: "UOL", name: "Buol - Pogogul Airport", country: "ID", size: "small" },
  { code: "UPG", name: "Makassar \u2013 Sultan Hasanuddin International Airport", country: "ID", size: "large" },
  {
    code: "UPN",
    name: "Uruapan - Licenciado y General Ignacio Lopez Rayon International Airport",
    country: "MX",
    size: "medium"
  },
  { code: "URA", name: "Uralsk \u2013 Manshuk Mametova International Airport", country: "KZ", size: "large" },
  { code: "URC", name: "\xDCr\xFCmqi Tianshan International Airport", country: "CN", size: "large" },
  { code: "URE", name: "Kuressaare Airport", country: "EE", size: "medium" },
  { code: "URG", name: "Uruguaiana \u2013 Rubem Berta Airport", country: "BR", size: "medium" },
  { code: "URJ", name: "Uray Airport", country: "RU", size: "medium" },
  { code: "URT", name: "Surat Thani Airport", country: "TH", size: "medium" },
  { code: "URY", name: "Gurayat Domestic Airport", country: "SA", size: "medium" },
  { code: "USA", name: "Concord-Padgett Regional Airport", country: "US", size: "medium" },
  { code: "USH", name: "Ushuaia - Malvinas Argentinas International Airport", country: "AR", size: "medium" },
  { code: "USJ", name: "Usharal Airport", country: "KZ", size: "small" },
  { code: "USK", name: "Usinsk Airport", country: "RU", size: "medium" },
  { code: "USM", name: "Na Thon (Ko Samui Island) \u2013 Samui International Airport", country: "TH", size: "large" },
  { code: "USN", name: "Ulsan Airport", country: "KR", size: "medium" },
  { code: "USR", name: "Ust-Nera Airport", country: "RU", size: "medium" },
  { code: "UST", name: "St Augustine \u2013 Northeast Florida Regional Airport", country: "US", size: "medium" },
  { code: "USU", name: "Coron \u2013 Francisco B. Reyes (Busuanga) Airport", country: "PH", size: "medium" },
  { code: "UTH", name: "Udon Thani International Airport", country: "TH", size: "large" },
  { code: "UTK", name: "Utirik Island \u2013 Utirik Airport", country: "MH", size: "small" },
  { code: "UTN", name: "Upington Airport", country: "ZA", size: "medium" },
  { code: "UTO", name: "Utopia Creek \u2013 Indian Mountain LRRS Airport", country: "US", size: "medium" },
  { code: "UTP", name: "Rayong \u2013 U-Tapao\u2013Rayong\u2013Pattaya International Airport", country: "TH", size: "large" },
  { code: "UTT", name: "Mthatha \u2013 K. D. Matanzima Airport", country: "ZA", size: "medium" },
  { code: "UUA", name: "Bugulma Airport", country: "RU", size: "medium" },
  { code: "UUD", name: "Ulan Ude \u2013 Baikal International Airport", country: "RU", size: "large" },
  { code: "UUS", name: "Yuzhno-Sakhalinsk International Airport", country: "RU", size: "large" },
  { code: "UVE", name: "Ouv\xE9a Airport", country: "NC", size: "medium" },
  { code: "UVF", name: "Vieux Fort \u2013 Hewanorra International Airport", country: "LC", size: "large" },
  { code: "UVI", name: "Uni\xE3o da Vit\xF3ria \u2013 Jos\xE9 Cleto Airport", country: "BR", size: "small" },
  { code: "UYL", name: "Nyala Airport", country: "SD", size: "medium" },
  { code: "UYN", name: "Yulin Yuyang Airport", country: "CN", size: "medium" },
  { code: "UYU", name: "Quijarro \u2013 Joya Andina International Airport", country: "BO", size: "large" },
  { code: "UZR", name: "Urzhar Airport", country: "KZ", size: "small" },
  { code: "VAA", name: "Vaasa Airport", country: "FI", size: "large" },
  { code: "VAI", name: "Vanimo Airport", country: "PG", size: "medium" },
  { code: "VAK", name: "Chevak Airport", country: "US", size: "small" },
  { code: "VAL", name: "Valen\xE7a Airport", country: "BR", size: "small" },
  { code: "VAM", name: "Maamigili \u2013 Villa International Airport Maamigili", country: "MV", size: "medium" },
  { code: "VAN", name: "Van Ferit Melen Airport", country: "TR", size: "medium" },
  { code: "VAO", name: "Suavanao Airport", country: "SB", size: "small" },
  { code: "VAQ", name: "Vanavara Airport", country: "RU", size: "medium" },
  { code: "VAR", name: "Varna Airport", country: "BG", size: "large" },
  { code: "VAS", name: "Sivas Nuri Demira\u011F Airport", country: "TR", size: "medium" },
  { code: "VAV", name: "Vava'u Island \u2013 Vava'u International Airport", country: "TO", size: "large" },
  { code: "VAW", name: "Vard\xF8 Airport, Svartnes", country: "NO", size: "medium" },
  { code: "VBS", name: "Montichiari (BS) \u2013 Brescia Gabriele d'Annunzio Airport", country: "IT", size: "medium" },
  { code: "VBV", name: "Vanua Balavu Airport", country: "FJ", size: "small" },
  { code: "VBY", name: "Visby Airport", country: "SE", size: "large" },
  { code: "VCA", name: "Can Tho International Airport", country: "VN", size: "large" },
  { code: "VCE", name: "Venezia (VE) \u2013 Venice Marco Polo Airport", country: "IT", size: "large" },
  { code: "VCL", name: "Tam Ngh\u0129a \u2013 Chu Lai Airport", country: "VN", size: "small" },
  { code: "VCP", name: "Campinas \u2013 Viracopos International Airport", country: "BR", size: "large" },
  { code: "VCS", name: "Con Dao Airport", country: "VN", size: "medium" },
  { code: "VCT", name: "Victoria Regional Airport", country: "US", size: "medium" },
  { code: "VDC", name: "Vit\xF3ria da Conquista \u2013 Glauber de Andrade Rocha Airport", country: "BR", size: "medium" },
  { code: "VDE", name: "El Hierro Island \u2013 El Hierro Airport", country: "ES", size: "medium" },
  { code: "VDH", name: "Dong Hoi Airport", country: "VN", size: "medium" },
  { code: "VDM", name: "Viedma / Carmen de Patagones \u2013 Gobernador Castello Airport", country: "AR", size: "medium" },
  { code: "VDO", name: "Van Don International Airport", country: "VN", size: "medium" },
  { code: "VDS", name: "Vads\xF8 Airport", country: "NO", size: "medium" },
  { code: "VDZ", name: "Valdez Pioneer Field", country: "US", size: "medium" },
  { code: "VEE", name: "Venetie Airport", country: "US", size: "small" },
  { code: "VEL", name: "Vernal Regional Airport", country: "US", size: "medium" },
  { code: "VEO", name: "Severo-Yeniseysk Airport", country: "RU", size: "medium" },
  { code: "VER", name: "Veracruz \u2013 General Heriberto Jara International Airport", country: "MX", size: "large" },
  { code: "VFA", name: "Victoria Falls International Airport", country: "ZW", size: "large" },
  { code: "VGA", name: "Vijayawada International Airport", country: "IN", size: "large" },
  { code: "VGO", name: "Vigo Airport", country: "ES", size: "medium" },
  { code: "VHM", name: "Vilhelmina South Lapland Airport", country: "SE", size: "medium" },
  { code: "VHV", name: "Verkhnevilyuisk Airport", country: "RU", size: "small" },
  { code: "VIE", name: "Vienna International Airport", country: "AT", size: "large" },
  { code: "VIG", name: "El Vig\xEDa \u2013 Juan Pablo P\xE9rez Alfonso Airport", country: "VE", size: "medium" },
  { code: "VII", name: "Vinh Airport", country: "VN", size: "medium" },
  { code: "VIJ", name: "Spanish Town \u2013 Virgin Gorda Airport", country: "VG", size: "medium" },
  { code: "VIL", name: "Dakhla Airport", country: "EH", size: "large" },
  { code: "VIT", name: "Alava \u2013 Vitoria Airport", country: "ES", size: "medium" },
  { code: "VIX", name: "Vit\xF3ria \u2013 Eurico de Aguiar Salles International Airport", country: "BR", size: "large" },
  { code: "VJB", name: "Xai-Xai Chongoene Airport", country: "MZ", size: "small" },
  { code: "VKG", name: "Rach Gia Airport", country: "VN", size: "medium" },
  { code: "VKO", name: "Moscow \u2013 Vnukovo International Airport", country: "RU", size: "large" },
  { code: "VKT", name: "Vorkuta Airport", country: "RU", size: "medium" },
  { code: "VLC", name: "Valencia Airport", country: "ES", size: "large" },
  { code: "VLD", name: "Valdosta Regional Airport", country: "US", size: "medium" },
  { code: "VLI", name: "Port Vila \u2013 Bauerfield International Airport", country: "VU", size: "large" },
  { code: "VLL", name: "Valladolid Airport", country: "ES", size: "medium" },
  { code: "VLN", name: "Valencia \u2013 Arturo Michelena International Airport", country: "VE", size: "large" },
  { code: "VLS", name: "Epi Island \u2013 Valesdir Airport", country: "VU", size: "small" },
  { code: "VLV", name: "Valera \u2013 Dr. Antonio Nicol\xE1s Brice\xF1o Airport", country: "VE", size: "medium" },
  { code: "VMU", name: "Baimuru Airport", country: "PG", size: "medium" },
  { code: "VNO", name: "Vilnius International Airport", country: "LT", size: "large" },
  { code: "VNS", name: "Varanasi \u2013 Lal Bahadur Shastri International Airport", country: "IN", size: "large" },
  { code: "VNX", name: "Vilanculo \u2013 Vilankulo Airport", country: "MZ", size: "medium" },
  { code: "VOG", name: "Volgograd International Airport", country: "RU", size: "large" },
  { code: "VOL", name: "Nea Anchialos National Airport", country: "GR", size: "medium" },
  { code: "VOZ", name: "Voronezh International Airport", country: "RU", size: "medium" },
  { code: "VPE", name: "Ngiva \u2013 Ngjiva Pereira Airport", country: "AO", size: "medium" },
  { code: "VPN", name: "Vopnafj\xF6r\xF0ur Airport", country: "IS", size: "medium" },
  { code: "VPS", name: "Valparaiso \u2013 Destin-Fort Walton Beach Airport", country: "US", size: "medium" },
  { code: "VPY", name: "Chimoio Airport", country: "MZ", size: "medium" },
  { code: "VQS", name: "Vieques \u2013 Antonio Rivera Rodriguez Airport", country: "PR", size: "medium" },
  { code: "VRA", name: "Matanzas \u2013 Juan Gualberto Gomez International Airport", country: "CU", size: "large" },
  { code: "VRB", name: "Vero Beach Regional Airport", country: "US", size: "medium" },
  { code: "VRC", name: "Virac Airport", country: "PH", size: "medium" },
  { code: "VRL", name: "Vila Real Airport", country: "PT", size: "medium" },
  { code: "VRN", name: "Caselle (VR) \u2013 Verona Villafranca Valerio Catullo Airport", country: "IT", size: "large" },
  { code: "VSA", name: "Villahermosa \u2013 Carlos Rovirosa P\xE9rez International Airport", country: "MX", size: "large" },
  { code: "VSE", name: "Viseu \u2013 Aerodromo Goncalves Lobato (Viseu Airport)", country: "PT", size: "medium" },
  { code: "VST", name: "Stockholm / V\xE4ster\xE5s \u2013 Stockholm V\xE4ster\xE5s Airport", country: "SE", size: "large" },
  { code: "VSV", name: "Shravasti Airport", country: "IN", size: "small" },
  { code: "VTE", name: "Vientiane \u2013 Wattay International Airport", country: "LA", size: "large" },
  { code: "VTU", name: "Las Tunas \u2013 Hermanos Ameijeiras Airport", country: "CU", size: "medium" },
  {
    code: "VTZ",
    name: "Visakhapatnam \u2013 Alluri Sitarama Raju International Airport (Vizag)",
    country: "IN",
    size: "large"
  },
  { code: "VUP", name: "Valledupar \u2013 Alfonso L\xF3pez Pumarejo Airport", country: "CO", size: "medium" },
  { code: "VUS", name: "Velikiy Ustyug Airport", country: "RU", size: "medium" },
  { code: "VVC", name: "Villavicencio \u2013 Vanguardia Airport", country: "CO", size: "medium" },
  { code: "VVI", name: "Santa Cruz \u2013 Viru Viru International Airport", country: "BO", size: "large" },
  { code: "VVO", name: "Artyom \u2013 Vladivostok International Airport", country: "RU", size: "large" },
  { code: "VVZ", name: "Illizi Takhamalt Airport", country: "DZ", size: "medium" },
  { code: "VXC", name: "Lichinga Airport", country: "MZ", size: "medium" },
  { code: "VXE", name: "S\xE3o Pedro \u2013 Cesaria Evora International Airport", country: "CV", size: "large" },
  { code: "VXO", name: "V\xE4xj\xF6 Kronoberg Airport", country: "SE", size: "medium" },
  { code: "VYI", name: "Vilyuisk Airport", country: "RU", size: "medium" },
  { code: "WAA", name: "Wales Airport", country: "US", size: "small" },
  { code: "WAE", name: "Wadi Al Dawasir Domestic Airport", country: "SA", size: "medium" },
  { code: "WAG", name: "Wanganui Airport", country: "NZ", size: "medium" },
  { code: "WAW", name: "Warszawa \u2013 Lotnisko Chopina", country: "PL", size: "large" },
  { code: "WBB", name: "Stebbins Airport", country: "US", size: "small" },
  { code: "WBM", name: "Wapenamanda Airport", country: "PG", size: "medium" },
  { code: "WBQ", name: "Beaver Airport", country: "US", size: "small" },
  { code: "WDH", name: "Windhoek \u2013 Hosea Kutako International Airport", country: "NA", size: "large" },
  { code: "WDN", name: "Eastsound \u2013 Waldron Airstrip", country: "US", size: "small" },
  { code: "WDS", name: "Shiyan (Maojian) \u2013 Shiyan Wudangshan Airport", country: "CN", size: "medium" },
  { code: "WEF", name: "Weifang (Kuiwen) \u2013 Weifang Nanyuan Airport", country: "CN", size: "medium" },
  { code: "WEH", name: "Weihai Dashuibo Airport", country: "CN", size: "medium" },
  { code: "WEI", name: "Weipa Airport", country: "AU", size: "medium" },
  { code: "WGA", name: "Forest Hill \u2013 Wagga Wagga Airport", country: "AU", size: "medium" },
  { code: "WGE", name: "Walgett Airport", country: "AU", size: "medium" },
  { code: "WGN", name: "Shaoyang (Wugang) \u2013 Shaoyang Wugang Airport", country: "CN", size: "medium" },
  { code: "WGP", name: "Waingapu-Sumba Island \u2013 Umbu Mehang Kunda Airport", country: "ID", size: "small" },
  { code: "WHA", name: "Wuhu Xuanzhou Airport", country: "CN", size: "medium" },
  { code: "WHK", name: "Whakat\u0101ne Airport", country: "NZ", size: "medium" },
  { code: "WIC", name: "Wick John O'Groats Airport", country: "GB", size: "medium" },
  { code: "WIL", name: "Nairobi Wilson Airport", country: "KE", size: "medium" },
  { code: "WIN", name: "Winton Airport", country: "AU", size: "medium" },
  { code: "WJR", name: "Wajir Airport", country: "KE", size: "medium" },
  { code: "WJU", name: "Wonju Airport / Hoengseong Air Base (K-38/K-46)", country: "KR", size: "medium" },
  { code: "WKA", name: "Wanaka Airport", country: "NZ", size: "medium" },
  { code: "WKJ", name: "Wakkanai Airport", country: "JP", size: "medium" },
  { code: "WKK", name: "Aleknagik / New Airport", country: "US", size: "medium" },
  { code: "WLG", name: "Wellington International Airport", country: "NZ", size: "large" },
  { code: "WLH", name: "Walaha Airport", country: "VU", size: "small" },
  { code: "WLK", name: "Selawik Airport", country: "US", size: "small" },
  { code: "WLS", name: "Wallis Island \u2013 Hihifo Airport", country: "WF", size: "large" },
  { code: "WMI", name: "Warszawa \u2013 Modlin", country: "PL", size: "large" },
  { code: "WMN", name: "Maroantsetra Airport", country: "MG", size: "medium" },
  { code: "WMO", name: "White Mountain Airport", country: "US", size: "small" },
  { code: "WMT", name: "Zunyi Maotai Airport", country: "CN", size: "medium" },
  { code: "WMX", name: "Wamena Airport", country: "ID", size: "medium" },
  { code: "WNA", name: "Napakiak Airport", country: "US", size: "small" },
  { code: "WNH", name: "Wenshan Puzhehei Airport", country: "CN", size: "small" },
  { code: "WNI", name: "Wangi-wangi Island \u2013 Matahora Airport", country: "ID", size: "medium" },
  { code: "WNN", name: "Wunnumin Lake Airport", country: "CA", size: "small" },
  { code: "WNP", name: "Naga Airport", country: "PH", size: "medium" },
  { code: "WNR", name: "Windorah Airport", country: "AU", size: "medium" },
  { code: "WNS", name: "Nawabashah \u2013 Shaheed Benazirabad Airport", country: "PK", size: "medium" },
  { code: "WNZ", name: "Wenzhou (Longwan) \u2013 Wenzhou Longwan International Airport", country: "CN", size: "large" },
  { code: "WOS", name: "Wonsan Kalma Airport", country: "KP", size: "medium" },
  { code: "WRE", name: "Whangarei Airport", country: "NZ", size: "medium" },
  { code: "WRG", name: "Wrangell Airport", country: "US", size: "medium" },
  { code: "WRO", name: "Wroc\u0142aw \u2013 Strachowice", country: "PL", size: "large" },
  { code: "WRY", name: "Westray, Orkney Islands \u2013 Westray Airport", country: "GB", size: "small" },
  { code: "WSK", name: "Wushan \u2013 Chongqing Wushan Airport", country: "CN", size: "small" },
  { code: "WSN", name: "South Naknek Number 2 Airport", country: "US", size: "small" },
  { code: "WST", name: "Westerly State Airport", country: "US", size: "medium" },
  { code: "WSZ", name: "Westport Airport", country: "NZ", size: "medium" },
  { code: "WTA", name: "Tambohorano Airport", country: "MG", size: "small" },
  { code: "WTB", name: "Toowoomba Wellcamp Airport", country: "AU", size: "large" },
  { code: "WTE", name: "Wotje Airport", country: "MH", size: "small" },
  { code: "WTK", name: "Noatak Airport", country: "US", size: "small" },
  { code: "WTL", name: "Tuntutuliak Airport", country: "US", size: "small" },
  { code: "WTO", name: "Wotho Island Airport", country: "MH", size: "small" },
  { code: "WUA", name: "Wuhai Airport", country: "CN", size: "medium" },
  { code: "WUH", name: "Wuhan (Huangpi) \u2013 Wuhan Tianhe International Airport", country: "CN", size: "large" },
  { code: "WUN", name: "Wiluna Airport", country: "AU", size: "medium" },
  { code: "WUS", name: "Wuyishan \u2013 Nanping Wuyishan Airport", country: "CN", size: "medium" },
  { code: "WUT", name: "Xinzhou Wutaishan Airport", country: "CN", size: "small" },
  { code: "WUU", name: "Wau Airport", country: "SS", size: "medium" },
  { code: "WUX", name: "Wuxi \u2013 Sunan Shuofang International Airport", country: "CN", size: "large" },
  { code: "WUZ", name: "Tangbu \u2013 Wuzhou Xijiang Airport", country: "CN", size: "medium" },
  { code: "WVB", name: "Walvis Bay(Rooikop) \u2013 Walvis Bay International Airport", country: "NA", size: "large" },
  { code: "WWK", name: "Wewak International Airport", country: "PG", size: "medium" },
  { code: "WWT", name: "Mertarvik Airport", country: "US", size: "small" },
  { code: "WXN", name: "Wanzhou Wuqiao Airport", country: "CN", size: "small" },
  { code: "WYA", name: "Whyalla Airport", country: "AU", size: "medium" },
  { code: "WYS", name: "West Yellowstone \u2013 Yellowstone Airport", country: "US", size: "medium" },
  { code: "XAI", name: "Xinyang Minggang Airport", country: "CN", size: "medium" },
  { code: "XAP", name: "Chapec\xF3 \u2013 Serafin Enoss Bertaso Airport", country: "BR", size: "medium" },
  { code: "XBE", name: "Bearskin Lake Airport", country: "CA", size: "small" },
  { code: "XBJ", name: "Birjand International Airport", country: "IR", size: "large" },
  { code: "XCH", name: "Flying Fish Cove \u2013 Christmas Island International Airport", country: "CX", size: "medium" },
  { code: "XCR", name: "Chalons en Champagne \u2013 Chalons Vatry airport", country: "FR", size: "medium" },
  { code: "XFN", name: "Xiangyang (Xiangzhou) \u2013 Xiangyang Liuji Airport", country: "CN", size: "medium" },
  { code: "XGR", name: "Kangiqsualujjuaq (Georges River) Airport", country: "CA", size: "small" },
  { code: "XIC", name: "Liangshan (Xichang) \u2013 Xichang Qingshan Airport", country: "CN", size: "medium" },
  { code: "XIL", name: "Xilinhot Airport", country: "CN", size: "medium" },
  { code: "XIY", name: "Xi'an Xianyang International Airport", country: "CN", size: "large" },
  { code: "XKH", name: "Xieng Khouang Airport", country: "LA", size: "small" },
  { code: "XKS", name: "Kasabonika Airport", country: "CA", size: "medium" },
  { code: "XLB", name: "Lac Brochet Airport", country: "CA", size: "small" },
  { code: "XMH", name: "Manihi Airport", country: "PF", size: "medium" },
  { code: "XMN", name: "Xiamen Gaoqi International Airport", country: "CN", size: "large" },
  { code: "XMS", name: "Macas \u2013 Coronel E Carvajal Airport", country: "EC", size: "medium" },
  { code: "XMY", name: "Yam Island Airport", country: "AU", size: "small" },
  {
    code: "XNA",
    name: "Fayetteville/Springdale/Rogers \u2013 Northwest Arkansas National Airport",
    country: "US",
    size: "medium"
  },
  {
    code: "XNN",
    name: "Haidong (Huzhu Tu Autonomous County) \u2013 Xining Caojiabao International Airport",
    country: "CN",
    size: "large"
  },
  { code: "XPK", name: "Pukatawagan Airport", country: "CA", size: "small" },
  { code: "XPL", name: "Palmerola International Airport", country: "HN", size: "large" },
  { code: "XQP", name: "Quepos Managua Airport", country: "CR", size: "medium" },
  { code: "XQU", name: "Qualicum Beach Airport", country: "CA", size: "medium" },
  { code: "XRY", name: "Jerez de la Frontera \u2013 Jerez Airport", country: "ES", size: "medium" },
  { code: "XSC", name: "South Caicos Airport", country: "TC", size: "medium" },
  { code: "XSI", name: "South Indian Lake Airport", country: "CA", size: "small" },
  { code: "XSP", name: "Seletar Airport", country: "SG", size: "medium" },
  { code: "XTG", name: "Thargomindah Airport", country: "AU", size: "medium" },
  { code: "XTL", name: "Tadoule Lake Airport", country: "CA", size: "small" },
  { code: "XUZ", name: "Xuzhou Guanyin International Airport", country: "CN", size: "medium" },
  { code: "XWA", name: "Williston Basin International Airport", country: "US", size: "medium" },
  { code: "XYA", name: "Yandina Airport", country: "SB", size: "small" },
  { code: "YAA", name: "Anahim Lake Airport", country: "CA", size: "medium" },
  { code: "YAB", name: "Arctic Bay Airport", country: "CA", size: "small" },
  { code: "YAC", name: "Cat Lake Airport", country: "CA", size: "small" },
  { code: "YAG", name: "Fort Frances Municipal Airport", country: "CA", size: "medium" },
  { code: "YAK", name: "Yakutat Airport", country: "US", size: "medium" },
  { code: "YAL", name: "Alert Bay Airport", country: "CA", size: "small" },
  { code: "YAM", name: "Sault Ste Marie Airport", country: "CA", size: "medium" },
  { code: "YAP", name: "Yap Island \u2013 Yap International Airport", country: "FM", size: "large" },
  { code: "YAS", name: "Yasawa Island Airport", country: "FJ", size: "small" },
  { code: "YAT", name: "Attawapiskat Airport", country: "CA", size: "small" },
  { code: "YAX", name: "Angling Lake \u2013 Wapekeka Airport", country: "CA", size: "small" },
  { code: "YAY", name: "St. Anthony Airport", country: "CA", size: "medium" },
  { code: "YAZ", name: "Tofino / Long Beach Airport", country: "CA", size: "medium" },
  { code: "YBB", name: "Kugaaruk Airport", country: "CA", size: "small" },
  { code: "YBC", name: "Baie-Comeau Airport", country: "CA", size: "medium" },
  { code: "YBE", name: "Uranium City Airport", country: "CA", size: "small" },
  { code: "YBG", name: "Saguenay-Bagotville Airport", country: "CA", size: "medium" },
  { code: "YBI", name: "Black Tickle Airport", country: "CA", size: "small" },
  { code: "YBK", name: "Baker Lake Airport", country: "CA", size: "medium" },
  { code: "YBL", name: "Campbell River Airport", country: "CA", size: "medium" },
  { code: "YBP", name: "Yibin (Cuiping) \u2013 Yibin Wuliangye Airport", country: "CN", size: "medium" },
  { code: "YBR", name: "Brandon Municipal Airport", country: "CA", size: "medium" },
  { code: "YBT", name: "Brochet Airport", country: "CA", size: "small" },
  { code: "YBV", name: "Berens River Airport", country: "CA", size: "small" },
  { code: "YBX", name: "Blanc-Sablon \u2013 Lourdes-de-Blanc-Sablon Airport", country: "CA", size: "medium" },
  { code: "YBY", name: "Bonnyville Airport", country: "CA", size: "medium" },
  { code: "YCB", name: "Cambridge Bay Airport", country: "CA", size: "medium" },
  { code: "YCD", name: "Nanaimo Airport", country: "CA", size: "medium" },
  { code: "YCG", name: "Castlegar/West Kootenay Regional Airport", country: "CA", size: "medium" },
  { code: "YCK", name: "Colville Lake \u2013 Tommy Kochon Airport", country: "CA", size: "small" },
  { code: "YCM", name: "Niagara-on-the-Lake \u2013 Niagara District Airport", country: "CA", size: "medium" },
  { code: "YCO", name: "Kugluktuk Airport", country: "CA", size: "small" },
  { code: "YCR", name: "Cross Lake (Charlie Sinclair Memorial) Airport", country: "CA", size: "small" },
  { code: "YCS", name: "Chesterfield Inlet Airport", country: "CA", size: "small" },
  { code: "YCU", name: "Yuncheng (Yanhu) \u2013 Yuncheng Yanhu International Airport", country: "CN", size: "large" },
  { code: "YCY", name: "Clyde River Airport", country: "CA", size: "small" },
  { code: "YDA", name: "Dawson City Airport", country: "CA", size: "medium" },
  { code: "YDF", name: "Deer Lake Airport", country: "CA", size: "medium" },
  { code: "YDL", name: "Dease Lake Airport", country: "CA", size: "small" },
  { code: "YDN", name: "Dauphin Barker Airport", country: "CA", size: "medium" },
  { code: "YDP", name: "Nain Airport", country: "CA", size: "small" },
  { code: "YDV", name: "Bloodvein River Airport", country: "CA", size: "small" },
  { code: "YEG", name: "Edmonton International Airport", country: "CA", size: "large" },
  { code: "YEI", name: "Yeni\u015Fehir \u2013 Bursa Yeni\u015Fehir Airport", country: "TR", size: "medium" },
  { code: "YEK", name: "Arviat Airport", country: "CA", size: "small" },
  { code: "YER", name: "Fort Severn Airport", country: "CA", size: "small" },
  { code: "YEV", name: "Inuvik Mike Zubko Airport", country: "CA", size: "medium" },
  { code: "YFA", name: "Fort Albany Airport", country: "CA", size: "small" },
  { code: "YFB", name: "Iqaluit Airport", country: "CA", size: "medium" },
  { code: "YFC", name: "Fredericton International Airport", country: "CA", size: "medium" },
  { code: "YFH", name: "Fort Hope Airport", country: "CA", size: "small" },
  { code: "YFJ", name: "Wekwe\xE8t\xEC Airport", country: "CA", size: "small" },
  { code: "YFO", name: "Flin Flon Airport", country: "CA", size: "small" },
  { code: "YFS", name: "Fort Simpson Airport", country: "CA", size: "medium" },
  { code: "YFX", name: "St. Lewis (Fox Harbour) Airport", country: "CA", size: "small" },
  { code: "YGH", name: "Fort Good Hope Airport", country: "CA", size: "small" },
  { code: "YGJ", name: "Yonago Kitaro Airport / JASDF Miho Air Base", country: "JP", size: "medium" },
  { code: "YGL", name: "La Grande Rivi\xE8re Airport", country: "CA", size: "medium" },
  { code: "YGO", name: "Gods Lake Narrows Airport", country: "CA", size: "small" },
  { code: "YGP", name: "Gasp\xE9 \u2013 Michel-Pouliot Gasp\xE9 Airport", country: "CA", size: "medium" },
  { code: "YGR", name: "Les \xCEles-de-la-Madeleine \u2013 \xCEles-de-la-Madeleine Airport", country: "CA", size: "medium" },
  { code: "YGT", name: "Igloolik Airport", country: "CA", size: "small" },
  { code: "YGV", name: "Havre-Saint-Pierre Airport", country: "CA", size: "medium" },
  { code: "YGW", name: "Kuujjuarapik Airport", country: "CA", size: "medium" },
  { code: "YGX", name: "Gillam Airport", country: "CA", size: "small" },
  { code: "YGZ", name: "Grise Fiord Airport", country: "CA", size: "small" },
  { code: "YHA", name: "Port Hope Simpson Airport", country: "CA", size: "small" },
  { code: "YHG", name: "Charlottetown Airport", country: "CA", size: "small" },
  { code: "YHI", name: "Ulukhaktok Holman Airport", country: "CA", size: "small" },
  { code: "YHK", name: "Gjoa Haven Airport", country: "CA", size: "small" },
  { code: "YHM", name: "Hamilton \u2013 John C. Munro Hamilton International Airport", country: "CA", size: "large" },
  { code: "YHO", name: "Hopedale Airport", country: "CA", size: "small" },
  { code: "YHP", name: "Poplar Hill Airport", country: "CA", size: "small" },
  { code: "YHR", name: "Chevery Airport", country: "CA", size: "small" },
  { code: "YHU", name: "Montr\xE9al / Saint-Hubert Metropolitan Airport", country: "CA", size: "medium" },
  { code: "YHY", name: "Hay River / Merlyn Carter Airport", country: "CA", size: "medium" },
  { code: "YHZ", name: "Halifax / Stanfield International Airport", country: "CA", size: "large" },
  { code: "YIA", name: "Yogyakarta International Airport", country: "ID", size: "large" },
  { code: "YIC", name: "Yichun Mingyueshan Airport", country: "CN", size: "medium" },
  { code: "YIE", name: "Arxan Yi'ershi Airport", country: "CN", size: "medium" },
  { code: "YIF", name: "St-Augustin \u2013 St Augustin Airport", country: "CA", size: "medium" },
  { code: "YIH", name: "Yichang (Xiaoting) \u2013 Yichang Sanxia Airport", country: "CN", size: "medium" },
  { code: "YIK", name: "Ivujivik Airport", country: "CA", size: "small" },
  { code: "YIN", name: "Ili (Yining / Ghulja) \u2013 Ili Yining International Airport", country: "CN", size: "medium" },
  { code: "YIO", name: "Pond Inlet Airport", country: "CA", size: "small" },
  { code: "YIV", name: "Island Lake Airport", country: "CA", size: "medium" },
  { code: "YIW", name: "Yiwu/Jinhua \u2013 Yiwu Airport", country: "CN", size: "large" },
  { code: "YKA", name: "Kamloops John Moose Fulton Field Regional Airport", country: "CA", size: "medium" },
  { code: "YKF", name: "Breslau \u2013 Region of Waterloo International Airport", country: "CA", size: "medium" },
  { code: "YKG", name: "Kangirsuk Airport", country: "CA", size: "small" },
  { code: "YKH", name: "Yingkou (Laobian) \u2013 Yingkou Lanqi Airport", country: "CN", size: "medium" },
  { code: "YKL", name: "Schefferville Airport", country: "CA", size: "medium" },
  { code: "YKM", name: "Yakima Air Terminal McAllister Field", country: "US", size: "medium" },
  { code: "YKO", name: "Hakkari Y\xFCksekova Airport", country: "TR", size: "medium" },
  { code: "YKQ", name: "Waskaganish Airport", country: "CA", size: "small" },
  { code: "YKS", name: "Yakutsk \u2013 Platon Oyunsky Yakutsk International Airport", country: "RU", size: "large" },
  { code: "YKU", name: "Chisasibi Airport", country: "CA", size: "small" },
  { code: "YLC", name: "Kimmirut Airport", country: "CA", size: "small" },
  { code: "YLE", name: "What\xEC Airport", country: "CA", size: "small" },
  { code: "YLH", name: "Lansdowne House Airport", country: "CA", size: "small" },
  { code: "YLK", name: "Barrie-Lake Simcoe Regional Airport", country: "CA", size: "medium" },
  { code: "YLL", name: "Lloydminster Airport", country: "CA", size: "medium" },
  { code: "YLW", name: "Kelowna International Airport", country: "CA", size: "large" },
  { code: "YLX", name: "Yulin Fumian Airport", country: "CN", size: "medium" },
  { code: "YMH", name: "Mary's Harbour Airport", country: "CA", size: "small" },
  { code: "YMM", name: "Fort McMurray International Airport", country: "CA", size: "medium" },
  { code: "YMN", name: "Makkovik Airport", country: "CA", size: "small" },
  { code: "YMO", name: "Moosonee Airport", country: "CA", size: "medium" },
  { code: "YMP", name: "Port McNeill Airport", country: "CA", size: "small" },
  { code: "YMS", name: "Yurimaguas \u2013 Moises Benzaquen Rengifo Airport", country: "PE", size: "medium" },
  { code: "YMT", name: "Chibougamau \u2013 Chapais Airport", country: "CA", size: "medium" },
  { code: "YMX", name: "Montr\xE9al \u2013 Montreal Mirabel International Airport", country: "CA", size: "medium" },
  { code: "YNA", name: "Natashquan Airport", country: "CA", size: "medium" },
  { code: "YNB", name: "Yanbu \u2013 Prince Abdulmohsen Bin Abdulaziz International Airport", country: "SA", size: "large" },
  { code: "YNC", name: "Wemindji Airport", country: "CA", size: "small" },
  { code: "YND", name: "Gatineau \u2013 Ottawa / Gatineau Airport", country: "CA", size: "medium" },
  { code: "YNE", name: "Norway House Airport", country: "CA", size: "small" },
  { code: "YNJ", name: "Yanji Chaoyangchuan Airport", country: "CN", size: "medium" },
  { code: "YNL", name: "Points North Landing Airport", country: "CA", size: "medium" },
  { code: "YNO", name: "North Spirit Lake Airport", country: "CA", size: "small" },
  { code: "YNP", name: "Natuashish Airport", country: "CA", size: "small" },
  { code: "YNS", name: "Nemiscau Airport", country: "CA", size: "small" },
  { code: "YNT", name: "Yantai Penglai International Airport", country: "CN", size: "large" },
  { code: "YNY", name: "Gonghang-ro \u2013 Yangyang International Airport", country: "KR", size: "large" },
  { code: "YNZ", name: "Yancheng (Tinghu) \u2013 Yancheng Nanyang International Airport", country: "CN", size: "large" },
  { code: "YOC", name: "Old Crow Airport", country: "CA", size: "small" },
  { code: "YOG", name: "Ogoki Post Airport", country: "CA", size: "small" },
  { code: "YOH", name: "Oxford House Airport", country: "CA", size: "small" },
  { code: "YOJ", name: "High Level Airport", country: "CA", size: "medium" },
  { code: "YOL", name: "Yola Airport", country: "NG", size: "medium" },
  { code: "YON", name: "Yongphulla Airport", country: "BT", size: "small" },
  { code: "YOW", name: "Ottawa Macdonald-Cartier International Airport", country: "CA", size: "large" },
  { code: "YPA", name: "Prince Albert Glass Field", country: "CA", size: "medium" },
  { code: "YPC", name: "Paulatuk (Nora Aliqatchialuk Ruben) Airport", country: "CA", size: "small" },
  { code: "YPE", name: "Peace River Airport", country: "CA", size: "medium" },
  { code: "YPH", name: "Inukjuak Airport", country: "CA", size: "small" },
  { code: "YPJ", name: "Aupaluk Airport", country: "CA", size: "small" },
  { code: "YPL", name: "Pickle Lake Airport", country: "CA", size: "medium" },
  { code: "YPM", name: "Pikangikum Airport", country: "CA", size: "small" },
  { code: "YPN", name: "Port-Menier Airport", country: "CA", size: "medium" },
  { code: "YPO", name: "Peawanuck Airport", country: "CA", size: "small" },
  { code: "YPQ", name: "Peterborough Regional Airport", country: "CA", size: "medium" },
  { code: "YPR", name: "Prince Rupert Airport", country: "CA", size: "medium" },
  { code: "YPW", name: "Powell River Airport", country: "CA", size: "medium" },
  { code: "YPX", name: "Puvirnituq Airport", country: "CA", size: "medium" },
  { code: "YPY", name: "Fort Chipewyan Airport", country: "CA", size: "medium" },
  { code: "YPZ", name: "Burns Lake Airport", country: "CA", size: "medium" },
  { code: "YQA", name: "Gravenhurst \u2013 Muskoka Airport", country: "CA", size: "medium" },
  { code: "YQB", name: "Quebec Jean Lesage International Airport", country: "CA", size: "large" },
  { code: "YQC", name: "Quaqtaq Airport", country: "CA", size: "small" },
  { code: "YQD", name: "The Pas Airport", country: "CA", size: "medium" },
  { code: "YQG", name: "Windsor International Airport", country: "CA", size: "large" },
  { code: "YQH", name: "Watson Lake Airport", country: "CA", size: "medium" },
  { code: "YQK", name: "Kenora Airport", country: "CA", size: "medium" },
  { code: "YQL", name: "Lethbridge County Airport", country: "CA", size: "medium" },
  { code: "YQM", name: "Moncton \u2013 Greater Moncton Rom\xE9o LeBlanc International Airport", country: "CA", size: "medium" },
  { code: "YQN", name: "Nakina Airport", country: "CA", size: "medium" },
  { code: "YQQ", name: "Comox Valley International Airport / CFB Comox", country: "CA", size: "medium" },
  { code: "YQR", name: "Regina International Airport", country: "CA", size: "medium" },
  { code: "YQT", name: "Thunder Bay International Airport", country: "CA", size: "medium" },
  { code: "YQU", name: "Grande Prairie Airport", country: "CA", size: "medium" },
  { code: "YQX", name: "Gander International Airport", country: "CA", size: "medium" },
  { code: "YQY", name: "Sydney / J.A. Douglas McCurdy Airport", country: "CA", size: "medium" },
  { code: "YQZ", name: "Quesnel Airport", country: "CA", size: "medium" },
  { code: "YRA", name: "Gam\xE8t\xEC \u2013 Rae Lakes Airport", country: "CA", size: "small" },
  { code: "YRB", name: "Resolute Bay Airport", country: "CA", size: "medium" },
  { code: "YRF", name: "Cartwright Airport", country: "CA", size: "small" },
  { code: "YRG", name: "Rigolet Airport", country: "CA", size: "small" },
  { code: "YRJ", name: "Roberval Airport", country: "CA", size: "medium" },
  { code: "YRL", name: "Red Lake Airport", country: "CA", size: "medium" },
  { code: "YRO", name: "Ottawa / Rockcliffe Airport", country: "CA", size: "medium" },
  { code: "YRS", name: "Red Sucker Lake Airport", country: "CA", size: "small" },
  { code: "YRT", name: "Rankin Inlet Airport", country: "CA", size: "medium" },
  { code: "YSB", name: "Sudbury Airport", country: "CA", size: "medium" },
  { code: "YSF", name: "Stony Rapids Airport", country: "CA", size: "medium" },
  { code: "YSG", name: "Lutselk'e Airport", country: "CA", size: "small" },
  { code: "YSJ", name: "Saint John Airport", country: "CA", size: "medium" },
  { code: "YSK", name: "Sanikiluaq Airport", country: "CA", size: "small" },
  { code: "YSM", name: "Fort Smith Airport", country: "CA", size: "medium" },
  { code: "YSO", name: "Postville Airport", country: "CA", size: "small" },
  {
    code: "YSQ",
    name: "Qian Gorlos Mongol Autonomous County \u2013 Songyuan Chaganhu Airport",
    country: "CN",
    size: "medium"
  },
  { code: "YST", name: "St. Theresa Point Airport", country: "CA", size: "small" },
  { code: "YSY", name: "Sachs Harbour (David Nasogaluak Jr. Saaryuaq) Airport", country: "CA", size: "small" },
  { code: "YTE", name: "Kinngait \u2013 Cape Dorset Airport", country: "CA", size: "small" },
  { code: "YTH", name: "Thompson Airport", country: "CA", size: "medium" },
  { code: "YTL", name: "Big Trout Lake Airport", country: "CA", size: "small" },
  { code: "YTQ", name: "Tasiujaq Airport", country: "CA", size: "small" },
  { code: "YTS", name: "Timmins/Victor M. Power", country: "CA", size: "medium" },
  { code: "YTW", name: "Hotan (Yutian) \u2013 Yutian Wanfang Airport", country: "CN", size: "small" },
  { code: "YTY", name: "Yangzhou Taizhou Airport", country: "CN", size: "medium" },
  { code: "YTZ", name: "Toronto \u2013 Billy Bishop Toronto City Airport", country: "CA", size: "medium" },
  { code: "YUD", name: "Umiujaq Airport", country: "CA", size: "small" },
  {
    code: "YUL",
    name: "Montr\xE9al \u2013 Montreal / Pierre Elliott Trudeau International Airport",
    country: "CA",
    size: "large"
  },
  { code: "YUM", name: "Yuma International Airport / Marine Corps Air Station Yuma", country: "US", size: "medium" },
  { code: "YUS", name: "Yushu (Batang) \u2013 Yushu Batang Airport", country: "CN", size: "medium" },
  { code: "YUT", name: "Repulse Bay \u2013 Naujaat Airport", country: "CA", size: "small" },
  { code: "YUX", name: "Sanirajak \u2013 Hall Beach Airport", country: "CA", size: "medium" },
  { code: "YUY", name: "Rouyn-Noranda \u2013 Rouyn Noranda Airport", country: "CA", size: "medium" },
  { code: "YVB", name: "Bonaventure Airport", country: "CA", size: "medium" },
  { code: "YVC", name: "La Ronge Airport", country: "CA", size: "medium" },
  { code: "YVM", name: "Qikiqtarjuaq Airport", country: "CA", size: "small" },
  { code: "YVO", name: "Val-d'Or Airport", country: "CA", size: "medium" },
  { code: "YVP", name: "Kuujjuaq Airport", country: "CA", size: "medium" },
  { code: "YVQ", name: "Norman Wells Airport", country: "CA", size: "medium" },
  { code: "YVR", name: "Vancouver International Airport", country: "CA", size: "large" },
  { code: "YVV", name: "Wiarton Airport", country: "CA", size: "medium" },
  { code: "YVZ", name: "Deer Lake Airport", country: "CA", size: "small" },
  { code: "YWB", name: "Kangiqsujuaq (Wakeham Bay) Airport", country: "CA", size: "small" },
  { code: "YWG", name: "Winnipeg / James Armstrong Richardson International Airport", country: "CA", size: "large" },
  { code: "YWJ", name: "D\xE9line Airport", country: "CA", size: "small" },
  { code: "YWK", name: "Wabush Airport", country: "CA", size: "medium" },
  { code: "YWL", name: "Williams Lake Airport", country: "CA", size: "medium" },
  { code: "YWM", name: "Williams Harbour Airport", country: "CA", size: "small" },
  { code: "YWP", name: "Webequie Airport", country: "CA", size: "small" },
  { code: "YXC", name: "Cranbrook/Canadian Rockies International Airport", country: "CA", size: "medium" },
  { code: "YXE", name: "Saskatoon John G. Diefenbaker International Airport", country: "CA", size: "large" },
  { code: "YXH", name: "Medicine Hat Regional Airport", country: "CA", size: "medium" },
  { code: "YXJ", name: "Fort Saint John \u2013 Fort St John / North Peace Regional Airport", country: "CA", size: "medium" },
  { code: "YXL", name: "Sioux Lookout Airport", country: "CA", size: "medium" },
  { code: "YXN", name: "Whale Cove Airport", country: "CA", size: "small" },
  { code: "YXP", name: "Pangnirtung Airport", country: "CA", size: "small" },
  { code: "YXS", name: "Prince George (International) Airport", country: "CA", size: "medium" },
  { code: "YXT", name: "Terrace \u2013 Northwest Regional Airport Terrace-Kitimat", country: "CA", size: "medium" },
  { code: "YXU", name: "London International Airport", country: "CA", size: "medium" },
  { code: "YXX", name: "Abbotsford International Airport", country: "CA", size: "medium" },
  { code: "YXY", name: "Whitehorse / Erik Nielsen International Airport", country: "CA", size: "medium" },
  { code: "YYA", name: "Yueyang (Yueyanglou) \u2013 Yueyang Sanhe Airport", country: "CN", size: "medium" },
  { code: "YYB", name: "North Bay Jack Garland Airport", country: "CA", size: "medium" },
  { code: "YYC", name: "Calgary International Airport", country: "CA", size: "large" },
  { code: "YYD", name: "Smithers Airport", country: "CA", size: "medium" },
  { code: "YYE", name: "Fort Nelson Airport", country: "CA", size: "medium" },
  { code: "YYF", name: "Penticton Airport", country: "CA", size: "medium" },
  { code: "YYG", name: "Charlottetown Airport", country: "CA", size: "medium" },
  { code: "YYH", name: "Taloyoak Airport", country: "CA", size: "small" },
  { code: "YYJ", name: "Victoria International Airport", country: "CA", size: "large" },
  { code: "YYL", name: "Lynn Lake Airport", country: "CA", size: "medium" },
  { code: "YYQ", name: "Churchill Airport", country: "CA", size: "medium" },
  { code: "YYR", name: "Goose Bay Airport", country: "CA", size: "medium" },
  { code: "YYT", name: "St. John's International Airport", country: "CA", size: "large" },
  { code: "YYY", name: "Mont-Joli \u2013 Mont Joli Airport", country: "CA", size: "medium" },
  { code: "YYZ", name: "Toronto Pearson International Airport", country: "CA", size: "large" },
  { code: "YZF", name: "Yellowknife International Airport", country: "CA", size: "medium" },
  { code: "YZG", name: "Salluit Airport", country: "CA", size: "small" },
  { code: "YZP", name: "Sandspit Airport", country: "CA", size: "medium" },
  { code: "YZS", name: "Coral Harbour Airport", country: "CA", size: "medium" },
  { code: "YZT", name: "Port Hardy Airport", country: "CA", size: "medium" },
  { code: "YZU", name: "Whitecourt Airport", country: "CA", size: "medium" },
  { code: "YZV", name: "Sept-\xCEles Airport", country: "CA", size: "medium" },
  { code: "YZY", name: "Zhangye (Ganzhou) \u2013 Zhangye Ganzhou Airport", country: "CN", size: "medium" },
  { code: "YZZ", name: "Trail Regional Airport", country: "CA", size: "small" },
  { code: "ZAD", name: "Zadar Airport", country: "HR", size: "large" },
  { code: "ZAG", name: "Velika Gorica \u2013 Zagreb Franjo Tu\u0111man International Airport", country: "HR", size: "large" },
  { code: "ZAH", name: "Zahedan International Airport", country: "IR", size: "large" },
  { code: "ZAL", name: "Valdivia \u2013 Pichoy Airport", country: "CL", size: "medium" },
  { code: "ZAM", name: "Zamboanga International Airport", country: "PH", size: "large" },
  { code: "ZAT", name: "Zhaotong Zhaoyang Airport", country: "CN", size: "medium" },
  { code: "ZAZ", name: "Zaragoza Airport", country: "ES", size: "large" },
  { code: "ZBF", name: "South Tetagouche \u2013 Bathurst Airport", country: "CA", size: "medium" },
  { code: "ZBR", name: "Konarak \u2013 Chabahar Konarak International Airport", country: "IR", size: "medium" },
  { code: "ZCL", name: "Zacatecas \u2013 General Leobardo C. Ruiz International Airport", country: "MX", size: "medium" },
  { code: "ZCO", name: "Temuco \u2013 La Araucan\xEDa International Airport", country: "CL", size: "large" },
  { code: "ZDY", name: "Delma Island \u2013 Delma Airport", country: "AE", size: "small" },
  { code: "ZEL", name: "Bella Bella (Campbell Island) Airport", country: "CA", size: "medium" },
  { code: "ZEM", name: "Eastmain River Airport", country: "CA", size: "small" },
  { code: "ZFD", name: "Fond-du-Lac Airport", country: "CA", size: "small" },
  { code: "ZFL", name: "Zhaosu Tianma Airport", country: "CN", size: "small" },
  { code: "ZFM", name: "Fort Mcpherson Airport", country: "CA", size: "small" },
  { code: "ZFN", name: "Tulita Airport", country: "CA", size: "small" },
  { code: "ZGI", name: "Gods River Airport", country: "CA", size: "small" },
  { code: "ZGS", name: "Le Golfe-du-Saint-Laurent \u2013 La Romaine Airport", country: "CA", size: "small" },
  { code: "ZHA", name: "Zhanjiang Wuchuan International Airport", country: "CN", size: "large" },
  { code: "ZHY", name: "Zhongwei (Shapotou) \u2013 Zhongwei Shapotou Airport", country: "CN", size: "medium" },
  { code: "ZIA", name: "Moscow \u2013 Zhukovsky International Airport", country: "RU", size: "large" },
  { code: "ZIG", name: "Ziguinchor Airport", country: "SN", size: "medium" },
  { code: "ZIH", name: "Ixtapa-Zihuatanejo International Airport", country: "MX", size: "large" },
  { code: "ZIX", name: "Zhigansk Airport", country: "RU", size: "medium" },
  { code: "ZKE", name: "Kashechewan Airport", country: "CA", size: "small" },
  { code: "ZKP", name: "Zyryanka Airport", country: "RU", size: "medium" },
  { code: "ZLO", name: "Manzanillo \u2013 Playa de Oro International Airport", country: "MX", size: "medium" },
  { code: "ZLT", name: "La Tabati\xE8re Airport", country: "CA", size: "small" },
  { code: "ZMT", name: "Masset Airport", country: "CA", size: "medium" },
  { code: "ZND", name: "Zinder Airport", country: "NE", size: "medium" },
  { code: "ZNE", name: "Newman Airport", country: "AU", size: "medium" },
  { code: "ZNZ", name: "Zanzibar \u2013 Abeid Amani Karume International Airport", country: "TZ", size: "large" },
  { code: "ZOS", name: "Osorno \u2013 Ca\xF1al Bajo Carlos Hott Siebert Airport", country: "CL", size: "medium" },
  { code: "ZPB", name: "Sachigo Lake Airport", country: "CA", size: "small" },
  { code: "ZPC", name: "Pucon \u2013 Puc\xF3n Airport", country: "CL", size: "small" },
  { code: "ZQN", name: "Queenstown Airport", country: "NZ", size: "large" },
  { code: "ZQZ", name: "Zhangjiakou Ningyuan Airport", country: "CN", size: "medium" },
  { code: "ZRH", name: "Zurich \u2013 Z\xFCrich Airport", country: "CH", size: "large" },
  { code: "ZRJ", name: "Round Lake (Weagamow Lake) Airport", country: "CA", size: "small" },
  { code: "ZSA", name: "San Salvador International Airport", country: "BS", size: "large" },
  { code: "ZSE", name: "Saint-Pierre Pierrefonds Airport", country: "RE", size: "large" },
  { code: "ZSJ", name: "Sandy Lake Airport", country: "CA", size: "medium" },
  { code: "ZTB", name: "T\xEAte-\xE0-la-Baleine Airport", country: "CA", size: "small" },
  { code: "ZTH", name: "Zakynthos International Airport Dionysios Solomos", country: "GR", size: "medium" },
  { code: "ZTM", name: "Shamattawa Airport", country: "CA", size: "small" },
  { code: "ZUH", name: "Zhuhai (Jinwan) \u2013 Zhuhai Jinwan Airport", country: "CN", size: "large" },
  { code: "ZUM", name: "Churchill Falls Airport", country: "CA", size: "small" },
  { code: "ZWL", name: "Wollaston Lake Airport", country: "CA", size: "small" },
  { code: "ZYI", name: "Zunyi Xinzhou Airport", country: "CN", size: "medium" },
  { code: "ZYL", name: "Sylhet \u2013 Osmany International Airport", country: "BD", size: "large" }
];
var AIRPORT_CODES = AIRPORTS.map((airport) => airport.code);
var AIRPORT_CODES_SET = new Set(AIRPORT_CODES);
var AIRPORTS_BY_CODE = new Map(
  AIRPORTS.map((airport) => [airport.code, airport])
);

// src/airport-input.ts
var AIRPORT_VALUE_MAX_LENGTH = 1e3;
function isAllowedAirportInput(value) {
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= AIRPORT_VALUE_MAX_LENGTH;
}

// src/airport-hubs.ts
var AIRPORT_HUBS_BY_COUNTRY = {
  PL: ["WAW", "KRK", "GDN", "WRO", "KTW", "POZ", "WMI", "RZE", "LUZ", "SZZ", "BZG", "LCJ", "SZY", "IEG", "RDO"],
  US: [
    "ATL",
    "DFW",
    "DEN",
    "ORD",
    "LAX",
    "JFK",
    "LAS",
    "MCO",
    "MIA",
    "CLT",
    "SEA",
    "EWR",
    "PHX",
    "IAH",
    "BOS",
    "MSP",
    "SFO",
    "DTW",
    "FLL",
    "LGA",
    "BWI",
    "IAD",
    "SLC",
    "DCA",
    "SAN",
    "TPA",
    "PHL",
    "BNA",
    "AUS",
    "MDW"
  ]
};

// src/polish-airport-names.json
var polish_airport_names_default = {
  WAW: "Warszawa \u2013 Lotnisko Chopina",
  WMI: "Warszawa \u2013 Modlin",
  RDO: "Warszawa \u2013 Radom",
  KRK: "Krak\xF3w \u2013 Balice",
  GDN: "Gda\u0144sk \u2013 R\u0119biechowo",
  WRO: "Wroc\u0142aw \u2013 Strachowice",
  POZ: "Pozna\u0144 \u2013 \u0141awica",
  KTW: "Katowice \u2013 Pyrzowice",
  LUZ: "Lublin \u2013 \u015Awidnik",
  SZZ: "Szczecin \u2013 Goleni\xF3w",
  RZE: "Rzesz\xF3w \u2013 Jasionka",
  BZG: "Bydgoszcz \u2013 Szwederowo",
  SZY: "Olsztyn \u2013 Mazury (Szymany)",
  IEG: "Zielona G\xF3ra \u2013 Babimost",
  LCJ: "\u0141\xF3d\u017A \u2013 Lublinek"
};

// src/polish-airport-names.ts
var POLISH_AIRPORT_NAMES = polish_airport_names_default;

// src/airport-city-aliases.json
var airport_city_aliases_default = {
  "Abu Dhabi": ["Abu Zabi"],
  "Addis Ababa": ["Addis Abeba"],
  Algiers: ["Algier"],
  Antananarivo: ["Antananarywa"],
  Asunci\u00F3n: ["Asuncion"],
  Athens: ["Ateny"],
  Baghdad: ["Bagdad"],
  Beijing: ["Pekin"],
  Beirut: ["Bejrut"],
  Belgrade: ["Belgrad"],
  Bratislava: ["Bratys\u0142awa"],
  Brussels: ["Bruksela"],
  Bucharest: ["Bukareszt"],
  Budapest: ["Budapeszt"],
  Cairo: ["Kair"],
  "Cape Town": ["Kapsztad"],
  Cologne: ["Kolonia"],
  Colombo: ["Kolombo"],
  Copenhagen: ["Kopenhaga"],
  Damascus: ["Damaszek"],
  Dhaka: ["Dakka"],
  Dubai: ["Dubaj"],
  Edinburgh: ["Edynburg"],
  Florence: ["Florencja"],
  Geneva: ["Genewa"],
  Genoa: ["Genua"],
  "Guatemala City": ["Gwatemala"],
  Havana: ["Hawana"],
  "Hong Kong": ["Hongkong"],
  Istanbul: ["Stambu\u0142"],
  Jakarta: ["D\u017Cakarta"],
  Kathmandu: ["Katmandu"],
  Khartoum: ["Chartum"],
  Kinshasa: ["Kinszasa"],
  "Kuwait City": ["Kuwejt"],
  Lisbon: ["Lizbona"],
  Ljubljana: ["Lublana"],
  London: ["Londyn"],
  Luxembourg: ["Luksemburg"],
  Madrid: ["Madryt"],
  Marseille: ["Marsylia"],
  "Mexico City": ["Meksyk"],
  Milan: ["Mediolan"],
  Milano: ["Mediolan"],
  Minsk: ["Mi\u0144sk"],
  Moscow: ["Moskwa"],
  Mumbai: ["Mumbaj"],
  Munich: ["Monachium"],
  Muscat: ["Maskat"],
  Naples: ["Neapol"],
  "New Delhi": ["Nowe Delhi"],
  "New Orleans": ["Nowy Orlean"],
  "New York": ["Nowy Jork", "NYC"],
  Nice: ["Nicea"],
  Nuremberg: ["Norymberga"],
  Paris: ["Pary\u017C"],
  Philadelphia: ["Filadelfia"],
  Prague: ["Praga"],
  Pyongyang: ["Phenian"],
  Reykjavik: ["Rejkiawik"],
  Riga: ["Ryga"],
  Riyadh: ["Rijad"],
  Rome: ["Rzym"],
  Sarajevo: ["Sarajewo"],
  Seoul: ["Seul"],
  Seville: ["Sewilla"],
  Shanghai: ["Szanghaj"],
  Singapore: ["Singapur"],
  Stockholm: ["Sztokholm"],
  Taipei: ["Tajpej"],
  Tallinn: ["Tallin"],
  Tashkent: ["Taszkent"],
  Tehran: ["Teheran"],
  "Tel Aviv": ["Tel Awiw"],
  Tokyo: ["Tokio"],
  Tripoli: ["Trypolis"],
  Turin: ["Turyn"],
  Ulaanbaatar: ["U\u0142an Bator"],
  Valencia: ["Walencja"],
  Venice: ["Wenecja"],
  Vienna: ["Wiede\u0144"],
  Vilnius: ["Wilno"],
  Washington: ["Waszyngton"],
  Windhoek: ["Windhuk"],
  Yangon: ["Rangun"],
  Yerevan: ["Erywa\u0144"],
  Zagreb: ["Zagrzeb"],
  Zurich: ["Zurych"]
};

// src/airport-search-aliases.ts
var AIRPORT_CITY_ALIASES = airport_city_aliases_default;
var PLACE_ALIAS_ENTRIES = Object.entries(AIRPORT_CITY_ALIASES).sort(
  (left, right) => right[0].length - left[0].length
);
var escapeRegExp = (value) => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};
var foldDiacritics = (value) => {
  return value.normalize("NFD").replace(/\p{M}/gu, "");
};
var nameIncludesPlace = (name, place) => {
  const foldedName = foldDiacritics(name);
  const foldedPlace = foldDiacritics(place);
  const pattern = new RegExp(`(?<![\\p{L}-])${escapeRegExp(foldedPlace)}(?![\\p{L}])`, "iu");
  return pattern.test(foldedName);
};
var getAirportSearchAliases = (name) => {
  const aliases = /* @__PURE__ */ new Set();
  for (const [place, placeAliases] of PLACE_ALIAS_ENTRIES) {
    if (!nameIncludesPlace(name, place)) continue;
    for (const alias of placeAliases) {
      aliases.add(alias);
    }
  }
  return [...aliases];
};

// src/airport-preference.ts
var SIZE_RANK = {
  large: 0,
  medium: 1,
  small: 2
};
var HUB_RANK_BY_PREFERENCE = {
  pl: new Map(AIRPORT_HUBS_BY_COUNTRY.PL.map((code, index) => [code, index])),
  us: new Map(AIRPORT_HUBS_BY_COUNTRY.US.map((code, index) => [code, index]))
};
var preferenceCountry = (preference) => {
  return preference === "pl" ? "PL" : "US";
};
var compareAirports = (left, right, preference) => {
  const hubRank = preference === "pl" ? HUB_RANK_BY_PREFERENCE.pl : HUB_RANK_BY_PREFERENCE.us;
  const leftHub = hubRank.get(left.code);
  const rightHub = hubRank.get(right.code);
  const leftIsHub = leftHub != null;
  const rightIsHub = rightHub != null;
  if (leftIsHub && rightIsHub) {
    return (leftHub ?? 0) - (rightHub ?? 0);
  }
  if (leftIsHub) return -1;
  if (rightIsHub) return 1;
  const country = preferenceCountry(preference);
  const leftPreferredCountry = left.country === country;
  const rightPreferredCountry = right.country === country;
  if (leftPreferredCountry !== rightPreferredCountry) {
    return leftPreferredCountry ? -1 : 1;
  }
  const sizeDelta = (SIZE_RANK[left.size] ?? 2) - (SIZE_RANK[right.size] ?? 2);
  if (sizeDelta !== 0) {
    return sizeDelta;
  }
  return left.name.localeCompare(right.name, "en");
};
var getAirportPreferenceForField = (fieldKey) => {
  const isReturn = fieldKey.startsWith("return");
  const isDestination = fieldKey.endsWith("DestinationAirport");
  const isDeparture = fieldKey.endsWith("DepartureAirport");
  if (isDestination) {
    return isReturn ? "us" : "pl";
  }
  if (isDeparture) {
    return isReturn ? "pl" : "us";
  }
  return "us";
};
var sortAirportsForPreference = (airports, preference) => {
  return [...airports].sort((left, right) => compareAirports(left, right, preference));
};

// src/accommodation-categories.ts
var ACCOMMODATION_CATEGORIES = [
  { value: "5", stars: 5, translations: { pl: "5", en: "5" } },
  { value: "4", stars: 4, translations: { pl: "4", en: "4" } },
  { value: "3", stars: 3, translations: { pl: "3", en: "3" } },
  { value: "2", stars: 2, translations: { pl: "2", en: "2" } },
  { value: "other", stars: 0, translations: { pl: "Inne", en: "Other" } }
];
var ACCOMMODATION_CATEGORY_VALUES = ACCOMMODATION_CATEGORIES.map((c) => c.value);
var ACCOMMODATION_CATEGORY_VALUES_SET = new Set(ACCOMMODATION_CATEGORY_VALUES);

// src/countries.ts
import countries from "i18n-iso-countries";
var COUNTRY_CODES = Object.keys(countries.getAlpha2Codes());
var COUNTRY_CODES_SET = new Set(COUNTRY_CODES);

// src/genders.ts
var Gender = {
  MALE: "male",
  FEMALE: "female",
  OTHER: "other",
  PREFER_NOT_TO_SAY: "prefer_not_to_say"
};
var GENDER_VALUES = Object.values(Gender);
var GENDERS = [
  { value: Gender.MALE, translations: { pl: "M\u0119\u017Cczyzna", en: "Male" } },
  { value: Gender.FEMALE, translations: { pl: "Kobieta", en: "Female" } },
  { value: Gender.OTHER, translations: { pl: "Inna", en: "Other" } },
  { value: Gender.PREFER_NOT_TO_SAY, translations: { pl: "Wol\u0119 nie podawa\u0107", en: "Prefer not to say" } }
];
var GENDER_VALUES_SET = new Set(GENDER_VALUES);

// src/contact-emails.ts
var CONTACT_EMAIL_BY_LANGUAGE = {
  pl: "kontakt@poloniamedica.com",
  en: "contact@poloniamedica.com"
};
var FALLBACK_EMAIL = "contact@poloniamedica.com";
function getContactEmail(lang) {
  if (lang && lang in CONTACT_EMAIL_BY_LANGUAGE) {
    return CONTACT_EMAIL_BY_LANGUAGE[lang] ?? FALLBACK_EMAIL;
  }
  return FALLBACK_EMAIL;
}

// src/contact.ts
var CONTACT_PHONE_E164 = "+48343241183";
var CONTACT_PHONE_DISPLAY = "+48 34 324 11 83";
var CONTACT_OPERATOR_LEGAL_NAME = "Cargo & Travel Agency Jan Korsak";
var CONTACT_ADDRESS_BY_LANGUAGE = {
  pl: {
    name: "Polonia Medica",
    street: "ul. \u015Aw Rocha 31",
    zip: "42-202",
    city: "Cz\u0119stochowa",
    country: "POLAND"
  },
  en: {
    name: "Polonia Medica",
    street: "Sw Rocha 31",
    zip: "42-202",
    city: "Czestochowa",
    country: "POLAND"
  }
};
var FALLBACK_ADDRESS = CONTACT_ADDRESS_BY_LANGUAGE.en;
function getContactAddress(lang) {
  if (lang === "pl" || lang === "en") {
    return { ...CONTACT_ADDRESS_BY_LANGUAGE[lang] };
  }
  return { ...FALLBACK_ADDRESS };
}
function getContactPhoneDisplay() {
  return CONTACT_PHONE_DISPLAY;
}
function formatContactAddressBlock(address) {
  return [address.name, address.street, `${address.zip} ${address.city}`, address.country].join("\n");
}

// src/voivodeships.ts
var VOIVODESHIPS = [
  { id: 1, name: "dolno\u015Bl\u0105skie" },
  { id: 2, name: "kujawsko-pomorskie" },
  { id: 3, name: "lubelskie" },
  { id: 4, name: "lubuskie" },
  { id: 5, name: "\u0142\xF3dzkie" },
  { id: 6, name: "ma\u0142opolskie" },
  { id: 7, name: "mazowieckie" },
  { id: 8, name: "opolskie" },
  { id: 9, name: "podkarpackie" },
  { id: 10, name: "podlaskie" },
  { id: 11, name: "pomorskie" },
  { id: 12, name: "\u015Bl\u0105skie" },
  { id: 13, name: "\u015Bwi\u0119tokrzyskie" },
  { id: 14, name: "warmi\u0144sko-mazurskie" },
  { id: 15, name: "wielkopolskie" },
  { id: 16, name: "zachodniopomorskie" }
];
var VOIVODESHIP_NAMES = VOIVODESHIPS.map((v) => v.name);

// src/cities-data.json
var cities_data_default = [
  { name: "Aleksandr\xF3w Kujawski", voivodeshipId: 2 },
  { name: "Aleksandr\xF3w \u0141\xF3dzki", voivodeshipId: 5 },
  { name: "Alwernia", voivodeshipId: 6 },
  { name: "Andrych\xF3w", voivodeshipId: 6 },
  { name: "Annopol", voivodeshipId: 3 },
  { name: "August\xF3w", voivodeshipId: 10 },
  { name: "Babimost", voivodeshipId: 4 },
  { name: "Babor\xF3w", voivodeshipId: 8 },
  { name: "Baran\xF3w Sandomierski", voivodeshipId: 9 },
  { name: "Barcin", voivodeshipId: 2 },
  { name: "Barczewo", voivodeshipId: 14 },
  { name: "Bardo", voivodeshipId: 1 },
  { name: "Barlinek", voivodeshipId: 16 },
  { name: "Bartoszyce", voivodeshipId: 14 },
  { name: "Barwice", voivodeshipId: 16 },
  { name: "Be\u0142chat\xF3w", voivodeshipId: 5 },
  { name: "Be\u0142\u017Cyce", voivodeshipId: 3 },
  { name: "B\u0119dzin", voivodeshipId: 12 },
  { name: "Bia\u0142a", voivodeshipId: 8 },
  { name: "Bia\u0142a Piska", voivodeshipId: 14 },
  { name: "Bia\u0142a Podlaska", voivodeshipId: 3 },
  { name: "Bia\u0142a Rawska", voivodeshipId: 5 },
  { name: "Bia\u0142obrzegi", voivodeshipId: 7 },
  { name: "Bia\u0142ogard", voivodeshipId: 16 },
  { name: "Bia\u0142y B\xF3r", voivodeshipId: 16 },
  { name: "Bia\u0142ystok", voivodeshipId: 10 },
  { name: "Biecz", voivodeshipId: 6 },
  { name: "Bielawa", voivodeshipId: 1 },
  { name: "Bielsk Podlaski", voivodeshipId: 10 },
  { name: "Bielsko-Bia\u0142a", voivodeshipId: 12 },
  { name: "Bieru\u0144", voivodeshipId: 12 },
  { name: "Bierut\xF3w", voivodeshipId: 1 },
  { name: "Bie\u017Cu\u0144", voivodeshipId: 7 },
  { name: "Bi\u0142goraj", voivodeshipId: 3 },
  { name: "Biskupiec", voivodeshipId: 14 },
  { name: "Bisztynek", voivodeshipId: 14 },
  { name: "Blachownia", voivodeshipId: 12 },
  { name: "B\u0142aszki", voivodeshipId: 5 },
  { name: "B\u0142a\u017Cowa", voivodeshipId: 9 },
  { name: "B\u0142onie", voivodeshipId: 7 },
  { name: "Bobolice", voivodeshipId: 16 },
  { name: "Bobowa", voivodeshipId: 6 },
  { name: "Bochnia", voivodeshipId: 6 },
  { name: "Bodzentyn", voivodeshipId: 13 },
  { name: "Bogatynia", voivodeshipId: 1 },
  { name: "Boguchwa\u0142a", voivodeshipId: 9 },
  { name: "Bogusz\xF3w-Gorce", voivodeshipId: 1 },
  { name: "Bojanowo", voivodeshipId: 15 },
  { name: "Boles\u0142awiec", voivodeshipId: 1 },
  { name: "Bolk\xF3w", voivodeshipId: 1 },
  { name: "Borek Wielkopolski", voivodeshipId: 15 },
  { name: "Borne Sulinowo", voivodeshipId: 16 },
  { name: "Braniewo", voivodeshipId: 14 },
  { name: "Bra\u0144sk", voivodeshipId: 10 },
  { name: "Brodnica", voivodeshipId: 2 },
  { name: "Brusy", voivodeshipId: 11 },
  { name: "Brwin\xF3w", voivodeshipId: 7 },
  { name: "Brzeg", voivodeshipId: 8 },
  { name: "Brzeg Dolny", voivodeshipId: 1 },
  { name: "Brzesko", voivodeshipId: 6 },
  { name: "Brzeszcze", voivodeshipId: 6 },
  { name: "Brze\u015B\u0107 Kujawski", voivodeshipId: 2 },
  { name: "Brzeziny", voivodeshipId: 5 },
  { name: "Brzostek", voivodeshipId: 9 },
  { name: "Brzoz\xF3w", voivodeshipId: 9 },
  { name: "Budzy\u0144", voivodeshipId: 15 },
  { name: "Buk", voivodeshipId: 15 },
  { name: "Bukowno", voivodeshipId: 6 },
  { name: "Busko-Zdr\xF3j", voivodeshipId: 13 },
  { name: "Bychawa", voivodeshipId: 3 },
  { name: "Byczyna", voivodeshipId: 8 },
  { name: "Bydgoszcz", voivodeshipId: 2 },
  { name: "Bystrzyca K\u0142odzka", voivodeshipId: 1 },
  { name: "Bytom", voivodeshipId: 12 },
  { name: "Bytom Odrza\u0144ski", voivodeshipId: 4 },
  { name: "Byt\xF3w", voivodeshipId: 11 },
  { name: "Cedynia", voivodeshipId: 16 },
  { name: "Che\u0142m", voivodeshipId: 3 },
  { name: "Che\u0142mek", voivodeshipId: 6 },
  { name: "Che\u0142mno", voivodeshipId: 2 },
  { name: "Che\u0142m\u017Ca", voivodeshipId: 2 },
  { name: "Ch\u0119ciny", voivodeshipId: 13 },
  { name: "Chmielnik", voivodeshipId: 13 },
  { name: "Chocian\xF3w", voivodeshipId: 1 },
  { name: "Chociwel", voivodeshipId: 16 },
  { name: "Chocz", voivodeshipId: 15 },
  { name: "Chodecz", voivodeshipId: 2 },
  { name: "Chodzie\u017C", voivodeshipId: 15 },
  { name: "Chojna", voivodeshipId: 16 },
  { name: "Chojnice", voivodeshipId: 11 },
  { name: "Chojn\xF3w", voivodeshipId: 1 },
  { name: "Choroszcz", voivodeshipId: 10 },
  { name: "Chorzele", voivodeshipId: 7 },
  { name: "Chorz\xF3w", voivodeshipId: 12 },
  { name: "Choszczno", voivodeshipId: 16 },
  { name: "Chrzan\xF3w", voivodeshipId: 6 },
  { name: "Ciechanowiec", voivodeshipId: 10 },
  { name: "Ciechan\xF3w", voivodeshipId: 7 },
  { name: "Ciechocinek", voivodeshipId: 2 },
  { name: "Cieszan\xF3w", voivodeshipId: 9 },
  { name: "Cieszyn", voivodeshipId: 12 },
  { name: "Ci\u0119\u017Ckowice", voivodeshipId: 6 },
  { name: "Cybinka", voivodeshipId: 4 },
  { name: "Czaplinek", voivodeshipId: 16 },
  { name: "Czarna Bia\u0142ostocka", voivodeshipId: 10 },
  { name: "Czarna Woda", voivodeshipId: 11 },
  { name: "Czarne", voivodeshipId: 11 },
  { name: "Czarnk\xF3w", voivodeshipId: 15 },
  { name: "Czch\xF3w", voivodeshipId: 6 },
  { name: "Czechowice-Dziedzice", voivodeshipId: 12 },
  { name: "Czelad\u017A", voivodeshipId: 12 },
  { name: "Czempi\u0144", voivodeshipId: 15 },
  { name: "Czerniejewo", voivodeshipId: 15 },
  { name: "Czersk", voivodeshipId: 11 },
  { name: "Czerwie\u0144sk", voivodeshipId: 4 },
  { name: "Czerwi\u0144sk nad Wis\u0142\u0105", voivodeshipId: 7 },
  { name: "Czerwionka-Leszczyny", voivodeshipId: 12 },
  { name: "Cz\u0119stochowa", voivodeshipId: 12 },
  { name: "Cz\u0142opa", voivodeshipId: 16 },
  { name: "Cz\u0142uch\xF3w", voivodeshipId: 11 },
  { name: "Czy\u017Cew", voivodeshipId: 10 },
  { name: "\u0106miel\xF3w", voivodeshipId: 13 },
  { name: "Daleszyce", voivodeshipId: 13 },
  { name: "Dar\u0142owo", voivodeshipId: 16 },
  { name: "D\u0105bie", voivodeshipId: 15 },
  { name: "D\u0105browa Bia\u0142ostocka", voivodeshipId: 10 },
  { name: "D\u0105browa G\xF3rnicza", voivodeshipId: 12 },
  { name: "D\u0105browa Tarnowska", voivodeshipId: 6 },
  { name: "Debrzno", voivodeshipId: 11 },
  { name: "D\u0119bica", voivodeshipId: 9 },
  { name: "D\u0119blin", voivodeshipId: 3 },
  { name: "D\u0119bno", voivodeshipId: 16 },
  { name: "Dobczyce", voivodeshipId: 6 },
  { name: "Dobiegniew", voivodeshipId: 4 },
  { name: "Dobra", voivodeshipId: 15 },
  { name: "Dobra", voivodeshipId: 16 },
  { name: "Dobre Miasto", voivodeshipId: 14 },
  { name: "Dobrodzie\u0144", voivodeshipId: 8 },
  { name: "Dobrzany", voivodeshipId: 16 },
  { name: "Dobrzyca", voivodeshipId: 15 },
  { name: "Dobrzy\u0144 nad Wis\u0142\u0105", voivodeshipId: 2 },
  { name: "Dolsk", voivodeshipId: 15 },
  { name: "Drawno", voivodeshipId: 16 },
  { name: "Drawsko Pomorskie", voivodeshipId: 16 },
  { name: "Drezdenko", voivodeshipId: 4 },
  { name: "Drobin", voivodeshipId: 7 },
  { name: "Drohiczyn", voivodeshipId: 10 },
  { name: "Drzewica", voivodeshipId: 5 },
  { name: "Dubiecko", voivodeshipId: 9 },
  { name: "Dukla", voivodeshipId: 9 },
  { name: "Duszniki-Zdr\xF3j", voivodeshipId: 1 },
  { name: "Dyn\xF3w", voivodeshipId: 9 },
  { name: "Dzia\u0142dowo", voivodeshipId: 14 },
  { name: "Dzia\u0142oszyce", voivodeshipId: 13 },
  { name: "Dzia\u0142oszyn", voivodeshipId: 5 },
  { name: "Dzierzgo\u0144", voivodeshipId: 11 },
  { name: "Dzier\u017Coni\xF3w", voivodeshipId: 1 },
  { name: "Dziwn\xF3w", voivodeshipId: 16 },
  { name: "Elbl\u0105g", voivodeshipId: 14 },
  { name: "E\u0142k", voivodeshipId: 14 },
  { name: "Frampol", voivodeshipId: 3 },
  { name: "Frombork", voivodeshipId: 14 },
  { name: "Garwolin", voivodeshipId: 7 },
  { name: "G\u0105bin", voivodeshipId: 7 },
  { name: "Gda\u0144sk", voivodeshipId: 11 },
  { name: "Gdynia", voivodeshipId: 11 },
  { name: "Gi\u017Cycko", voivodeshipId: 14 },
  { name: "Glinojeck", voivodeshipId: 7 },
  { name: "Gliwice", voivodeshipId: 12 },
  { name: "G\u0142og\xF3w", voivodeshipId: 1 },
  { name: "G\u0142og\xF3w Ma\u0142opolski", voivodeshipId: 9 },
  { name: "G\u0142og\xF3wek", voivodeshipId: 8 },
  { name: "G\u0142owno", voivodeshipId: 5 },
  { name: "G\u0142ubczyce", voivodeshipId: 8 },
  { name: "G\u0142ucho\u0142azy", voivodeshipId: 8 },
  { name: "G\u0142uszyca", voivodeshipId: 1 },
  { name: "Gniew", voivodeshipId: 11 },
  { name: "Gniewkowo", voivodeshipId: 2 },
  { name: "Gniezno", voivodeshipId: 15 },
  { name: "Gogolin", voivodeshipId: 8 },
  { name: "Golczewo", voivodeshipId: 16 },
  { name: "Goleni\xF3w", voivodeshipId: 16 },
  { name: "Golina", voivodeshipId: 15 },
  { name: "Golub-Dobrzy\u0144", voivodeshipId: 2 },
  { name: "Go\u0142a\u0144cz", voivodeshipId: 15 },
  { name: "Go\u0142dap", voivodeshipId: 14 },
  { name: "Goni\u0105dz", voivodeshipId: 10 },
  { name: "Goraj", voivodeshipId: 3 },
  { name: "Gorlice", voivodeshipId: 6 },
  { name: "Gorz\xF3w \u015Al\u0105ski", voivodeshipId: 8 },
  { name: "Gorz\xF3w Wielkopolski", voivodeshipId: 4 },
  { name: "Gostynin", voivodeshipId: 7 },
  { name: "Gosty\u0144", voivodeshipId: 15 },
  { name: "Go\u015Bcino", voivodeshipId: 16 },
  { name: "Gozdnica", voivodeshipId: 4 },
  { name: "G\xF3ra", voivodeshipId: 1 },
  { name: "G\xF3ra Kalwaria", voivodeshipId: 7 },
  { name: "G\xF3rowo I\u0142aweckie", voivodeshipId: 14 },
  { name: "G\xF3rzno", voivodeshipId: 2 },
  { name: "Grab\xF3w nad Prosn\u0105", voivodeshipId: 15 },
  { name: "Grajewo", voivodeshipId: 10 },
  { name: "Grodk\xF3w", voivodeshipId: 8 },
  { name: "Grodzisk Mazowiecki", voivodeshipId: 7 },
  { name: "Grodzisk Wielkopolski", voivodeshipId: 15 },
  { name: "Gr\xF3jec", voivodeshipId: 7 },
  { name: "Grudzi\u0105dz", voivodeshipId: 2 },
  { name: "Gryb\xF3w", voivodeshipId: 6 },
  { name: "Gryfice", voivodeshipId: 16 },
  { name: "Gryfino", voivodeshipId: 16 },
  { name: "Gryf\xF3w \u015Al\u0105ski", voivodeshipId: 1 },
  { name: "Gubin", voivodeshipId: 4 },
  { name: "Hajn\xF3wka", voivodeshipId: 10 },
  { name: "Halin\xF3w", voivodeshipId: 7 },
  { name: "Hel", voivodeshipId: 11 },
  { name: "Hrubiesz\xF3w", voivodeshipId: 3 },
  { name: "I\u0142awa", voivodeshipId: 14 },
  { name: "I\u0142owa", voivodeshipId: 4 },
  { name: "I\u0142\u017Ca", voivodeshipId: 7 },
  { name: "Imielin", voivodeshipId: 12 },
  { name: "Inowroc\u0142aw", voivodeshipId: 2 },
  { name: "I\u0144sko", voivodeshipId: 16 },
  { name: "Iwonicz-Zdr\xF3j", voivodeshipId: 9 },
  { name: "Izbica Kujawska", voivodeshipId: 2 },
  { name: "Jab\u0142onowo Pomorskie", voivodeshipId: 2 },
  { name: "Janikowo", voivodeshipId: 2 },
  { name: "Janowiec Wielkopolski", voivodeshipId: 2 },
  { name: "Jan\xF3w Lubelski", voivodeshipId: 3 },
  { name: "Jaraczewo", voivodeshipId: 15 },
  { name: "Jarocin", voivodeshipId: 15 },
  { name: "Jaros\u0142aw", voivodeshipId: 9 },
  { name: "Jasie\u0144", voivodeshipId: 4 },
  { name: "Jas\u0142o", voivodeshipId: 9 },
  { name: "Jastarnia", voivodeshipId: 11 },
  { name: "Jastrowie", voivodeshipId: 15 },
  { name: "Jastrz\u0119bie-Zdr\xF3j", voivodeshipId: 12 },
  { name: "Jawor", voivodeshipId: 1 },
  { name: "Jaworzno", voivodeshipId: 12 },
  { name: "Jaworzyna \u015Al\u0105ska", voivodeshipId: 1 },
  { name: "Jedlicze", voivodeshipId: 9 },
  { name: "Jedlina-Zdr\xF3j", voivodeshipId: 1 },
  { name: "Jedwabne", voivodeshipId: 10 },
  { name: "Jelcz-Laskowice", voivodeshipId: 1 },
  { name: "Jelenia G\xF3ra", voivodeshipId: 1 },
  { name: "Jeziorany", voivodeshipId: 14 },
  { name: "J\u0119drzej\xF3w", voivodeshipId: 13 },
  { name: "Jordan\xF3w", voivodeshipId: 6 },
  { name: "J\xF3zef\xF3w", voivodeshipId: 7 },
  { name: "J\xF3zef\xF3w", voivodeshipId: 3 },
  { name: "J\xF3zef\xF3w nad Wis\u0142\u0105", voivodeshipId: 3 },
  { name: "Jutrosin", voivodeshipId: 15 },
  { name: "Kalety", voivodeshipId: 12 },
  { name: "Kalisz", voivodeshipId: 15 },
  { name: "Kalisz Pomorski", voivodeshipId: 16 },
  { name: "Kalwaria Zebrzydowska", voivodeshipId: 6 },
  { name: "Ka\u0142uszyn", voivodeshipId: 7 },
  { name: "Kamieniec Z\u0105bkowicki", voivodeshipId: 1 },
  { name: "Kamienna G\xF3ra", voivodeshipId: 1 },
  { name: "Kamie\u0144 Kraje\u0144ski", voivodeshipId: 2 },
  { name: "Kamie\u0144 Pomorski", voivodeshipId: 16 },
  { name: "Kamie\u0144sk", voivodeshipId: 5 },
  { name: "Kamionka", voivodeshipId: 3 },
  { name: "Ka\u0144czuga", voivodeshipId: 9 },
  { name: "Karczew", voivodeshipId: 7 },
  { name: "Kargowa", voivodeshipId: 4 },
  { name: "Karlino", voivodeshipId: 16 },
  { name: "Karpacz", voivodeshipId: 1 },
  { name: "Kartuzy", voivodeshipId: 11 },
  { name: "Katowice", voivodeshipId: 12 },
  { name: "Kazimierz Dolny", voivodeshipId: 3 },
  { name: "Kazimierza Wielka", voivodeshipId: 13 },
  { name: "K\u0105ty Wroc\u0142awskie", voivodeshipId: 1 },
  { name: "Kcynia", voivodeshipId: 2 },
  { name: "K\u0119dzierzyn-Ko\u017Ale", voivodeshipId: 8 },
  { name: "K\u0119pice", voivodeshipId: 11 },
  { name: "K\u0119pno", voivodeshipId: 15 },
  { name: "K\u0119trzyn", voivodeshipId: 14 },
  { name: "K\u0119ty", voivodeshipId: 6 },
  { name: "Kielce", voivodeshipId: 13 },
  { name: "Kietrz", voivodeshipId: 8 },
  { name: "Kisielice", voivodeshipId: 14 },
  { name: "Kleczew", voivodeshipId: 15 },
  { name: "Kleszczele", voivodeshipId: 10 },
  { name: "Klimont\xF3w", voivodeshipId: 13 },
  { name: "Kluczbork", voivodeshipId: 8 },
  { name: "K\u0142ecko", voivodeshipId: 15 },
  { name: "K\u0142obuck", voivodeshipId: 12 },
  { name: "K\u0142odawa", voivodeshipId: 15 },
  { name: "K\u0142odzko", voivodeshipId: 1 },
  { name: "Knur\xF3w", voivodeshipId: 12 },
  { name: "Knyszyn", voivodeshipId: 10 },
  { name: "Kobylin", voivodeshipId: 15 },
  { name: "Koby\u0142ka", voivodeshipId: 7 },
  { name: "Kock", voivodeshipId: 3 },
  { name: "Kolbuszowa", voivodeshipId: 9 },
  { name: "Kolno", voivodeshipId: 10 },
  { name: "Kolonowskie", voivodeshipId: 8 },
  { name: "Koluszki", voivodeshipId: 5 },
  { name: "Ko\u0142aczyce", voivodeshipId: 9 },
  { name: "Ko\u0142o", voivodeshipId: 15 },
  { name: "Ko\u0142obrzeg", voivodeshipId: 16 },
  { name: "Koniecpol", voivodeshipId: 12 },
  { name: "Konin", voivodeshipId: 15 },
  { name: "Konstancin-Jeziorna", voivodeshipId: 7 },
  { name: "Konstantyn\xF3w \u0141\xF3dzki", voivodeshipId: 5 },
  { name: "Ko\u0144skie", voivodeshipId: 13 },
  { name: "Koprzywnica", voivodeshipId: 13 },
  { name: "Korfant\xF3w", voivodeshipId: 8 },
  { name: "Koronowo", voivodeshipId: 2 },
  { name: "Korsze", voivodeshipId: 14 },
  { name: "Kos\xF3w Lacki", voivodeshipId: 7 },
  { name: "Kostrzyn", voivodeshipId: 15 },
  { name: "Kostrzyn nad Odr\u0105", voivodeshipId: 4 },
  { name: "Koszalin", voivodeshipId: 16 },
  { name: "Koszyce", voivodeshipId: 6 },
  { name: "Ko\u015Bcian", voivodeshipId: 15 },
  { name: "Ko\u015Bcierzyna", voivodeshipId: 11 },
  { name: "Kowal", voivodeshipId: 2 },
  { name: "Kowalewo Pomorskie", voivodeshipId: 2 },
  { name: "Kowary", voivodeshipId: 1 },
  { name: "Kozieg\u0142owy", voivodeshipId: 12 },
  { name: "Kozienice", voivodeshipId: 7 },
  { name: "Ko\u017Amin Wielkopolski", voivodeshipId: 15 },
  { name: "Ko\u017Aminek", voivodeshipId: 15 },
  { name: "Ko\u017Cuch\xF3w", voivodeshipId: 4 },
  { name: "K\xF3rnik", voivodeshipId: 15 },
  { name: "Krajenka", voivodeshipId: 15 },
  { name: "Krak\xF3w", voivodeshipId: 6 },
  { name: "Krapkowice", voivodeshipId: 8 },
  { name: "Krasnobr\xF3d", voivodeshipId: 3 },
  { name: "Krasnystaw", voivodeshipId: 3 },
  { name: "Kra\u015Bnik", voivodeshipId: 3 },
  { name: "Krobia", voivodeshipId: 15 },
  { name: "Krosno", voivodeshipId: 9 },
  { name: "Krosno Odrza\u0144skie", voivodeshipId: 4 },
  { name: "Kro\u015Bniewice", voivodeshipId: 5 },
  { name: "Krotoszyn", voivodeshipId: 15 },
  { name: "Kruszwica", voivodeshipId: 2 },
  { name: "Krynica-Zdr\xF3j", voivodeshipId: 6 },
  { name: "Krynica Morska", voivodeshipId: 11 },
  { name: "Krynki", voivodeshipId: 10 },
  { name: "Krzanowice", voivodeshipId: 12 },
  { name: "Krzepice", voivodeshipId: 12 },
  { name: "Krzeszowice", voivodeshipId: 6 },
  { name: "Krzywi\u0144", voivodeshipId: 15 },
  { name: "Krzy\u017C Wielkopolski", voivodeshipId: 15 },
  { name: "Ksi\u0105\u017C Wielkopolski", voivodeshipId: 15 },
  { name: "Kudowa-Zdr\xF3j", voivodeshipId: 1 },
  { name: "Kun\xF3w", voivodeshipId: 13 },
  { name: "Kutno", voivodeshipId: 5 },
  { name: "Ku\u017Ania Raciborska", voivodeshipId: 12 },
  { name: "Kwidzyn", voivodeshipId: 11 },
  { name: "L\u0105dek-Zdr\xF3j", voivodeshipId: 1 },
  { name: "Legionowo", voivodeshipId: 7 },
  { name: "Legnica", voivodeshipId: 1 },
  { name: "Lesko", voivodeshipId: 9 },
  { name: "Leszno", voivodeshipId: 15 },
  { name: "Le\u015Bna", voivodeshipId: 1 },
  { name: "Le\u015Bnica", voivodeshipId: 8 },
  { name: "Lewin Brzeski", voivodeshipId: 8 },
  { name: "Le\u017Cajsk", voivodeshipId: 9 },
  { name: "L\u0119bork", voivodeshipId: 11 },
  { name: "L\u0119dziny", voivodeshipId: 12 },
  { name: "Libi\u0105\u017C", voivodeshipId: 6 },
  { name: "Lidzbark", voivodeshipId: 14 },
  { name: "Lidzbark Warmi\u0144ski", voivodeshipId: 14 },
  { name: "Limanowa", voivodeshipId: 6 },
  { name: "Lipiany", voivodeshipId: 16 },
  { name: "Lipno", voivodeshipId: 2 },
  { name: "Lipsk", voivodeshipId: 10 },
  { name: "Lipsko", voivodeshipId: 7 },
  { name: "Lubacz\xF3w", voivodeshipId: 9 },
  { name: "Luba\u0144", voivodeshipId: 1 },
  { name: "Lubart\xF3w", voivodeshipId: 3 },
  { name: "Lubawa", voivodeshipId: 14 },
  { name: "Lubawka", voivodeshipId: 1 },
  { name: "Lubie\u0144 Kujawski", voivodeshipId: 2 },
  { name: "Lubin", voivodeshipId: 1 },
  { name: "Lublin", voivodeshipId: 3 },
  { name: "Lubliniec", voivodeshipId: 12 },
  { name: "Lubniewice", voivodeshipId: 4 },
  { name: "Lubomierz", voivodeshipId: 1 },
  { name: "Lubo\u0144", voivodeshipId: 15 },
  { name: "Lubowidz", voivodeshipId: 7 },
  { name: "Lubraniec", voivodeshipId: 2 },
  { name: "Lubsko", voivodeshipId: 4 },
  { name: "Lubycza Kr\xF3lewska", voivodeshipId: 3 },
  { name: "Lutut\xF3w", voivodeshipId: 5 },
  { name: "Lw\xF3wek", voivodeshipId: 15 },
  { name: "Lw\xF3wek \u015Al\u0105ski", voivodeshipId: 1 },
  { name: "\u0141abiszyn", voivodeshipId: 2 },
  { name: "\u0141ag\xF3w", voivodeshipId: 13 },
  { name: "\u0141a\u0144cut", voivodeshipId: 9 },
  { name: "\u0141apy", voivodeshipId: 10 },
  { name: "\u0141asin", voivodeshipId: 2 },
  { name: "\u0141ask", voivodeshipId: 5 },
  { name: "\u0141askarzew", voivodeshipId: 7 },
  { name: "\u0141aszcz\xF3w", voivodeshipId: 3 },
  { name: "\u0141aziska G\xF3rne", voivodeshipId: 12 },
  { name: "\u0141azy", voivodeshipId: 12 },
  { name: "\u0141eba", voivodeshipId: 11 },
  { name: "\u0141\u0119czna", voivodeshipId: 3 },
  { name: "\u0141\u0119czyca", voivodeshipId: 5 },
  { name: "\u0141\u0119knica", voivodeshipId: 4 },
  { name: "\u0141obez", voivodeshipId: 16 },
  { name: "\u0141ob\u017Cenica", voivodeshipId: 15 },
  { name: "\u0141och\xF3w", voivodeshipId: 7 },
  { name: "\u0141omianki", voivodeshipId: 7 },
  { name: "\u0141om\u017Ca", voivodeshipId: 10 },
  { name: "\u0141osice", voivodeshipId: 7 },
  { name: "\u0141owicz", voivodeshipId: 5 },
  { name: "\u0141\xF3d\u017A", voivodeshipId: 5 },
  { name: "\u0141uk\xF3w", voivodeshipId: 3 },
  { name: "Mak\xF3w Mazowiecki", voivodeshipId: 7 },
  { name: "Mak\xF3w Podhala\u0144ski", voivodeshipId: 6 },
  { name: "Malbork", voivodeshipId: 11 },
  { name: "Ma\u0142ogoszcz", voivodeshipId: 13 },
  { name: "Ma\u0142omice", voivodeshipId: 4 },
  { name: "Margonin", voivodeshipId: 15 },
  { name: "Marki", voivodeshipId: 7 },
  { name: "Maszewo", voivodeshipId: 16 },
  { name: "Miasteczko \u015Al\u0105skie", voivodeshipId: 12 },
  { name: "Miastko", voivodeshipId: 11 },
  { name: "Micha\u0142owo", voivodeshipId: 10 },
  { name: "Miech\xF3w", voivodeshipId: 6 },
  { name: "Miejska G\xF3rka", voivodeshipId: 15 },
  { name: "Mielec", voivodeshipId: 9 },
  { name: "Mielno", voivodeshipId: 16 },
  { name: "Mierosz\xF3w", voivodeshipId: 1 },
  { name: "Mieszkowice", voivodeshipId: 16 },
  { name: "Mi\u0119dzyb\xF3rz", voivodeshipId: 1 },
  { name: "Mi\u0119dzych\xF3d", voivodeshipId: 15 },
  { name: "Mi\u0119dzylesie", voivodeshipId: 1 },
  { name: "Mi\u0119dzyrzec Podlaski", voivodeshipId: 3 },
  { name: "Mi\u0119dzyrzecz", voivodeshipId: 4 },
  { name: "Mi\u0119dzyzdroje", voivodeshipId: 16 },
  { name: "Miko\u0142ajki", voivodeshipId: 14 },
  { name: "Miko\u0142\xF3w", voivodeshipId: 12 },
  { name: "Mikstat", voivodeshipId: 15 },
  { name: "Milan\xF3wek", voivodeshipId: 7 },
  { name: "Milicz", voivodeshipId: 1 },
  { name: "Mi\u0142akowo", voivodeshipId: 14 },
  { name: "Mi\u0142om\u0142yn", voivodeshipId: 14 },
  { name: "Mi\u0142os\u0142aw", voivodeshipId: 15 },
  { name: "Mi\u0144sk Mazowiecki", voivodeshipId: 7 },
  { name: "Miros\u0142awiec", voivodeshipId: 16 },
  { name: "Mirsk", voivodeshipId: 1 },
  { name: "M\u0142awa", voivodeshipId: 7 },
  { name: "M\u0142ynary", voivodeshipId: 14 },
  { name: "Modliborzyce", voivodeshipId: 3 },
  { name: "Mogielnica", voivodeshipId: 7 },
  { name: "Mogilno", voivodeshipId: 2 },
  { name: "Mo\u0144ki", voivodeshipId: 10 },
  { name: "Morawica", voivodeshipId: 13 },
  { name: "Mor\u0105g", voivodeshipId: 14 },
  { name: "Mordy", voivodeshipId: 7 },
  { name: "Mory\u0144", voivodeshipId: 16 },
  { name: "Mosina", voivodeshipId: 15 },
  { name: "Mr\u0105gowo", voivodeshipId: 14 },
  { name: "Mrocza", voivodeshipId: 2 },
  { name: "Mrozy", voivodeshipId: 7 },
  { name: "Mszana Dolna", voivodeshipId: 6 },
  { name: "Mszczon\xF3w", voivodeshipId: 7 },
  { name: "Murowana Go\u015Blina", voivodeshipId: 15 },
  { name: "Muszyna", voivodeshipId: 6 },
  { name: "Mys\u0142owice", voivodeshipId: 12 },
  { name: "Myszk\xF3w", voivodeshipId: 12 },
  { name: "Myszyniec", voivodeshipId: 7 },
  { name: "My\u015Blenice", voivodeshipId: 6 },
  { name: "My\u015Blib\xF3rz", voivodeshipId: 16 },
  { name: "Nak\u0142o nad Noteci\u0105", voivodeshipId: 2 },
  { name: "Na\u0142\u0119cz\xF3w", voivodeshipId: 3 },
  { name: "Namys\u0142\xF3w", voivodeshipId: 8 },
  { name: "Narol", voivodeshipId: 9 },
  { name: "Nasielsk", voivodeshipId: 7 },
  { name: "Nekla", voivodeshipId: 15 },
  { name: "Nidzica", voivodeshipId: 14 },
  { name: "Niemcza", voivodeshipId: 1 },
  { name: "Niemodlin", voivodeshipId: 8 },
  { name: "Niepo\u0142omice", voivodeshipId: 6 },
  { name: "Nieszawa", voivodeshipId: 2 },
  { name: "Nisko", voivodeshipId: 9 },
  { name: "Nowa D\u0119ba", voivodeshipId: 9 },
  { name: "Nowa Ruda", voivodeshipId: 1 },
  { name: "Nowa Sarzyna", voivodeshipId: 9 },
  { name: "Nowa S\u0142upia", voivodeshipId: 13 },
  { name: "Nowa S\xF3l", voivodeshipId: 4 },
  { name: "Nowe", voivodeshipId: 2 },
  { name: "Nowe Brzesko", voivodeshipId: 6 },
  { name: "Nowe Miasteczko", voivodeshipId: 4 },
  { name: "Nowe Miasto Lubawskie", voivodeshipId: 14 },
  { name: "Nowe Miasto nad Pilic\u0105", voivodeshipId: 7 },
  { name: "Nowe Skalmierzyce", voivodeshipId: 15 },
  { name: "Nowe Warpno", voivodeshipId: 16 },
  { name: "Nowogard", voivodeshipId: 16 },
  { name: "Nowogrodziec", voivodeshipId: 1 },
  { name: "Nowogr\xF3d", voivodeshipId: 10 },
  { name: "Nowogr\xF3d Bobrza\u0144ski", voivodeshipId: 4 },
  { name: "Nowy Dw\xF3r Gda\u0144ski", voivodeshipId: 11 },
  { name: "Nowy Dw\xF3r Mazowiecki", voivodeshipId: 7 },
  { name: "Nowy Korczyn", voivodeshipId: 13 },
  { name: "Nowy S\u0105cz", voivodeshipId: 6 },
  { name: "Nowy Staw", voivodeshipId: 11 },
  { name: "Nowy Targ", voivodeshipId: 6 },
  { name: "Nowy Tomy\u015Bl", voivodeshipId: 15 },
  { name: "Nowy Wi\u015Bnicz", voivodeshipId: 6 },
  { name: "Nysa", voivodeshipId: 8 },
  { name: "Oborniki", voivodeshipId: 15 },
  { name: "Oborniki \u015Al\u0105skie", voivodeshipId: 1 },
  { name: "Obrzycko", voivodeshipId: 15 },
  { name: "Odolan\xF3w", voivodeshipId: 15 },
  { name: "Ogrodzieniec", voivodeshipId: 12 },
  { name: "Okonek", voivodeshipId: 15 },
  { name: "Olecko", voivodeshipId: 14 },
  { name: "Olesno", voivodeshipId: 8 },
  { name: "Oleszyce", voivodeshipId: 9 },
  { name: "Ole\u015Bnica", voivodeshipId: 1 },
  { name: "Ole\u015Bnica", voivodeshipId: 13 },
  { name: "Olkusz", voivodeshipId: 6 },
  { name: "Olsztyn", voivodeshipId: 14 },
  { name: "Olsztynek", voivodeshipId: 14 },
  { name: "Olszyna", voivodeshipId: 1 },
  { name: "O\u0142awa", voivodeshipId: 1 },
  { name: "Opalenica", voivodeshipId: 15 },
  { name: "Opatowiec", voivodeshipId: 13 },
  { name: "Opat\xF3w", voivodeshipId: 13 },
  { name: "Opat\xF3wek", voivodeshipId: 15 },
  { name: "Opoczno", voivodeshipId: 5 },
  { name: "Opole", voivodeshipId: 8 },
  { name: "Opole Lubelskie", voivodeshipId: 3 },
  { name: "Orneta", voivodeshipId: 14 },
  { name: "Orzesze", voivodeshipId: 12 },
  { name: "Orzysz", voivodeshipId: 14 },
  { name: "Osieczna", voivodeshipId: 15 },
  { name: "Osiek", voivodeshipId: 13 },
  { name: "Ostro\u0142\u0119ka", voivodeshipId: 7 },
  { name: "Ostror\xF3g", voivodeshipId: 15 },
  { name: "Ostrowiec \u015Awi\u0119tokrzyski", voivodeshipId: 13 },
  { name: "Ostr\xF3da", voivodeshipId: 14 },
  { name: "Ostr\xF3w Lubelski", voivodeshipId: 3 },
  { name: "Ostr\xF3w Mazowiecka", voivodeshipId: 7 },
  { name: "Ostr\xF3w Wielkopolski", voivodeshipId: 15 },
  { name: "Ostrzesz\xF3w", voivodeshipId: 15 },
  { name: "O\u015Bno Lubuskie", voivodeshipId: 4 },
  { name: "O\u015Bwi\u0119cim", voivodeshipId: 6 },
  { name: "Otmuch\xF3w", voivodeshipId: 8 },
  { name: "Otwock", voivodeshipId: 7 },
  { name: "Oty\u0144", voivodeshipId: 4 },
  { name: "Ozimek", voivodeshipId: 8 },
  { name: "Ozork\xF3w", voivodeshipId: 5 },
  { name: "O\u017Car\xF3w", voivodeshipId: 13 },
  { name: "O\u017Car\xF3w Mazowiecki", voivodeshipId: 7 },
  { name: "Pabianice", voivodeshipId: 5 },
  { name: "Pacan\xF3w", voivodeshipId: 13 },
  { name: "Paczk\xF3w", voivodeshipId: 8 },
  { name: "Paj\u0119czno", voivodeshipId: 5 },
  { name: "Pako\u015B\u0107", voivodeshipId: 2 },
  { name: "Parczew", voivodeshipId: 3 },
  { name: "Pas\u0142\u0119k", voivodeshipId: 14 },
  { name: "Pasym", voivodeshipId: 14 },
  { name: "Pelplin", voivodeshipId: 11 },
  { name: "Pe\u0142czyce", voivodeshipId: 16 },
  { name: "Piaseczno", voivodeshipId: 7 },
  { name: "Piaski", voivodeshipId: 3 },
  { name: "Piast\xF3w", voivodeshipId: 7 },
  { name: "Pi\u0105tek", voivodeshipId: 5 },
  { name: "Piechowice", voivodeshipId: 1 },
  { name: "Piekary \u015Al\u0105skie", voivodeshipId: 12 },
  { name: "Pieni\u0119\u017Cno", voivodeshipId: 14 },
  { name: "Pie\u0144sk", voivodeshipId: 1 },
  { name: "Pierzchnica", voivodeshipId: 13 },
  { name: "Pieszyce", voivodeshipId: 1 },
  { name: "Pilawa", voivodeshipId: 7 },
  { name: "Pilica", voivodeshipId: 12 },
  { name: "Pilzno", voivodeshipId: 9 },
  { name: "Pi\u0142a", voivodeshipId: 15 },
  { name: "Pi\u0142awa G\xF3rna", voivodeshipId: 1 },
  { name: "Pi\u0144cz\xF3w", voivodeshipId: 13 },
  { name: "Pionki", voivodeshipId: 7 },
  { name: "Piotrk\xF3w Kujawski", voivodeshipId: 2 },
  { name: "Piotrk\xF3w Trybunalski", voivodeshipId: 5 },
  { name: "Pisz", voivodeshipId: 14 },
  { name: "Piwniczna-Zdr\xF3j", voivodeshipId: 6 },
  { name: "Pleszew", voivodeshipId: 15 },
  { name: "P\u0142ock", voivodeshipId: 7 },
  { name: "P\u0142o\u0144sk", voivodeshipId: 7 },
  { name: "P\u0142oty", voivodeshipId: 16 },
  { name: "Pniewy", voivodeshipId: 15 },
  { name: "Pobiedziska", voivodeshipId: 15 },
  { name: "Podd\u0119bice", voivodeshipId: 5 },
  { name: "Podkowa Le\u015Bna", voivodeshipId: 7 },
  { name: "Pogorzela", voivodeshipId: 15 },
  { name: "Polanica-Zdr\xF3j", voivodeshipId: 1 },
  { name: "Polan\xF3w", voivodeshipId: 16 },
  { name: "Police", voivodeshipId: 16 },
  { name: "Polkowice", voivodeshipId: 1 },
  { name: "Po\u0142aniec", voivodeshipId: 13 },
  { name: "Po\u0142czyn-Zdr\xF3j", voivodeshipId: 16 },
  { name: "Poniatowa", voivodeshipId: 3 },
  { name: "Poniec", voivodeshipId: 15 },
  { name: "Por\u0119ba", voivodeshipId: 12 },
  { name: "Pozna\u0144", voivodeshipId: 15 },
  { name: "Prabuty", voivodeshipId: 11 },
  { name: "Praszka", voivodeshipId: 8 },
  { name: "Prochowice", voivodeshipId: 1 },
  { name: "Proszowice", voivodeshipId: 6 },
  { name: "Pr\xF3szk\xF3w", voivodeshipId: 8 },
  { name: "Pruchnik", voivodeshipId: 9 },
  { name: "Prudnik", voivodeshipId: 8 },
  { name: "Prusice", voivodeshipId: 1 },
  { name: "Pruszcz Gda\u0144ski", voivodeshipId: 11 },
  { name: "Pruszk\xF3w", voivodeshipId: 7 },
  { name: "Przasnysz", voivodeshipId: 7 },
  { name: "Przec\u0142aw", voivodeshipId: 9 },
  { name: "Przedb\xF3rz", voivodeshipId: 5 },
  { name: "Przedecz", voivodeshipId: 15 },
  { name: "Przemk\xF3w", voivodeshipId: 1 },
  { name: "Przemy\u015Bl", voivodeshipId: 9 },
  { name: "Przeworsk", voivodeshipId: 9 },
  { name: "Przysucha", voivodeshipId: 7 },
  { name: "Pszczyna", voivodeshipId: 12 },
  { name: "Psz\xF3w", voivodeshipId: 12 },
  { name: "Puck", voivodeshipId: 11 },
  { name: "Pu\u0142awy", voivodeshipId: 3 },
  { name: "Pu\u0142tusk", voivodeshipId: 7 },
  { name: "Puszczykowo", voivodeshipId: 15 },
  { name: "Pyrzyce", voivodeshipId: 16 },
  { name: "Pyskowice", voivodeshipId: 12 },
  { name: "Pyzdry", voivodeshipId: 15 },
  { name: "Rabka-Zdr\xF3j", voivodeshipId: 6 },
  { name: "Raci\u0105\u017C", voivodeshipId: 7 },
  { name: "Racib\xF3rz", voivodeshipId: 12 },
  { name: "Radk\xF3w", voivodeshipId: 1 },
  { name: "Radlin", voivodeshipId: 12 },
  { name: "Rad\u0142\xF3w", voivodeshipId: 6 },
  { name: "Radom", voivodeshipId: 7 },
  { name: "Radomsko", voivodeshipId: 5 },
  { name: "Radomy\u015Bl Wielki", voivodeshipId: 9 },
  { name: "Radoszyce", voivodeshipId: 13 },
  { name: "Radymno", voivodeshipId: 9 },
  { name: "Radziej\xF3w", voivodeshipId: 2 },
  { name: "Radzionk\xF3w", voivodeshipId: 12 },
  { name: "Radzymin", voivodeshipId: 7 },
  { name: "Radzy\u0144 Che\u0142mi\u0144ski", voivodeshipId: 2 },
  { name: "Radzy\u0144 Podlaski", voivodeshipId: 3 },
  { name: "Rajgr\xF3d", voivodeshipId: 10 },
  { name: "Rakoniewice", voivodeshipId: 15 },
  { name: "Raszk\xF3w", voivodeshipId: 15 },
  { name: "Rawa Mazowiecka", voivodeshipId: 5 },
  { name: "Rawicz", voivodeshipId: 15 },
  { name: "Recz", voivodeshipId: 16 },
  { name: "Reda", voivodeshipId: 11 },
  { name: "Rejowiec", voivodeshipId: 3 },
  { name: "Rejowiec Fabryczny", voivodeshipId: 3 },
  { name: "Resko", voivodeshipId: 16 },
  { name: "Reszel", voivodeshipId: 14 },
  { name: "Rogo\u017Ano", voivodeshipId: 15 },
  { name: "Ropczyce", voivodeshipId: 9 },
  { name: "R\xF3\u017Can", voivodeshipId: 7 },
  { name: "Ruciane-Nida", voivodeshipId: 14 },
  { name: "Ruda \u015Al\u0105ska", voivodeshipId: 12 },
  { name: "Rudnik nad Sanem", voivodeshipId: 9 },
  { name: "Rumia", voivodeshipId: 11 },
  { name: "Rybnik", voivodeshipId: 12 },
  { name: "Rychwa\u0142", voivodeshipId: 15 },
  { name: "Rydu\u0142towy", voivodeshipId: 12 },
  { name: "Rydzyna", voivodeshipId: 15 },
  { name: "Ryglice", voivodeshipId: 6 },
  { name: "Ryki", voivodeshipId: 3 },
  { name: "Ryman\xF3w", voivodeshipId: 9 },
  { name: "Ryn", voivodeshipId: 14 },
  { name: "Rypin", voivodeshipId: 2 },
  { name: "Rzepin", voivodeshipId: 4 },
  { name: "Rzesz\xF3w", voivodeshipId: 9 },
  { name: "Rzg\xF3w", voivodeshipId: 5 },
  { name: "Sandomierz", voivodeshipId: 13 },
  { name: "Sanniki", voivodeshipId: 7 },
  { name: "Sanok", voivodeshipId: 9 },
  { name: "Sejny", voivodeshipId: 10 },
  { name: "Serock", voivodeshipId: 7 },
  { name: "S\u0119dzisz\xF3w", voivodeshipId: 13 },
  { name: "S\u0119dzisz\xF3w Ma\u0142opolski", voivodeshipId: 9 },
  { name: "S\u0119popol", voivodeshipId: 14 },
  { name: "S\u0119p\xF3lno Kraje\u0144skie", voivodeshipId: 2 },
  { name: "Sian\xF3w", voivodeshipId: 16 },
  { name: "Siechnice", voivodeshipId: 1 },
  { name: "Siedlce", voivodeshipId: 7 },
  { name: "Siedliszcze", voivodeshipId: 3 },
  { name: "Siemianowice \u015Al\u0105skie", voivodeshipId: 12 },
  { name: "Siemiatycze", voivodeshipId: 10 },
  { name: "Sieniawa", voivodeshipId: 9 },
  { name: "Sieradz", voivodeshipId: 5 },
  { name: "Sierak\xF3w", voivodeshipId: 15 },
  { name: "Sierpc", voivodeshipId: 7 },
  { name: "Siewierz", voivodeshipId: 12 },
  { name: "Skalbmierz", voivodeshipId: 13 },
  { name: "Ska\u0142a", voivodeshipId: 6 },
  { name: "Skarszewy", voivodeshipId: 11 },
  { name: "Skaryszew", voivodeshipId: 7 },
  { name: "Skar\u017Cysko-Kamienna", voivodeshipId: 13 },
  { name: "Skawina", voivodeshipId: 6 },
  { name: "Sk\u0119pe", voivodeshipId: 2 },
  { name: "Skierniewice", voivodeshipId: 5 },
  { name: "Skocz\xF3w", voivodeshipId: 12 },
  { name: "Skoki", voivodeshipId: 15 },
  { name: "Sk\xF3rcz", voivodeshipId: 11 },
  { name: "Skwierzyna", voivodeshipId: 4 },
  { name: "S\u0142awa", voivodeshipId: 4 },
  { name: "S\u0142awk\xF3w", voivodeshipId: 12 },
  { name: "S\u0142awno", voivodeshipId: 16 },
  { name: "S\u0142omniki", voivodeshipId: 6 },
  { name: "S\u0142ubice", voivodeshipId: 4 },
  { name: "S\u0142upca", voivodeshipId: 15 },
  { name: "S\u0142upsk", voivodeshipId: 11 },
  { name: "Sob\xF3tka", voivodeshipId: 1 },
  { name: "Sochaczew", voivodeshipId: 7 },
  { name: "Sochocin", voivodeshipId: 7 },
  { name: "Soko\u0142\xF3w Ma\u0142opolski", voivodeshipId: 9 },
  { name: "Soko\u0142\xF3w Podlaski", voivodeshipId: 7 },
  { name: "Sok\xF3\u0142ka", voivodeshipId: 10 },
  { name: "Solec Kujawski", voivodeshipId: 2 },
  { name: "Solec nad Wis\u0142\u0105", voivodeshipId: 7 },
  { name: "Sompolno", voivodeshipId: 15 },
  { name: "Sopot", voivodeshipId: 11 },
  { name: "Sosnowiec", voivodeshipId: 12 },
  { name: "So\u015Bnicowice", voivodeshipId: 12 },
  { name: "Stalowa Wola", voivodeshipId: 9 },
  { name: "Starachowice", voivodeshipId: 13 },
  { name: "Stargard", voivodeshipId: 16 },
  { name: "Starogard Gda\u0144ski", voivodeshipId: 11 },
  { name: "Stary S\u0105cz", voivodeshipId: 6 },
  { name: "Stasz\xF3w", voivodeshipId: 13 },
  { name: "Stawiski", voivodeshipId: 10 },
  { name: "Stawiszyn", voivodeshipId: 15 },
  { name: "St\u0105pork\xF3w", voivodeshipId: 13 },
  { name: "Stepnica", voivodeshipId: 16 },
  { name: "St\u0119szew", voivodeshipId: 15 },
  { name: "Stoczek \u0141ukowski", voivodeshipId: 3 },
  { name: "Stopnica", voivodeshipId: 13 },
  { name: "Stronie \u015Al\u0105skie", voivodeshipId: 1 },
  { name: "Strumie\u0144", voivodeshipId: 12 },
  { name: "Stryk\xF3w", voivodeshipId: 5 },
  { name: "Strzegom", voivodeshipId: 1 },
  { name: "Strzelce Kraje\u0144skie", voivodeshipId: 4 },
  { name: "Strzelce Opolskie", voivodeshipId: 8 },
  { name: "Strzelin", voivodeshipId: 1 },
  { name: "Strzelno", voivodeshipId: 2 },
  { name: "Strzy\u017C\xF3w", voivodeshipId: 9 },
  { name: "Sucha Beskidzka", voivodeshipId: 6 },
  { name: "Sucha\u0144", voivodeshipId: 16 },
  { name: "Suchedni\xF3w", voivodeshipId: 13 },
  { name: "Suchowola", voivodeshipId: 10 },
  { name: "Sulech\xF3w", voivodeshipId: 4 },
  { name: "Sulej\xF3w", voivodeshipId: 5 },
  { name: "Sulej\xF3wek", voivodeshipId: 7 },
  { name: "Sul\u0119cin", voivodeshipId: 4 },
  { name: "Sulmierzyce", voivodeshipId: 15 },
  { name: "Su\u0142kowice", voivodeshipId: 6 },
  { name: "Supra\u015Bl", voivodeshipId: 10 },
  { name: "Sura\u017C", voivodeshipId: 10 },
  { name: "Susz", voivodeshipId: 14 },
  { name: "Suwa\u0142ki", voivodeshipId: 10 },
  { name: "Swarz\u0119dz", voivodeshipId: 15 },
  { name: "Syc\xF3w", voivodeshipId: 1 },
  { name: "Szadek", voivodeshipId: 5 },
  { name: "Szamocin", voivodeshipId: 15 },
  { name: "Szamotu\u0142y", voivodeshipId: 15 },
  { name: "Szczawnica", voivodeshipId: 6 },
  { name: "Szczawno-Zdr\xF3j", voivodeshipId: 1 },
  { name: "Szczebrzeszyn", voivodeshipId: 3 },
  { name: "Szczecin", voivodeshipId: 16 },
  { name: "Szczecinek", voivodeshipId: 16 },
  { name: "Szczekociny", voivodeshipId: 12 },
  { name: "Szczucin", voivodeshipId: 6 },
  { name: "Szczuczyn", voivodeshipId: 10 },
  { name: "Szczyrk", voivodeshipId: 12 },
  { name: "Szczytna", voivodeshipId: 1 },
  { name: "Szczytno", voivodeshipId: 14 },
  { name: "Szepietowo", voivodeshipId: 10 },
  { name: "Szklarska Por\u0119ba", voivodeshipId: 1 },
  { name: "Szlichtyngowa", voivodeshipId: 4 },
  { name: "Szprotawa", voivodeshipId: 4 },
  { name: "Sztum", voivodeshipId: 11 },
  { name: "Szubin", voivodeshipId: 2 },
  { name: "Szyd\u0142owiec", voivodeshipId: 7 },
  { name: "Szyd\u0142\xF3w", voivodeshipId: 13 },
  { name: "\u015Acinawa", voivodeshipId: 1 },
  { name: "\u015Alesin", voivodeshipId: 15 },
  { name: "\u015Amigiel", voivodeshipId: 15 },
  { name: "\u015Arem", voivodeshipId: 15 },
  { name: "\u015Aroda \u015Al\u0105ska", voivodeshipId: 1 },
  { name: "\u015Aroda Wielkopolska", voivodeshipId: 15 },
  { name: "\u015Awi\u0105tniki G\xF3rne", voivodeshipId: 6 },
  { name: "\u015Awidnica", voivodeshipId: 1 },
  { name: "\u015Awidnik", voivodeshipId: 3 },
  { name: "\u015Awidwin", voivodeshipId: 16 },
  { name: "\u015Awiebodzice", voivodeshipId: 1 },
  { name: "\u015Awiebodzin", voivodeshipId: 4 },
  { name: "\u015Awiecie", voivodeshipId: 2 },
  { name: "\u015Awierad\xF3w-Zdr\xF3j", voivodeshipId: 1 },
  { name: "\u015Awierzawa", voivodeshipId: 1 },
  { name: "\u015Awi\u0119toch\u0142owice", voivodeshipId: 12 },
  { name: "\u015Awinouj\u015Bcie", voivodeshipId: 16 },
  { name: "Tarczyn", voivodeshipId: 7 },
  { name: "Tarnobrzeg", voivodeshipId: 9 },
  { name: "Tarnogr\xF3d", voivodeshipId: 3 },
  { name: "Tarnowskie G\xF3ry", voivodeshipId: 12 },
  { name: "Tarn\xF3w", voivodeshipId: 6 },
  { name: "Tczew", voivodeshipId: 11 },
  { name: "Terespol", voivodeshipId: 3 },
  { name: "T\u0142uszcz", voivodeshipId: 7 },
  { name: "Tolkmicko", voivodeshipId: 14 },
  { name: "Tomasz\xF3w Lubelski", voivodeshipId: 3 },
  { name: "Tomasz\xF3w Mazowiecki", voivodeshipId: 5 },
  { name: "Toru\u0144", voivodeshipId: 2 },
  { name: "Torzym", voivodeshipId: 4 },
  { name: "Toszek", voivodeshipId: 12 },
  { name: "Trzcianka", voivodeshipId: 15 },
  { name: "Trzciel", voivodeshipId: 4 },
  { name: "Trzci\u0144sko-Zdr\xF3j", voivodeshipId: 16 },
  { name: "Trzebiat\xF3w", voivodeshipId: 16 },
  { name: "Trzebinia", voivodeshipId: 6 },
  { name: "Trzebnica", voivodeshipId: 1 },
  { name: "Trzemeszno", voivodeshipId: 15 },
  { name: "Tuchola", voivodeshipId: 2 },
  { name: "Tuch\xF3w", voivodeshipId: 6 },
  { name: "Tuczno", voivodeshipId: 16 },
  { name: "Tuliszk\xF3w", voivodeshipId: 15 },
  { name: "Tu\u0142owice", voivodeshipId: 8 },
  { name: "Turek", voivodeshipId: 15 },
  { name: "Tuszyn", voivodeshipId: 5 },
  { name: "Twardog\xF3ra", voivodeshipId: 1 },
  { name: "Tychowo", voivodeshipId: 16 },
  { name: "Tychy", voivodeshipId: 12 },
  { name: "Tyczyn", voivodeshipId: 9 },
  { name: "Tykocin", voivodeshipId: 10 },
  { name: "Tyszowce", voivodeshipId: 3 },
  { name: "Ujazd", voivodeshipId: 8 },
  { name: "Uj\u015Bcie", voivodeshipId: 15 },
  { name: "Ulan\xF3w", voivodeshipId: 9 },
  { name: "Uniej\xF3w", voivodeshipId: 5 },
  { name: "Urz\u0119d\xF3w", voivodeshipId: 3 },
  { name: "Ustka", voivodeshipId: 11 },
  { name: "Ustro\u0144", voivodeshipId: 12 },
  { name: "Ustrzyki Dolne", voivodeshipId: 9 },
  { name: "Wadowice", voivodeshipId: 6 },
  { name: "Wa\u0142brzych", voivodeshipId: 1 },
  { name: "Wa\u0142cz", voivodeshipId: 16 },
  { name: "Warka", voivodeshipId: 7 },
  { name: "Warszawa", voivodeshipId: 7 },
  { name: "Warta", voivodeshipId: 5 },
  { name: "Wasilk\xF3w", voivodeshipId: 10 },
  { name: "W\u0105brze\u017Ano", voivodeshipId: 2 },
  { name: "W\u0105chock", voivodeshipId: 13 },
  { name: "W\u0105growiec", voivodeshipId: 15 },
  { name: "W\u0105sosz", voivodeshipId: 1 },
  { name: "Wejherowo", voivodeshipId: 11 },
  { name: "W\u0119gliniec", voivodeshipId: 1 },
  { name: "W\u0119gorzewo", voivodeshipId: 14 },
  { name: "W\u0119gorzyno", voivodeshipId: 16 },
  { name: "W\u0119gr\xF3w", voivodeshipId: 7 },
  { name: "Wi\u0105z\xF3w", voivodeshipId: 1 },
  { name: "Wielbark", voivodeshipId: 14 },
  { name: "Wiele\u0144", voivodeshipId: 15 },
  { name: "Wielichowo", voivodeshipId: 15 },
  { name: "Wieliczka", voivodeshipId: 6 },
  { name: "Wielu\u0144", voivodeshipId: 5 },
  { name: "Wierusz\xF3w", voivodeshipId: 5 },
  { name: "Wi\u0119cbork", voivodeshipId: 2 },
  { name: "Wilamowice", voivodeshipId: 12 },
  { name: "Wiskitki", voivodeshipId: 7 },
  { name: "Wis\u0142a", voivodeshipId: 12 },
  { name: "Wi\u015Blica", voivodeshipId: 13 },
  { name: "Witkowo", voivodeshipId: 15 },
  { name: "Witnica", voivodeshipId: 4 },
  { name: "Wle\u0144", voivodeshipId: 1 },
  { name: "W\u0142adys\u0142awowo", voivodeshipId: 11 },
  { name: "W\u0142oc\u0142awek", voivodeshipId: 2 },
  { name: "W\u0142odawa", voivodeshipId: 3 },
  { name: "W\u0142oszczowa", voivodeshipId: 13 },
  { name: "Wodzis\u0142aw", voivodeshipId: 13 },
  { name: "Wodzis\u0142aw \u015Al\u0105ski", voivodeshipId: 12 },
  { name: "Wojciesz\xF3w", voivodeshipId: 1 },
  { name: "Wojkowice", voivodeshipId: 12 },
  { name: "Wojnicz", voivodeshipId: 6 },
  { name: "Wolb\xF3rz", voivodeshipId: 5 },
  { name: "Wolbrom", voivodeshipId: 6 },
  { name: "Wolin", voivodeshipId: 16 },
  { name: "Wolsztyn", voivodeshipId: 15 },
  { name: "Wo\u0142czyn", voivodeshipId: 8 },
  { name: "Wo\u0142omin", voivodeshipId: 7 },
  { name: "Wo\u0142\xF3w", voivodeshipId: 1 },
  { name: "Wo\u017Aniki", voivodeshipId: 12 },
  { name: "Wroc\u0142aw", voivodeshipId: 1 },
  { name: "Wronki", voivodeshipId: 15 },
  { name: "Wrze\u015Bnia", voivodeshipId: 15 },
  { name: "Wschowa", voivodeshipId: 4 },
  { name: "Wyrzysk", voivodeshipId: 15 },
  { name: "Wysoka", voivodeshipId: 15 },
  { name: "Wysokie Mazowieckie", voivodeshipId: 10 },
  { name: "Wyszk\xF3w", voivodeshipId: 7 },
  { name: "Wyszogr\xF3d", voivodeshipId: 7 },
  { name: "Wy\u015Bmierzyce", voivodeshipId: 7 },
  { name: "Zab\u0142ud\xF3w", voivodeshipId: 10 },
  { name: "Zabrze", voivodeshipId: 12 },
  { name: "Zag\xF3r\xF3w", voivodeshipId: 15 },
  { name: "Zag\xF3rz", voivodeshipId: 9 },
  { name: "Zakliczyn", voivodeshipId: 6 },
  { name: "Zaklik\xF3w", voivodeshipId: 9 },
  { name: "Zakopane", voivodeshipId: 6 },
  { name: "Zakroczym", voivodeshipId: 7 },
  { name: "Zalewo", voivodeshipId: 14 },
  { name: "Zambr\xF3w", voivodeshipId: 10 },
  { name: "Zamo\u015B\u0107", voivodeshipId: 3 },
  { name: "Zator", voivodeshipId: 6 },
  { name: "Zawadzkie", voivodeshipId: 8 },
  { name: "Zawichost", voivodeshipId: 13 },
  { name: "Zawid\xF3w", voivodeshipId: 1 },
  { name: "Zawiercie", voivodeshipId: 12 },
  { name: "Z\u0105bki", voivodeshipId: 7 },
  { name: "Z\u0105bkowice \u015Al\u0105skie", voivodeshipId: 1 },
  { name: "Zb\u0105szynek", voivodeshipId: 4 },
  { name: "Zb\u0105szy\u0144", voivodeshipId: 15 },
  { name: "Zduny", voivodeshipId: 15 },
  { name: "Zdu\u0144ska Wola", voivodeshipId: 5 },
  { name: "Zdzieszowice", voivodeshipId: 8 },
  { name: "Zel\xF3w", voivodeshipId: 5 },
  { name: "Zgierz", voivodeshipId: 5 },
  { name: "Zgorzelec", voivodeshipId: 1 },
  { name: "Zielona G\xF3ra", voivodeshipId: 4 },
  { name: "Zielonka", voivodeshipId: 7 },
  { name: "Zi\u0119bice", voivodeshipId: 1 },
  { name: "Z\u0142ocieniec", voivodeshipId: 16 },
  { name: "Z\u0142oczew", voivodeshipId: 5 },
  { name: "Z\u0142otoryja", voivodeshipId: 1 },
  { name: "Z\u0142ot\xF3w", voivodeshipId: 15 },
  { name: "Z\u0142oty Stok", voivodeshipId: 1 },
  { name: "Zwierzyniec", voivodeshipId: 3 },
  { name: "Zwole\u0144", voivodeshipId: 7 },
  { name: "\u017Babno", voivodeshipId: 6 },
  { name: "\u017Baga\u0144", voivodeshipId: 4 },
  { name: "\u017Barki", voivodeshipId: 12 },
  { name: "\u017Bar\xF3w", voivodeshipId: 1 },
  { name: "\u017Bary", voivodeshipId: 4 },
  { name: "\u017Belech\xF3w", voivodeshipId: 7 },
  { name: "\u017Berk\xF3w", voivodeshipId: 15 },
  { name: "\u017Bmigr\xF3d", voivodeshipId: 1 },
  { name: "\u017Bnin", voivodeshipId: 2 },
  { name: "\u017Bory", voivodeshipId: 12 },
  { name: "\u017Bukowo", voivodeshipId: 11 },
  { name: "\u017Buromin", voivodeshipId: 7 },
  { name: "\u017Bychlin", voivodeshipId: 5 },
  { name: "\u017Byrard\xF3w", voivodeshipId: 7 },
  { name: "\u017Bywiec", voivodeshipId: 12 },
  { name: "Brok", voivodeshipId: 7 }
];

// src/cities.ts
var CITIES = cities_data_default;
var voivodeshipMap = new Map(VOIVODESHIPS.map((v) => [v.id, v.name]));
function getVoivodeshipName(id) {
  return voivodeshipMap.get(id);
}
export {
  ACCOMMODATION_CATEGORIES,
  ACCOMMODATION_CATEGORY_VALUES,
  ACCOMMODATION_CATEGORY_VALUES_SET,
  AIRPORTS,
  AIRPORTS_BY_CODE,
  AIRPORT_CITY_ALIASES,
  AIRPORT_CODES,
  AIRPORT_CODES_SET,
  AIRPORT_HUBS_BY_COUNTRY,
  AIRPORT_VALUE_MAX_LENGTH,
  CITIES,
  CONTACT_EMAIL_BY_LANGUAGE,
  CONTACT_OPERATOR_LEGAL_NAME,
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_E164,
  COUNTRY_CODES,
  COUNTRY_CODES_SET,
  DOCUMENT_CONTEXT_REGISTRY,
  GENDERS,
  GENDER_VALUES,
  GENDER_VALUES_SET,
  LANGUAGE_CODES,
  LANGUAGE_CODES_SET,
  OPTIONS_SOURCES,
  PATIENT_FACILITY_AGREEMENT_PATHS,
  PATIENT_FACILITY_AGREEMENT_SCHEMA,
  POLISH_AIRPORT_NAMES,
  SUPPORTED_LANGUAGES,
  VOIVODESHIPS,
  VOIVODESHIP_NAMES,
  flattenSchemaPaths,
  formatContactAddressBlock,
  getAirportPreferenceForField,
  getAirportSearchAliases,
  getContactAddress,
  getContactEmail,
  getContactPhoneDisplay,
  getContextPaths,
  getContextSchema,
  getVoivodeshipName,
  isAllowedAirportInput,
  schemaToSnapshot,
  sortAirportsForPreference
};
